import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { PUBLISH_FOCUS_PARAM, publishStepPath } from "../lib/draftWizard";
import {
  buildGridRange,
  calendarTimeOptionsFor,
  durationForServer,
  friendlyTime12h,
  intervalLabel,
  matchGridInterval,
  occupancyInventory,
  parseTimeMinutes,
  startAvailability,
  timeFromMinutes,
  venueKey,
  type CourtGridItem,
  type CourtGridItemKind,
} from "../lib/scheduleGrid";
import CourtTimeGrid, {
  CourtTimeGridBar,
  type CourtTimeGridCell,
  type CourtTimeGridItemInfo,
} from "../components/CourtTimeGrid";
import {
  api,
  ApiError,
  matchParticipantShortName,
  pendingSideLabel,
  type CourtBlock,
  type DivisionCalendarSettings,
  type JornadaDatesRow,
  type JornadaOverviewResponse,
  type JornadaRestEntry,
  type MatchResultReport,
  type MatchStatus,
  type PlayerReportableSettings,
  type SchedulePublishPreview,
  type Tournament,
  type TournamentFormatConfig,
  type TournamentGroup,
  type TournamentMatch,
  type TournamentMatchHistoryEntry,
  type TournamentDivision,
} from "../lib/api";
import {
  calendarSettingsToSave,
  DEFAULT_CALENDAR_FIELDS,
  divisionCalendarSettings,
  divisionCourtSetup,
  fieldsFromSettings,
  hasCalendarSettings,
  latestDivisionCalendarSettings,
  matchDurationForGrid,
  sameCalendarSettings,
  sanitizeCalendarSettings,
  sortedSplitBases,
  validBlockFieldMinutes,
  type CalendarFieldValues,
  type DivisionCourtType,
} from "../lib/calendarSettings";
import {
  canonicalCourt,
  courtBase,
  courtColumns,
  courtDisplayName,
  courtKey,
  courtsFromCount,
  courtsOverlap,
  courtVisibleIn,
  cleanSplitParts,
  halfCourts,
  halfIndex,
  halfOf,
  isHalfCourt,
  isSplittableBase,
  parseHalfCourt,
  SPLIT_PARTS_OPTIONS,
} from "../lib/courts";
import CalendarDateField from "../components/CalendarDateField";
import TournamentBottomNav from "../components/TournamentBottomNav";
import TournamentSetupProgress from "../components/TournamentSetupProgress";
import TournamentPublishCard from "../components/TournamentPublishCard";
import PlayerTournamentBottomNav from "../components/PlayerTournamentBottomNav";
import { isThirdPlaceLabel, knockoutStageOrder } from "../lib/bracketFromMatches";
import { dayMonthEs, formatDate, formatDateTime, formatLongDateEs } from "../lib/dateFormat";
import { MATCH_STATUS_LABELS, scoreDeciderLabel } from "../lib/labels";
import {
  isActiveCalendarStatus,
  isBulkProgrammableCalendarStatus,
  isScheduleHiddenFromPlayers,
  isSchedulePublished,
  isScheduleUnpublished,
  SCHEDULE_PENDING_LABEL,
  unpublishedByDate,
} from "../lib/matchScheduleStatus";
import {
  hasKnownPlayer,
  horariosText,
  jugadoresText,
  publishConfirmMessage,
  publishedMoveNotice,
  publishSuccessMessage,
  scheduleBarConsequence,
  unpublishedHeadline,
  unpublishedTopSummary,
} from "../lib/schedulePublish";
import {
  calendarSettingsSummary,
  dayChipParts,
  dayTitleEs,
  filterByListDay,
  listDayOptions,
  matchCountByDate,
  partidosText,
  programPendingLabel,
  publishBarIdle,
  tournamentDayChips,
  type ListDayFilter,
  type PublishBarState,
} from "../lib/calendarSection";
import { isPickleballSport } from "../lib/scoreParsing";
import {
  MAX_SCORE_SETS,
  MIN_SCORE_SETS,
  addScoreSet,
  cleanScoreNumber,
  emptyScoreSet,
  removeLastScoreSet,
  scoreDraftFromSets,
  scoreDraftFromText,
  scoreDraftHasAnyValue,
  scoreSetDraftLabel,
  scoreTextFromDraft,
  toggleScoreSetSuperTb,
  updateScoreSet,
  type ScoreDraft,
  type ScoreSetField,
} from "../lib/scoreDraft";
import { escapeHtml, openPrintWindow } from "../lib/printTemplate";
import { DEAD_SIDE_LABEL, deadBracketSides, isDeadSide } from "../lib/bracketDeadSide";
import { jornadaTitleWithRange, savedJornadaRangesByDivision, savedJornadaScreenRange } from "../lib/jornadaDates";
import { restPrintHtml, restScreenLine, restScreenLines, restsForJornada } from "../lib/jornadaRests";
import { uploadAssetHref } from "../lib/assets";
import { divisionAccentStyle } from "../lib/tournamentDivisions";
import {
  DAY_ROLL_PRINT_CSS,
  compareCourtNames,
  dayPendingByDivision,
  dayRollPrintHtml,
  dayRollRows,
  dayRollSlots,
  divisionShortName,
  type DayRollRow,
} from "../lib/dayRoll";
import {
  CONTROL_SHEET_PRINT_CSS,
  controlSheetCategoryLabel,
  controlSheetHtml,
} from "../lib/controlSheet";
import { sortRefereesAZ } from "../lib/referees";
import type { TournamentReferee } from "../lib/api";
import "./referees/controlSheet.css";
import {
  groupNameWithDivision,
  namesInSeveralDivisions,
  repeatedTournamentGroupNames,
  tournamentGroupLabel,
} from "../lib/groupLabels";
import {
  activeRoundRobinPhaseConfig,
  eliminationRoundDisplayLabel,
  isGeneratedRoundRobinPlayoffMatch,
  isRoundRobinPlayoffPhase,
  matchGroupLabel,
  playoffStageLabelsByRound,
  roundRobinPhaseSummary,
} from "../lib/roundRobinPhases";
import { combinedRawLabelDisplay, sortRawLabels } from "../lib/roundLabelAliases";
import {
  esResultadoDeBaja,
  jornadaActualEliminatoria,
  jornadaActualNumero,
  ventanasDePartidos,
  ventanasPorPrioridad,
  ventanasSugeridas,
  type VentanaJornada,
} from "../lib/jornadaActual";
import GuideHelpButton from "../components/GuideHelpButton";
import JornadaDatesPanel from "../components/JornadaDatesPanel";
import FieldHint from "../components/FieldHint";
import { LineCompetitorLines, MatchCompetitorLines } from "../components/MatchCompetitorLines";
import { historyReasonLabel } from "../lib/historyReason";
import { useMediaQuery } from "../lib/useMediaQuery";
import { resultSummaryText } from "../lib/resultSummary";
import { matchScoreFromWinner, scoreTextFromWinner } from "../lib/scoreFromWinner";
import {
  DROP_REVIVE_NOTE,
  dropCancelReason,
  dropMatchLabel,
  dropMatchPillClass,
  dropMatchState,
  isCancelledByDrop,
  nameWithDropMark,
} from "../lib/dropMatch";

const STATUS_LABELS: Record<string, string> = MATCH_STATUS_LABELS;

const STATUS_PILL_CLASS: Record<string, string> = {
  scheduled: "kbd",
  in_progress: "kbd kbd--warning",
  completed: "kbd kbd--ok",
  cancelled: "kbd kbd--danger",
  walkover: "kbd kbd--ok",
  no_show: "kbd kbd--danger",
  defaulted: "kbd kbd--warning",
  retired: "kbd kbd--warning",
  postponed: "kbd kbd--warning",
  double_walkover: "kbd kbd--danger",
};

/** Píldora de estatus del partido. Eduardo 2026-10-05 (torneo 69, baja de
 * Vicky): un partido de alguien dado de baja dice qué pasó con él —
 * «Cancelado por baja», «W.O. por baja» o «Baja de Vicky · pendiente» — y
 * no «Programado» ni «Cancelado» a secas (lib/dropMatch). */
/** «1 cancelado por baja», «2 cancelados por baja», «1 cancelado». */
function cancelledCountText(count: number, byDrop: boolean): string {
  return `${count} ${count === 1 ? "cancelado" : "cancelados"}${byDrop ? " por baja" : ""}`;
}

/** Estado en Resultados (maqueta 2026-10-15): etiquetas en español con
 * mayúscula y del punto de vista de la captura. */
const RESULTS_STATUS_LABELS: Record<string, string> = {
  completed: "Publicado",
  defaulted: "Default (DEF)",
  retired: "Retiro (RET)",
  walkover: "W.O.",
  no_show: "No se presentó",
  cancelled: "Cancelado",
  postponed: "Pospuesto",
  double_walkover: "Doble W.O.",
};

function resultsStatusPill(match: TournamentMatch, hasDraft: boolean): { className: string; label: string } {
  const base = matchStatusPill(match);
  const drop = dropMatchState(match);
  if (drop) {
    const label = drop.kind === "pending" ? "Baja de un jugador" : drop.kind === "cancelled" ? "Cancelado por baja" : "W.O. por baja";
    return { className: base.className, label };
  }
  const label = RESULTS_STATUS_LABELS[match.status];
  if (label) return { className: base.className, label };
  return hasDraft ? { className: "kbd kbd--warning", label: "Sin publicar" } : { className: "kbd", label: "Pendiente" };
}

function matchStatusPill(match: TournamentMatch): { className: string; label: string } {
  const drop = dropMatchState(match);
  if (drop) return { className: dropMatchPillClass(drop), label: dropMatchLabel(drop) };
  return { className: STATUS_PILL_CLASS[match.status] ?? "kbd", label: STATUS_LABELS[match.status] ?? match.status };
}

/** Captura masiva de resultados por layout CSV ("Descargar layout" / "Cargar
 * layout"). Apagada por Eduardo (2026-09-21): confundía junto a la captura
 * normal. Se conserva la lógica; para reactivarla basta poner `true`. */
const SHOW_RESULTS_LAYOUT: boolean = false;

const ACTION_LABELS: Record<string, string> = {
  created: "Creado",
  updated: "Actualizado",
  result_recorded: "Resultado",
  postponed: "Pospuesto",
  cancelled: "Cancelado",
  walkover: "Walkover",
  double_walkover: "Doble W.O.",
  no_show: "No se presentó",
  defaulted: "Default",
  retired: "Retiro",
  players_changed: "Jugadores cambiados",
  deleted: "Eliminado",
  registration_deleted: "Registro eliminado",
  playoffs_auto_blocked: "Playoffs automáticos no generados",
  // «Publicar horarios» (Eduardo 2026-10-01): nunca «Publicar» a secas.
  schedule_published: "Horarios publicados",
};

const RESULT_REPORT_STATUS_LABELS: Record<string, string> = {
  pending: "Pendiente",
  approved: "Aprobado",
  rejected: "Rechazado",
  superseded: "Reemplazado",
};

const RESULT_REPORT_STATUS_CLASS: Record<string, string> = {
  pending: "kbd kbd--warning",
  approved: "kbd kbd--ok",
  rejected: "kbd kbd--danger",
  superseded: "kbd",
};

/** "unpublished" = con horario que los jugadores aún no ven (Publicar horarios). */
type StatusFilter = "all" | "unscheduled" | "unpublished" | "por_jugar" | MatchStatus;
type CalendarOrderMode = "manual" | "random" | "group";
/** Estado del guardado de los ajustes del calendario de la categoría abierta:
 * loaded = ya tenía su bloque guardado; inherited = aún no tiene bloque y se
 * tomaron los últimos usados en el torneo (`from` = de qué categoría);
 * pending = espera turno (`onBlur`: se guarda al salir del campo). */
type CalendarSettingsStatus =
  | { kind: "idle" }
  | { kind: "loaded" }
  | { kind: "inherited"; from: string | null }
  | { kind: "pending"; onBlur?: boolean }
  | { kind: "saving" }
  | { kind: "saved" }
  | { kind: "error"; message: string };
/** Ajustes de una categoría esperando a mandarse al servidor. */
type CalendarQueuedSave = { tournamentId: number; settings: DivisionCalendarSettings };
type ResultTypeInput = "" | "walkover" | "retired" | "defaulted" | "double_walkover";

/** Excepciones que se publican por /walkover (victoria al rival). */
function isExceptionResultType(value: ResultTypeInput): value is "walkover" | "retired" | "defaulted" {
  return value === "walkover" || value === "retired" || value === "defaulted";
}

/** Opciones de «Excepción» (Calendario y hoja del celular). Eduardo
 * 2026-09-16: solo Retiro (el W/O se conserva para partidos ya capturados
 * así); Eduardo 2026-10-13: «Agregar DEF (Default) a la Excepción». */
const RESULT_EXCEPTION_OPTIONS: Array<[ResultTypeInput, string]> = [
  ["", "Sin excepción"],
  ["retired", "Retiro (RET)"],
  ["defaulted", "Default (DEF)"],
];

const BRACKET_ROUND_LABELS = new Set([
  "Preliminar", "Round of 256", "Round of 128", "Round of 64", "Round of 32", "Round of 16",
  "Quarterfinals", "Semifinals", "Final", "3er Lugar",
]);

const LEGACY_NEXT_ROUND = /ganador partido\s+(\d+)\s+vs\s+ganador partido\s+(\d+)/i;
const JORNADA_LABEL = /^(?:semana|jornada|matchups?|j)\s*#?\s*(\d+)/i;

/** Cruce real del cuadro, leído de la fila. Par de `_is_bracket_cross` y
 * `_is_bracket_row` del servidor; las rondas «Playoff N» de Escalera y los
 * amistosos no lo son. `legacyCrossIds`: filas que un playoff legado
 * («ganador partido X vs ganador partido Y») tiene como fuente. */
export function isBracketCross(match: TournamentMatch, legacyCrossIds?: Set<number>): boolean {
  const label = (match.round_label ?? "").trim();
  return (
    BRACKET_ROUND_LABELS.has(label) ||
    label.startsWith("C · ") ||
    /Playoff v2|\[[pc][12]_from=|Destino: partido \d+ · lado [12]/.test(match.notes ?? "") ||
    (JORNADA_LABEL.test(label) && match.group_id == null && LEGACY_NEXT_ROUND.test(match.notes ?? "")) ||
    Boolean(legacyCrossIds?.has(match.id))
  );
}

/** Fuentes de los playoffs legados: «Jornada N» con «ganador partido X vs
 * ganador partido Y» se alimenta de los partidos X y Y de la jornada anterior. */
export function legacyBracketSources(matches: TournamentMatch[]): Set<number> {
  const byKey = new Map<string, TournamentMatch>();
  for (const m of matches) {
    if (m.status !== "cancelled" && m.match_number != null) {
      byKey.set(`${m.division_id ?? ""}|${(m.round_label ?? "").trim().toLowerCase()}|${m.match_number}`, m);
    }
  }
  const ids = new Set<number>();
  for (const m of matches) {
    const legacy = LEGACY_NEXT_ROUND.exec(m.notes ?? "");
    const jornada = JORNADA_LABEL.exec((m.round_label ?? "").trim());
    if (!legacy || !jornada || m.group_id != null || Number(jornada[1]) < 2) continue;
    for (const number of [legacy[1], legacy[2]]) {
      const source = byKey.get(`${m.division_id ?? ""}|jornada ${Number(jornada[1]) - 1}|${Number(number)}`);
      if (source) ids.add(source.id);
    }
  }
  return ids;
}

/** Doble W.O. (Eduardo 2026-10-31): no se presentó ninguno. Solo en un cruce
 * del cuadro con los dos lados definidos y sin resultado; fuera se cancela. */
function canMarkDoubleWalkover(match: TournamentMatch, legacyCrossIds?: Set<number>): boolean {
  return (
    isBracketCross(match, legacyCrossIds) &&
    !match.parent_match_id &&
    match.player1_participant_id != null &&
    match.player2_participant_id != null &&
    match.winner_participant_id == null &&
    !(match.score_text ?? "").trim() &&
    (match.status === "scheduled" || match.status === "postponed")
  );
}

function resultExceptionOptions(match: TournamentMatch, legacyCrossIds?: Set<number>): Array<[ResultTypeInput, string]> {
  return canMarkDoubleWalkover(match, legacyCrossIds)
    ? [...RESULT_EXCEPTION_OPTIONS, ["double_walkover", "Doble W.O."]]
    : RESULT_EXCEPTION_OPTIONS;
}

function addMinutesToTime(value: string, minutes: number): string {
  const [hours, mins] = value.split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(mins)) return value;
  const total = Math.min(hours * 60 + mins + minutes, 23 * 60 + 59);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

// Las opciones de hora (cada 15 min, 6:00 a. m.–11:45 p. m.) viven en
// lib/scheduleGrid: las comparte el Programador de Canchas.

/** Lo que pinta una tarjeta del grid: un partido o un bloqueo de cancha. */
type CalendarGridPayload =
  | { match: TournamentMatch; block: null }
  | { match: null; block: CourtBlock };
type CalendarGridItem = CourtGridItem<CalendarGridPayload>;

interface ResultInputState {
  winner?: number | "";
  score?: string;
  scoreDraft?: ScoreDraft;
}

interface BulkResultLayoutRow {
  matchId: number;
  winnerRaw: string;
  score: string;
  scoreDraft?: ScoreDraft;
  resultType: ResultTypeInput;
  notes: string;
}

const STATUS_FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "Todos" },
  { value: "unscheduled", label: "Sin programar" },
  { value: "scheduled", label: "Programados" },
  { value: "unpublished", label: "Sin publicar" },
  { value: "completed", label: "Terminados" },
  { value: "postponed", label: "Pospuestos" },
  { value: "cancelled", label: "Cancelados" },
  { value: "retired", label: "Retiro" },
];
// Eduardo 2026-09-21: lista acotada. Walkover, default y "no se presentó" ya no
// tienen opción propia: entran en "Terminados" (todo partido ya cerrado).

/** Torneo sin calendario de canchas: «Sin programar», «Programados» y «Sin
 * publicar» no significan nada (no hay horarios); en su lugar, «Por jugar»
 * (igual que el contador de arriba). */
const NO_COURT_STATUS_FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "Todos" },
  { value: "por_jugar", label: "Por jugar" },
  ...STATUS_FILTER_OPTIONS.filter(
    (opt) => opt.value !== "all" && opt.value !== "unscheduled" && opt.value !== "scheduled" && opt.value !== "unpublished",
  ),
];

/** Contrato display-only (gate #136): etapa canónica si el label es una ronda
 * de playoffs; en cualquier otro caso el round_label CRUDO exacto (trim) —
 * nunca reconstruir "Semana N"/"Jornada N" por configuración o audiencia. */
export function canonicalOrRawRoundLabel(
  label: string | null | undefined,
  stageByLabel: Record<string, string>,
  emptyFallback: string,
): string {
  const trimmed = (label ?? "").trim();
  if (trimmed && stageByLabel[trimmed]) return stageByLabel[trimmed];
  return trimmed || emptyFallback;
}

function matchPlayerName(brief: TournamentMatch["player1"]): string {
  if (!brief) return "Bye";
  return brief.display_name ?? `Jugador #${brief.player_profile_id}`;
}

/** Borrador de captura de un partido: el texto guardado manda; sin marcador,
 * tres casillas (Eduardo 2026-09-15: captura genérica) con el tercer set
 * como súper muerte si el formato del torneo lo sugiere. */
function scoreDraftFromMatch(match: TournamentMatch, pickleball: boolean, scoreText?: string, thirdIsSuperTb = false): ScoreDraft {
  if (scoreText !== undefined) return scoreDraftFromText(scoreText, pickleball);
  const draft = scoreDraftFromSets(match.score_sets, match.score_text, pickleball);
  if (draft.sets.some((set) => set.p1 || set.p2)) return draft;
  return { sets: [emptyScoreSet(), emptyScoreSet(), emptyScoreSet(thirdIsSuperTb)] };
}

function isSuperTiebreakAdminFormat(formatConfig: TournamentFormatConfig | null, sport?: string | null): boolean {
  // Pickleball has no super muerte/tiebreak: the 3rd game is a normal game
  // to 11, never prefixed with "TB ". Only tenis/padel use match_sets_format
  // to decide the decider label/prefix.
  if (isPickleballSport(sport)) return false;
  const config = (formatConfig?.config_json ?? {}) as { match_sets_format?: string };
  const format = config.match_sets_format;
  return !format || format === "two_sets_super_tiebreak" || format === "one_set";
}

function adminDeciderLabel(formatConfig: TournamentFormatConfig | null, sport?: string | null): string {
  return scoreDeciderLabel(sport, isSuperTiebreakAdminFormat(formatConfig, sport));
}

function exceptionTypeFromMatch(match: TournamentMatch): ResultTypeInput {
  if (match.status === "double_walkover") return "double_walkover";
  if (match.status === "walkover" || match.status === "no_show") return "walkover";
  if (match.status === "retired") return "retired";
  if (match.status === "defaulted") return "defaulted";
  const normalizedScore = normalizeLayoutHeader(match.score_text ?? "");
  if (["wo", "w_o", "walkover", "no_show", "noshow", "no_se_presento"].includes(normalizedScore)) return "walkover";
  if (["ret", "retiro", "retired"].includes(normalizedScore)) return "retired";
  if (["def", "default", "defaulted"].includes(normalizedScore)) return "defaulted";
  return "";
}

function csvCell(value: string | number | null | undefined): string {
  const text = String(value ?? "");
  if (!/[",\n\r]/.test(text)) return text;
  return `"${text.replace(/"/g, '""')}"`;
}

function splitDelimitedLine(line: string, delimiter: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const next = line[i + 1];
    if (char === '"' && inQuotes && next === '"') {
      current += '"';
      i += 1;
      continue;
    }
    if (char === '"') {
      inQuotes = !inQuotes;
      continue;
    }
    if (char === delimiter && !inQuotes) {
      result.push(current.trim());
      current = "";
      continue;
    }
    current += char;
  }
  result.push(current.trim());
  return result;
}

function normalizeLayoutHeader(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function columnValue(columns: string[], index: number): string {
  return index >= 0 ? columns[index] || "" : "";
}

function findHeaderIndex(headers: string[], aliases: string[]): number {
  return headers.findIndex((header) => aliases.includes(header));
}

function scoreDraftFromColumns(columns: string[], headers: string[], options: { thirdIsSuperTb: boolean }): ScoreDraft | undefined {
  // set_1..set_5 (retador/retado, jugador_1/2, p1/p2); "tb_*" = tercer set
  // como súper muerte. Sin ninguna columna estructurada → undefined.
  const aliasesFor = (n: number, side: 1 | 2): string[] => {
    const who = side === 1 ? ["jugador_1", "retador", "p1", "player1"] : ["jugador_2", "retado", "p2", "player2"];
    return [
      `set_${n}_${who[0]}`, `set${n}_${who[0]}`, `set_${n}_${who[1]}`, `set${n}_${who[1]}`,
      `${who[3]}_set${n}`, `${who[2]}_set${n}`,
    ];
  };
  const tbAliases = (side: 1 | 2): string[] => (
    side === 1 ? ["tb_jugador_1", "tb_retador", "player1_tb", "p1_tb"] : ["tb_jugador_2", "tb_retado", "player2_tb", "p2_tb"]
  );
  const sets: ScoreDraft["sets"] = [];
  let any = false;
  for (let n = 1; n <= MAX_SCORE_SETS; n += 1) {
    let i1 = findHeaderIndex(headers, aliasesFor(n, 1));
    let i2 = findHeaderIndex(headers, aliasesFor(n, 2));
    let superTb = false;
    if (n === 3 && i1 < 0 && i2 < 0) {
      i1 = findHeaderIndex(headers, tbAliases(1));
      i2 = findHeaderIndex(headers, tbAliases(2));
      superTb = i1 >= 0 || i2 >= 0;
    } else if (n === 3) {
      superTb = options.thirdIsSuperTb;
    }
    if (i1 < 0 && i2 < 0) continue;
    any = true;
    sets.push({
      ...emptyScoreSet(superTb),
      p1: cleanScoreNumber(columnValue(columns, i1)),
      p2: cleanScoreNumber(columnValue(columns, i2)),
    });
  }
  if (!any) return undefined;
  while (sets.length < MIN_SCORE_SETS) sets.push(emptyScoreSet());
  return { sets };
}

function parseBulkResultLayout(text: string, sport?: string | null): BulkResultLayoutRow[] {
  const normalizedText = text.replace(/^\uFEFF/, "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const lines = normalizedText.split("\n").filter((line) => line.trim().length > 0);
  if (lines.length === 0) return [];
  const headerLine = lines[0];
  const delimiter = headerLine.includes("\t")
    ? "\t"
    : headerLine.includes(";") && !headerLine.includes(",")
      ? ";"
      : ",";
  const headers = splitDelimitedLine(headerLine, delimiter).map(normalizeLayoutHeader);
  const idIndex = findHeaderIndex(headers, ["partido_id", "id_partido", "match_id"]);
  const winnerIndex = findHeaderIndex(headers, ["ganador", "winner", "ganador_1_o_2"]);
  const scoreIndex = findHeaderIndex(headers, ["marcador", "resultado", "score", "score_text"]);
  const typeIndex = findHeaderIndex(headers, ["excepcion", "excepcion_resultado", "tipo_resultado", "tipo_de_resultado", "tipo", "result_type"]);
  const notesIndex = findHeaderIndex(headers, ["notas", "nota", "observaciones", "notes"]);

  if (idIndex === -1) {
    throw new Error("El layout debe incluir la columna partido_id.");
  }

  return lines.slice(1).flatMap((line, index): BulkResultLayoutRow[] => {
    const columns = splitDelimitedLine(line, delimiter);
    const rawMatchId = columns[idIndex]?.trim() ?? "";
    if (!rawMatchId) return [];
    const matchId = Number(rawMatchId);
    if (!Number.isInteger(matchId) || matchId <= 0) {
      throw new Error(`Fila ${index + 2}: partido_id inválido.`);
    }
    const rawType = normalizeLayoutHeader(typeIndex >= 0 ? columns[typeIndex] || "" : "");
    const scoreDraft = scoreDraftFromColumns(columns, headers, {
      thirdIsSuperTb: !isPickleballSport(sport) && rawType !== "set_3" && rawType !== "set3",
    });
    const structuredScore = scoreDraft ? scoreTextFromDraft(scoreDraft) : "";
    const resultType: ResultTypeInput =
      rawType === "wo" || rawType === "w_o" || rawType === "walkover" || rawType === "no_show" || rawType === "noshow" || rawType === "no_se_presento"
        ? "walkover"
        : rawType === "ret" || rawType === "retiro" || rawType === "retired"
          ? "retired"
          : rawType === "def" || rawType === "default" || rawType === "defaulted"
            ? "defaulted"
            : "";
    return [
      {
        matchId,
        winnerRaw: winnerIndex >= 0 ? columns[winnerIndex] || "" : "",
        score: structuredScore || (scoreIndex >= 0 ? columns[scoreIndex] || "" : ""),
        scoreDraft,
        resultType,
        notes: notesIndex >= 0 ? columns[notesIndex] || "" : "",
      },
    ];
  });
}

function isUnscheduled(match: TournamentMatch): boolean {
  return !match.scheduled_date || !match.scheduled_time;
}

function isPlayedStatus(match: TournamentMatch): boolean {
  return ["completed", "walkover", "no_show", "defaulted", "retired", "double_walkover"].includes(String(match.status));
}

/** Etiqueta corta del partido ya jugado en la cuadrícula (Eduardo 2026-10-26). */
function playedChipLabel(match: TournamentMatch): string {
  switch (match.status) {
    case "walkover":
      return "W.O.";
    case "double_walkover":
      return "Doble W.O.";
    case "retired":
      return "RET";
    case "no_show":
      return "No se presentó";
    case "defaulted":
      return "Default";
    default:
      return "Con resultado";
  }
}

function isProgrammedStatus(match: TournamentMatch): boolean {
  return isActiveCalendarStatus(match.status);
}

function cleanReportNotes(notes: string | null | undefined): string | null {
  const text = notes?.trim();
  if (!text) return null;
  return /^legacy\b/i.test(text) ? null : text;
}

function todayIso(): string {
  return localDateIso(new Date());
}

/** "2026-10-04" válido de la URL (?fecha=) o null. */
function isoDateFromQuery(value: string | null): string | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const parsed = parseLocalDate(value);
  return parsed && localDateIso(parsed) === value ? value : null;
}

function localDateIso(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function parseLocalDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const [year, month, day] = value.slice(0, 10).split("-").map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  return new Date(year, month - 1, day);
}

/** Torneo y categoría de lo que está en pantalla (la jornada con la que abre
 * el Calendario se elige una vez por cada uno). */
function calendarScopeKey(tournamentId: number, divisionId: number | null): string {
  return `${tournamentId}|${divisionId ?? "todas"}`;
}

function formatTimeLabel(value: string): string {
  const [h, m] = value.split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return value;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}


/** `short`: nombres cortos («Ana / Beto» en dobles) para tarjetas angostas
 * (mini canchas). */
/** Los dos lados del partido con nombre completo (o «Por definir» / «Bye»). */
function matchSideNames(match: TournamentMatch, options: { short?: boolean } = {}): [string, string] {
  // Placeholder del esqueleto (sin resultado): el lado vacío es una línea del
  // cuadro por definir, no un BYE.
  const isPendingSlot = !match.winner_participant_id && !match.score_text;
  // Quien se dio de baja sigue en el renglón, marcado «… · baja».
  const side = (brief: TournamentMatch["player1"], sideNumber: 1 | 2) =>
    brief
      ? nameWithDropMark(options.short ? matchParticipantShortName(brief) : matchPlayerName(brief), brief)
      : isPendingSlot
        ? pendingSideLabel(match.notes, sideNumber)
        : "Bye";
  return [side(match.player1, 1), side(match.player2, 2)];
}

/** Vista del calendario recordada por usuario (Eduardo 2026-10-25). */
function calendarViewStorageKey(userId: number | null | undefined): string {
  return `t123-calendario-vista:${userId ?? "anon"}`;
}

function readStoredCalendarView(userId: number | null | undefined): "grid" | "list" | null {
  try {
    const value = window.localStorage.getItem(calendarViewStorageKey(userId));
    return value === "grid" || value === "list" ? value : null;
  } catch {
    return null;
  }
}

function storeCalendarView(userId: number | null | undefined, view: "grid" | "list") {
  try {
    window.localStorage.setItem(calendarViewStorageKey(userId), view);
  } catch {
    // Sin almacenamiento (modo privado): solo no se recuerda.
  }
}

function matchShortLabel(match: TournamentMatch, options: { short?: boolean } = {}): string {
  const isPendingSlot = !match.winner_participant_id && !match.score_text;
  const [side1, side2] = matchSideNames(match, options);
  const label = `${side1} vs ${side2}`;
  // Con algún lado "Por definir", el número de partido es la referencia
  // cruzable contra el draw (pedido Eduardo 2026-07-28: saber QUÉ partido
  // es el "Por definir vs Por definir" del calendario).
  if (isPendingSlot && (!match.player1 || !match.player2) && match.match_number) {
    return `M${match.match_number} · ${label}`;
  }
  return label;
}

function reportPriority(report: MatchResultReport): number {
  if (report.status === "pending") return 0;
  if (report.status === "approved") return 1;
  if (report.status === "rejected") return 2;
  return 3;
}

function reportTimestamp(report: MatchResultReport): string {
  return report.updated_at ?? report.created_at ?? "";
}

function chooseVisibleReport(
  current: MatchResultReport | undefined,
  candidate: MatchResultReport,
): MatchResultReport {
  if (!current) return candidate;
  const priorityDiff = reportPriority(candidate) - reportPriority(current);
  if (priorityDiff < 0) return candidate;
  if (priorityDiff > 0) return current;
  return reportTimestamp(candidate).localeCompare(reportTimestamp(current)) > 0
    ? candidate
    : current;
}

function reportMapKey(matchId: number, participantId: number): string {
  return `${matchId}:${participantId}`;
}


function seededOrderValue(matchId: number, seed: number): number {
  const x = Math.sin(matchId * 10007 + seed * 7919) * 10000;
  return x - Math.floor(x);
}

function matchGroupSortValue(match: TournamentMatch): number {
  return match.group_id ?? Number.MAX_SAFE_INTEGER;
}

function sortMatchesForCalendarOrder(
  matches: TournamentMatch[],
  mode: CalendarOrderMode,
  randomSeed: number,
  groups: TournamentGroup[],
): TournamentMatch[] {
  const groupOrder = new Map(groups.map((g) => [g.id, g.sort_order]));
  const ordered = [...matches];
  if (mode === "random") {
    return ordered.sort(
      (a, b) =>
        seededOrderValue(a.id, randomSeed) - seededOrderValue(b.id, randomSeed) ||
        a.id - b.id,
    );
  }
  if (mode === "group") {
    return ordered.sort((a, b) => {
      const groupA = groupOrder.get(matchGroupSortValue(a)) ?? matchGroupSortValue(a);
      const groupB = groupOrder.get(matchGroupSortValue(b)) ?? matchGroupSortValue(b);
      return (
        groupA - groupB ||
        matchSortKey(a)[0] - matchSortKey(b)[0] ||
        matchSortKey(a)[1] - matchSortKey(b)[1]
      );
    });
  }
  return ordered.sort(
    (a, b) =>
      matchSortKey(a)[0] - matchSortKey(b)[0] ||
      matchSortKey(a)[1] - matchSortKey(b)[1],
  );
}

// Rol de Juegos is now driven exclusively by Jornadas. "Semana N" matches
// belong to the legacy import and must not appear in ladder admin operations.
function parseRoundNumber(label: string | null | undefined): number | null {
  if (!label) return null;
  const m = /^\s*(?:semana|jornada|matchups?|j)\s*#?\s*(\d+)/i.exec(label);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Mismas anchuras en todas las tablas del PDF (secciones alineadas entre sí). */
const PRINT_TABLE_CSS = `
    .print-sheet table { table-layout: fixed; }
    .print-sheet th, .print-sheet td { overflow: hidden; text-overflow: ellipsis; }
    .print-sheet tr.print-line-row td { font-size: 0.86em; color: #3f4a3a; background: #fafcf6; }
    .print-sheet tr.print-line-row td.print-line-label { padding-left: 16px; }
`;

/** Motivos frecuentes de un cambio de horario (Eduardo 2026-09-17). */
const SCHEDULE_CHANGE_REASONS = ["Lluvia", "Retraso", "Cancha no disponible", "A petición de jugadores"];

const PRINT_LINE_KIND_LABELS: Record<string, string> = { singles: "Singles", doubles: "Dobles", mixed: "Mixto" };

/** "Serie 3" cuando el partido es un enfrentamiento por equipos (Mini Copa);
 * null en un partido normal. Eduardo 2026-09-17: que se lea "Serie" en
 * Calendario y en los PDF. */
export function tieSeriesLabel(match: TournamentMatch): string | null {
  if (match.match_kind !== "tie") return null;
  return match.match_number ? `Serie ${match.match_number}` : "Serie";
}

/** Líneas de una serie para el PDF: "L1 · Singles — Ana vs Beto". Sin
 * alineación capturada se dice explícitamente, no se deja en blanco. */
export function printableTieLines(match: TournamentMatch): Array<{ line: TournamentMatch; label: string }> {
  if (match.match_kind !== "tie") return [];
  return (match.lines ?? []).map((line, index) => {
    const names = (side: "team1" | "team2") =>
      (line.line_players?.[side] ?? []).map((player) => player.name?.trim()).filter(Boolean).join(" / ");
    const team1 = names("team1");
    const team2 = names("team2");
    const kind = PRINT_LINE_KIND_LABELS[line.line_kind ?? ""] ?? line.line_kind ?? "Línea";
    const players = team1 || team2 ? `${team1 || "Por definir"} vs ${team2 || "Por definir"}` : "Alineación por definir";
    return { line, label: `L${index + 1} · ${kind} — ${players}` };
  });
}

/** Nombres del lado ganador de una línea (la línea guarda como ganador al
 * EQUIPO; en el PDF se lee mejor quién jugó). */
export function printableLineWinner(line: TournamentMatch, parent: TournamentMatch): string {
  if (line.winner_participant_id == null) return "Por definir";
  const side = line.winner_participant_id === parent.player1_participant_id ? "team1" : "team2";
  const names = (line.line_players?.[side] ?? []).map((player) => player.name?.trim()).filter(Boolean).join(" / ");
  return names || line.winner?.display_name || "Por definir";
}

function isSemanaLabel(label: string | null | undefined): boolean {
  if (!label) return false;
  return /^\s*semana\b/i.test(label);
}

// Strict positive match for ladder Rol de Juegos: only labels that start
// with "Jornada" or the short "J" form followed by a number qualify. Any
// other label (Semana N, NULL, free text) is hidden on the ladder screen.
function isJornadaLabel(label: string | null | undefined): boolean {
  if (!label) return false;
  return /^\s*(?:jornada|j)\s*#?\s*\d+/i.test(label);
}

const LINE_KIND_LABELS: Record<string, string> = { singles: "Singles", doubles: "Dobles", mixed: "Mixto" };

type JornadaFilter = number | "all" | "none" | `label:${string}`;

/** Valor de <option> ⇄ JornadaFilter (el select de Resultados, Eduardo
 * 2026-09-05: "el dropdown para filtrar por jornada se ve mucho mejor"). */
function jornadaFilterValue(opt: JornadaFilter): string {
  return typeof opt === "number" ? `n:${opt}` : opt;
}

function parseJornadaFilterValue(value: string): JornadaFilter {
  if (value.startsWith("n:")) {
    const parsed = Number(value.slice(2));
    return Number.isFinite(parsed) ? parsed : "all";
  }
  if (value === "all" || value === "none" || value.startsWith("label:")) return value as JornadaFilter;
  return "all";
}

/** Partidos VISIBLES del Rol de Juegos. Ladder: solo "Jornada N" (o marcados
 * de playoffs) — "Semana N" legacy, NULL y texto libre se ocultan como defensa
 * en profundidad si el filtro del backend se brincara. No-ladder: se ocultan
 * las filas legacy "Semana N" importadas para que no dupliquen el planner.
 * playerMode muestra todo (el backend ya filtra su payload). Este MISMO scope
 * alimenta el filtro, el chip, los totales, el título Y el PDF. */
export function visibleScheduleMatches(
  matches: TournamentMatch[],
  options: { isLadder: boolean; playerMode: boolean },
): TournamentMatch[] {
  if (options.playerMode) return matches;
  if (options.isLadder) {
    return matches.filter(
      (m) =>
        isJornadaLabel(m.round_label) ||
        /playoff/i.test(`${m.round_label ?? ""} ${m.notes ?? ""}`),
    );
  }
  return matches.filter((m) => !isSemanaLabel(m.round_label));
}

export function countMatchesMissingStructuredScores(matches: TournamentMatch[]): number {
  return matches.filter((match) => {
    // Las series padre de Juego por Equipos guardan score_text global (2-1, 3-0)
    // calculado desde sus líneas. No son marcadores por sets y no deben exigir
    // score_sets estructurado; de lo contrario Minicopa mostraba 7 falsos avisos.
    if (match.match_kind === "tie" || match.status === "double_walkover") return false;
    const scoreText = match.score_text?.trim();
    if (!scoreText) return false;
    return !["BYE", "W/O", "WO", "DEF", "RET"].includes(scoreText.toUpperCase()) &&
      (match.score_sets?.length ?? 0) === 0;
  }).length;
}

const ELIMINATION_ROUND_ORDER = new Map<string, number>([
  ["Preliminar", 0],
  ["Round of 128", 1],
  ["Round of 64", 2],
  ["Round of 32", 3],
  ["Round of 16", 4],
  ["Quarterfinals", 5],
  ["Semifinals", 6],
  ["Final", 7],
  ["3er Lugar", 8],
]);

function eliminationRoundOrder(label: string | null | undefined): number | null {
  if (!label) return null;
  const direct = ELIMINATION_ROUND_ORDER.get(label.trim());
  if (direct !== undefined) return direct;
  const m = /^\s*Round of\s+(\d+)\s*$/i.exec(label);
  if (!m) return null;
  const size = Number(m[1]);
  if (!Number.isFinite(size) || size <= 0) return null;
  return 100 - size;
}

function roundLabelFilterValue(label: string): `label:${string}` {
  return `label:${label}`;
}

function roundLabelFromFilter(filter: JornadaFilter): string | null {
  return typeof filter === "string" && filter.startsWith("label:")
    ? filter.slice("label:".length)
    : null;
}

/** Raw labels REALES de un filtro de jornada: el label exacto de los filtros
 * `label:` (eliminación) o TODOS los round_labels distintos cuyo número
 * coincide (el valor numérico es mecánica interna de auto-selección por
 * calendario/resultados), ordenados de forma determinista. Un mismo número
 * puede mezclar aliases legados ("Semana 4" y "Jornada 4") y NINGUNO se
 * descarta en silencio. Devuelve [] SOLO cuando ningún match conserva raw
 * para ese número — únicamente entonces el caller puede construir
 * "${singular} N" (gate #136: el label construido es último recurso). */
export function jornadaFilterRawLabels(
  filter: JornadaFilter,
  matches: TournamentMatch[],
): string[] {
  if (filter === "all" || filter === "none") return [];
  const exact = roundLabelFromFilter(filter);
  if (exact !== null) {
    const trimmed = exact.trim();
    return trimmed ? [trimmed] : [];
  }
  const raws = new Set<string>();
  for (const m of matches) {
    if (parseRoundNumber(m.round_label) === filter) {
      const raw = (m.round_label ?? "").trim();
      if (raw) raws.add(raw);
    }
  }
  return sortRawLabels(raws);
}

/** Display fail-safe del filtro de jornada (chip del filtro Y título/subtítulo
 * del PDF — MISMA resolución): cada raw pasa por la resolución de display
 * (etapa canónica o crudo exacto) y se deduplica — si la etapa cubre a TODOS
 * los aliases queda solo la canónica; si no, el display combinado
 * determinista "Jornada 4 · Semana 4" (ordenado). null sin raw genuino. */
export function jornadaFilterDisplayLabel(
  filter: JornadaFilter,
  matches: TournamentMatch[],
  displayRoundLabel: (label: string) => string,
): string | null {
  const raws = jornadaFilterRawLabels(filter, matches);
  if (raws.length === 0) return null;
  // Regla fail-safe (gate #136, 7º re-gate): un solo raw → su display; varios
  // raws con el MISMO display → esa canónica común; displays DISTINTOS (p.ej.
  // "Semifinal" vs "Final" entre aliases) → los raws combinados — nunca
  // concatenar etapas incompatibles ("Final · Semifinal").
  if (raws.length === 1) return displayRoundLabel(raws[0]);
  const displays = [...new Set(raws.map(displayRoundLabel))];
  if (displays.length === 1) return displays[0];
  return combinedRawLabelDisplay(raws);
}

/** ¿El partido pertenece a la jornada del filtro? (número o etiqueta exacta;
 * "all"/"none" no restringen aquí). Compartido por la tabla y el PDF. */
export function matchInJornadaFilter(match: TournamentMatch, filter: JornadaFilter): boolean {
  if (filter === "all" || filter === "none") return true;
  const exactRoundLabel = roundLabelFromFilter(filter);
  if (exactRoundLabel !== null) return (match.round_label ?? "") === exactRoundLabel;
  return parseRoundNumber(match.round_label) === filter;
}

/** Filas candidatas al PDF: SIEMPRE desde los partidos VISIBLES (el mismo
 * scope que el filtro, el chip, los totales y el título), nunca desde la
 * lista completa — una fila legacy oculta en la UI no puede reaparecer en el
 * PDF (gate #136, 6º re-gate). */
export function printableScheduleRows(
  visibleMatches: TournamentMatch[],
  scope: "all" | "selected",
  filter: JornadaFilter,
): TournamentMatch[] {
  if (scope !== "selected") return [...visibleMatches];
  return visibleMatches.filter((match) => matchInJornadaFilter(match, filter));
}

function matchSortKey(m: TournamentMatch): [number, number] {
  return [m.match_number ?? Number.MAX_SAFE_INTEGER, m.id];
}

interface TournamentSchedulePageProps {
  resultsOnly?: boolean;
  readOnly?: boolean;
  playerMode?: boolean;
  /** Paso 10 del wizard (opcional): misma página con el stepper arriba. */
  setupMode?: boolean;
}

export default function AdminTournamentSchedulePage({
  resultsOnly = false,
  readOnly = false,
  playerMode = false,
  setupMode = false,
}: TournamentSchedulePageProps = {}) {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tournamentId = id ? Number(id) : NaN;
  const divisionFromQuery = searchParams.get("division");
  const initialDivisionId = divisionFromQuery && Number.isInteger(Number(divisionFromQuery))
    ? Number(divisionFromQuery)
    : null;
  const { token, hasRole, user: authUser } = useAuth();
  const authUserId = authUser?.id ?? null;

  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [formatConfig, setFormatConfig] = useState<TournamentFormatConfig | null>(null);
  const [groups, setGroups] = useState<TournamentGroup[]>([]);
  const [divisions, setDivisions] = useState<TournamentDivision[]>([]);
  const [selectedDivisionId, setSelectedDivisionId] = useState<number | null>(initialDivisionId);
  // Las preguntas son del torneo y la categoría a la vista: salir o cambiar
  // de uno u otro las retira y contestan «no» (extensión del PR #601).
  const { askConfirm, askChoice, currentSignal } = useAbortableConfirm([tournamentId, selectedDivisionId]);
  const [matches, setMatches] = useState<TournamentMatch[]>([]);
  const legacyCrossIds = useMemo(() => legacyBracketSources(matches), [matches]);
  const printDeadSides = useMemo(() => deadBracketSides(matches), [matches]);
  // Partidos de OTRAS categorías del mismo torneo (solo con ?division=):
  // el grid los pinta como ocupación informativa (pedido Eduardo 2026-07-28).
  const [crossDivisionMatches, setCrossDivisionMatches] = useState<TournamentMatch[]>([]);
  const [resultReports, setResultReports] = useState<MatchResultReport[]>([]);
  const [history, setHistory] = useState<TournamentMatchHistoryEntry[]>([]);
  const [myParticipantId, setMyParticipantId] = useState<number | null>(null);
  const [playerTournamentSettings, setPlayerTournamentSettings] =
    useState<PlayerReportableSettings | null>(null);
  // «¿Este torneo usa calendario de canchas?» (Eduardo 2026-10-03): se decide
  // solo aquí, en Calendario (antes se repetía en Formato, Comunicación y
  // Configuración). null = aún no se sabe: se ve como «Sí» mientras carga.
  const [usesCourtCalendar, setUsesCourtCalendar] = useState<boolean | null>(null);
  const [courtCalendarSaving, setCourtCalendarSaving] = useState(false);
  const [courtCalendarStatus, setCourtCalendarStatus] = useState<{ kind: "saved" | "error"; text: string } | null>(
    null,
  );
  // «Tipo de cancha: Normal / Mini cancha» de la categoría (en «Ajustes de
  // esta categoría»; antes en Formato): lo elegido mientras el servidor
  // confirma y el aviso de guardado / no se pudo.
  const [courtTypeSaving, setCourtTypeSaving] = useState<{ divisionId: number; next: DivisionCourtType } | null>(null);
  const [courtTypeStatus, setCourtTypeStatus] = useState<
    { divisionId: number; kind: "saved" | "error"; text: string } | null
  >(null);
  const [loading, setLoading] = useState(true);
  // El spinner de página completa solo aplica a la PRIMERA carga: en las
  // recargas se conserva el DOM para no desmontar botones bajo el cursor
  // (QA Cowork #12: el remonte se tragaba el primer click).
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [filterGroupId, setFilterGroupId] = useState<number | "all" | "none" | "playoffs">(
    "all",
  );
  const [filterStatus, setFilterStatus] = useState<StatusFilter>("all");
  const [filterJornada, setFilterJornada] = useState<JornadaFilter>("all");
  const [didSelectCurrentJornada, setDidSelectCurrentJornada] = useState(false);
  const [didSelectScheduleRound, setDidSelectScheduleRound] = useState(false);
  // Calendario del organizador: la jornada con la que abre cada categoría
  // (sin canchas, `currentJornada`; con canchas, «Todas»). Se elige una vez
  // por torneo y categoría, con sus
  // partidos ya cargados y, sin calendario de canchas, con las fechas de cada
  // jornada; un clic en el selector también cuenta como elegida.
  const [scheduleLoadedScope, setScheduleLoadedScope] = useState<string | null>(null);
  // Fechas de cada jornada (sin calendario de canchas): las guardadas y la
  // sugerencia del formato de cada categoría, como las da el servidor.
  const [savedJornadaWindows, setSavedJornadaWindows] = useState<{
    scope: string;
    guardadas: VentanaJornada[];
    sugeridas: VentanaJornada[];
  } | null>(null);
  // Eduardo 2026-10-05: fechas GUARDADAS de cada jornada y quién descansa
  // (todas las categorías, con o sin calendario de canchas) para los PDF y
  // las listas por jornada. Se vuelve a leer al cambiar los partidos o al
  // guardar fechas en «Fechas de cada jornada».
  const [jornadaOverview, setJornadaOverview] = useState<{
    tournamentId: number;
    data: JornadaOverviewResponse["data"];
  } | null>(null);
  const [jornadaOverviewReload, setJornadaOverviewReload] = useState(0);
  const [calendarJornadaChosenFor, setCalendarJornadaChosenFor] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [playerDateFilter, setPlayerDateFilter] = useState("");

  // La Fecha es del torneo (se queda al cambiar de categoría); Desde, Último
  // inicio, Bloque min y #Canchas son de CADA categoría (se cargan al abrirla
  // y se guardan para ella al cambiarlos, ver "ajustes por categoría").
  // ?fecha=YYYY-MM-DD (aviso «sin publicar» de Canchas): abre ese día.
  const [calendarDate, setCalendarDate] = useState(() => isoDateFromQuery(searchParams.get("fecha")) ?? todayIso());
  const [calendarStartTime, setCalendarStartTime] = useState(DEFAULT_CALENDAR_FIELDS.startTime);
  const [calendarEndTime, setCalendarEndTime] = useState(DEFAULT_CALENDAR_FIELDS.endTime);
  const [calendarInterval, setCalendarInterval] = useState(DEFAULT_CALENDAR_FIELDS.blockMinutes);
  const [calendarCourtCount, setCalendarCourtCount] = useState(DEFAULT_CALENDAR_FIELDS.courtCount);
  // Lo guardado en esta pestaña por categoría (manda sobre la lista de
  // categorías, que puede venir de antes de guardar).
  const [calendarSettingsSaved, setCalendarSettingsSaved] = useState<Record<number, DivisionCalendarSettings>>({});
  const [calendarSettingsStatus, setCalendarSettingsStatus] = useState<CalendarSettingsStatus>({ kind: "idle" });
  // El Bloque min en pantalla es de la categoría abierta (lo tenía guardado o
  // el admin lo cambió o lo confirmó en ella). Si no, vino de otra categoría
  // (`calendarInheritedFrom`) y no se le guarda solo (revisión 2026-10-01).
  const [calendarBlockOwn, setCalendarBlockOwn] = useState(true);
  const [calendarInheritedFrom, setCalendarInheritedFrom] = useState<string | null>(null);
  // Multi-sede (2/2 de sedes): sede activa del calendario; null = principal.
  const [calendarVenueName, setCalendarVenueName] = useState<string | null>(null);
  const [calendarMode, setCalendarMode] = useState<"programar" | "bloquear">("programar");
  const [calendarBlockReason, setCalendarBlockReason] = useState("Mantenimiento");
  const [calendarBlocks, setCalendarBlocks] = useState<CourtBlock[]>([]);
  // Partidos de TODO el club en la fecha (otros torneos con la misma sede):
  // solo lectura, ocupan cancha en el grid (Eduardo 2026-10-01).
  const [calendarClubMatches, setCalendarClubMatches] = useState<TournamentMatch[]>([]);
  // Se incrementa para volver a pedir bloqueos y partidos del club.
  const [calendarClubVersion, setCalendarClubVersion] = useState(0);
  const [calendarOrderMode, setCalendarOrderMode] = useState<CalendarOrderMode>("manual");
  const [calendarRandomSeed, setCalendarRandomSeed] = useState(1);
  const [selectedMatchId, setSelectedMatchId] = useState<number | null>(null);
  // Bloqueo tocado en la cuadrícula (Eduardo 2026-10-25): se abre en el panel
  // derecho con «Desbloquear». Se limpia al cambiar día, categoría, sede o modo.
  const [selectedBlockId, setSelectedBlockId] = useState<number | null>(null);
  // Partido ya jugado tocado en la cuadrícula: solo se lee (panel con su
  // resultado y la liga a Resultados); no se mueve.
  const [selectedPlayedId, setSelectedPlayedId] = useState<number | null>(null);
  const blockScopeSeqRef = useRef(0);
  // Motivo del cambio al mover/posponer un partido ya programado.
  const [scheduleChangeReason, setScheduleChangeReason] = useState("");
  const calendarDisclosureRef = useRef<HTMLElement | null>(null);
  // Revisión #418: tocar una hora del grid solo PROPONE el inicio; se guarda
  // (y se avisa a los jugadores) hasta Confirmar en la barra del grid.
  const [calendarProposal, setCalendarProposal] = useState<{ court: string; time: string } | null>(null);
  // Panel «Cambiar horario / cancha» (Eduardo 2026-10-15): la hora o la
  // cancha elegida en los desplegables mientras falta la otra.
  const [panelSlotDraft, setPanelSlotDraft] = useState<{ time: string; court: string }>({ time: "", court: "" });
  // «Bloquear» por rango (Eduardo 2026-10-15): Desde / Hasta, todo el día y
  // una o varias canchas. Crea los mismos bloqueos que tocar celdas.
  const [blockRange, setBlockRange] = useState<{ from: string; to: string; allDay: boolean; courts: string[] }>({
    from: "",
    to: "",
    allDay: false,
    courts: [],
  });
  // Explicación del último toque en una celda/tarjeta (aviso en la barra,
  // no un error en rojo arriba de la página).
  const [calendarNotice, setCalendarNotice] = useState<string | null>(null);
  // Orden de rondas (Eduardo 2026-10-03): al Confirmar, el servidor avisó que
  // el partido empezaría antes de que terminen sus partidos previos (409
  // `round_order_conflict`). Vale solo para ESA propuesta (partido, día,
  // cancha y hora): la barra lo explica y ofrece «Confirmar de todos modos».
  const [calendarRoundOrderWarning, setCalendarRoundOrderWarning] = useState<{
    matchId: number;
    date: string;
    court: string;
    time: string;
    message: string;
    afterFeeders: boolean;
  } | null>(null);
  // Los que «Ordenar automático» dejó sin programar por esperar a sus
  // partidos previos, con el motivo (aviso fijo en la tarjeta, no un toast).
  const [calendarWaitingPrevious, setCalendarWaitingPrevious] = useState<string[]>([]);
  const calendarConfirmRef = useRef<HTMLButtonElement | null>(null);
  const calendarFocusConfirmRef = useRef(false);
  // Mini canchas (Eduardo 2026-10-01): canchas divididas recién marcadas,
  // mientras el servidor confirma ("categoría|sede" → bases). Si falla, se
  // regresa la casilla.
  const [calendarSplitOverrides, setCalendarSplitOverrides] = useState<Record<string, string[]>>({});
  // En cuántas mini canchas (2, 3 o 4) se parte cada cancha, recién elegido
  // mientras el servidor confirma ("categoría|sede" → {base: partes}).
  const [calendarPartsOverrides, setCalendarPartsOverrides] = useState<Record<string, Record<string, number>>>({});
  const [calendarSplitSaving, setCalendarSplitSaving] = useState(false);
  const [calendarSplitStatus, setCalendarSplitStatus] = useState<
    { divisionId: number; kind: "saving" | "saved" | "error"; message?: string } | null
  >(null);
  useEffect(() => {
    setScheduleChangeReason("");
  }, [selectedMatchId]);
  useEffect(() => {
    setCalendarProposal(null);
    setCalendarNotice(null);
    setCalendarRoundOrderWarning(null);
    setPanelSlotDraft({ time: "", court: "" });
  }, [selectedMatchId, calendarMode, calendarDate, calendarVenueName]);
  useEffect(() => {
    // Otro día/categoría/sede/modo: el bloqueo elegido ya no aplica y una
    // respuesta de «Desbloquear» en vuelo ya no debe pintar nada.
    blockScopeSeqRef.current += 1;
    setSelectedBlockId(null);
    setSelectedPlayedId(null);
  }, [calendarMode, calendarDate, calendarVenueName, selectedDivisionId]);
  useEffect(() => {
    if (selectedMatchId !== null) {
      setSelectedBlockId(null);
      setSelectedPlayedId(null);
    }
  }, [selectedMatchId]);
  useEffect(() => {
    // La sesión llega después del primer render: se lee lo recordado.
    const stored = readStoredCalendarView(authUserId);
    if (stored) setCalendarDayView(stored);
  }, [authUserId]);
  useEffect(() => {
    setCalendarMobileCourt(null);
  }, [selectedDivisionId, calendarVenueName]);
  useEffect(() => {
    // Otra hora o cancha propuesta: el aviso de orden de rondas ya no aplica
    // (se vuelve a preguntar al servidor con «Confirmar»).
    setCalendarRoundOrderWarning(null);
  }, [calendarProposal]);
  useEffect(() => {
    setCalendarWaitingPrevious([]);
  }, [calendarDate, selectedDivisionId]);
  // Categoría y día a la vista AHORA (no los que un clic dejó capturados):
  // «Quedaron sin programar» es de la categoría y el día que se ordenaron. Si
  // el admin cambia mientras «Ordenar automático» espera al servidor, la
  // lista vieja no se pinta bajo la nueva (mismo arreglo que la recarga de
  // la categoría a la vista).
  const calendarWaitingScopeRef = useRef({ divisionId: selectedDivisionId, date: calendarDate });
  useLayoutEffect(() => {
    calendarWaitingScopeRef.current = { divisionId: selectedDivisionId, date: calendarDate };
  }, [selectedDivisionId, calendarDate]);
  useEffect(() => {
    if (!calendarProposal || !calendarFocusConfirmRef.current) return;
    // Con teclado (Enter en la celda) el foco pasa a Confirmar.
    calendarFocusConfirmRef.current = false;
    calendarConfirmRef.current?.focus({ preventScroll: true });
  }, [calendarProposal]);
  const [calendarBusy, setCalendarBusy] = useState(false);
  // «Publicar horarios» (Eduardo 2026-10-01). Lo que el servidor contestó al
  // intentarlo: el candado del jugador (409: aún no ven el calendario) o que
  // este usuario no publica (403: capturista). El perfil en el torneo se pide
  // siempre que se vea la barra del día (para no ofrecerle el botón ni la
  // liga a publicar el torneo a un capturista o a finanzas).
  const [publishLockMessage, setPublishLockMessage] = useState<string | null>(null);
  const [publishForbidden, setPublishForbidden] = useState(false);
  const [myTournamentRole, setMyTournamentRole] = useState<string | null | undefined>(undefined);
  // Árbitros (Eduardo 2026-10-16): los del torneo (A→Z) para la tarjeta y el
  // panel del partido, y la «🖨 Hoja de control» del día.
  const [tournamentReferees, setTournamentReferees] = useState<TournamentReferee[]>([]);
  const [chiefReferees, setChiefReferees] = useState<Record<string, string>>({});
  const [controlSheetOpen, setControlSheetOpen] = useState(false);
  const [controlSheetDivision, setControlSheetDivision] = useState<string>("all");
  const [controlSheetShowReferee, setControlSheetShowReferee] = useState(true);
  const [controlSheetChief, setControlSheetChief] = useState("");
  // Motivo (opcional) del aviso de «Desprogramar el día» a los ya publicados.
  const [bulkUnscheduleReason, setBulkUnscheduleReason] = useState("");
  // Sección del calendario (Eduardo 2026-10-02): «Desprogramar el día…» abre
  // su paso de confirmación (con los motivos) dentro de «Ordenar automático»;
  // «Ajustes de esta categoría» va en una línea con «Editar» (abierto solo
  // cuando la categoría no tiene bloque guardado); «Partidos de la jornada»
  // se filtra por día.
  const [bulkUnscheduleConfirmOpen, setBulkUnscheduleConfirmOpen] = useState(false);
  const [calendarSettingsOpen, setCalendarSettingsOpen] = useState(false);
  const [listDay, setListDay] = useState<ListDayFilter>("all");
  // El día completo con todas las categorías (Alex Zayat vía Eduardo,
  // 2026-10-02: «si quieres ver el calendario por día de un torneo completo
  // con todas las categorías»). «Esta categoría» / «Todas las categorías»
  // arriba del calendario (?vista=todas la recuerda en la liga); «Cuadrícula»
  // / «Lista» (null = sola: en el celular, «Todas» abre en la lista, porque
  // 15 canchas no caben a 375px). Tocar un partido de otra categoría dice
  // de cuál es y ofrece abrirla (ahí se mueve, como siempre).
  const [calendarScope, setCalendarScope] = useState<"division" | "all">(() =>
    searchParams.get("vista") === "todas" ? "all" : "division",
  );
  const [calendarDayView, setCalendarDayView] = useState<"grid" | "list" | null>(() => readStoredCalendarView(authUserId));
  // Cuadrícula / Lista elegida a mano: se recuerda para este usuario.
  function chooseCalendarDayView(view: "grid" | "list") {
    setCalendarDayView(view);
    storeCalendarView(authUserId, view);
  }
  // Celular: una cancha a la vista (pestañas C1–C5). null = la primera.
  const [calendarMobileCourt, setCalendarMobileCourt] = useState<string | null>(null);
  // «Programar aquí» de la lista sin partido elegido: el hueco espera a que
  // se elija uno de «Por programar».
  const [calendarPendingSlot, setCalendarPendingSlot] = useState<{ court: string; time: string } | null>(null);
  // Pantalla única (maqueta Cal-Unico, 2026-10-15): la vista «Lista» trae
  // «Del día» (hora → cancha) o «De la jornada» (tabla con Editar); en el
  // celular el riel «Por programar» es una hoja inferior plegable.
  const [calendarListKind, setCalendarListKind] = useState<"court" | "day" | "jornada">("court");
  useEffect(() => {
    // Otro torneo, día, categoría, sede, alcance, modo, vista o subvista de la
    // lista: el hueco pendiente ya no aplica (revisión Torni #587: sobrevivía
    // al pasar a «Del día» y reaparecía al elegir partido).
    setCalendarPendingSlot(null);
  }, [tournamentId, calendarDate, selectedDivisionId, calendarVenueName, calendarScope, calendarMode, calendarDayView, calendarListKind]);
  const [calendarRailOpen, setCalendarRailOpen] = useState(false);
  const calendarMoreRef = useRef<HTMLDetailsElement | null>(null);
  const calendarHistoryRef = useRef<HTMLDetailsElement | null>(null);
  const calendarSettingsRef = useRef<HTMLElement | null>(null);
  const [noCourtPanelRounds, setNoCourtPanelRounds] = useState<number[]>([]);
  const [calendarForeignPick, setCalendarForeignPick] = useState<TournamentMatch | null>(null);
  // Partido a elegir en cuanto llegue su categoría («Abrir su categoría»).
  const pendingSelectMatchIdRef = useRef<number | null>(null);
  // Aviso del partido de otra categoría: flota sobre el grid (no lo empuja
  // bajo el dedo); con teclado, el foco pasa a «Abrir {categoría}».
  const foreignPickRef = useRef<HTMLDivElement | null>(null);
  const foreignOpenRef = useRef<HTMLButtonElement | null>(null);
  const foreignPickViaKeyboardRef = useRef(false);
  const calendarMatrixRef = useRef<HTMLDivElement | null>(null);
  const publishNoticeRef = useRef<HTMLElement | null>(null);
  const calendarDayChipsRef = useRef<HTMLDivElement | null>(null);
  // Al cerrar el paso «Desprogramar el día…» el foco regresa a su botón (o,
  // si ya no hay qué desprogramar, al título de «Ordenar automático»).
  const unscheduleDayButtonRef = useRef<HTMLButtonElement | null>(null);
  const orderCardTitleRef = useRef<HTMLHeadingElement | null>(null);
  useEffect(() => {
    setPublishLockMessage(null);
    setPublishForbidden(false);
    setMyTournamentRole(undefined);
  }, [tournamentId]);
  // El paso «Desprogramar el día…» es del día, sede y categoría en pantalla.
  useEffect(() => {
    setBulkUnscheduleConfirmOpen(false);
    setBulkUnscheduleReason("");
  }, [calendarDate, calendarVenueName, selectedDivisionId, tournamentId]);

  const [edits, setEdits] = useState<Record<number, Partial<TournamentMatch>>>(
    {},
  );
  const [resultInputs, setResultInputs] = useState<
    Record<number, ResultInputState>
  >({});
  const doubleWalkoverBusy = useRef(false);
  const [resultTypeInputs, setResultTypeInputs] = useState<
    Record<number, ResultTypeInput>
  >({});
  const [actionReasons, setActionReasons] = useState<Record<number, string>>(
    {},
  );
  const [savingMatchId, setSavingMatchId] = useState<number | null>(null);
  const [bulkSavingResults, setBulkSavingResults] = useState(false);
  // Captura móvil (Eduardo 2026-09-11): en pantallas angostas el marcador
  // no va incrustado en la tarjeta; se abre una hoja a pantalla completa.
  const [mobileCaptureMatchId, setMobileCaptureMatchId] = useState<number | null>(null);
  const isNarrowViewport = useMediaQuery("(max-width: 640px)");
  useEffect(() => {
    if (mobileCaptureMatchId === null) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileCaptureMatchId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKey);
    };
  }, [mobileCaptureMatchId]);
  const canEditResults = !readOnly && hasRole("admin", "superadmin");
  // Captura desde el cuadro (Eduardo 2026-09-12): el Draw manda a
  // /results?capturar=<id>. En móvil abre la hoja de captura de ese partido;
  // en escritorio muestra su ronda, lo enfoca y lo resalta unos segundos.
  const captureFromQueryId = (() => {
    const raw = Number(searchParams.get("capturar"));
    return Number.isInteger(raw) && raw > 0 ? raw : null;
  })();
  const [captureFromQueryHandled, setCaptureFromQueryHandled] = useState(false);
  const [focusedMatchId, setFocusedMatchId] = useState<number | null>(null);
  // Alineación de líneas (series por equipos): declarado AQUÍ, antes del JSX
  // que llama renderMatchRow (ver nota TDZ de #300).
  const [savingLineupId, setSavingLineupId] = useState<number | null>(null);
  useEffect(() => {
    if (!resultsOnly || captureFromQueryHandled || captureFromQueryId === null || matches.length === 0) return;
    const target = matches.find((item) => item.id === captureFromQueryId);
    if (!target) return;
    setCaptureFromQueryHandled(true);
    setFilterJornada("all");
    setDidSelectCurrentJornada(true);
    if (isNarrowViewport && canEditResults && target.player1 && target.player2) {
      setMobileCaptureMatchId(target.id);
      return;
    }
    setFocusedMatchId(target.id);
  }, [canEditResults, captureFromQueryHandled, captureFromQueryId, isNarrowViewport, matches, resultsOnly]);
  // El desplazamiento espera a que la fila exista (la tabla se vuelve a pintar
  // al quitar el filtro de ronda) y abre el acordeón del grupo si está cerrado
  // (smoke de Hermes 2026-09-13: resaltaba pero no bajaba).
  useEffect(() => {
    if (focusedMatchId === null) return;
    let cancelled = false;
    let attempts = 0;
    const timers: number[] = [];
    // Desplazamiento INSTANTÁNEO (el suave no termina si algo más repinta o
    // si la pestaña no anima) y repetido: la tabla puede repintarse después
    // del primer intento y devolver la página arriba (smoke de Hermes
    // 2026-09-13: fila resaltada, scrollY = 0).
    const bringIntoView = (element: HTMLElement) => {
      const accordion = element.closest("details");
      if (accordion && !accordion.open) accordion.open = true;
      element.scrollIntoView({ behavior: "auto", block: "center" });
      const rect = element.getBoundingClientRect();
      if (rect.top < 0 || rect.bottom > window.innerHeight) {
        window.scrollTo({ top: Math.max(0, rect.top + window.scrollY - window.innerHeight / 2), behavior: "auto" });
      }
    };
    const tryScroll = () => {
      if (cancelled) return;
      const element = document.getElementById(`results-match-${focusedMatchId}`);
      if (element) {
        bringIntoView(element);
        for (const delay of [300, 900, 1800]) {
          timers.push(window.setTimeout(() => {
            if (cancelled) return;
            const again = document.getElementById(`results-match-${focusedMatchId}`);
            if (again) bringIntoView(again);
          }, delay));
        }
        return;
      }
      if (attempts++ < 40) timers.push(window.setTimeout(tryScroll, 150));
    };
    tryScroll();
    const clear = window.setTimeout(() => setFocusedMatchId(null), 8000);
    return () => {
      cancelled = true;
      timers.forEach((timer) => window.clearTimeout(timer));
      window.clearTimeout(clear);
    };
  }, [focusedMatchId]);
  const canEditSchedule = !readOnly && !playerMode && hasRole("admin", "superadmin");
  // «No» usa calendario de canchas: se esconde todo lo de canchas (grid,
  // «Ajustes de esta categoría», «Ordenar automático», «Publicar horarios»).
  // Solo en el calendario del organizador (Resultados y jugador no cambian).
  const courtCalendarOff = !playerMode && !resultsOnly && usesCourtCalendar === false;
  const statusFilterOptions = courtCalendarOff ? NO_COURT_STATUS_FILTER_OPTIONS : STATUS_FILTER_OPTIONS;
  // Un filtro de Estatus que ya no se ofrece (p. ej. «Sin publicar» al abrir
  // un torneo sin calendario de canchas, o «Por jugar» al volver a «Sí») no
  // se queda puesto a ciegas: regresa a «Todos».
  useEffect(() => {
    if (!statusFilterOptions.some((opt) => opt.value === filterStatus)) setFilterStatus("all");
  }, [statusFilterOptions, filterStatus]);
  const loadContextSeqRef = useRef(0);
  const loadScheduleSeqRef = useRef(0);

  useEffect(() => {
    setSelectedDivisionId((current) => (current === initialDivisionId ? current : initialDivisionId));
    setFilterGroupId("all");
  }, [initialDivisionId]);

  // Cambiar de torneo sí vuelve al spinner completo: los datos del torneo
  // anterior no deben verse mientras carga el nuevo. Cambiar de división NO
  // (ahí es donde viven los chips que perdían el primer click).
  useEffect(() => {
    setHasLoadedOnce(false);
    setUsesCourtCalendar(null);
    setCourtCalendarStatus(null);
    setCourtTypeStatus(null);
  }, [tournamentId]);

  const describeGroup = useCallback(
    (groupId: number | null): string => {
      if (groupId === null) return "Sin grupo";
      const g = groups.find((x) => x.id === groupId);
      return g ? tournamentGroupLabel(g, repeatedTournamentGroupNames(groups)) : `Grupo #${groupId}`;
    },
    [groups],
  );

  const loadContext = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId) || playerMode) return;
    const requestSeq = ++loadContextSeqRef.current;
    try {
      const divisionParams = initialDivisionId ? { division_id: initialDivisionId } : {};
      const [tResp, gResp, divisionsResp] = await Promise.all([
        api.tournaments.get(token, tournamentId),
        api.groups.list(token, tournamentId, divisionParams),
        api.tournaments.listDivisions(token, tournamentId),
      ]);
      if (requestSeq !== loadContextSeqRef.current) return;
      setTournament(tResp.data.tournament);
      setFormatConfig(tResp.data.format_config ?? null);
      // Sin el dato (respuesta vieja) se queda como hoy: con calendario.
      setUsesCourtCalendar(tResp.data.settings?.uses_court_calendar !== false);
      setGroups(gResp.data.groups);
      setDivisions(divisionsResp.data.divisions);
    } catch (err) {
      // Latest-only también en el error: el fallo tardío de una carga vieja
      // no pinta su error sobre la categoría ya cargada.
      if (requestSeq !== loadContextSeqRef.current) return;
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar el contexto del torneo.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    }
  }, [token, tournamentId, playerMode, initialDivisionId]);

  const loadSchedule = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId)) return;
    const requestSeq = ++loadScheduleSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      if (playerMode) {
        const response = await api.resultReports.listTournamentResultsForPlayer(
          token,
          tournamentId,
        );
        // Latest-only tambien aqui (gate #146): una respuesta VIEJA del
        // torneo A no debe pisar al torneo B ya cargado.
        if (requestSeq !== loadScheduleSeqRef.current) return;
        setTournament({
          id: response.data.tournament.id,
          name: response.data.tournament.name,
        } as Tournament);
        setGroups([]);
        setMatches(response.data.matches);
        setCrossDivisionMatches([]);
        setHistory([]);
        setResultReports(response.data.reports);
        setMyParticipantId(response.data.my_participant_id);
        setPlayerTournamentSettings(response.data.settings ?? null);
        return;
      }

      const divisionParams = initialDivisionId ? { division_id: initialDivisionId } : {};
      const [mResp, hResp, rResp, allResp] = await Promise.all([
        api.matches.list(token, tournamentId, divisionParams),
        api.matches.history(token, tournamentId),
        api.resultReports.listForAdmin(token, tournamentId, {}),
        // Con la vista scoped a una división, el resto del torneo también
        // ocupa canchas: se trae aparte SIN el filtro de presentación ladder
        // (include_all_labels) — un padre ladder con divisiones elimination
        // ocultaba sus Round of N y las celdas parecían libres (gate #146).
        initialDivisionId
          ? api.matches.list(token, tournamentId, { include_all_labels: true })
          : Promise.resolve(null),
      ]);
      if (requestSeq !== loadScheduleSeqRef.current) return;
      setMatches(mResp.data.matches);
      setCrossDivisionMatches(
        allResp
          ? allResp.data.matches.filter((m) => m.division_id !== initialDivisionId)
          : [],
      );
      setHistory(hResp.data.history);
      setResultReports(rResp.data.reports);
      setPlayerTournamentSettings(null);
      setScheduleLoadedScope(calendarScopeKey(tournamentId, initialDivisionId));
    } catch (err) {
      // Un fallo TARDÍO de una carga vieja no debe pisar con su error a la
      // división ya cargada (gate #146): misma regla latest-only del éxito.
      if (requestSeq !== loadScheduleSeqRef.current) return;
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar los resultados.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      if (requestSeq === loadScheduleSeqRef.current) {
        setLoading(false);
        setHasLoadedOnce(true);
      }
    }
  }, [token, tournamentId, playerMode, initialDivisionId]);

  // Después de guardar se recarga con el cargador del render MÁS NUEVO, no
  // con el que el clic dejó capturado: si mientras esperaban cambiaron la
  // categoría, el capturado pediría la de antes y, con su secuencia más
  // nueva, pintaría sus partidos bajo la categoría elegida (mismo arreglo que
  // Jugadores, #434). Se llama con await: los botones siguen en orden lo que
  // va después de recargar (avisos, foco).
  const loadScheduleRef = useRef(loadSchedule);
  const loadContextRef = useRef(loadContext);
  useLayoutEffect(() => {
    loadScheduleRef.current = loadSchedule;
    loadContextRef.current = loadContext;
  }, [loadSchedule, loadContext]);
  const reloadSchedule = useCallback(() => loadScheduleRef.current(), []);
  const reloadContext = useCallback(() => loadContextRef.current(), []);

  useEffect(() => {
    void loadContext();
  }, [loadContext]);

  useEffect(() => {
    void loadSchedule();
  }, [loadSchedule]);

  // ---- ajustes del calendario por categoría --------------------------------
  // Eduardo 2026-10-01 (torneo 59): «los bloques de minutos son por categoría,
  // cada que cambie de categoría se debe guardar su bloque de minutos y su
  // número de canchas». Al abrir una categoría se cargan SUS valores; si aún
  // no tiene, se quedan los últimos usados en el torneo (pedido anterior: «si
  // el admin seleccionó 7 o 10 canchas, que en la siguiente ya aparezcan»).
  // Al cambiar un campo se guarda para ESA categoría en el servidor.
  // REVISIÓN (2026-10-01):
  // - Un Bloque min heredado de otra categoría NO se guarda solo: le
  //   cambiaría la duración a los partidos que esa categoría ya tiene
  //   programados (y a cómo los ven las demás). Se guarda cuando el admin lo
  //   cambia o lo confirma en ella, o al programar si la categoría todavía no
  //   tiene partidos programados. Canchas y horario sí se heredan y guardan.
  // - El Bloque min se guarda al salir del campo (o con Enter), no a cada
  //   tecla: un "12" a medio escribir "120" ya no se guarda.
  // - Un guardado a la vez por categoría y comparando contra lo ÚLTIMO que se
  //   mandó, no solo contra lo confirmado: el servidor termina con lo de la
  //   pantalla aunque el admin cambie y regrese un valor mientras se guarda.
  //   Lo que no se pudo guardar se conserva y vuelve a la pantalla al
  //   regresar a esa categoría.

  // Categoría cuyos valores están en pantalla (undefined = todavía ninguna).
  const calendarAppliedDivisionRef = useRef<number | null | undefined>(undefined);
  // Los valores en pantalla ya son "los últimos usados" en este torneo.
  const calendarValuesUsedRef = useRef(false);
  const calendarSettingsTournamentRef = useRef(tournamentId);
  const calendarSaveTimerRef = useRef<number | null>(null);
  // Por categoría: lo confirmado por el servidor en esta pestaña (espejo de
  // calendarSettingsSaved, sin esperar al render), lo que va en camino, lo
  // que espera turno (o falló y se reintenta), el error y el guardado en curso.
  const calendarConfirmedRef = useRef<Record<number, DivisionCalendarSettings>>({});
  const calendarSentRef = useRef<Record<number, DivisionCalendarSettings>>({});
  const calendarQueuedRef = useRef<Record<number, CalendarQueuedSave>>({});
  const calendarFailedRef = useRef<Record<number, string>>({});
  const calendarSavingRef = useRef<Record<number, Promise<void> | undefined>>({});

  /** Lo que el servidor tiene (o tendrá) de una categoría: lo que va en
   * camino, lo confirmado en esta pestaña o lo de la lista de categorías. */
  const knownCalendarSettingsFor = useCallback(
    (divisionId: number): DivisionCalendarSettings =>
      calendarSentRef.current[divisionId] ??
      calendarConfirmedRef.current[divisionId] ??
      divisionCalendarSettings(divisions.find((division) => division.id === divisionId)),
    [divisions],
  );

  /** Manda lo que espera turno de una categoría: un guardado a la vez y
   * siempre con lo más reciente. */
  const sendCalendarSettings = useCallback(
    async (divisionId: number): Promise<void> => {
      for (;;) {
        const running = calendarSavingRef.current[divisionId];
        if (!running) break;
        await running;
      }
      const entry = calendarQueuedRef.current[divisionId];
      if (!entry || !token) return;
      delete calendarQueuedRef.current[divisionId];
      calendarSentRef.current[divisionId] = entry.settings;
      // El aviso solo se muestra si esa categoría sigue abierta.
      const isOpen = () => calendarAppliedDivisionRef.current === divisionId;
      if (isOpen()) setCalendarSettingsStatus({ kind: "saving" });
      const run = (async () => {
        try {
          const response = await api.tournaments.updateDivisionCalendarSettings(
            token,
            entry.tournamentId,
            divisionId,
            entry.settings,
          );
          const data = response?.data ?? {};
          const fromServer = data.division
            ? divisionCalendarSettings(data.division)
            : sanitizeCalendarSettings(data.calendar_settings_json ?? null);
          const saved = hasCalendarSettings(fromServer) ? fromServer : entry.settings;
          calendarConfirmedRef.current[divisionId] = saved;
          delete calendarFailedRef.current[divisionId];
          setCalendarSettingsSaved((prev) => ({ ...prev, [divisionId]: saved }));
          if (isOpen() && !calendarQueuedRef.current[divisionId]) setCalendarSettingsStatus({ kind: "saved" });
        } catch (err) {
          const message = err instanceof ApiError && err.message ? err.message : "Revisa tu conexión e intenta otra vez.";
          calendarFailedRef.current[divisionId] = message;
          // El valor se queda en pantalla y se usa igual; se reintenta al
          // cambiarlo otra vez, al salir del campo o al programar.
          if (!calendarQueuedRef.current[divisionId]) calendarQueuedRef.current[divisionId] = entry;
          if (isOpen()) setCalendarSettingsStatus({ kind: "error", message });
        } finally {
          delete calendarSentRef.current[divisionId];
        }
      })();
      calendarSavingRef.current[divisionId] = run;
      try {
        await run;
      } finally {
        if (calendarSavingRef.current[divisionId] === run) delete calendarSavingRef.current[divisionId];
      }
    },
    [token],
  );

  /** Guarda ya todo lo que espera turno (al salir de un campo, al cambiar de
   * categoría o al salir de la página). */
  const flushCalendarSettingsSave = useCallback(async (): Promise<void> => {
    if (calendarSaveTimerRef.current !== null) {
      window.clearTimeout(calendarSaveTimerRef.current);
      calendarSaveTimerRef.current = null;
    }
    const divisionIds = Object.keys(calendarQueuedRef.current).map(Number);
    await Promise.all(divisionIds.map((divisionId) => sendCalendarSettings(divisionId)));
  }, [sendCalendarSettings]);

  // Al salir de la página se guarda lo que estaba por guardarse.
  const flushCalendarSettingsSaveRef = useRef(flushCalendarSettingsSave);
  flushCalendarSettingsSaveRef.current = flushCalendarSettingsSave;
  useEffect(() => () => {
    void flushCalendarSettingsSaveRef.current();
  }, []);

  function applyCalendarFields(fields: CalendarFieldValues) {
    setCalendarStartTime(fields.startTime);
    setCalendarEndTime(fields.endTime);
    setCalendarInterval(fields.blockMinutes);
    setCalendarCourtCount(fields.courtCount);
  }

  function currentCalendarFields(): CalendarFieldValues {
    return {
      startTime: calendarStartTime,
      endTime: calendarEndTime,
      blockMinutes: calendarInterval,
      courtCount: calendarCourtCount,
    };
  }

  // Otro torneo sin desmontar la página: nada del anterior se cruza.
  useEffect(() => {
    if (calendarSettingsTournamentRef.current === tournamentId) return;
    calendarSettingsTournamentRef.current = tournamentId;
    void flushCalendarSettingsSaveRef.current();
    calendarAppliedDivisionRef.current = undefined;
    calendarValuesUsedRef.current = false;
    calendarConfirmedRef.current = {};
    setCalendarSettingsSaved({});
    setCalendarSettingsStatus({ kind: "idle" });
    setCalendarBlockOwn(true);
    setCalendarInheritedFrom(null);
    setCalendarSettingsOpen(false);
    applyCalendarFields(DEFAULT_CALENDAR_FIELDS);
  }, [tournamentId]);

  // Al abrir una categoría: sus valores guardados o, si no tiene, los
  // últimos usados en el torneo. La Fecha no se toca.
  useEffect(() => {
    if (playerMode) return;
    // Lo pendiente de la categoría anterior se guarda para ELLA.
    const previousApplied = calendarAppliedDivisionRef.current;
    if (previousApplied != null && previousApplied !== selectedDivisionId) {
      void flushCalendarSettingsSave();
    }
    if (selectedDivisionId === null) {
      // Vista sin categoría: los valores se usan pero no se guardan.
      if (calendarAppliedDivisionRef.current !== null) setCalendarSettingsOpen(false);
      calendarAppliedDivisionRef.current = null;
      setCalendarBlockOwn(true);
      setCalendarSettingsStatus({ kind: "idle" });
      return;
    }
    if (calendarAppliedDivisionRef.current === selectedDivisionId) return;
    if (!divisions.some((division) => division.id === selectedDivisionId)) return; // aún no llega la lista
    const previousDivisionId = calendarAppliedDivisionRef.current;
    // Lo que la categoría no tenga guardado: los últimos usados en el torneo
    // (los de la pantalla) o, recién abierta la página, los de la categoría
    // guardada más recientemente.
    let fallback: CalendarFieldValues;
    let from: string | null;
    if (calendarValuesUsedRef.current) {
      fallback = currentCalendarFields();
      from = previousDivisionId != null
        ? divisions.find((division) => division.id === previousDivisionId)?.name ?? null
        : null;
    } else {
      const latest = latestDivisionCalendarSettings(divisions, calendarConfirmedRef.current, selectedDivisionId);
      fallback = latest ? fieldsFromSettings(latest.settings, DEFAULT_CALENDAR_FIELDS) : DEFAULT_CALENDAR_FIELDS;
      from = latest?.name ?? null;
    }
    calendarAppliedDivisionRef.current = selectedDivisionId;
    // Lo que espera turno o no se pudo guardar manda sobre la lista (si no,
    // regresar a la categoría borraba lo que el admin había puesto).
    const queued = calendarQueuedRef.current[selectedDivisionId]?.settings;
    const settings = queued ?? knownCalendarSettingsFor(selectedDivisionId);
    applyCalendarFields(fieldsFromSettings(settings, fallback));
    const blockOwn = settings.match_duration_minutes != null;
    setCalendarBlockOwn(blockOwn);
    setCalendarInheritedFrom(from);
    // «Ajustes de esta categoría» se abre solo si la categoría aún no tiene
    // su bloque guardado (hay que revisarlo); si no, queda en una línea.
    setCalendarSettingsOpen(!blockOwn || Boolean(calendarFailedRef.current[selectedDivisionId]));
    const failed = calendarFailedRef.current[selectedDivisionId];
    if (queued && failed) setCalendarSettingsStatus({ kind: "error", message: failed });
    else if (queued) setCalendarSettingsStatus({ kind: "pending" });
    else if (calendarSavingRef.current[selectedDivisionId]) setCalendarSettingsStatus({ kind: "saving" });
    else if (blockOwn) setCalendarSettingsStatus({ kind: "loaded" });
    else setCalendarSettingsStatus({ kind: "inherited", from });
    calendarValuesUsedRef.current = true;
    // Solo al cambiar de categoría (o cuando llega su lista): no se pisa lo
    // que el admin está escribiendo cuando la lista se recarga.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerMode, selectedDivisionId, divisions]);

  /** Deja en espera lo que hay que guardar para la categoría (o nada, si el
   * servidor ya lo tiene o lo va a tener) y decide cuándo se manda: "soon" =
   * poco después, "blur" = al salir del campo, "now" = ya, "caller" = quien
   * llama lo manda. */
  function queueCalendarSettingsSave(
    divisionId: number,
    fields: CalendarFieldValues,
    includeBlock: boolean,
    when: "soon" | "blur" | "now" | "caller",
  ): void {
    if (calendarSaveTimerRef.current !== null) {
      window.clearTimeout(calendarSaveTimerRef.current);
      calendarSaveTimerRef.current = null;
    }
    const known = knownCalendarSettingsFor(divisionId);
    const settings = calendarSettingsToSave(fields, known, includeBlock);
    if (!hasCalendarSettings(settings) || sameCalendarSettings(settings, known)) {
      // Nada nuevo que guardar (a medio escribir o de vuelta a lo que el
      // servidor ya tiene o va a tener).
      delete calendarQueuedRef.current[divisionId];
      delete calendarFailedRef.current[divisionId];
      setCalendarSettingsStatus((status) => {
        if (calendarSavingRef.current[divisionId]) return { kind: "saving" };
        if (status.kind !== "pending" && status.kind !== "error") return status;
        return hasCalendarSettings(known) ? { kind: "saved" } : { kind: "inherited", from: calendarInheritedFrom };
      });
      return;
    }
    calendarQueuedRef.current[divisionId] = { tournamentId, settings };
    delete calendarFailedRef.current[divisionId];
    setCalendarSettingsStatus({ kind: "pending", onBlur: when === "blur" });
    if (when === "soon") {
      calendarSaveTimerRef.current = window.setTimeout(() => {
        calendarSaveTimerRef.current = null;
        void flushCalendarSettingsSave();
      }, 700);
    } else if (when === "now") {
      void flushCalendarSettingsSave();
    }
  }

  /** Cambio de Desde, Último inicio, Bloque min o #Canchas: se ve de
   * inmediato y se guarda para la categoría abierta poco después (Bloque
   * min: al salir del campo). */
  function handleCalendarFieldChange(patch: Partial<CalendarFieldValues>) {
    const next: CalendarFieldValues = { ...currentCalendarFields(), ...patch };
    if (patch.startTime !== undefined) setCalendarStartTime(patch.startTime);
    if (patch.endTime !== undefined) setCalendarEndTime(patch.endTime);
    if (patch.blockMinutes !== undefined) setCalendarInterval(patch.blockMinutes);
    if (patch.courtCount !== undefined) setCalendarCourtCount(patch.courtCount);
    calendarValuesUsedRef.current = true;
    const divisionId = calendarAppliedDivisionRef.current;
    if (!canEditSchedule || divisionId == null || Number.isNaN(tournamentId)) return;
    // Cambiar el Bloque min estando en esta categoría lo hace suyo.
    const blockOwn = calendarBlockOwn || patch.blockMinutes !== undefined;
    if (blockOwn && !calendarBlockOwn) setCalendarBlockOwn(true);
    queueCalendarSettingsSave(divisionId, next, blockOwn, patch.blockMinutes !== undefined ? "blur" : "soon");
  }

  /** «Usar N min en …»: el admin confirma el Bloque min heredado para la
   * categoría abierta. */
  function handleConfirmCalendarBlock() {
    const divisionId = calendarAppliedDivisionRef.current;
    if (!canEditSchedule || divisionId == null || Number.isNaN(tournamentId)) return;
    setCalendarBlockOwn(true);
    queueCalendarSettingsSave(divisionId, currentCalendarFields(), true, "now");
  }

  /** La categoría ya tiene partidos activos con fecha y hora. */
  function divisionHasScheduledMatches(divisionId: number): boolean {
    return [...matches, ...crossDivisionMatches].some(
      (match) =>
        match.division_id === divisionId &&
        Boolean(match.scheduled_date) &&
        Boolean((match.scheduled_time ?? "").trim()) &&
        isActiveCalendarStatus(match.status),
    );
  }

  /** Antes de programar: lo de la pantalla queda guardado para la categoría
   * (y lo que iba en camino, ya en el servidor), así las demás categorías y el
   * servidor la ven con su duración real. Un Bloque min heredado solo se
   * guarda si la categoría todavía no tiene partidos programados. Si falla,
   * se programa igual (el aviso queda junto a los campos y el servidor usa
   * el bloque que la categoría tenga guardado). */
  async function ensureCalendarSettingsSaved(): Promise<void> {
    const divisionId = calendarAppliedDivisionRef.current;
    if (!canEditSchedule || divisionId == null || Number.isNaN(tournamentId)) return;
    let includeBlock = calendarBlockOwn;
    if (!includeBlock && !divisionHasScheduledMatches(divisionId)) {
      includeBlock = true;
      setCalendarBlockOwn(true);
    }
    queueCalendarSettingsSave(divisionId, currentCalendarFields(), includeBlock, "caller");
    await sendCalendarSettings(divisionId);
  }

  function flashOk(msg: string) {
    setMessage(msg);
    setError(null);
  }

  /** «Publicar el torneo» (paso 10, Eduardo 2026-10-05): al publicar el admin
   * se queda aquí; la página pasa a publicado (barra «Publicar horarios» y
   * «Empezar torneo») sin salir de Calendario. */
  function handleTournamentPublished(published: Tournament) {
    setTournament((current) => (current ? { ...current, status: published.status } : published));
    flashOk("Listo: el torneo y sus horarios quedaron publicados; los jugadores ya los ven.");
    // Misma página sin `?publicar=1` ni el aviso «Este torneo sigue en
    // borrador» que trae la navegación cuando el asistente regresó aquí.
    const next = new URLSearchParams(searchParams);
    next.delete(PUBLISH_FOCUS_PARAM);
    setSearchParams(next, { replace: true });
    void reloadContext();
  }

  /** Liga de la barra «Publicar horarios» en borrador: baja a la tarjeta
   * «Publicar el torneo» (al final de la página) y enfoca su botón. */
  function goToPublishTournamentCard() {
    const card = document.getElementById("publicar-torneo");
    if (!card) return;
    card.scrollIntoView?.({ behavior: "smooth", block: "center" });
    card.querySelector<HTMLButtonElement>("button:not([disabled])")?.focus({ preventScroll: true });
  }

  function flashErr(err: unknown, fallback: string) {
    if (err instanceof ApiError) {
      setError(err.message || fallback);
    } else {
      setError(fallback);
    }
    setMessage(null);
  }

  function getEdit(match: TournamentMatch): Partial<TournamentMatch> {
    return edits[match.id] ?? {};
  }

  function hasPendingEdit(match: TournamentMatch): boolean {
    const edit = edits[match.id];
    if (!edit) return false;
    const fields: (keyof TournamentMatch)[] = [
      "scheduled_date",
      "scheduled_time",
      "court",
      "status",
      "round_label",
      "notes",
    ];
    return fields.some((f) => edit[f] !== undefined);
  }

  async function handleSaveEdit(match: TournamentMatch): Promise<boolean> {
    if (!token || !canEditResults) return false;
    const edit = getEdit(match);
    setSavingMatchId(match.id);
    try {
      await api.matches.update(token, tournamentId, match.id, {
        scheduled_date:
          edit.scheduled_date !== undefined
            ? edit.scheduled_date
            : match.scheduled_date,
        scheduled_time:
          edit.scheduled_time !== undefined
            ? edit.scheduled_time
            : match.scheduled_time,
        court: edit.court !== undefined ? edit.court : match.court,
        round_label:
          edit.round_label !== undefined ? edit.round_label : match.round_label,
        status: (edit.status as string | undefined) ?? match.status,
        notes: edit.notes !== undefined ? edit.notes : match.notes,
      });
      setEdits((prev) => {
        const next = { ...prev };
        delete next[match.id];
        return next;
      });
      flashOk("Partido actualizado.");
      await reloadSchedule();
      return true;
    } catch (err) {
      flashErr(err, "No se pudo actualizar el partido.");
      return false;
    } finally {
      setSavingMatchId(null);
    }
  }

  async function syncRankingAfterResultChange() {
    if (!token || !canEditResults) return;
    try {
      await api.standings.recalculate(token, tournamentId);
    } catch (err) {
      console.warn("No se pudo recalcular ranking después de publicar resultado", err);
    }
  }

  async function handleRecordResult(match: TournamentMatch): Promise<boolean> {
    if (!token || !canEditResults) return false;
    const input = resultInputs[match.id] ?? {};
    try {
      const response = await api.matches.recordResult(token, tournamentId, match.id, {
        winner_participant_id:
          input.winner === undefined || input.winner === ""
            ? null
            : Number(input.winner),
        score_text: (input.score ?? "").trim() || null,
      });
      const notices = response?.data?.notices ?? [];
      setResultInputs((prev) => {
        const next = { ...prev };
        delete next[match.id];
        return next;
      });
      setResultTypeInputs((prev) => {
        const next = { ...prev };
        delete next[match.id];
        return next;
      });
      flashOk(["Resultado registrado. Ranking actualizado.", ...notices.map((notice) => `Ojo: ${notice}`)].join(" "));
      await syncRankingAfterResultChange();
      await reloadSchedule();
      return true;
    } catch (err) {
      flashErr(err, "No se pudo registrar el resultado.");
      return false;
    }
  }

  async function handleAction(
    match: TournamentMatch,
    action: "postpone" | "cancel" | "no-show",
  ) {
    if (!token || !canEditResults) return;
    const reason = (actionReasons[match.id] ?? "").trim() || null;
    let cancelNotice: string | null = null;
    try {
      if (action === "postpone") {
        await api.matches.postpone(token, tournamentId, match.id, { reason });
      } else if (action === "cancel") {
        const response = await api.matches.cancel(token, tournamentId, match.id, { reason });
        // Con un lado dado de baja el cuadro no se cancela: W.O. o sigue vivo.
        const status = response?.data?.match?.status;
        if (status === "walkover") cancelNotice = "No se canceló: es un partido del cuadro y su rival ganó por W.O.";
        else if (status && status !== "cancelled") {
          cancelNotice = "No se canceló: es un partido del cuadro con un jugador de baja. Su rival gana por W.O. al llegar.";
        }
      } else {
        await api.matches.noShow(token, tournamentId, match.id, { reason });
      }
      setActionReasons((prev) => {
        const next = { ...prev };
        delete next[match.id];
        return next;
      });
      setResultTypeInputs((prev) => {
        const next = { ...prev };
        delete next[match.id];
        return next;
      });
      flashOk(cancelNotice ?? "Partido actualizado.");
      if (action === "no-show") {
        await syncRankingAfterResultChange();
      }
      await reloadSchedule();
    } catch (err) {
      flashErr(err, "No se pudo actualizar el partido.");
    }
  }

  /** «Cancelar por baja» (Eduardo 2026-10-05, torneo 69): un pendiente
   * contra alguien dado de baja (se eligió «Dejarlos como están») se cancela
   * con la misma nota que deja la baja, «Baja de <nombre>»; esa jornada su
   * rival descansa. Capturista no cancela partidos: si lo intenta, el
   * servidor lo dice. */
  function canManageDropMatches(): boolean {
    return (
      canEditResults &&
      !playerMode &&
      myTournamentRole !== "capturista" &&
      myTournamentRole !== "finanzas" &&
      (tournament?.status !== "closed" || hasRole("superadmin"))
    );
  }

  function canActOnDropMatch(match: TournamentMatch): boolean {
    return (
      canManageDropMatches() &&
      // Revisión 2026-10-05: solo partidos de grupo. En eliminatoria o
      // playoffs cancelar no hace avanzar a nadie ni hay «jornada de
      // descanso» (ahí va W.O.); una serie de equipos se cancela completa
      // desde el detalle de la baja, no línea por línea.
      match.group_id != null &&
      match.match_kind !== "tie" &&
      match.parent_match_id == null
    );
  }

  function canCancelByDrop(match: TournamentMatch): boolean {
    return dropMatchState(match)?.kind === "pending" && canActOnDropMatch(match);
  }

  /** Revisión 2026-10-05: si al dar de baja se eligió «Dar W/O al rival»
   * (la opción por omisión), Eduardo pidió que su rival tenga BYE, no la
   * victoria: este W.O. se puede cambiar a «Cancelado por baja». */
  function canTurnDropWalkoverIntoCancel(match: TournamentMatch): boolean {
    return dropMatchState(match)?.kind === "walkover" && canActOnDropMatch(match);
  }

  async function handleTurnDropWalkoverIntoCancel(match: TournamentMatch) {
    const drop = dropMatchState(match);
    if (!token || !canEditResults || drop?.kind !== "walkover") return;
    const droppedName = drop.dropped?.display_name?.trim() || drop.shortName || "quien se dio de baja";
    const rivalName = match.winner?.display_name?.trim() || "Su rival";
    const confirmed = await askConfirm(
      `¿Cambiar este W.O. por la baja de ${droppedName} a «Cancelado por baja»?\n` +
        `${rivalName} pierde la victoria por W.O. y descansa esa jornada.`,
      { acceptLabel: "Sí, cambiar a cancelado", tone: "danger" },
    );
    if (!confirmed) return;
    setSavingMatchId(match.id);
    try {
      // Como «↺ Despublicar» (vuelve a pendiente, sin resultado ni horario) y
      // luego la misma cancelación que «Cancelar por baja».
      await api.matches.update(token, tournamentId, match.id, {
        status: "scheduled",
        winner_participant_id: null,
        score_text: null,
        scheduled_date: null,
        scheduled_time: null,
        court: null,
      });
      await api.matches.cancel(token, tournamentId, match.id, { reason: dropCancelReason(drop) });
      flashOk(`Partido cancelado por la baja de ${droppedName}: ${rivalName} descansa esa jornada.`);
      await syncRankingAfterResultChange();
      await reloadSchedule();
    } catch (err) {
      flashErr(err, "No se pudo cambiar el W.O. a cancelado.");
      await reloadSchedule();
    } finally {
      setSavingMatchId(null);
    }
  }

  /** ↷ Reactivar un «Cancelado por baja» (revisión 2026-10-05): por error o
   * porque se reincorporó. Es el mismo «Posponer» de siempre (vuelve a
   * pendiente) con una nota que anula la de la baja. */
  async function handleReviveDropCancel(match: TournamentMatch) {
    if (!token || !canEditResults) return;
    const confirmed = await askConfirm(
      "¿Reactivar este partido?\nVuelve a pendiente (pospuesto) y su rival ya no descansa esa jornada.",
      { acceptLabel: "Sí, reactivar" },
    );
    if (!confirmed) return;
    setSavingMatchId(match.id);
    try {
      await api.matches.postpone(token, tournamentId, match.id, { reason: DROP_REVIVE_NOTE });
      flashOk("Partido reactivado: vuelve a pendiente.");
      await reloadSchedule();
    } catch (err) {
      flashErr(err, "No se pudo reactivar el partido.");
    } finally {
      setSavingMatchId(null);
    }
  }

  async function handleCancelByDrop(match: TournamentMatch) {
    const drop = dropMatchState(match);
    if (!token || !canEditResults || drop?.kind !== "pending") return;
    const droppedName = drop.dropped?.display_name?.trim() || drop.shortName || "quien se dio de baja";
    const rival = drop.dropped && drop.dropped.id === match.player1?.id ? match.player2 : match.player1;
    const rivalName = rival?.display_name?.trim();
    const confirmed = await askConfirm(
      `¿Cancelar este partido por la baja de ${droppedName}?\n` +
        `Queda como «Cancelado por baja» (no cuenta como jugado)${
          rivalName ? ` y ${rivalName} descansa esa jornada` : ""
        }.`,
      { acceptLabel: "Sí, cancelar por baja", tone: "danger" },
    );
    if (!confirmed) return;
    setSavingMatchId(match.id);
    try {
      await api.matches.cancel(token, tournamentId, match.id, { reason: dropCancelReason(drop) });
      flashOk(`Partido cancelado por la baja de ${droppedName}.`);
      await reloadSchedule();
    } catch (err) {
      flashErr(err, "No se pudo cancelar el partido.");
    } finally {
      setSavingMatchId(null);
    }
  }

  async function handleDoubleWalkover(match: TournamentMatch): Promise<boolean> {
    if (!token || !canEditResults || doubleWalkoverBusy.current) return false;
    doubleWalkoverBusy.current = true;
    try {
      return await registerDoubleWalkover(match, token);
    } finally {
      doubleWalkoverBusy.current = false;
    }
  }

  async function registerDoubleWalkover(match: TournamentMatch, authToken: string): Promise<boolean> {
    const confirmed = await askConfirm(
      `¿Registrar Doble W.O. en ${matchShortLabel(match)}?\n` +
        "Ninguno se presentó: el partido termina sin ganador y nadie avanza. Su rival en la ronda siguiente gana por W.O.\n" +
        "Se corrige con «Despublicar» mientras esa ronda no se juegue.",
      { acceptLabel: "Sí, registrar Doble W.O.", tone: "danger" },
    );
    if (!confirmed) return false;
    setSavingMatchId(match.id);
    try {
      const reason = (actionReasons[match.id] ?? "").trim() || null;
      await api.matches.doubleWalkover(authToken, tournamentId, match.id, { reason });
      setResultTypeInputs((prev) => {
        const next = { ...prev };
        delete next[match.id];
        return next;
      });
      flashOk("Doble W.O. registrado: nadie avanza y su rival en la ronda siguiente gana por W.O.");
      await syncRankingAfterResultChange();
      await reloadSchedule();
      return true;
    } catch (err) {
      flashErr(err, "No se pudo registrar el Doble W.O.");
      return false;
    } finally {
      setSavingMatchId(null);
    }
  }

  async function handleWalkover(match: TournamentMatch, exceptionType: string = "walkover"): Promise<boolean> {
    if (!token || !canEditResults) return false;
    const reason = (actionReasons[match.id] ?? "").trim() || null;
    // El ganador marcado (o el que ya tenía el partido, como lo pinta el radio).
    const winnerId = resultWinnerValue(match);
    if (winnerId === null) {
      flashErr(
        null,
        "Selecciona al ganador: un walkover, default o retiro otorga la victoria al rival.",
      );
      return false;
    }
    try {
      await api.matches.walkover(token, tournamentId, match.id, {
        winner_participant_id: winnerId,
        notes: reason,
        exception_type: exceptionType,
        // Retiro y default guardan el marcador hasta donde se jugó (puede ir
        // vacío). El W.O. no se jugó: no lleva marcador.
        ...(exceptionType === "walkover" ? {} : { score_text: resultScoreValue(match) }),
      });
      setResultInputs((prev) => {
        const next = { ...prev };
        delete next[match.id];
        return next;
      });
      setResultTypeInputs((prev) => {
        const next = { ...prev };
        delete next[match.id];
        return next;
      });
      const labelMap: Record<string, string> = { walkover: "Walkover", defaulted: "Default", retired: "Retiro" };
      flashOk(`${labelMap[exceptionType] || "Excepción"} registrado. Ranking actualizado.`);
      await syncRankingAfterResultChange();
      await reloadSchedule();
      return true;
    } catch (err) {
      flashErr(err, "No se pudo registrar el walkover.");
      return false;
    }
  }

  async function handleUnpublish(match: TournamentMatch) {
    if (!token || !canEditResults) return;
    try {
      await api.matches.update(token, tournamentId, match.id, {
        status: "scheduled",
        winner_participant_id: null,
        score_text: null,
        // Un partido ya cerrado deja de reservar su slot. Si ese horario fue
        // reutilizado, reactivarlo con la programación histórica daría 409
        // (o, peor, tentaría a saltarse la validación). Despublicar lo regresa
        // a pendiente sin programación para conservar la agenda consistente.
        scheduled_date: null,
        scheduled_time: null,
        court: null,
      });
      setResultInputs((prev) => {
        const next = { ...prev };
        delete next[match.id];
        return next;
      });
      setResultTypeInputs((prev) => {
        const next = { ...prev };
        delete next[match.id];
        return next;
      });
      flashOk(
        "Resultado despublicado: el partido vuelve a pendiente sin programación y el avance del cuadro se regresa.",
      );
      await syncRankingAfterResultChange();
      await reloadSchedule();
    } catch (err) {
      flashErr(err, "No se pudo despublicar el resultado.");
    }
  }

  async function handleDelete(match: TournamentMatch) {
    if (!token || !canEditResults) return;
    if (!(await askConfirm("¿Eliminar este partido? Esta acción no se puede deshacer.", { acceptLabel: "Sí, eliminar partido", tone: "danger" }))) {
      return;
    }
    try {
      await api.matches.remove(token, tournamentId, match.id);
      flashOk("Partido eliminado.");
      await reloadSchedule();
    } catch (err) {
      flashErr(err, "No se pudo eliminar el partido.");
    }
  }

  function printableParticipantName(
    participant: TournamentMatch["player1"],
    notes?: string | null,
    sideNumber: 1 | 2 = 1,
  ): string {
    const name = participant?.display_name?.trim();
    return name ? nameWithDropMark(name, participant) : pendingSideLabel(notes, sideNumber);
  }

  function printableMatchLabel(match: TournamentMatch): string {
    const side = (participant: TournamentMatch["player1"], number: 1 | 2) =>
      !participant && isDeadSide(printDeadSides, match, number)
        ? DEAD_SIDE_LABEL
        : printableParticipantName(participant, match.notes, number);
    const base = `${side(match.player1, 1)} vs ${side(match.player2, 2)}`;
    const series = tieSeriesLabel(match);
    return series ? `${series} · ${base}` : base;
  }

  /** Marcador de la fila padre: en una serie es el global ("Serie 2-1"). */
  function printableScoreCell(match: TournamentMatch): string {
    // Un W.O. (también el automático del cuadro) no tiene marcador: dice W.O.
    if (!(match.score_text ?? "").trim() && match.winner_participant_id != null) {
      if (match.status === "walkover" || match.status === "no_show") return "W.O.";
      if (match.status === "defaulted") return "DEF";
    }
    // Eduardo 2026-10-06: el marcador se lee desde quien ganó (también la serie).
    const score = scoreTextForPrint(matchScoreFromWinner(match));
    return match.match_kind === "tie" && score !== "Por definir" ? `Serie ${score}` : score;
  }

  function scoreTextForPrint(score: string | null | undefined): string {
    const trimmed = score?.trim();
    if (!trimmed) return "Por definir";
    return trimmed.replace(/(?<=\d)\s*\/\s*(?=\d)/g, "-");
  }

  /** Ganador en el PDF de resultados: un Doble W.O. no tiene ganador. */
  function printableWinnerCell(match: TournamentMatch): string {
    if (match.status === "double_walkover") return "Doble W.O. · sin ganador";
    return match.winner?.display_name ?? "Por definir";
  }

  function printableResultLabel(match: TournamentMatch): string {
    // Partidos de una baja (Eduardo 2026-10-05): «Cancelado por baja» (antes
    // salía «Pendiente»), «W.O. por baja» o «Baja de Vicky · pendiente».
    const drop = dropMatchState(match);
    if (drop?.kind === "walkover") {
      // Revisión 2026-10-05: el W.O. por baja también dice quién ganó.
      const winner = match.winner?.display_name?.trim();
      return winner ? `${dropMatchLabel(drop)} · Ganó ${winner}` : dropMatchLabel(drop);
    }
    if (drop) return dropMatchLabel(drop);
    if (match.status === "cancelled") return STATUS_LABELS.cancelled ?? "Cancelado";
    if (isPlayedStatus(match)) {
      const score = scoreTextForPrint(matchScoreFromWinner(match));
      if (score !== "Por definir") return score;
      if (match.winner?.display_name) return `Ganó ${match.winner.display_name}`;
      return STATUS_LABELS[match.status] ?? match.status ?? "Resultado capturado";
    }
    return match.scheduled_date && match.scheduled_time && match.court ? "Programado" : "Pendiente";
  }

  function printableRoundSortValue(match: TournamentMatch): number {
    const roundNumber = parseRoundNumber(match.round_label);
    if (roundNumber !== null) return roundNumber;
    const eliminationOrder = eliminationRoundOrder(match.round_label);
    if (eliminationOrder !== null) return 1000 + eliminationOrder;
    // Etiquetas legacy en español ("Dieciseisavos de final", "Cuartos…").
    const stageOrder = knockoutStageOrder(match.round_label);
    if (stageOrder !== null) return 1000 + stageOrder;
    if (isThirdPlaceLabel(match.round_label)) return 1020;
    return Number.MAX_SAFE_INTEGER;
  }

  function comparePrintableMatches(a: TournamentMatch, b: TournamentMatch): number {
    const roundDelta = printableRoundSortValue(a) - printableRoundSortValue(b);
    if (roundDelta !== 0) return roundDelta;
    const labelDelta = displayAdminRoundLabel(a.round_label).localeCompare(
      displayAdminRoundLabel(b.round_label),
      "es-MX",
      { numeric: true, sensitivity: "base" },
    );
    if (labelDelta !== 0) return labelDelta;
    const dateDelta = (a.scheduled_date ?? "9999-12-31").localeCompare(b.scheduled_date ?? "9999-12-31");
    if (dateDelta !== 0) return dateDelta;
    const timeDelta = (a.scheduled_time ?? "99:99").localeCompare(b.scheduled_time ?? "99:99");
    if (timeDelta !== 0) return timeDelta;
    return matchSortKey(a)[0] - matchSortKey(b)[0] || matchSortKey(a)[1] - matchSortKey(b)[1];
  }

  function selectedJornadaPrintLabel(): string | null {
    if (filterJornada === "all" || filterJornada === "none") return null;
    // Misma resolución que el chip del filtro (gate #136): raws reales de los
    // matches VISIBLES → displayAdminRoundLabel por alias, deduplicado (etapa
    // canónica si cubre a todos; si no, display combinado determinista); el
    // label construido es último recurso.
    const display = jornadaFilterDisplayLabel(filterJornada, visibleMatches, displayAdminRoundLabel);
    if (display !== null) return display;
    return `${adminRoundSingularLabel} ${String(filterJornada)}`;
  }

  function printableMatchRows(scope: "all" | "selected" = "all"): TournamentMatch[] {
    // SIEMPRE desde visibleMatches (no `matches`): el PDF comparte scope con
    // el filtro, el chip y el título — una fila legacy/oculta de la UI no
    // puede colarse a la impresión (gate #136, 6º re-gate).
    return printableScheduleRows(visibleMatches, scope, filterJornada).sort(comparePrintableMatches);
  }

  /**
   * Secciones del PDF ordenadas de forma determinista: jornadas 1..N primero
   * y luego la fase eliminatoria en orden canónico (Dieciseisavos → Final),
   * sin depender del orden de inserción del Map ni de las fechas.
   */
  function printableSections(rows: TournamentMatch[]): Array<{ label: string; matches: TournamentMatch[] }> {
    const byRound = new Map<string, { label: string; sort: number; matches: TournamentMatch[] }>();
    for (const m of rows) {
      const label = displayAdminRoundLabel(m.round_label);
      const sort = printableRoundSortValue(m);
      const existing = byRound.get(label);
      if (existing) {
        existing.matches.push(m);
        if (sort < existing.sort) existing.sort = sort;
      } else {
        byRound.set(label, { label, sort, matches: [m] });
      }
    }
    return [...byRound.values()]
      .sort((a, b) => a.sort - b.sort || a.label.localeCompare(b.label, "es-MX", { numeric: true, sensitivity: "base" }))
      .map(({ label, matches: sectionMatches }) => ({ label, matches: sectionMatches }));
  }

  /** Jornada de una sección del PDF: el número si todos sus partidos son de
   * la misma jornada de la temporada (no playoffs ni rondas de eliminación). */
  function printableSectionJornada(sectionMatches: TournamentMatch[]): number | null {
    const numbers = new Set<number>();
    for (const match of sectionMatches) {
      const numero = isRoundRobinPlayoffMatch(match) ? null : parseRoundNumber(match.round_label);
      if (numero === null) return null;
      numbers.add(numero);
    }
    return numbers.size === 1 ? [...numbers][0] : null;
  }

  /** Eduardo 2026-10-05 («Jornada 1 de xx a xx del 2026» en todos los PDF y
   * quién descansa): cada sección con el rango GUARDADO de su jornada (de
   * las categorías de sus partidos; sin fechas guardadas, como antes) y el
   * renglón «Descansan: …» de sus grupos debajo de la tabla. Si las
   * categorías de la sección tienen fechas distintas, el título queda sin
   * rango y debajo va uno por categoría («Sub-10: del … · Sub-12: del …»). */
  function printableJornadaSections(rows: TournamentMatch[]) {
    return printableSections(rows).map((section) => {
      const numero = printableSectionJornada(section.matches);
      if (numero === null) {
        return { ...section, range: null as string | null, title: section.label, rangesHtml: "", restsHtml: "" };
      }
      const ranges = savedJornadaRangesByDivision(
        jornadaSavedWindows,
        numero,
        section.matches.map((match) => match.division_id ?? null),
      );
      const range = ranges && new Set(ranges.map((item) => item.range)).size === 1 ? ranges[0].range : null;
      const rangesHtml =
        ranges && !range
          ? `<p class="print-section-ranges" data-print-ranges>${escapeHtml(
              ranges
                .map((item) => {
                  const name = divisions.find((division) => division.id === item.divisionId)?.name ?? "Sin categoría";
                  return `${name}: ${item.range}`;
                })
                .join(" · "),
            )}</p>`
          : "";
      const groupIds = new Set(
        section.matches.map((match) => match.group_id).filter((id): id is number => id !== null),
      );
      return {
        ...section,
        range,
        title: jornadaTitleWithRange(section.label, range),
        rangesHtml,
        restsHtml: restPrintHtml(restsForJornada(jornadaRestEntries, numero, groupIds), repeatedGroupNames),
      };
    });
  }

  /** Encabezado del PDF de UNA sola jornada: «Jornada 1 · del martes 6 al
   * domingo 11 de octubre de 2026» (sin fechas guardadas, como antes). */
  function printableJornadaHeader(
    sections: Array<{ label: string; range: string | null }>,
    jornadaLabel: string | null,
  ): string | null {
    const only = sections.length === 1 ? sections[0] : null;
    if (only?.range) return jornadaTitleWithRange(jornadaLabel ?? only.label, only.range);
    return jornadaLabel;
  }

  /** «Calendario PDF» (Publicar horarios, Eduardo 2026-10-01): el impreso se
   * reparte a los jugadores, así que si trae horarios sin publicar se pregunta
   * (respuesta al clic) si se imprimen solo los publicados o todo marcado.
   * Cuenta todo lo que el jugador no ve, también un cancelado sin publicar
   * (el jugador lo ve «Cancelado» sin hora). */
  async function handlePrintCalendarPdf(scope: "all" | "selected" = "all") {
    const rows = printableMatchRows(scope);
    const drafts = rows.filter((match) => isScheduleHiddenFromPlayers(match)).length;
    if (drafts === 0) {
      printCalendarPdf(scope);
      return;
    }
    const choice = await askChoice(
      `Hay ${horariosText(drafts)} sin publicar en esta impresión.\nLos jugadores aún no ${drafts === 1 ? "lo ven" : "los ven"}: si repartes el impreso, se enterarían antes de tiempo.`,
      {
        acceptLabel: "Imprimir solo publicados",
        secondaryLabel: "Imprimir todo y marcar los sin publicar",
      },
    );
    if (choice === "accept") printCalendarPdf(scope, "published-only");
    else if (choice === "secondary") printCalendarPdf(scope, "mark");
  }

  function printCalendarPdf(scope: "all" | "selected" = "all", drafts: "none" | "published-only" | "mark" = "none") {
    const printableMatches = printableMatchRows(scope).filter(
      (match) => drafts !== "published-only" || !isScheduleHiddenFromPlayers(match),
    );
    if (printableMatches.length === 0) {
      flashErr(null, "No hay partidos para imprimir.");
      return;
    }
    // «Imprimir todo y marcar»: la hora de cada sin publicar lleva «(sin publicar)».
    const draftMark = (match: TournamentMatch) =>
      drafts === "mark" && isScheduleHiddenFromPlayers(match) ? " (sin publicar)" : "";
    const jornadaSections = printableJornadaSections(printableMatches);
    // Sin calendario de canchas no hay fecha, hora ni cancha por partido (todo
    // salía «Por definir»): esas columnas se quitan y las fechas de la jornada
    // ya van en el título (Eduardo 2026-10-05).
    const withSchedule = usesCourtCalendar !== false;
    const colgroup = withSchedule
      ? '<col style="width:11%"/><col style="width:8%"/><col style="width:20%"/><col style="width:35%"/><col style="width:13%"/><col style="width:13%"/>'
      : '<col style="width:56%"/><col style="width:22%"/><col style="width:22%"/>';
    const scheduleHead = withSchedule ? "<th>Fecha</th><th>Hora</th><th>Cancha</th>" : "";
    const scheduleCells = (date: string, time: string, court: string) =>
      withSchedule ? `<td>${escapeHtml(date)}</td>
          <td>${escapeHtml(time)}</td>
          <td>${escapeHtml(court)}</td>
          ` : "";
    const sections = jornadaSections
      .map(
        ({ title: roundTitle, matches: roundMatches, rangesHtml, restsHtml }) => `
      <h2 class="print-section-title">${escapeHtml(roundTitle)}</h2>${rangesHtml}
      <table>
        <colgroup>${colgroup}</colgroup>
        <thead><tr>${scheduleHead}<th>Partido</th><th>Grupo</th><th>Resultado</th></tr></thead>
        <tbody>${roundMatches
          .map(
            (m) => `<tr>
          ${scheduleCells(
            m.scheduled_date ? formatDate(m.scheduled_date) : "Por definir",
            `${m.scheduled_time ?? "Por definir"}${draftMark(m)}`,
            m.court ?? "Por definir",
          )}<td>${escapeHtml(printableMatchLabel(m))}</td>
          <td>${escapeHtml(describeMatchGroup(m) || "Por definir")}</td>
          <td>${escapeHtml(m.match_kind === "tie" && isPlayedStatus(m) ? printableScoreCell(m) : printableResultLabel(m))}</td>
        </tr>${printableTieLines(m)
          .map(
            ({ line, label }) => `<tr class="print-line-row">
          ${scheduleCells(
            line.scheduled_date ? formatDate(line.scheduled_date) : "",
            line.scheduled_time ? `${line.scheduled_time}${draftMark(line)}` : "",
            line.court ?? "",
          )}<td class="print-line-label">${escapeHtml(`↳ ${label}`)}</td>
          <td></td>
          <td>${escapeHtml(isPlayedStatus(line) ? printableResultLabel(line) : "Pendiente")}</td>
        </tr>`,
          )
          .join("")}`,
          )
          .join("")}</tbody>
      </table>${restsHtml}`,
      )
      .join("");
    try {
      const jornadaLabel = scope === "selected" ? selectedJornadaPrintLabel() : null;
      const jornadaHeader = printableJornadaHeader(jornadaSections, jornadaLabel);
      openPrintWindow({
        title: `Calendario${jornadaLabel ? ` ${jornadaLabel}` : ""} · ${tournament?.name ?? "Torneos123"}`,
        eyebrow: "Calendario de juegos",
        heading: tournament?.name ?? "Torneos123",
        subtitle: [activeDivision?.name, jornadaHeader].filter(Boolean).join(" · ") || undefined,
        bodyHtml: sections,
        landscape: true,
        extraCss: PRINT_TABLE_CSS,
        // Eduardo 2026-10-01: logo del organizador arriba a la derecha, como
        // el resto de los impresos del admin.
        organizerSlot: true,
        organizerLogoUrl: uploadAssetHref(tournament?.organizer_logo_url),
      });
    } catch (err) {
      flashErr(err, "No se pudo abrir la impresión.");
    }
  }

  /** Ronda y grupo de cualquier categoría para el rol del día («Octavos de
   * final», «Jornada 2 · Grupo 1»): cada partido con la etapa de SU categoría. */
  function dayRollRoundLabel(match: TournamentMatch): string {
    const raw = (match.round_label ?? "").trim();
    const round =
      eliminationRoundOrder(raw) !== null
        ? eliminationRoundDisplayLabel(raw)
        : canonicalOrRawRoundLabel(raw, playoffStagesByDivision.get(match.division_id ?? null) ?? {}, "");
    const group =
      match.group_id !== null
        ? match.group_name?.trim() || groups.find((item) => item.id === match.group_id)?.name || ""
        : "";
    return [round, group].filter(Boolean).join(" · ");
  }

  /** Resultado o estado del partido en el rol («6-4 6-3», «Pospuesto»). */
  function dayRollNote(match: TournamentMatch): string {
    if (isPlayedStatus(match)) return match.match_kind === "tie" ? printableScoreCell(match) : printableResultLabel(match);
    if (match.status === "postponed" || match.status === "in_progress") return STATUS_LABELS[match.status] ?? match.status;
    return "";
  }

  /** «Rol de juego del día» (2026-10-02): el orden de juego del día en
   * pantalla (todas las categorías con «Todas las categorías»), por hora y
   * cancha. Como el «Calendario PDF», si trae horarios sin publicar pregunta
   * si se imprimen solo los publicados o todo marcado. */
  async function handlePrintDayRoll() {
    const rows = dayRoll;
    if (rows.length === 0) {
      flashErr(null, "No hay partidos en este día para imprimir.");
      return;
    }
    const drafts = rows.filter((row) => row.hiddenFromPlayers).length;
    if (drafts === 0) {
      printDayRoll(rows, "none");
      return;
    }
    const choice = await askChoice(
      `Hay ${horariosText(drafts)} sin publicar en este rol de juego.\nLos jugadores aún no ${drafts === 1 ? "lo ven" : "los ven"}: si repartes el impreso, se enterarían antes de tiempo.`,
      {
        acceptLabel: "Imprimir solo publicados",
        secondaryLabel: "Imprimir todo y marcar los sin publicar",
      },
    );
    if (choice === "accept") printDayRoll(rows, "published-only");
    else if (choice === "secondary") printDayRoll(rows, "mark");
  }

  function printDayRoll(rows: DayRollRow[], drafts: "none" | "published-only" | "mark") {
    if (drafts === "published-only" && rows.every((row) => row.hiddenFromPlayers)) {
      flashErr(null, "Aún no hay horarios publicados en este día.");
      return;
    }
    const scopeLabel = calendarShowsAllDivisions
      ? hasSeveralDivisions
        ? "Todas las categorías"
        : divisions[0]?.name ?? null
      : activeDivision?.name ?? null;
    try {
      openPrintWindow({
        title: `Rol de juego ${calendarDate} · ${tournament?.name ?? "Torneos123"}`,
        eyebrow: "Rol de juego del día",
        heading: tournament?.name ?? "Torneos123",
        subtitle: [formatLongDateEs(calendarDate) || calendarDate, scopeLabel].filter(Boolean).join(" · "),
        bodyHtml: dayRollPrintHtml(rows, {
          drafts,
          showVenue: tournamentVenues.length > 1 || rows.some((row) => row.venue !== null),
          roundLabel: dayRollRoundLabel,
          matchLabel: printableMatchLabel,
          note: dayRollNote,
          tieLines: printableTieLines,
          lineNote: (line) => (isPlayedStatus(line) ? printableResultLabel(line) : ""),
        }),
        landscape: true,
        extraCss: DAY_ROLL_PRINT_CSS,
        organizerSlot: true,
        organizerLogoUrl: uploadAssetHref(tournament?.organizer_logo_url),
      });
    } catch (err) {
      flashErr(err, "No se pudo abrir la impresión.");
    }
  }

  function openControlSheet() {
    setControlSheetDivision("all");
    setControlSheetChief(chiefReferees[calendarDate] ?? "");
    setControlSheetOpen(true);
  }

  function controlSheetRows(): DayRollRow[] {
    return controlSheetDivision === "all"
      ? dayRoll
      : dayRoll.filter((row) => String(row.divisionId) === controlSheetDivision);
  }

  function controlSheetBody(): string {
    const rows = controlSheetRows();
    return controlSheetHtml(rows, {
      dateLabel: formatLongDateEs(calendarDate) || calendarDate,
      venue: calendarEffectiveVenueLabel || tournament?.venue_name || "",
      categoryLabel: controlSheetCategoryLabel(rows, divisions),
      chiefReferee: controlSheetChief,
      showReferee: controlSheetShowReferee,
      divisions,
    });
  }

  async function printControlSheet() {
    const rows = controlSheetRows();
    if (rows.length === 0) {
      flashErr(null, "No hay partidos para esta hoja.");
      return;
    }
    const chief = controlSheetChief.trim();
    if (token && canAssignReferees && chief !== (chiefReferees[calendarDate] ?? "")) {
      // El nombre del árbitro general se guarda por día (solo texto).
      try {
        const saved = await api.referees.updateSettings(token, tournamentId, {
          chief_referee_date: calendarDate,
          chief_referee_name: chief,
        });
        setChiefReferees(saved.data.chief_referees);
      } catch {
        // Si no se pudo guardar, la hoja sale igual con el nombre escrito.
      }
    }
    try {
      openPrintWindow({
        title: `Hoja de control ${calendarDate} · ${tournament?.name ?? "Torneos123"}`,
        eyebrow: "Hoja de control del día",
        heading: tournament?.name ?? "Torneos123",
        subtitle: formatLongDateEs(calendarDate) || calendarDate,
        bodyHtml: controlSheetBody(),
        landscape: true,
        extraCss: CONTROL_SHEET_PRINT_CSS,
        organizerSlot: true,
        organizerLogoUrl: uploadAssetHref(tournament?.organizer_logo_url),
      });
    } catch (err) {
      flashErr(err, "No se pudo abrir la impresión.");
    }
  }

  function renderControlSheetDialog() {
    const divisionIds = [...new Set(dayRoll.map((row) => row.divisionId).filter((id): id is number => id != null))];
    const dayDivisions = divisions.filter((division) => divisionIds.includes(division.id));
    return (
      <div className="control-sheet-dialog" role="dialog" aria-modal="true" aria-labelledby="control-sheet-title">
        <div className="control-sheet-dialog__panel">
          <div className="control-sheet-dialog__bar">
            <strong id="control-sheet-title" className="control-sheet-dialog__eyebrow">
              IMPRIMIR HOJA DE CONTROL
            </strong>
            {dayDivisions.length > 1 ? (
              <select className="t123-select"
                aria-label="Categoría de la hoja"
                value={controlSheetDivision}
                onChange={(event) => setControlSheetDivision(event.target.value)}
              >
                <option value="all">Todas las categorías</option>
                {dayDivisions.map((division) => (
                  <option key={division.id} value={String(division.id)}>
                    {division.name}
                  </option>
                ))}
              </select>
            ) : null}
            <span className="control-sheet-dialog__day">{formatLongDateEs(calendarDate) || calendarDate}</span>
            <input
              aria-label="Árbitro general"
              list="control-sheet-chiefs"
              placeholder="Árbitro general"
              value={controlSheetChief}
              maxLength={120}
              onChange={(event) => setControlSheetChief(event.target.value)}
            />
            <datalist id="control-sheet-chiefs">
              {tournamentReferees.map((ref) => (
                <option key={ref.user_id} value={ref.full_name} />
              ))}
            </datalist>
            <label className="control-sheet-dialog__check">
              <input
                type="checkbox"
                checked={controlSheetShowReferee}
                onChange={(event) => setControlSheetShowReferee(event.target.checked)}
              />
              Mostrar árbitro asignado
            </label>
            <span className="control-sheet-dialog__spacer" />
            <button type="button" className="btn btn--ghost" onClick={() => setControlSheetOpen(false)}>
              Cerrar
            </button>
            <button type="button" className="btn btn--primary" onClick={() => void printControlSheet()} data-control-sheet-print>
              🖨 Imprimir
            </button>
          </div>
          <div className="control-sheet-dialog__paper">
            <div className="control-sheet-dialog__paper-head">
              <strong>HOJA DE CONTROL DEL DÍA</strong>
              <span>{tournament?.name ?? ""}</span>
            </div>
            <style>{CONTROL_SHEET_PRINT_CSS}</style>
            <div dangerouslySetInnerHTML={{ __html: controlSheetBody() }} />
          </div>
        </div>
      </div>
    );
  }

  function printResultsPdf(scope: "all" | "selected" = "all") {
    const printableMatches = printableMatchRows(scope);
    if (printableMatches.length === 0) {
      flashErr(null, "No hay partidos para imprimir.");
      return;
    }
    const jornadaSections = printableJornadaSections(printableMatches);
    const sections = jornadaSections
      .map(
        ({ title: roundTitle, matches: roundMatches, rangesHtml, restsHtml }) => `
      <h2 class="print-section-title">${escapeHtml(roundTitle)}</h2>${rangesHtml}
      <table>
        <colgroup><col style="width:34%"/><col style="width:15%"/><col style="width:21%"/><col style="width:15%"/><col style="width:15%"/></colgroup>
        <thead><tr><th>Partido</th><th>Marcador</th><th>Ganador</th><th>Grupo</th><th>Resultado</th></tr></thead>
        <tbody>${roundMatches
          .map(
            (m) => `<tr>
          <td>${escapeHtml(printableMatchLabel(m))}</td>
          <td>${escapeHtml(printableScoreCell(m))}</td>
          <td>${escapeHtml(printableWinnerCell(m))}</td>
          <td>${escapeHtml(describeMatchGroup(m) || "Por definir")}</td>
          <td>${escapeHtml(m.match_kind === "tie" && isPlayedStatus(m) ? "Serie definida" : printableResultLabel(m))}</td>
        </tr>${printableTieLines(m)
          .map(
            ({ line, label }) => `<tr class="print-line-row">
          <td class="print-line-label">${escapeHtml(`↳ ${label}`)}</td>
          <td>${escapeHtml(scoreTextForPrint(matchScoreFromWinner(line)))}</td>
          <td>${escapeHtml(printableLineWinner(line, m))}</td>
          <td></td>
          <td>${escapeHtml(isPlayedStatus(line) ? (STATUS_LABELS[line.status] ?? line.status) : "Pendiente")}</td>
        </tr>`,
          )
          .join("")}`,
          )
          .join("")}</tbody>
      </table>${restsHtml}`,
      )
      .join("");
    try {
      const jornadaLabel = scope === "selected" ? selectedJornadaPrintLabel() : null;
      const jornadaHeader = printableJornadaHeader(jornadaSections, jornadaLabel);
      openPrintWindow({
        title: `Resultados${jornadaLabel ? ` ${jornadaLabel}` : ""} · ${tournament?.name ?? "Torneos123"}`,
        eyebrow: "Resultados oficiales",
        heading: tournament?.name ?? "Torneos123",
        subtitle: [activeDivision?.name, jornadaHeader].filter(Boolean).join(" · ") || undefined,
        bodyHtml: sections,
        extraCss: PRINT_TABLE_CSS,
        organizerSlot: true,
        organizerLogoUrl: uploadAssetHref(tournament?.organizer_logo_url),
      });
    } catch (err) {
      flashErr(err, "No se pudo abrir la impresión.");
    }
  }

  async function handleAdvancedSave(match: TournamentMatch): Promise<boolean> {
    if (!canEditResults) return false;
    const resultType = resultExceptionValue(match);
    const result = resultInputs[match.id] ?? {};
    const hasResultInput =
      result.winner !== undefined || result.score !== undefined;
    const pending = hasPendingEdit(match);
    if (resultType === "double_walkover" && canMarkDoubleWalkover(match, legacyCrossIds)) {
      return handleDoubleWalkover(match);
    }
    if (isExceptionResultType(resultType)) {
      return handleWalkover(match, resultType);
    } else if (hasResultInput) {
      return handleRecordResult(match);
    } else if (pending) {
      return handleSaveEdit(match);
    } else {
      // QA Cowork #4: esto era un flash VERDE — "publicar" sin nada capturado
      // parecía éxito. Ahora es un aviso de error que explica qué falta.
      setMessage(null);
      setError(
        `No se publicó nada en ${matchShortLabel(match)}: captura el marcador (o marca Retiro o Default) antes de Publicar.`,
      );
      return false;
    }
  }

  function resultExceptionValue(match: TournamentMatch): ResultTypeInput {
    return resultTypeInputs[match.id] ?? exceptionTypeFromMatch(match);
  }

  function resultWinnerValue(match: TournamentMatch): number | null {
    const winner = resultInputs[match.id]?.winner;
    if (winner === undefined) return match.winner_participant_id ?? null;
    if (winner === "") return null;
    return Number(winner);
  }

  function resultScoreValue(match: TournamentMatch): string {
    const score = resultInputs[match.id]?.score;
    if (score === undefined) return match.score_text?.trim() ?? "";
    return score.trim();
  }

  function hasResultDraft(match: TournamentMatch): boolean {
    const result = resultInputs[match.id];
    return Boolean(
      resultTypeInputs[match.id] ||
        result?.winner !== undefined ||
        result?.score !== undefined ||
        result?.scoreDraft !== undefined,
    );
  }

  function resultScoreDraft(match: TournamentMatch): ScoreDraft {
    const result = resultInputs[match.id];
    return result?.scoreDraft ?? scoreDraftFromMatch(match, isPickleballSport(tournament?.sport), result?.score, isSuperTiebreakAdminFormat(formatConfig, tournament?.sport));
  }

  function mutateResultScoreDraft(match: TournamentMatch, nextDraft: ScoreDraft) {
    const score = scoreTextFromDraft(nextDraft);
    setResultInputs((prev) => ({
      ...prev,
      [match.id]: {
        ...(prev[match.id] ?? {}),
        score,
        scoreDraft: nextDraft,
      },
    }));
  }

  function updateResultScoreDraft(match: TournamentMatch, index: number, field: ScoreSetField, rawValue: string) {
    mutateResultScoreDraft(match, updateScoreSet(resultScoreDraft(match), index, field, rawValue));
  }

  /** Controles "+ Set / − Set" compartidos por la hoja móvil y la fila. */
  function renderScoreSetControls(match: TournamentMatch, draft: ScoreDraft, busy: boolean) {
    const pickleball = isPickleballSport(tournament?.sport);
    return (
      <div className="score-sets-controls">
        {draft.sets.length < MAX_SCORE_SETS ? (
          <button
            type="button"
            className="btn btn--ghost btn--compact"
            disabled={busy}
            onClick={() => mutateResultScoreDraft(match, addScoreSet(draft, !pickleball && draft.sets.length === 2 && isSuperTiebreakAdminFormat(formatConfig, tournament?.sport)))}
          >
            + {pickleball ? "Game" : "Set"}
          </button>
        ) : null}
        {draft.sets.length > MIN_SCORE_SETS ? (
          <button type="button" className="btn btn--ghost btn--compact" disabled={busy} onClick={() => mutateResultScoreDraft(match, removeLastScoreSet(draft))}>
            − {pickleball ? "Game" : "Set"}
          </button>
        ) : null}
      </div>
    );
  }

  function mobileResultSummary(match: TournamentMatch): string {
    const winnerId = resultWinnerValue(match);
    const winner = winnerId === match.player1?.id ? match.player1 : winnerId === match.player2?.id ? match.player2 : null;
    return resultSummaryText({
      // Eduardo 2026-10-06: el resumen se lee desde quien gana.
      score: scoreTextFromWinner(resultScoreValue(match), winner ? winner === match.player1 : null),
      winnerName: winner ? matchPlayerName(winner) : null,
      exception: resultExceptionValue(match),
    });
  }

  function renderMobileCaptureCell(match: TournamentMatch) {
    const hasSomething = Boolean(resultScoreValue(match)) || resultWinnerValue(match) !== null || Boolean(resultExceptionValue(match));
    // Con el resultado ya publicado, los renglones de arriba (formato de la vista
    // del jugador) ya lo dicen: el texto "7-6 6-0 · Gana X" solo se muestra
    // mientras hay una captura sin publicar.
    const showSummaryText = match.winner_participant_id == null || hasResultDraft(match);
    // Sobre un Doble W.O. no se captura: primero se despublica.
    if (match.status === "double_walkover") {
      return (
        <div className="result-capture-summary">
          <span className="result-capture-summary__text">{mobileResultSummary(match)}</span>
        </div>
      );
    }
    return (
      <div className="result-capture-summary">
        {showSummaryText ? (
          <span className={`result-capture-summary__text${hasSomething ? "" : " muted"}`}>{mobileResultSummary(match)}</span>
        ) : null}
        <button
          type="button"
          className="btn btn--compact result-capture-summary__open"
          onClick={() => setMobileCaptureMatchId(match.id)}
          disabled={bulkSavingResults || savingMatchId === match.id}
        >
          {hasSomething ? "Editar resultado" : "Capturar resultado"}
        </button>
      </div>
    );
  }

  function renderMobileResultSheet() {
    if (mobileCaptureMatchId === null) return null;
    const match = visibleMatches.flatMap((item) => [item, ...(item.lines ?? [])]).find((item) => item.id === mobileCaptureMatchId) ?? null;
    if (!match || !match.player1 || !match.player2) return null;
    const p1 = match.player1;
    const p2 = match.player2;
    const draft = resultScoreDraft(match);
    const pickleball = isPickleballSport(tournament?.sport);
    const busy = bulkSavingResults || savingMatchId === match.id;
    const winnerId = resultWinnerValue(match);
    const exception = resultExceptionValue(match);
    const close = () => setMobileCaptureMatchId(null);
    const chooseWinner = (participantId: number) =>
      setResultInputs((prev) => ({
        ...prev,
        [match.id]: { ...(prev[match.id] ?? {}), winner: winnerId === participantId ? "" : participantId },
      }));
    const summary = mobileResultSummary(match);
    return createPortal(
      <div className="result-sheet" role="dialog" aria-modal="true" aria-label={`Capturar resultado: ${matchShortLabel(match)}`}>
        <div className="result-sheet__backdrop" onClick={close} />
        <div className="result-sheet__panel">
          <header className="result-sheet__header">
            <div>
              <span className="player-pro-eyebrow">Capturar resultado</span>
              <strong>{[`Juego ${match.match_number ?? match.id}`, displayMatchupLabel(match.round_label), describeMatchGroup(match)].filter(Boolean).join(" · ")}</strong>
            </div>
            <button type="button" className="result-sheet__close" onClick={close} aria-label="Cerrar">×</button>
          </header>

          <div className="result-sheet__body">
            <p className="result-sheet__hint">
              Toca el nombre del ganador y captura los games de cada {pickleball ? "game" : "set"} en su columna.
              {pickleball ? "" : " Con 7-6 aparece abajo el tie-break de cada jugador; toca el encabezado de un set para capturarlo como súper muerte (a puntos)."}
            </p>

            {/* Eduardo 2026-09-20: mismo acomodo que ve el jugador — jugadores en
                FILAS a la izquierda y sets en COLUMNAS a la derecha — pero editable. */}
            {/* Con 4-5 sets el nombre va en su propio renglón (ancho completo)
                arriba de sus casillas: en 360-390 px la columna del nombre
                quedaba de ~40 px y partía «Paulo Fernández» letra por letra. */}
            <div
              className={`result-sheet__board${draft.sets.length >= 4 ? " result-sheet__board--many-sets" : ""}`}
              style={{
                gridTemplateColumns: draft.sets.length >= 4
                  ? `repeat(${draft.sets.length}, minmax(2.6rem, 1fr))`
                  : `minmax(0, 1fr) repeat(${draft.sets.length}, 3.1rem)`,
              }}
            >
              {draft.sets.length >= 4 ? null : <span className="result-sheet__board-corner">Jugador</span>}
              {draft.sets.map((set, index) =>
                pickleball ? (
                  <span key={index} className="result-sheet__board-head">{scoreSetDraftLabel(index, set, true)}</span>
                ) : (
                  <button
                    key={index}
                    type="button"
                    className={`result-sheet__board-head result-sheet__board-head--toggle${set.superTb ? " is-active" : ""}`}
                    onClick={() => mutateResultScoreDraft(match, toggleScoreSetSuperTb(draft, index))}
                    disabled={busy}
                    aria-pressed={set.superTb}
                    title={set.superTb ? "Súper muerte a puntos · toca para volver a set normal" : "Set normal · toca para capturarlo como súper muerte (a puntos)"}
                  >
                    {set.superTb ? "TB" : `S${index + 1}`}
                  </button>
                ),
              )}
              {([[p1, "p1"], [p2, "p2"]] as Array<[typeof p1, "p1" | "p2"]>).map(([participant, side]) => {
                const isWinner = winnerId === participant.id;
                const displayName = matchPlayerName(participant);
                return (
                  <div key={participant.id} className={`result-sheet__board-row${isWinner ? " is-winner" : ""}`}>
                    <button
                      type="button"
                      className="result-sheet__board-name"
                      onClick={() => chooseWinner(participant.id)}
                      disabled={busy}
                      aria-pressed={isWinner}
                      title={isWinner ? "Ganador · toca para quitar" : "Toca para marcarlo como ganador"}
                    >
                      <span className="result-sheet__board-check" aria-hidden="true">{isWinner ? "✓" : ""}</span>
                      <span className="result-sheet__board-player">{displayName}</span>
                    </button>
                    {draft.sets.map((set, index) => {
                      const label = scoreSetDraftLabel(index, set, pickleball);
                      // Eduardo 2026-09-24: el tie-break se captura en cualquier set
                      // (mini-sets 4-3, 5-4…), no solo en 7-6.
                      const tbEnabled = !pickleball && !set.superTb && Boolean(set.p1 || set.p2);
                      // El tie-break se guarda como ganador/perdedor del SET: el que
                      // tiene más games en el set es quien captura "tbWin".
                      const other = side === "p1" ? set.p2 : set.p1;
                      const tbField: ScoreSetField = Number(set[side]) > Number(other) ? "tbWin" : "tbLose";
                      return (
                        <span key={index} className="result-sheet__board-cell">
                          <input
                            className="result-sheet__board-score"
                            type="number"
                            min="0"
                            max="99"
                            inputMode="numeric"
                            aria-label={`${label} ${displayName}`}
                            value={set[side]}
                            onChange={(event) => updateResultScoreDraft(match, index, side, event.target.value)}
                            disabled={busy}
                          />
                          {tbEnabled ? (
                            <input
                              className="result-sheet__board-tb"
                              type="number"
                              min="0"
                              max="99"
                              inputMode="numeric"
                              placeholder="TB"
                              aria-label={`Puntos del tie-break de ${displayName} (${label})`}
                              value={set[tbField]}
                              onChange={(event) => updateResultScoreDraft(match, index, tbField, event.target.value)}
                              disabled={busy}
                            />
                          ) : null}
                        </span>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            {renderScoreSetControls(match, draft, busy)}

            <div className="result-sheet__exception" role="group" aria-label="Excepción">
              {resultExceptionOptions(match, legacyCrossIds).map(([value, label]) => (
                <button
                  type="button"
                  key={label}
                  className={`result-sheet__chip${exception === value ? " is-active" : ""}`}
                  onClick={() => setResultTypeInputs((prev) => ({ ...prev, [match.id]: value }))}
                  disabled={busy}
                  aria-pressed={exception === value}
                >
                  {label}
                </button>
              ))}
            </div>

            <p className="result-sheet__summary" aria-live="polite">{summary}</p>
          </div>

          <footer className="result-sheet__footer">
            <button type="button" className="btn btn--ghost" onClick={close} disabled={busy}>Cancelar</button>
            <button
              type="button"
              className="btn result-sheet__publish"
              disabled={busy}
              onClick={() => {
                void handleAdvancedSave(match).then((ok) => {
                  if (ok) setMobileCaptureMatchId(null);
                });
              }}
            >
              {busy ? "Publicando…" : "✓ Publicar"}
            </button>
          </footer>
        </div>
      </div>,
      document.body,
    );
  }

  function renderAdminScoreboard(match: TournamentMatch, opts: { linkNames?: boolean; compact?: boolean } = {}) {
    const draft = resultScoreDraft(match);
    const pickleball = isPickleballSport(tournament?.sport);
    const busy = bulkSavingResults || savingMatchId === match.id;
    // 4-5 sets en la columna compacta: el nombre va en su propio renglón arriba
    // de sus casillas (si no, las casillas se salían de la columna).
    const manySets = Boolean(opts.compact) && draft.sets.length >= 4;
    const rows = [
      { participant: match.player1, side: "p1" as const },
      { participant: match.player2, side: "p2" as const },
    ];
    return (
      <div className={`player-result-scoreboard admin-result-scoreboard${opts.compact ? " admin-result-scoreboard--compact" : ""}${manySets ? " admin-result-scoreboard--many-sets" : ""}`} aria-label="Captura estructurada de marcador">
        <div
          className="player-result-scoreboard__grid"
          style={{
            gridTemplateColumns: manySets
              ? `3.4rem repeat(${draft.sets.length}, minmax(2.3rem, 2.5rem))`
              : opts.compact ? `minmax(52px, 1fr) 3.4rem repeat(${draft.sets.length}, 2.5rem)` : `minmax(150px, 1fr) 4.5rem repeat(${draft.sets.length}, 3.25rem)`,
          }}
        >
          {manySets ? null : <div className="player-result-scoreboard__corner">Jugador</div>}
          <div className="player-result-scoreboard__winner-label">Ganador</div>
          {draft.sets.map((set, index) => (
            pickleball ? (
              <div key={index} className="player-result-scoreboard__set-label">{scoreSetDraftLabel(index, set, true)}</div>
            ) : (
              <button
                key={index}
                type="button"
                className={`player-result-scoreboard__set-label score-set-toggle${set.superTb ? " is-super" : ""}`}
                title={set.superTb ? "Súper muerte a puntos · clic para volver a set normal" : "Set normal · clic para capturarlo como súper muerte (a puntos)"}
                onClick={() => mutateResultScoreDraft(match, toggleScoreSetSuperTb(draft, index))}
                disabled={busy}
                aria-pressed={set.superTb}
              >
                {set.superTb ? "TB" : `Set ${index + 1}`}
              </button>
            )
          ))}
          {rows.map((row) => {
            if (!row.participant) return null;
            // Quien se dio de baja sigue aquí, marcado «… · baja».
            const displayName = nameWithDropMark(matchPlayerName(row.participant), row.participant);
            return (
              <div className="player-result-scoreboard__row" key={row.participant.id}>
                <div className="player-result-scoreboard__player">
                  {opts.linkNames ? (
                    <Link className="player-result-scoreboard__name" to={`/admin/tournaments/${tournamentId}/players/${row.participant.id}`} title={displayName}>
                      {displayName}
                    </Link>
                  ) : (
                    <span className="player-result-scoreboard__name" title={displayName}>
                      {/* Compacto (Resultados): el nombre completo ya está en
                          la columna Partido; aquí basta el primer nombre. */}
                      {opts.compact && !manySets ? matchPlayerName(row.participant).split(/\s+/)[0] ?? "" : displayName}
                    </span>
                  )}
                </div>
                <label className="player-result-scoreboard__winner-cell" aria-label={`Ganador ${displayName}`}>
                  <input
                    type="radio"
                    name={`admin-winner-${match.id}`}
                    value={row.participant.id}
                    checked={resultWinnerValue(match) === row.participant.id}
                    onChange={() =>
                      setResultInputs((prev) => ({
                        ...prev,
                        [match.id]: {
                          ...(prev[match.id] ?? {}),
                          winner: row.participant?.id ?? "",
                        },
                      }))
                    }
                    disabled={busy}
                  />
                  <span className="player-result-scoreboard__winner-indicator">✓</span>
                </label>
                {draft.sets.map((set, index) => (
                  <input
                    key={index}
                    className="player-result-scoreboard__score-input"
                    aria-label={`${scoreSetDraftLabel(index, set, pickleball)} ${displayName}`}
                    type="number"
                    min="0"
                    max="99"
                    inputMode="numeric"
                    value={set[row.side]}
                    onChange={(event) => updateResultScoreDraft(match, index, row.side, event.target.value)}
                    disabled={busy}
                  />
                ))}
              </div>
            );
          })}
          {/* Puntos del tie-break: editable en cualquier set que no sea súper
              muerte (Eduardo 2026-09-24: hay mini-sets 4-3 con tie-break); se
              guarda como "7-6(7-5)" / "4-3(7-5)". */}
          {!pickleball && (
            <>
              {([
                ["TB (ganador)", "tbWin", "Puntos del ganador del tie-break"],
                ["TB (perdedor)", "tbLose", "Puntos del perdedor del tie-break"],
              ] as Array<[string, ScoreSetField, string]>).map(([rowLabel, field, rowTitle]) => (
                <div key={rowLabel} className="player-result-scoreboard__row admin-result-scoreboard__tiebreak-row">
                  <div className="player-result-scoreboard__player">
                    <span className="player-result-scoreboard__name muted" title={`${rowTitle} — se guarda como 7-6(7-5)`}>
                      {opts.compact ? rowLabel.replace("TB (ganador)", "TB gan.").replace("TB (perdedor)", "TB perd.") : rowLabel}
                    </span>
                  </div>
                  <div className="player-result-scoreboard__winner-cell" aria-hidden="true" />
                  {draft.sets.map((set, index) => {
                    const enabled = !set.superTb;
                    return (
                      <input
                        key={index}
                        className="player-result-scoreboard__score-input admin-result-scoreboard__tiebreak-input"
                        aria-label={`${rowTitle} (${scoreSetDraftLabel(index, set, false)})`}
                        type="number"
                        min="0"
                        max="99"
                        inputMode="numeric"
                        placeholder={enabled ? "TB" : "—"}
                        value={enabled ? set[field] : ""}
                        onChange={(event) => updateResultScoreDraft(match, index, field, event.target.value)}
                        disabled={!enabled || busy}
                      />
                    );
                  })}
                </div>
              ))}
            </>
          )}
        </div>
        {renderScoreSetControls(match, draft, busy)}
      </div>
    );
  }

  function isMatchFinalLike(match: TournamentMatch): boolean {
    return ["completed", "walkover", "no_show", "defaulted", "retired", "double_walkover", "cancelled"].includes(
      String(match.status),
    );
  }

  function needsBulkResultPublish(match: TournamentMatch): boolean {
    // El Doble W.O. pide su propia confirmación: no entra al «Publicar todo».
    if (resultExceptionValue(match) === "double_walkover") return false;
    return !isMatchFinalLike(match) || hasResultDraft(match);
  }

  function isBulkResultComplete(match: TournamentMatch): boolean {
    const resultType = resultExceptionValue(match);
    if (isExceptionResultType(resultType)) return Boolean(resultWinnerValue(match));
    if (match.status === "walkover" || match.status === "no_show" || match.status === "defaulted" || match.status === "retired" || match.status === "cancelled") {
      return true;
    }
    // QA Cowork #6: mismo criterio que el Publicar individual — con un
    // marcador capturado el BACKEND infiere al ganador (implied_winner_from_
    // sets) o responde 409 si no es decisivo; el radio es opcional.
    return Boolean(resultWinnerValue(match)) || Boolean(resultScoreValue(match));
  }

  function getBulkResultRows(): TournamentMatch[] {
    const rows: TournamentMatch[] = [];
    for (const group of visibleGroupRows) {
      rows.push(...(visibleByGroup.get(group.id) ?? []));
    }
    return rows;
  }

  function handleDownloadResultsLayout() {
    const rows = getBulkResultRows();
    if (rows.length === 0) {
      flashOk("No hay partidos visibles para descargar con los filtros actuales.");
      return;
    }
    const deciderColumn = adminDeciderLabel(formatConfig, tournament?.sport);
    const deciderKey = deciderColumn === "TB" ? "tb" : "set_3";
    const header = [
      "partido_id",
      "grupo",
      "jornada",
      "retador",
      "retado",
      "ganador",
      "set_1_retador",
      "set_1_retado",
      "set_2_retador",
      "set_2_retado",
      `${deciderKey}_retador`,
      `${deciderKey}_retado`,
      "marcador",
      "excepcion",
      "notas",
    ];
    const sampleHelp = [
      "",
      "",
      "",
      "",
      "",
      "Usa 1 o 2",
      "6",
      "4",
      "6",
      "2",
      deciderColumn === "TB" ? "" : "",
      deciderColumn === "TB" ? "" : "",
      "Auto si llenas sets; también puedes escribir 6-4 6-2",
      "normal | walkover | retired | defaulted",
      "Opcional",
    ];
    const body = rows.map((match) => {
      const draft = scoreDraftFromMatch(match, isPickleballSport(tournament?.sport));
      return [
        match.id,
        describeMatchGroup(match),
        displayMatchupLabel(match.round_label),
        matchPlayerName(match.player1),
        matchPlayerName(match.player2),
        match.winner_participant_id === match.player1?.id
          ? "1"
          : match.winner_participant_id === match.player2?.id
            ? "2"
            : "",
        draft.sets[0]?.p1 ?? "",
        draft.sets[0]?.p2 ?? "",
        draft.sets[1]?.p1 ?? "",
        draft.sets[1]?.p2 ?? "",
        draft.sets[2]?.p1 ?? "",
        draft.sets[2]?.p2 ?? "",
        match.score_text ?? "",
        match.status === "retired" || match.status === "defaulted" ? match.status : match.status === "walkover" || match.status === "no_show" ? "walkover" : "normal",
        match.notes ?? "",
      ];
    });
    const csv = [header, sampleHelp, ...body]
      .map((row) => row.map(csvCell).join(","))
      .join("\n");
    const blob = new Blob([`\uFEFF${csv}\n`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `layout_resultados_torneo_${tournamentId}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  function resolveBulkWinner(match: TournamentMatch, rawWinner: string): number | "" {
    const value = rawWinner.trim();
    if (!value) return "";
    const normalized = normalizeLayoutHeader(value);
    if (["1", "jugador_1", "player_1", "local"].includes(normalized)) return match.player1?.id ?? "";
    if (["2", "jugador_2", "player_2", "visitante"].includes(normalized)) return match.player2?.id ?? "";
    const numeric = Number(value);
    if (numeric === match.player1?.id || numeric === match.player2?.id) return numeric;
    if (normalizeLayoutHeader(matchPlayerName(match.player1)) === normalized) return match.player1?.id ?? "";
    if (normalizeLayoutHeader(matchPlayerName(match.player2)) === normalized) return match.player2?.id ?? "";
    throw new Error(`Ganador inválido para ${matchShortLabel(match)}. Usa 1, 2 o el ID del participante.`);
  }

  async function handleBulkResultsLayoutUpload(file: File | null) {
    if (!file || !canEditResults) return;
    try {
      const text = await file.text();
      const rows = parseBulkResultLayout(text, tournament?.sport).filter(
        (row) => row.winnerRaw.trim() || row.score.trim() || scoreDraftHasAnyValue(row.scoreDraft) || row.resultType || row.notes.trim(),
      );
      if (rows.length === 0) {
        flashOk("El layout no trae resultados para cargar.");
        return;
      }
      const matchesById = new Map(visibleMatches.map((match) => [match.id, match]));
      const nextResults: Record<number, { winner?: number | ""; score?: string }> = {};
      const nextTypes: Record<number, ResultTypeInput> = {};
      const nextReasons: Record<number, string> = {};

      for (const row of rows) {
        const match = matchesById.get(row.matchId);
        if (!match) {
          throw new Error(`Partido #${row.matchId} no existe en este torneo o está oculto por filtros.`);
        }
        const score = row.score.trim();
        nextResults[match.id] = {
          winner: resolveBulkWinner(match, row.winnerRaw),
          score,
          ...(row.scoreDraft ? { scoreDraft: row.scoreDraft } : { scoreDraft: scoreDraftFromText(score, isPickleballSport(tournament?.sport)) }),
        };
        nextTypes[match.id] = row.resultType;
        if (row.notes.trim()) nextReasons[match.id] = row.notes.trim();
      }

      setResultInputs((prev) => ({ ...prev, ...nextResults }));
      setResultTypeInputs((prev) => ({ ...prev, ...nextTypes }));
      setActionReasons((prev) => ({ ...prev, ...nextReasons }));
      flashOk(`Layout cargado: ${rows.length} resultado(s) listo(s) para revisar y publicar.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo leer el layout de resultados.");
      setMessage(null);
    }
  }

  async function handlePublishAllResults() {
    if (!token || !canEditResults || bulkSavingResults) return;
    const rows = getBulkResultRows();
    const rowsToPublish = rows.filter(needsBulkResultPublish);
    const incompleteRows = rowsToPublish.filter((match) => !isBulkResultComplete(match));

    if (incompleteRows.length > 0) {
      const first = incompleteRows[0];
      setError(
        `No se puede publicar todavía: hay ${incompleteRows.length} resultado(s) incompleto(s). Revisa ${matchShortLabel(first)}.`,
      );
      setMessage(null);
      return;
    }

    if (rowsToPublish.length === 0) {
      flashOk("No hay resultados pendientes por publicar con los filtros actuales.");
      return;
    }

    setBulkSavingResults(true);
    setSavingMatchId(null);
    try {
      for (const match of rowsToPublish) {
        const resultType = resultExceptionValue(match);
        if (isExceptionResultType(resultType)) {
          await api.matches.walkover(token, tournamentId, match.id, {
            winner_participant_id: resultWinnerValue(match),
            notes: (actionReasons[match.id] ?? "").trim() || null,
            exception_type: resultType,
            ...(resultType === "walkover" ? {} : { score_text: resultScoreValue(match) }),
          });
        } else {
          await api.matches.recordResult(token, tournamentId, match.id, {
            winner_participant_id: resultWinnerValue(match),
            score_text: resultScoreValue(match),
          });
        }
      }
      setResultInputs((prev) => {
        const next = { ...prev };
        for (const match of rowsToPublish) delete next[match.id];
        return next;
      });
      setResultTypeInputs((prev) => {
        const next = { ...prev };
        for (const match of rowsToPublish) delete next[match.id];
        return next;
      });
      setActionReasons((prev) => {
        const next = { ...prev };
        for (const match of rowsToPublish) delete next[match.id];
        return next;
      });
      flashOk(`Publicados ${rowsToPublish.length} resultado(s). Ranking actualizado.`);
      await syncRankingAfterResultChange();
      await reloadSchedule();
    } catch (err) {
      // Primero recargar (los que sí se guardaron se ven) y DESPUÉS el error:
      // la carga limpia el error al empezar y el aviso se perdía.
      await reloadSchedule();
      flashErr(err, "No se pudieron publicar todos los resultados.");
    } finally {
      setBulkSavingResults(false);
    }
  }

  function handleCalendarCardSelect(match: TournamentMatch) {
    if (!canEditSchedule) return;
    setSelectedMatchId(match.id);
    setError(null);
    setMessage(
      `Seleccionado para mover: ${matchShortLabel(match)}. Toca una hora libre para cambiarlo.`,
    );
  }

  /** Elegir de la cola: limpia el error anterior (si no, el aviso rojo
   * "Selecciona un partido primero" volvía a salir justo después de hacerlo). */
  function handleCalendarQueueSelect(match: TournamentMatch) {
    setSelectedMatchId(match.id);
    setError(null);
    setCalendarNotice(null);
  }

  /** El partido ya está en esa cancha y hora (misma fecha y sede). */
  function isCurrentCalendarSlot(match: TournamentMatch, court: string, time: string): boolean {
    return (
      match.scheduled_date === calendarDate &&
      (match.scheduled_time ?? "").trim() === time &&
      (match.court ?? "").trim() === court &&
      (match.venue_name ?? null) === calendarVenueName
    );
  }

  async function handleCalendarSchedule(
    match: TournamentMatch,
    scheduledDate: string,
    scheduledTime: string,
    court: string,
    allowRoundOrderConflict = false,
  ) {
    if (!token || !canEditSchedule) return;
    if (!scheduledDate) {
      setError("Selecciona una fecha para el calendario.");
      setMessage(null);
      return;
    }
    const startMin = parseTimeMinutes(scheduledTime);
    if (startMin === null) {
      setError("Esa hora no es válida.");
      setMessage(null);
      return;
    }
    // Choque por INTERVALO [inicio, inicio+bloque) contra todo lo de la
    // cancha (propios, otras categorías, otros torneos del club, bloqueos);
    // el partido que se mueve no se estorba a sí mismo (17:00 → 17:15). El
    // bloque es el de SU categoría (la abierta: el de la pantalla).
    const matchDuration = calendarOwnDurationFor(match);
    const check = startAvailability(court, startMin, matchDuration, calendarGridItems, {
      excludeMatchId: match.id,
    });
    if (!check.ok && check.reason !== "window") {
      setError(
        check.reason === "blocked"
          ? `${calendarConflictText(check.item, "programar", court)} Libérala en modo Bloquear.`
          : `${calendarConflictText(check.item, "programar", court)} Elige un espacio libre.`,
      );
      setMessage(null);
      return;
    }
    // Publicar horarios: mover uno ya publicado avisa a sus jugadores (con
    // motivo); uno sin publicar, o sin horario todavía, queda sin publicar y
    // nadie se entera hasta «Publicar horarios».
    const wasPublished = isSchedulePublished(match);
    const changeReason = wasPublished ? scheduleChangeReason.trim() : "";
    setCalendarBusy(true);
    try {
      await ensureCalendarSettingsSaved();
      const response = await api.matches.update(token, tournamentId, match.id, {
        scheduled_date: scheduledDate,
        scheduled_time: scheduledTime,
        court,
        // Multi-sede: el partido queda en la sede activa (null = principal,
        // el backend lo canonicaliza igual).
        venue_name: calendarVenueName,
        status: match.status,
        round_label: match.round_label,
        notes: match.notes,
        // El servidor revisa choques de cancha y jugador por intervalo con
        // este mismo bloque (sin él conserva la revisión por hora exacta);
        // a los partidos de otras categorías les aplica el bloque de la suya.
        duration_minutes: durationForServer(matchDuration),
        ...(match.scheduled_date && changeReason ? { schedule_change_reason: changeReason } : {}),
        // Orden de rondas: el admin ya vio el aviso y confirmó de todos modos.
        ...(allowRoundOrderConflict ? { allow_round_order_conflict: true } : {}),
      });
      setCalendarRoundOrderWarning(null);
      setSelectedMatchId(null);
      // «Avisamos» solo si el servidor dice que sí salió el aviso.
      flashOk(
        wasPublished
          ? `Movido: ${matchShortLabel(match)} · ${courtDisplayName(court)} ${scheduledTime}. ${publishedMoveNotice({
              notified: response?.data?.players_notified === true,
              hasPlayers: hasKnownPlayer(match),
              reason: changeReason,
            })}`
          : `Programado (sin publicar): ${matchShortLabel(match)} · ${courtDisplayName(court)} ${scheduledTime}`,
      );
      setCalendarClubVersion((v) => v + 1);
      await reloadSchedule();
    } catch (err) {
      if (err instanceof ApiError && err.code === "round_order_conflict") {
        // No es un error en rojo arriba: la barra (fija, data-no-toast) dice
        // por qué y deja confirmar esta misma propuesta de todos modos.
        setCalendarRoundOrderWarning({
          matchId: match.id,
          date: scheduledDate,
          court,
          time: scheduledTime,
          message: err.message,
          afterFeeders: err.details?.kind === "feeders" && typeof err.details?.limit_time === "string",
        });
        return;
      }
      flashErr(err, "No se pudo programar el partido en ese horario.");
    } finally {
      setCalendarBusy(false);
    }
  }

  /** El aviso de orden de rondas, si es de la propuesta que está en la barra. */
  function activeRoundOrderWarning() {
    const warning = calendarRoundOrderWarning;
    if (!warning || !selectedMatch || !calendarProposal) return null;
    return warning.matchId === selectedMatch.id &&
      warning.date === calendarDate &&
      warning.court === calendarProposal.court &&
      warning.time === calendarProposal.time
      ? warning
      : null;
  }

  /** Modo Bloquear: bloquea el BLOQUE configurado a partir de la fila tocada
   * (con filas de 15 min, tocar 17:15 bloquea 17:15–18:45 con bloque 90). */
  async function handleCalendarBlockCreate(slot: string, court: string) {
    if (!token || !canEditSchedule) return;
    if (!calendarEffectiveVenueLabel) {
      setError("Define la sede del torneo en Datos para poder bloquear horarios.");
      setMessage(null);
      return;
    }
    const startMin = parseTimeMinutes(slot);
    if (startMin === null) return;
    const check = startAvailability(court, startMin, calendarMatchDurationMinutes, calendarGridItems);
    if (!check.ok && check.reason !== "window") {
      setError(calendarConflictText(check.item, "bloquear", court));
      setMessage(null);
      return;
    }
    const endTime = timeFromMinutes(Math.min(startMin + calendarMatchDurationMinutes, 23 * 60 + 59));
    setCalendarBusy(true);
    try {
      await api.matches.createCourtBlock(token, {
        venue_name: calendarEffectiveVenueLabel,
        scheduled_date: calendarDate,
        start_time: slot,
        end_time: endTime,
        court,
        reason: calendarBlockReason,
        match_duration_minutes: durationForServer(calendarMatchDurationMinutes),
      });
      flashOk(`Bloqueada ${courtDisplayName(court)} ${slot}–${endTime} · ${calendarBlockReason}`);
      setCalendarClubVersion((v) => v + 1);
    } catch (err) {
      flashErr(err, "No se pudo actualizar el bloqueo.");
    } finally {
      setCalendarBusy(false);
    }
  }

  /** Tocar la tarjeta del bloqueo lo libera completo, sin importar con qué
   * tamaño de fila se creó (los de 90 min de antes también). */
  async function handleCalendarBlockDelete(block: CourtBlock) {
    if (!token || !canEditSchedule) return;
    setCalendarBusy(true);
    try {
      await api.matches.deleteCourtBlock(token, block.id);
      flashOk(`Bloqueo liberado: ${courtDisplayName(block.court)} ${block.start_time}–${block.end_time}`);
      setCalendarClubVersion((v) => v + 1);
    } catch (err) {
      flashErr(err, "No se pudo actualizar el bloqueo.");
    } finally {
      setCalendarBusy(false);
    }
  }

  /** «Desbloquear» desde el panel del bloqueo tocado (sin entrar a Bloquear). */
  async function handleSelectedBlockRelease(block: CourtBlock) {
    if (!token || !canEditSchedule) return;
    const seq = blockScopeSeqRef.current;
    setCalendarBusy(true);
    try {
      await api.matches.deleteCourtBlock(token, block.id);
      if (seq !== blockScopeSeqRef.current) return;
      setSelectedBlockId(null);
      flashOk(`Cancha liberada ${block.start_time}–${block.end_time}`);
      setCalendarClubVersion((v) => v + 1);
    } catch (err) {
      if (seq !== blockScopeSeqRef.current) return;
      flashErr(err, "No se pudo desbloquear la cancha.");
    } finally {
      setCalendarBusy(false);
    }
  }

  async function handleAutoCalendarProgram() {
      if (!token || !canEditSchedule) return;
      if (!calendarDate) {
        setError("Selecciona una fecha para ordenar automáticamente.");
        setMessage(null);
        return;
      }
      if (!calendarWindow || calendarBulkCourts.length === 0) {
        setError("Define horarios y canchas válidos antes de ordenar.");
        setMessage(null);
        return;
      }
      if (calendarOrderedUnscheduledMatches.length === 0) {
        flashOk("No hay partidos sin programar con los filtros actuales.");
        return;
      }

      setCalendarBusy(true);
      // Los motivos de la corrida anterior ya no aplican.
      setCalendarWaitingPrevious([]);
      const orderedDivisionId = selectedDivisionId;
      const orderedDate = calendarDate;
      try {
        await ensureCalendarSettingsSaved();
        // El bloque de la categoría abierta es la duración de los partidos
        // que se acomodan; los ya programados de otras categorías ocupan con
        // la suya (el servidor la toma de su categoría).
        const response = await api.matches.bulkProgram(token, tournamentId, {
          division_id: selectedDivisionId,
          group_id: typeof filterGroupId === "number" ? filterGroupId : undefined,
          match_ids: calendarOrderedUnscheduledMatches.map((match) => match.id),
          base_date: calendarDate,
          start_time: calendarStartTime,
          end_time: calendarEndTime,
          duration_minutes: calendarMatchDurationMinutes,
          // Categoría de Mini cancha con canchas divididas: solo sus mini
          // canchas (dos partidos a la vez por cancha); si no, las completas.
          courts: calendarBulkCourts,
          venue_name: calendarVenueName ?? undefined,
          only_unscheduled: true,
        });
        setSelectedMatchId(null);
        const summary = response.data.summary;
        const scope = calendarWaitingScopeRef.current;
        if (scope.divisionId === orderedDivisionId && scope.date === orderedDate) {
          setCalendarWaitingPrevious((summary.waiting_previous_matches ?? []).map((item) => item.reason));
        }
        const details = [
          // Publicar horarios: lo que acomoda queda sin publicar (nadie se entera aún).
          summary.updated === 0
            ? "ningún partido programado"
            : summary.updated === 1
              ? "1 partido sin publicar"
              : `${summary.updated} partidos sin publicar`,
          summary.deferred_player_busy > 0
            ? `${summary.deferred_player_busy} horarios saltados por jugador ocupado`
            : "",
          (summary.skipped_occupied_slot ?? 0) > 0
            ? `${summary.skipped_occupied_slot} horarios ocupados evitados`
            : "",
          // Orden de rondas: la Final no se juega a la hora de sus semis.
          (summary.waiting_previous ?? 0) > 0
            ? summary.waiting_previous === 1
              ? "1 quedó sin programar: espera a que terminen sus partidos previos"
              : `${summary.waiting_previous} quedaron sin programar: esperan a que terminen sus partidos previos`
            : "",
          summary.skipped_protected > 0
            ? `${summary.skipped_protected} partidos protegidos omitidos`
            : "",
          summary.skipped_already_scheduled > 0
            ? `${summary.skipped_already_scheduled} ya programados omitidos`
            : "",
          (summary.skipped_published ?? 0) > 0
            ? `${summary.skipped_published} ya estaban publicados y no se movieron`
            : "",
        ].filter(Boolean);
        flashOk(
          `Orden automático aplicado: ${details.join(" · ")}.` +
            (summary.updated > 0
              ? " Revísalos y toca «Publicar horarios» para avisar a los jugadores."
              : ""),
        );
        setCalendarClubVersion((v) => v + 1);
        await reloadSchedule();
      } catch (err) {
        // Recargar primero: la carga limpia el error al empezar.
        await reloadSchedule();
        flashErr(err, "No se pudo aplicar el orden automático.");
      } finally {
        setCalendarBusy(false);
      }
    }

  async function handleBulkUnscheduleCalendarDate() {
    if (!token || !canEditSchedule) return;
    if (!calendarDate) {
      setError("Selecciona una fecha para desprogramar.");
      setMessage(null);
      return;
    }
    if (calendarScheduledMatches.length === 0) {
      flashOk("No hay partidos programados en esta fecha con los filtros actuales.");
      return;
    }

    // Publicar horarios: los ya publicados avisan «sin horario» (un aviso por
    // jugador, con el motivo elegido) y vuelven a «sin publicar»; los sin
    // publicar se quitan callados. La confirmación es el paso que abre
    // «Desprogramar el día…» (con sus motivos): aquí ya se confirmó.
    const publishedCount = calendarScheduledMatches.filter((match) => isSchedulePublished(match)).length;
    const reason = publishedCount > 0 ? bulkUnscheduleReason.trim() : "";

    setCalendarBusy(true);
    try {
      const response = await api.matches.bulkUnschedule(token, tournamentId, {
        match_ids: calendarScheduledMatches.map((match) => match.id),
        ...(reason ? { reason } : {}),
      });
      setSelectedMatchId(null);
      setBulkUnscheduleReason("");
      setBulkUnscheduleConfirmOpen(false);
      const { updated, skipped, unpublished: unpublishedCount = 0, notified_players: notified = 0 } = response.data.summary;
      flashOk(
        `Desprogramados ${updated} partidos${skipped > 0 ? ` · ${skipped} omitidos` : ""}.` +
          (unpublishedCount > 0
            ? ` ${unpublishedCount === 1 ? "1 ya publicado volvió" : `${unpublishedCount} ya publicados volvieron`} a «sin publicar»${notified > 0 ? `; avisamos a ${jugadoresText(notified)}` : ""}.`
            : ""),
      );
      setCalendarClubVersion((v) => v + 1);
      await reloadSchedule();
      // El paso de confirmación desaparece (y su botón se apaga sin
      // programados): el foco va al título de la tarjeta, no al inicio.
      orderCardTitleRef.current?.focus({ preventScroll: true });
    } catch (err) {
      // Recargar primero: la carga limpia el error al empezar.
      await reloadSchedule();
      flashErr(err, "No se pudieron desprogramar los partidos.");
    } finally {
      setCalendarBusy(false);
    }
  }

  // ---- derived data --------------------------------------------------------

  const activeDivision = selectedDivisionId
    ? divisions.find((division) => division.id === selectedDivisionId) ?? null
    : null;
  const activeFormatType = activeDivision?.format_type || formatConfig?.format_type || tournament?.tournament_type;
  const phaseConfig = activeRoundRobinPhaseConfig(formatConfig, activeDivision);
  const isRoundRobinPlayoffFormat = ["round_robin", "ladder"].includes(activeFormatType ?? "") && isRoundRobinPlayoffPhase(phaseConfig);
  // Spanish knockout stage name per playoff round (Dieciseisavos, Octavos, …),
  // derivada de la ESTRUCTURA del cuadro con el mismo criterio que el backend.
  const playoffStageByLabel = useMemo(
    () => playoffStageLabelsByRound(matches),
    [matches],
  );
  const isLadder = activeFormatType === "ladder";
  const isElimination = activeFormatType === "elimination";
  const showPlayerReportColumns = isLadder;
  const visibleMatches = useMemo(
    () => visibleScheduleMatches(matches, { isLadder, playerMode }),
    [matches, isLadder, playerMode],
  );
  const matchesMissingStructuredScores = useMemo(
    () => countMatchesMissingStructuredScores(visibleMatches),
    [visibleMatches],
  );

  const playerUsesCourtCalendar = playerTournamentSettings?.uses_court_calendar ?? true;
  const playerUsesWeekLabels =
    /\bsemana\b/i.test(tournament?.name ?? "") ||
    visibleMatches.some((m) => isSemanaLabel(m.round_label));

  // Eduardo 2026-09-09: el filtro siempre dice "Jornada" (aunque el torneo
  // sea semanal — las opciones conservan su etiqueta cruda "Semana N").
  const adminRoundSingularLabel = isElimination ? "Ronda" : "Jornada";

  function displayPlayerRoundLabel(label: string | null | undefined): string {
    return canonicalOrRawRoundLabel(label, playoffStageByLabel, "Sin jornada");
  }

  function displayAdminRoundLabel(label: string | null | undefined): string {
    if (isElimination && eliminationRoundOrder(label) !== null) {
      return eliminationRoundDisplayLabel(label);
    }
    return canonicalOrRawRoundLabel(
      label,
      playoffStageByLabel,
      `Sin ${adminRoundSingularLabel.toLowerCase()}`,
    );
  }

  function isRoundRobinPlayoffMatch(match: TournamentMatch): boolean {
    const formatType = activeFormatType;
    if (!["round_robin", "ladder"].includes(formatType ?? "") || match.group_id !== null) return false;
    if (isGeneratedRoundRobinPlayoffMatch(match, phaseConfig)) return true;
    const config = (phaseConfig ?? formatConfig?.config_json ?? {}) as { round_count?: number; playoff_round_count?: number };
    const regularRoundCount = Number(config.round_count ?? 0);
    const playoffRoundCount = Number(config.playoff_round_count ?? 0);
    if (!Number.isFinite(regularRoundCount) || regularRoundCount <= 0 || playoffRoundCount <= 0) return false;
    const roundNumber = parseRoundNumber(match.round_label);
    return roundNumber !== null && roundNumber > regularRoundCount;
  }

  // «Grupo 1» puede existir en varias categorías (Eduardo 2026-10-01). Con la
  // lista de grupos cargada (sin categoría elegida trae todas) se mira esa;
  // en la vista del jugador, los grupos de los partidos visibles.
  const repeatedGroupNames = useMemo(
    () =>
      groups.length > 0
        ? repeatedTournamentGroupNames(groups)
        : namesInSeveralDivisions(
            visibleMatches
              .filter((m) => m.group_id !== null)
              .map((m) => ({ name: m.group_name, divisionKey: m.division_id })),
          ),
    [groups, visibleMatches],
  );

  function groupKeyForMatch(match: TournamentMatch): number | "none" | "playoffs" {
    if (match.group_id !== null) return match.group_id;
    return isRoundRobinPlayoffMatch(match) ? "playoffs" : "none";
  }

  function describeMatchGroup(match: TournamentMatch): string {
    // Nombre real del grupo («Grupo 1»), no «Fase de grupos» (Eduardo
    // 2026-10-01); los cruces sin grupo de playoffs siguen como «Playoffs».
    // Si el nombre se repite entre categorías, lleva la del partido.
    return matchGroupLabel(match, groups, isRoundRobinPlayoffMatch(match), repeatedGroupNames);
  }

  function playerCurrentBadgeLabel(): string {
    return playerUsesWeekLabels ? "Semana actual" : "Jornada actual";
  }

  const groupsWithUnassigned = useMemo<
    { id: number | "none" | "playoffs"; name: string; isActive: boolean }[]
  >(() => {
    const rows: { id: number | "none" | "playoffs"; name: string; isActive: boolean }[] =
      groups.map((g) => ({ id: g.id, name: tournamentGroupLabel(g, repeatedGroupNames), isActive: g.is_active }));

    if (rows.length === 0) {
      const seen = new Set<number>();
      for (const m of visibleMatches) {
        if (m.group_id === null || seen.has(m.group_id)) continue;
        seen.add(m.group_id);
        rows.push({
          id: m.group_id,
          name: m.group_name
            ? groupNameWithDivision(m.group_name, m.division_name, repeatedGroupNames)
            : `Grupo #${m.group_id}`,
          isActive: true,
        });
      }
    }

    if (visibleMatches.some((m) => isRoundRobinPlayoffMatch(m))) {
      rows.push({ id: "playoffs", name: "Playoffs", isActive: true });
    }
    if (visibleMatches.some((m) => m.group_id === null && !isRoundRobinPlayoffMatch(m))) {
      rows.push({ id: "none", name: "Sin grupo", isActive: false });
    }
    return rows;
  }, [groups, visibleMatches, activeFormatType, formatConfig, repeatedGroupNames]);

  function matchMatchesFilter(match: TournamentMatch): boolean {
    if (filterStatus === "unscheduled") {
      if (!isUnscheduled(match)) return false;
    } else if (filterStatus === "unpublished") {
      if (!isScheduleUnpublished(match)) return false;
    } else if (filterStatus === "por_jugar") {
      if (!isActiveCalendarStatus(match.status)) return false;
    } else if (filterStatus === "completed") {
      if (!isPlayedStatus(match)) return false;
    } else if (filterStatus !== "all") {
      if (match.status !== filterStatus) return false;
    }
    if (filterJornada !== "all") {
      const exactRoundLabel = roundLabelFromFilter(filterJornada);
      if (exactRoundLabel !== null) {
        if ((match.round_label ?? "") !== exactRoundLabel) return false;
      } else {
        const n = parseRoundNumber(match.round_label);
        if (filterJornada === "none") {
          if (n !== null || (isElimination && eliminationRoundOrder(match.round_label) !== null)) return false;
        } else if (n !== filterJornada) {
          return false;
        }
      }
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const a = matchPlayerName(match.player1).toLowerCase();
      const b = matchPlayerName(match.player2).toLowerCase();
      if (!a.includes(q) && !b.includes(q)) return false;
    }
    return true;
  }

  const jornadaOptions = useMemo<JornadaFilter[]>(() => {
    if (isElimination) {
      const labels = new Set<string>();
      let hasUnlabeled = false;
      for (const m of visibleMatches) {
        const cleanLabel = m.round_label?.trim();
        if (!cleanLabel) {
          hasUnlabeled = true;
          continue;
        }
        labels.add(cleanLabel);
      }
      const sortedLabels = Array.from(labels).sort((a, b) => {
        const orderA = eliminationRoundOrder(a);
        const orderB = eliminationRoundOrder(b);
        if (orderA !== null || orderB !== null) {
          return (orderA ?? 999) - (orderB ?? 999) || a.localeCompare(b);
        }
        return a.localeCompare(b);
      });
      const opts: JornadaFilter[] = ["all", ...sortedLabels.map(roundLabelFilterValue)];
      if (hasUnlabeled) opts.push("none");
      return opts;
    }

    const numbers = new Set<number>();
    let hasUnlabeled = false;
    for (const m of visibleMatches) {
      const n = parseRoundNumber(m.round_label);
      if (n === null) {
        hasUnlabeled = true;
      } else {
        numbers.add(n);
      }
    }
    const sorted = Array.from(numbers).sort((a, b) => a - b);
    const opts: JornadaFilter[] = ["all", ...sorted];
    if (hasUnlabeled) opts.push("none");
    return opts;
  }, [visibleMatches, isElimination]);

  // Calendario del organizador (paso 10 y operación), por jornadas.
  const calendarJornadaMode = !playerMode && !resultsOnly && !isElimination;

  // Eduardo 2026-10-05 (torneo SIN calendario de canchas): el selector del
  // Calendario abre en la jornada actual —al inicio, la Jornada 1— y no en
  // «Todas las jornadas» (salía una lista enorme con todos los partidos del
  // torneo). CON calendario de canchas sigue abriendo en «Todas» (Eduardo
  // 2026-09-09, #267). En los dos casos «Todas» sigue en el selector y lo que
  // el admin elija manda.
  // Jornada actual = definición compartida (lib/jornadaActual), la MISMA para
  // Calendario, Resultados y la vista del jugador, y la misma que usa el
  // servidor para el «Pendiente» del Resumen: fechas de cada jornada (las
  // guardadas sin calendario de canchas; si no, las de sus partidos
  // programados; si tampoco, la sugerencia del formato), la más alta que ya
  // empezó, nunca antes de la jornada más alta con algún resultado y, si nada
  // aplica, la primera. En el Calendario una jornada en la liga
  // (?round_label= o ?round=) manda, y con ?fecha= ese día cuenta como «hoy».
  const calendarJornadaScope = calendarScopeKey(tournamentId, initialDivisionId);
  const needsSavedJornadaWindows = !playerMode && !isElimination && usesCourtCalendar === false;
  const jornadaWindowDivisionIds = (
    initialDivisionId !== null ? [initialDivisionId] : divisions.map((division) => division.id)
  ).join(",");
  useEffect(() => {
    if (!token || !needsSavedJornadaWindows || Number.isNaN(tournamentId)) return;
    const scope = calendarJornadaScope;
    // Torneo sin categorías: la fila es del torneo (división null).
    const divisionIds: Array<number | null> = jornadaWindowDivisionIds
      ? jornadaWindowDivisionIds.split(",").map(Number)
      : [null];
    let cancelled = false;
    void Promise.all(
      divisionIds.map((divisionId) =>
        Promise.resolve()
          .then(() => api.rounds.jornadaDates(token, tournamentId, divisionId))
          .then((response) => response.data.jornadas ?? [])
          .catch((): JornadaDatesRow[] => []),
      ),
    ).then((lists) => {
      if (cancelled) return;
      const guardadas: VentanaJornada[] = [];
      const sugeridas: VentanaJornada[] = [];
      for (const row of lists.flat()) {
        if (row.starts_on && row.ends_on) {
          guardadas.push({ numero: row.round_number, inicio: row.starts_on, fin: row.ends_on });
        }
        // La sugerencia del formato de CADA categoría (el servidor la saca de
        // su formato): solo cuenta si la jornada no tiene otras fechas.
        if (row.suggested_starts_on && row.suggested_ends_on) {
          sugeridas.push({ numero: row.round_number, inicio: row.suggested_starts_on, fin: row.suggested_ends_on });
        }
      }
      setSavedJornadaWindows({ scope, guardadas, sugeridas });
    });
    return () => {
      cancelled = true;
    };
  }, [token, tournamentId, needsSavedJornadaWindows, calendarJornadaScope, jornadaWindowDivisionIds]);

  // Fechas guardadas y descansos de cada jornada (Eduardo 2026-10-05). Si
  // falla, los PDF y las listas quedan como antes (sin fechas ni descansos).
  useEffect(() => {
    if (!token || playerMode || Number.isNaN(tournamentId)) return;
    let cancelled = false;
    void Promise.resolve()
      .then(() => api.rounds.jornadaOverview(token, tournamentId))
      .then((response) => {
        if (!cancelled && response?.data) setJornadaOverview({ tournamentId, data: response.data });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [token, tournamentId, playerMode, matches, jornadaOverviewReload]);
  const jornadaSavedWindows = jornadaOverview?.tournamentId === tournamentId ? jornadaOverview.data.saved_windows ?? [] : [];
  const jornadaRestEntries = jornadaOverview?.tournamentId === tournamentId ? jornadaOverview.data.rest_by_round ?? [] : [];

  /** Quién descansa la jornada elegida en el selector (Eduardo 2026-10-05:
   * «para que no se pierdan»), de los grupos de los partidos que la lista
   * muestra (`shown`: ya con la búsqueda y los filtros de estado y grupo);
   * con `groupId`, solo ese grupo. Con una búsqueda, también el grupo donde
   * descansa quien se busca (ese día no tiene partido en la lista). Con
   * «Todas las jornadas», nada. */
  function selectedJornadaRests(
    shown: ReadonlyArray<TournamentMatch>,
    groupId?: number,
    jornada: JornadaFilter = filterJornada,
  ): JornadaRestEntry[] {
    if (typeof jornada !== "number") return [];
    const inScope = (match: TournamentMatch): match is TournamentMatch & { group_id: number } =>
      match.group_id !== null &&
      !isRoundRobinPlayoffMatch(match) &&
      parseRoundNumber(match.round_label) === jornada &&
      (filterGroupId === "all" || groupKeyForMatch(match) === filterGroupId) &&
      (groupId === undefined || match.group_id === groupId);
    const shownGroupIds = new Set(shown.filter(inScope).map((match) => match.group_id));
    const query = search.trim().toLowerCase();
    const jornadaGroupIds = query ? new Set(visibleMatches.filter(inScope).map((match) => match.group_id)) : null;
    return restsForJornada(jornadaRestEntries, jornada, jornadaGroupIds ?? shownGroupIds).filter(
      (entry) =>
        shownGroupIds.has(entry.group_id) ||
        entry.participants.some((participant) => participant.name.toLowerCase().includes(query)),
    );
  }

  /** Eduardo 2026-10-05 («también en admin agrega fechas»): las fechas
   * GUARDADAS de la jornada elegida, debajo del selector de Resultados y del
   * Calendario, igual que «Juegos» del jugador. Las de la categoría elegida;
   * sin categoría, las de las categorías de esa jornada solo si coinciden.
   * Con «Todas», playoffs o sin fechas guardadas, null (queda «Mostrando…»). */
  function selectedJornadaRange(): string | null {
    if (typeof filterJornada !== "number" || isElimination) return null;
    const divisionIds =
      selectedDivisionId !== null
        ? [selectedDivisionId]
        : visibleMatches
            .filter((match) => !isRoundRobinPlayoffMatch(match) && parseRoundNumber(match.round_label) === filterJornada)
            .map((match) => match.division_id ?? null);
    return savedJornadaScreenRange(jornadaSavedWindows, filterJornada, divisionIds);
  }

  // Sugerencia del formato cuando no se piden las fechas de jornada (con
  // calendario de canchas o en la vista del jugador): el formato propio de la
  // categoría manda; si no, el del torneo (mismo criterio que el servidor).
  const suggestionConfig = (
    activeDivision?.format_config_json && Object.keys(activeDivision.format_config_json).length > 0
      ? activeDivision.format_config_json
      : formatConfig?.config_json ?? {}
  ) as { start_date?: unknown; round_duration_days?: unknown };
  const suggestionStart =
    typeof suggestionConfig.start_date === "string" && suggestionConfig.start_date
      ? suggestionConfig.start_date
      : tournament?.start_date ?? null;
  const suggestionDays = suggestionConfig.round_duration_days;

  const calendarReferenceDay = isoDateFromQuery(searchParams.get("fecha")) ?? todayIso();
  const currentJornada = useMemo<number | null>(() => {
    if (isElimination) return null;
    const jornadas: number[] = [];
    const conResultado: number[] = [];
    const partidos: Array<{ numero: number; fecha: string | null }> = [];
    let jornadasPorJugar = false;
    let otrosPorJugar = false;
    for (const match of visibleMatches) {
      const porJugar = !isPlayedStatus(match) && match.status !== "cancelled";
      // Los playoffs no son jornadas de la temporada aunque se guarden como
      // «Jornada N» (igual que en el servidor): ni mueven la jornada actual.
      const numero = isRoundRobinPlayoffMatch(match) ? null : parseRoundNumber(match.round_label);
      if (numero === null) {
        if (porJugar) otrosPorJugar = true;
        continue;
      }
      jornadas.push(numero);
      if (porJugar) jornadasPorJugar = true;
      // Los W.O. que deja una baja en jornadas futuras no adelantan la jornada.
      if ((isPlayedStatus(match) || match.winner_participant_id != null) && !esResultadoDeBaja(match)) {
        conResultado.push(numero);
      }
      if (match.status !== "cancelled") partidos.push({ numero, fecha: match.scheduled_date });
    }
    // Round robin con playoffs: con las jornadas ya jugadas y los playoffs
    // por jugar, abre en «Todas» como antes.
    if (!jornadasPorJugar && otrosPorJugar) return null;
    const fechas = savedJornadaWindows?.scope === calendarJornadaScope ? savedJornadaWindows : null;
    const ventanas = ventanasPorPrioridad([
      fechas?.guardadas ?? [],
      ventanasDePartidos(partidos),
      fechas
        ? fechas.sugeridas
        : ventanasSugeridas({ jornadas, inicio: suggestionStart, diasEntreJornadas: suggestionDays }),
    ]);
    return jornadaActualNumero({ jornadas, ventanas, conResultado, hoy: calendarReferenceDay });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isElimination,
    visibleMatches,
    savedJornadaWindows,
    calendarJornadaScope,
    calendarReferenceDay,
    suggestionStart,
    suggestionDays,
    activeFormatType,
    formatConfig,
    activeDivision,
  ]);

  // Se elige con todo a la mano: los partidos de ESTA categoría, el torneo
  // (fecha de inicio y formato) y, sin canchas, las fechas de cada jornada.
  // La vista del jugador trae todo en una sola respuesta.
  const currentJornadaReady =
    playerMode ||
    (usesCourtCalendar !== null &&
      scheduleLoadedScope === calendarJornadaScope &&
      (!needsSavedJornadaWindows || savedJornadaWindows?.scope === calendarJornadaScope));

  useEffect(() => {
    // El auto-select de aquí es el de Resultados y la vista jugador; el
    // Calendario del organizador elige abajo y la eliminación conserva su
    // auto-select de ronda.
    if (calendarJornadaMode || !currentJornadaReady) return;
    if (didSelectCurrentJornada || currentJornada === null) return;
    setFilterJornada(currentJornada);
    setDidSelectCurrentJornada(true);
  }, [calendarJornadaMode, currentJornadaReady, currentJornada, didSelectCurrentJornada]);

  useEffect(() => {
    if (!calendarJornadaMode || !currentJornadaReady) return;
    if (calendarJornadaChosenFor === calendarJornadaScope) return;
    setCalendarJornadaChosenFor(calendarJornadaScope);
    const requestedRaw = searchParams.get("round_label")?.trim() || searchParams.get("round")?.trim() || "";
    const requested = parseRoundNumber(requestedRaw) ?? (/^\d+$/.test(requestedRaw) ? Number(requestedRaw) : null);
    if (requested !== null && jornadaOptions.includes(requested)) {
      setFilterJornada(requested);
      return;
    }
    // Con calendario de canchas, «Todas» (Eduardo 2026-09-09, #267); sin
    // canchas, la jornada actual (Eduardo 2026-10-05).
    setFilterJornada(usesCourtCalendar === false ? currentJornada ?? "all" : "all");
  }, [
    calendarJornadaMode,
    currentJornadaReady,
    calendarJornadaChosenFor,
    calendarJornadaScope,
    searchParams,
    jornadaOptions,
    currentJornada,
    usesCourtCalendar,
  ]);

  useEffect(() => {
    if (playerMode || resultsOnly || didSelectScheduleRound || !isElimination || visibleMatches.length === 0) return;
    const requestedRoundLabel =
      searchParams.get("round_label")?.trim() ||
      searchParams.get("round")?.trim() ||
      "";
    const availableLabels = new Set(
      visibleMatches
        .map((match) => match.round_label?.trim())
        .filter((label): label is string => Boolean(label)),
    );

    let targetLabel = requestedRoundLabel && availableLabels.has(requestedRoundLabel)
      ? requestedRoundLabel
      : "";

    if (!targetLabel) {
      const orderedLabels = Array.from(availableLabels).sort((a, b) => {
        const orderA = eliminationRoundOrder(a);
        const orderB = eliminationRoundOrder(b);
        return (orderA ?? 999) - (orderB ?? 999) || a.localeCompare(b);
      });
      // Jornada ACTUAL = la primera del bracket con pendientes (pedido
      // Eduardo 2026-08-15: abría en "3er lugar" por buscar la última).
      const labelsConPendientes = new Set(
        orderedLabels.filter((label) =>
          visibleMatches.some(
            (match) =>
              match.round_label?.trim() === label &&
              !isPlayedStatus(match),
          ),
        ),
      );
      targetLabel = jornadaActualEliminatoria(orderedLabels, labelsConPendientes) ?? "";
    }

    if (targetLabel) {
      setFilterJornada(roundLabelFilterValue(targetLabel));
      setDidSelectScheduleRound(true);
    }
  }, [
    didSelectScheduleRound,
    isElimination,
    playerMode,
    resultsOnly,
    searchParams,
    visibleMatches,
  ]);

  function displayMatchupLabel(label: string | null | undefined): string {
    return displayAdminRoundLabel(label);
  }

  const reportsByMatchParticipant = useMemo(() => {
    const map = new Map<string, MatchResultReport>();
    for (const report of resultReports) {
      if (!report.reported_by_participant_id) continue;
      const key = reportMapKey(report.match_id, report.reported_by_participant_id);
      map.set(key, chooseVisibleReport(map.get(key), report));
    }
    return map;
  }, [resultReports]);

  function getParticipantReport(
    match: TournamentMatch,
    participantId: number | null | undefined,
  ): MatchResultReport | undefined {
    if (!participantId) return undefined;
    return reportsByMatchParticipant.get(reportMapKey(match.id, participantId));
  }

  function renderReportCell(report: MatchResultReport | undefined) {
    if (!report) {
      return <span className="muted schedule-report-empty">Sin reporte</span>;
    }
    return (
      <div className="schedule-report-cell">
        <span className={RESULT_REPORT_STATUS_CLASS[report.status] ?? "kbd"}>
          {RESULT_REPORT_STATUS_LABELS[report.status] ?? report.status}
        </span>
        <strong>
          {matchScoreFromWinner({
            score_text: report.score_text,
            winner_participant_id: report.winner_participant_id,
            player1: report.match?.player1,
            player2: report.match?.player2,
          }).trim() || "Sin marcador"}
        </strong>
        <span className="muted">
          Ganó: {report.winner?.display_name ?? "—"}
        </span>
        {report.created_at && (
          <span className="muted schedule-cell-meta">{formatDateTime(report.created_at)}</span>
        )}
        {cleanReportNotes(report.notes) && (
          <span className="muted schedule-report-notes" title={cleanReportNotes(report.notes) ?? undefined}>
            Nota: {cleanReportNotes(report.notes)}
          </span>
        )}
      </div>
    );
  }

  function handleJornadaFilterClick(value: JornadaFilter) {
    setFilterJornada(value);
    // Un click manual apaga AMBOS auto-selects: si solo se marca uno, el otro
    // efecto corre al resolver los matches y revierte la selección del usuario
    // (el "primer click no hace nada" del QA Cowork #12).
    setDidSelectCurrentJornada(true);
    setDidSelectScheduleRound(true);
    setCalendarJornadaChosenFor(calendarJornadaScope);
  }

  function jornadaLabel(opt: JornadaFilter): string {
    if (opt === "all") return "Todas";
    if (opt === "none") return `Sin ${adminRoundSingularLabel.toLowerCase()}`;
    // Contrato display-only (gate #136): canonical o raw EXACTO — nada de
    // chips compactos "P1"/"J.4" ni del "primer" alias silencioso cuando el
    // número mezcla raws legados. Misma resolución que el título del PDF.
    const display = jornadaFilterDisplayLabel(opt, visibleMatches, displayAdminRoundLabel);
    if (display !== null) return display;
    // Solo cuando NINGÚN match conserva raw para ese número.
    return `${adminRoundSingularLabel} ${String(opt)}`;
  }

  /** Jornada con la que trabaja la cuadrícula del Calendario; null con
   * «Todas». Eduardo 2026-10-05: con una jornada elegida (con canchas el
   * Calendario abre en «Todas» y el admin la elige), «Programar…», la lista
   * por programar y «Desprogramar el día…» trabajan solo con esa jornada;
   * se dice cuál, porque el selector está en otra
   * tarjeta y nadie debe creer que programa o desprograma todo el torneo. */
  function calendarGridJornadaLabel(): string | null {
    if (!calendarJornadaMode || filterJornada === "all") return null;
    return jornadaLabel(filterJornada);
  }

  // Eduardo 2026-10-05: «si ya pasó una jornada y hay pendientes, entonces sí
  // poner los pendientes». Con una jornada elegida en el Calendario, los
  // partidos por jugar de las jornadas anteriores no se esconden en silencio:
  // se dice cuántos quedan en cada una, con un botón para verlos.
  const earlierJornadasPending = useMemo<Array<[number, number]>>(() => {
    if (!calendarJornadaMode || typeof filterJornada !== "number") return [];
    const counts = new Map<number, number>();
    for (const match of visibleMatches) {
      if (!isActiveCalendarStatus(match.status) || isRoundRobinPlayoffMatch(match)) continue;
      const numero = parseRoundNumber(match.round_label);
      if (numero === null || numero >= filterJornada) continue;
      counts.set(numero, (counts.get(numero) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => a[0] - b[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarJornadaMode, filterJornada, visibleMatches, activeFormatType, formatConfig, activeDivision]);

  const visibleByGroup = useMemo(() => {
    const map = new Map<number | "none" | "playoffs", TournamentMatch[]>();
    for (const m of visibleMatches) {
      if (!matchMatchesFilter(m)) continue;
      const key = groupKeyForMatch(m);
      const list = map.get(key) ?? [];
      list.push(m);
      map.set(key, list);
    }
    for (const list of map.values()) {
      list.sort(
        (a, b) =>
          matchSortKey(a)[0] - matchSortKey(b)[0] ||
          matchSortKey(a)[1] - matchSortKey(b)[1],
      );
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleMatches, filterStatus, filterJornada, search, activeFormatType, formatConfig]);

  const visibleGroupRows = useMemo(() => {
    const rows = groupsWithUnassigned.filter((g) => {
      if (filterGroupId !== "all" && filterGroupId !== g.id) return false;
      const list = visibleByGroup.get(g.id) ?? [];
      return list.length > 0;
    });
    return rows;
  }, [groupsWithUnassigned, visibleByGroup, filterGroupId]);

  const visibleResultRows = useMemo(() => getBulkResultRows(), [visibleGroupRows, visibleByGroup]);
  const pendingVisibleResultCount = visibleResultRows.filter(needsBulkResultPublish).length;
  const allVisibleResultsCompleted = resultsOnly && visibleResultRows.length > 0 && pendingVisibleResultCount === 0;

  const totals = useMemo(() => {
    let total = 0;
    let scheduled = 0;
    let unscheduled = 0;
    let played = 0;
    // Eduardo 2026-10-05 (torneo 69): lo cancelado por una baja no está «por
    // jugar» ni «sin programar»; sale aparte.
    let cancelledByDrop = 0;
    for (const m of visibleMatches) {
      total += 1;
      if (isPlayedStatus(m)) {
        played += 1;
      } else if (isCancelledByDrop(m)) {
        cancelledByDrop += 1;
      } else if (m.status === "cancelled") {
        // Cancelado por otra cosa: tampoco está por jugar (como en cada grupo).
      } else if (resultsOnly ? isProgrammedStatus(m) : (m.status === "scheduled" || m.status === "postponed")) {
        scheduled += 1;
      } else if (resultsOnly ? !isProgrammedStatus(m) : isUnscheduled(m)) {
        unscheduled += 1;
      }
    }
    return { total, scheduled, unscheduled, played, cancelledByDrop };
  }, [resultsOnly, visibleMatches]);

  const calendarMatchDurationMinutes = useMemo(() => {
    const duration = Number(calendarInterval);
    return Number.isFinite(duration) && duration > 0 ? duration : 60;
  }, [calendarInterval]);

  // Ventana de INICIOS (Desde–Último inicio, inclusive). El grid muestra
  // filas de 15 min; el bloque solo define cuánto ocupa cada partido.
  const calendarWindow = useMemo(() => {
    const startMin = parseTimeMinutes(calendarStartTime);
    const endMin = parseTimeMinutes(calendarEndTime);
    if (startMin === null || endMin === null || endMin < startMin) return null;
    return { startMin, endMin };
  }, [calendarStartTime, calendarEndTime]);
  // Inicios posibles del día (filas de 15 min, Desde–Último inicio) para los
  // desplegables del panel (cambiar hora, bloquear por rango).
  const calendarStartOptions = useMemo(() => {
    const out: string[] = [];
    if (!calendarWindow) return out;
    for (let min = calendarWindow.startMin; min <= calendarWindow.endMin; min += 15) out.push(timeFromMinutes(min));
    return out;
  }, [calendarWindow]);

  const tournamentVenues = useMemo(() => tournament?.venues ?? [], [tournament?.venues]);
  const multiVenueCalendar = tournamentVenues.length > 1;
  const activeAdditionalVenue = useMemo(
    () => (calendarVenueName
      ? tournamentVenues.find((v) => !v.is_primary && v.name === calendarVenueName) ?? null
      : null),
    [calendarVenueName, tournamentVenues],
  );
  // Nombre de sede EFECTIVO para bloqueos (la principal usa su nombre real).
  const calendarEffectiveVenueLabel = calendarVenueName ?? tournament?.venue_name ?? "";

  // ---- El día completo con todas las categorías (2026-10-02) ---------------
  // Con una categoría abierta, «Todas las categorías» pinta también los
  // partidos de las demás (ya llegan en crossDivisionMatches: ninguna
  // petición más) con su color y nombre; se mueven desde su categoría. Sin
  // categoría (torneo de una sola o liga vieja) la página ya trae todo.
  const hasSeveralDivisions = divisions.length > 1;
  const calendarScopeAvailable = !playerMode && !resultsOnly && selectedDivisionId !== null && hasSeveralDivisions;
  const calendarAllCategories = calendarScopeAvailable && calendarScope === "all";
  const calendarShowsAllDivisions = selectedDivisionId === null || calendarAllCategories;
  const tournamentWideMatches = useMemo(() => {
    if (selectedDivisionId === null || crossDivisionMatches.length === 0) return matches;
    const ids = new Set(matches.map((match) => match.id));
    return [...matches, ...crossDivisionMatches.filter((match) => !ids.has(match.id))];
  }, [matches, crossDivisionMatches, selectedDivisionId]);

  // Canchas FÍSICAS de la sede ("1".."N"); las columnas del grid pueden
  // partir algunas en mini canchas (calendarGridColumns, más abajo).
  const calendarBaseCourts = useMemo(() => {
    // Multi-sede: la sede adicional usa SUS canchas (court_count de Datos);
    // la principal conserva el control local #Canchas de siempre.
    if (activeAdditionalVenue?.court_count) {
      return courtsFromCount(String(activeAdditionalVenue.court_count));
    }
    return courtsFromCount(calendarCourtCount);
  }, [calendarCourtCount, activeAdditionalVenue]);

  function divisionForMatch(match: TournamentMatch): TournamentDivision | null {
    return divisions.find((division) => division.id === match.division_id) ?? null;
  }

  function matchDivisionStyle(match: TournamentMatch): CSSProperties | undefined {
    const division = divisionForMatch(match);
    return division ? (divisionAccentStyle(division) as CSSProperties) : undefined;
  }

  const calendarMatches = useMemo(() => {
    return visibleMatches
      .filter((match) => {
        if (!matchMatchesFilter(match)) return false;
        if (filterGroupId !== "all" && groupKeyForMatch(match) !== filterGroupId) return false;
        return true;
      })
      .sort(
        (a, b) =>
          matchSortKey(a)[0] - matchSortKey(b)[0] ||
          matchSortKey(a)[1] - matchSortKey(b)[1],
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleMatches, filterStatus, filterJornada, filterGroupId, search, activeFormatType, formatConfig]);

  const calendarUnscheduledMatches = useMemo(
    () =>
      calendarMatches.filter(
        (m) => isUnscheduled(m) && isBulkProgrammableCalendarStatus(m.status),
      ),
    [calendarMatches],
  );

  const calendarOrderedUnscheduledMatches = useMemo(
    () =>
      sortMatchesForCalendarOrder(
        calendarUnscheduledMatches,
        calendarOrderMode,
        calendarRandomSeed,
        groups,
      ),
    [calendarUnscheduledMatches, calendarOrderMode, calendarRandomSeed, groups],
  );

  const programmableVisibleMatches = useMemo(
    () => calendarMatches.filter((m) => isProgrammedStatus(m)),
    [calendarMatches],
  );
  const noProgrammableBecauseCompleted =
    !resultsOnly && calendarMatches.length > 0 && programmableVisibleMatches.length === 0;

  const selectedMatch = useMemo(
    () => visibleMatches.find((m) => m.id === selectedMatchId) ?? null,
    [visibleMatches, selectedMatchId],
  );

  // Ocupación del grid (Eduardo 2026-10-01, filas de 15 min): cada partido
  // ACTIVO con hora en la fecha y sede es UNA tarjeta [hora, hora+bloque).
  // Inventario SIN filtros de presentación (gate #146): lo que el filtro
  // esconde también ocupa cancha — se ve atenuado y se puede seleccionar —, y
  // lo de otras categorías y otros torneos del club ocupa en solo lectura.
  // Duración de cada partido en el grid (Eduardo 2026-10-01: «los bloques de
  // minutos son por categoría»): la de SU categoría. Antes todo se pintaba
  // con el bloque de la categoría abierta y los partidos de 60 min de otra
  // categoría salían encimados en carriles falsos al ver una de 75.
  const savedDurationByDivision = useMemo(() => {
    const map = new Map<number, number>();
    for (const division of divisions) {
      const minutes = divisionCalendarSettings(division).match_duration_minutes;
      if (minutes) map.set(division.id, minutes);
    }
    return map;
  }, [divisions]);
  const overrideDurationByDivision = useMemo(() => {
    const map = new Map<number, number>();
    for (const [divisionId, settings] of Object.entries(calendarSettingsSaved)) {
      if (settings.match_duration_minutes) map.set(Number(divisionId), settings.match_duration_minutes);
    }
    return map;
  }, [calendarSettingsSaved]);
  // Lo que dura un partido al programarlo o moverlo: el bloque de su
  // categoría (en la vista de una categoría, el de la pantalla) o, si su
  // categoría no tiene, el de la pantalla.
  const calendarOwnDurationFor = useCallback(
    (match: TournamentMatch): number =>
      matchDurationForGrid(match, {
        tournamentId,
        currentDivisionId: selectedDivisionId,
        currentBlockMinutes: calendarMatchDurationMinutes,
        savedDurationByDivision,
        overrideDurationByDivision,
      }),
    [tournamentId, selectedDivisionId, calendarMatchDurationMinutes, savedDurationByDivision, overrideDurationByDivision],
  );

  // Lo que ocupará el partido elegido al programarlo o moverlo.
  // Hueco «Programar aquí» que esperaba partido: al elegirlo se propone ahí.
  useEffect(() => {
    if (!calendarPendingSlot || !selectedMatch || calendarMode !== "programar") return;
    const slot = calendarPendingSlot;
    setCalendarPendingSlot(null);
    setCalendarDayView("grid");
    handleCalendarCellClick(slot.court, slot.time);
    // Solo al elegir partido con un hueco pendiente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMatch?.id, calendarPendingSlot]);
  const calendarPlacingDuration = selectedMatch ? calendarOwnDurationFor(selectedMatch) : calendarMatchDurationMinutes;

  // Cada tarjeta del grid: su propia duración. Un partido cuya categoría no
  // tiene bloque guardado se pinta tan largo como el partido elegido (sin
  // elegido, el bloque de la pantalla): es lo que el servidor asume para él
  // al revisar choques, así una celda "libre" no se rechaza al confirmar
  // (revisión 2026-10-01).
  const calendarDurationFor = useCallback(
    (match: TournamentMatch): number =>
      matchDurationForGrid(match, {
        tournamentId,
        currentDivisionId: selectedDivisionId,
        currentBlockMinutes: calendarMatchDurationMinutes,
        savedDurationByDivision,
        overrideDurationByDivision,
        fallbackMinutes: calendarPlacingDuration,
      }),
    [
      tournamentId,
      selectedDivisionId,
      calendarMatchDurationMinutes,
      savedDurationByDivision,
      overrideDurationByDivision,
      calendarPlacingDuration,
    ],
  );

  const calendarMatchItems = useMemo(() => {
    const items: CalendarGridItem[] = [];
    const placed = new Set<number>();
    const filteredIds = new Set(calendarMatches.map((m) => m.id));
    const visibleIds = new Set(visibleMatches.map((m) => m.id));
    const push = (match: TournamentMatch, kind: CourtGridItemKind) => {
      if (placed.has(match.id)) return;
      placed.add(match.id);
      const interval = matchGridInterval(match.scheduled_time, calendarDurationFor(match));
      const court = (match.court ?? "").trim();
      if (!interval || !court) return;
      items.push({
        key: `m-${match.id}`,
        court,
        startMin: interval.startMin,
        endMin: interval.endMin,
        kind,
        matchId: match.id,
        payload: { match, block: null },
      });
    };
    for (const match of occupancyInventory(matches, calendarDate)) {
      // Multi-sede: el grid muestra SOLO la sede activa (null = principal).
      if ((match.venue_name ?? null) !== calendarVenueName) continue;
      push(match, filteredIds.has(match.id) ? "own" : visibleIds.has(match.id) ? "own-hidden" : "division");
    }
    for (const match of occupancyInventory(crossDivisionMatches, calendarDate)) {
      if ((match.venue_name ?? null) !== calendarVenueName) continue;
      push(match, "division");
    }
    // Del calendario del club solo entra lo que esta página no conoce ya (su
    // sede real viene en `matches`): otros torneos y, del propio, lo que el
    // servidor no mandó en la lista (etiquetas ocultas).
    const knownIds = new Set([...matches, ...crossDivisionMatches].map((m) => m.id));
    const clubVenueKey = venueKey(calendarEffectiveVenueLabel);
    for (const match of occupancyInventory(calendarClubMatches, calendarDate)) {
      if (knownIds.has(match.id)) continue;
      if (match.match_venue_name && venueKey(match.match_venue_name) !== clubVenueKey) continue;
      push(match, match.tournament_id === tournamentId ? "division" : "club");
    }
    return items;
  }, [
    matches,
    crossDivisionMatches,
    calendarClubMatches,
    calendarMatches,
    visibleMatches,
    calendarDate,
    calendarVenueName,
    calendarEffectiveVenueLabel,
    calendarDurationFor,
    tournamentId,
  ]);

  // Bloqueos y partidos del club para la fecha (mismo modelo CourtBlock del
  // calendario del club; correlacionan por nombre de sede).
  useEffect(() => {
    if (!token || !calendarEffectiveVenueLabel || !calendarDate) {
      setCalendarBlocks([]);
      setCalendarClubMatches([]);
      return;
    }
    let cancelled = false;
    api.matches
      .clubCalendar(token, {
        venue_name: calendarEffectiveVenueLabel,
        scheduled_date: calendarDate,
      })
      .then((resp) => {
        if (cancelled) return;
        setCalendarBlocks(resp.data.blocks ?? []);
        setCalendarClubMatches(resp.data.matches ?? []);
      })
      .catch(() => {
        if (cancelled) return;
        setCalendarBlocks([]);
        setCalendarClubMatches([]);
      });
    return () => {
      cancelled = true;
    };
  }, [token, calendarEffectiveVenueLabel, calendarDate, calendarClubVersion]);

  const selectedBlock = useMemo(
    () =>
      selectedBlockId === null
        ? null
        : calendarBlocks.find((b) => b.id === selectedBlockId && b.scheduled_date === calendarDate) ?? null,
    [calendarBlocks, selectedBlockId, calendarDate],
  );

  const calendarBlockItems = useMemo(() => {
    const items: CalendarGridItem[] = [];
    for (const block of calendarBlocks) {
      if (block.scheduled_date !== calendarDate) continue;
      const startMin = parseTimeMinutes(block.start_time);
      const endMin = parseTimeMinutes(block.end_time);
      if (startMin === null || endMin === null || endMin <= startMin) continue;
      items.push({
        key: `b-${block.id}`,
        court: block.court.trim(),
        startMin,
        endMin,
        kind: "block",
        matchId: null,
        payload: { match: null, block },
      });
    }
    return items;
  }, [calendarBlocks, calendarDate]);

  const calendarGridItems = useMemo(
    () => [...calendarMatchItems, ...calendarBlockItems],
    [calendarMatchItems, calendarBlockItems],
  );
  // Partidos ya jugados del día (Eduardo 2026-10-26): se DIBUJAN en gris con
  // su marcador, pero no entran a `calendarGridItems`: las reglas de qué hora
  // está libre no cambian.
  const calendarPlayedItems = useMemo(() => {
    const items: CalendarGridItem[] = [];
    const seen = new Set<number>();
    for (const match of [...matches, ...crossDivisionMatches]) {
      if (seen.has(match.id)) continue;
      seen.add(match.id);
      if (!isPlayedStatus(match) || match.scheduled_date !== calendarDate) continue;
      if ((match.venue_name ?? null) !== calendarVenueName) continue;
      const interval = matchGridInterval(match.scheduled_time, calendarDurationFor(match));
      const court = (match.court ?? "").trim();
      if (!interval || !court) continue;
      items.push({
        key: `m-${match.id}`,
        court,
        startMin: interval.startMin,
        endMin: interval.endMin,
        kind: "division",
        matchId: match.id,
        payload: { match, block: null },
      });
    }
    return items;
  }, [matches, crossDivisionMatches, calendarDate, calendarVenueName, calendarDurationFor]);
  const calendarDrawnItems = useMemo(
    () => [...calendarGridItems, ...calendarPlayedItems],
    [calendarGridItems, calendarPlayedItems],
  );
  const selectedPlayed = useMemo(
    () => calendarPlayedItems.find((item) => item.matchId === selectedPlayedId)?.payload.match ?? null,
    [calendarPlayedItems, selectedPlayedId],
  );

  // ---- Mini canchas (Eduardo 2026-10-01) ----------------------------------
  // Sede del calendario como la guarda el servidor ("" = principal).
  const calendarSplitVenueKey = venueKey(calendarVenueName ?? "");
  const activeCourtSetup = useMemo(
    () => divisionCourtSetup(activeDivision, calendarSplitVenueKey),
    [activeDivision, calendarSplitVenueKey],
  );
  const activeIsMini = activeDivision !== null && activeCourtSetup.courtType === "mini";
  // Canchas marcadas como divididas (lo recién marcado manda mientras se guarda).
  const calendarTickedSplitBases = useMemo(() => {
    if (!activeIsMini || selectedDivisionId === null) return new Set<string>();
    const override = calendarSplitOverrides[`${selectedDivisionId}|${calendarSplitVenueKey}`];
    const ticked = override ? new Set(override) : activeCourtSetup.splitBases;
    const bases = new Set(calendarBaseCourts);
    return new Set([...ticked].filter((base) => bases.has(base)));
  }, [activeIsMini, selectedDivisionId, calendarSplitOverrides, calendarSplitVenueKey, activeCourtSetup, calendarBaseCourts]);
  // Canchas con algo en sus mini canchas este día (partidos o bloqueos), con
  // la letra más alta ocupada (C → 3 partes, D → 4).
  const calendarBusyHalfParts = useMemo(() => {
    const parts = new Map<string, number>();
    for (const item of calendarGridItems) {
      const half = parseHalfCourt(item.court);
      if (half) parts.set(half.base, Math.max(parts.get(half.base) ?? 2, halfIndex(half.half) + 1));
    }
    return parts;
  }, [calendarGridItems]);
  const calendarBusyHalfBases = useMemo(() => new Set(calendarBusyHalfParts.keys()), [calendarBusyHalfParts]);
  // En cuántas mini canchas se parte cada cancha marcada (lo recién elegido
  // manda mientras se guarda; sin dato = 2).
  const calendarTickedSplitParts = useMemo(() => {
    const parts = new Map<string, number>();
    if (!activeIsMini || selectedDivisionId === null) return parts;
    const override = calendarPartsOverrides[`${selectedDivisionId}|${calendarSplitVenueKey}`];
    for (const base of calendarTickedSplitBases) {
      parts.set(base, cleanSplitParts(override?.[base] ?? activeCourtSetup.splitParts.get(base) ?? 2));
    }
    return parts;
  }, [activeIsMini, selectedDivisionId, calendarPartsOverrides, calendarSplitVenueKey, calendarTickedSplitBases, activeCourtSetup]);
  // Columnas por cancha dividida: lo elegido, pero nunca menos que la letra
  // más alta con partidos o bloqueos ese día (bajar de 4 a 2 no esconde la 9-D).
  const calendarSplitParts = useMemo(() => {
    const parts = new Map(calendarTickedSplitParts);
    for (const [base, busy] of calendarBusyHalfParts) parts.set(base, Math.max(parts.get(base) ?? 2, busy));
    return parts;
  }, [calendarTickedSplitParts, calendarBusyHalfParts]);
  // Columnas divididas: solo en la vista de una categoría de Mini cancha (lo
  // marcado + lo que ya tiene partidos en 9-A/9-B ese día: desmarcar nunca
  // esconde un partido). Las demás vistas dejan la 9 en una columna y los
  // partidos de mini cancha salen como tarjetas de media columna.
  const calendarSplitBases = useMemo(() => {
    if (!activeIsMini) return new Set<string>();
    return new Set([...calendarTickedSplitBases, ...calendarBusyHalfBases]);
  }, [activeIsMini, calendarTickedSplitBases, calendarBusyHalfBases]);
  const calendarOwnColumns = useMemo(
    () => courtColumns(calendarBaseCourts, calendarSplitBases, calendarSplitParts),
    [calendarBaseCourts, calendarSplitBases, calendarSplitParts],
  );
  // «Todas las categorías» (revisión 2026-10-02): #Canchas es de cada
  // categoría; si otra juega ese día en canchas que la abierta no tiene (la
  // 7–15 con #Canchas 6), el grid suma esas columnas para que el día se vea
  // completo (antes desaparecían callados). Son solo de lectura: aquí se
  // programa en las canchas de la categoría abierta.
  const calendarExtraCourts = useMemo(() => {
    if (!calendarAllCategories) return [] as string[];
    const extras = new Map<string, string>();
    for (const match of occupancyInventory(tournamentWideMatches, calendarDate)) {
      if ((match.venue_name ?? null) !== calendarVenueName) continue;
      if (match.division_id === selectedDivisionId) continue;
      const court = canonicalCourt(match.court);
      if (!court || courtVisibleIn(court, calendarOwnColumns)) continue;
      const base = courtBase(court);
      const key = courtKey(base);
      if (key && !extras.has(key)) extras.set(key, base);
    }
    return [...extras.values()].sort(compareCourtNames);
  }, [calendarAllCategories, tournamentWideMatches, calendarDate, calendarVenueName, selectedDivisionId, calendarOwnColumns]);
  const calendarExtraCourtKeys = useMemo(
    () => new Set(calendarExtraCourts.map((court) => courtKey(court))),
    [calendarExtraCourts],
  );
  const calendarGridColumns = useMemo(
    () => (calendarExtraCourts.length > 0 ? [...calendarOwnColumns, ...calendarExtraCourts] : calendarOwnColumns),
    [calendarOwnColumns, calendarExtraCourts],
  );
  // «Programar pendientes» en una categoría de Mini cancha con canchas marcadas:
  // solo sus mini canchas; sin marcar (o en otra categoría), las completas.
  const calendarBulkCourts = useMemo(() => {
    if (!activeIsMini || calendarTickedSplitBases.size === 0) return calendarBaseCourts;
    return calendarBaseCourts
      .filter((base) => calendarTickedSplitBases.has(base))
      .flatMap((base) => halfCourts(base, calendarTickedSplitParts.get(base)));
  }, [activeIsMini, calendarTickedSplitBases, calendarTickedSplitParts, calendarBaseCourts]);
  // Algún dato de mini cancha en el torneo o en el grid: se explica en la leyenda.
  const calendarHasMiniCourts =
    divisions.some((division) => divisionCourtSetup(division).courtType === "mini") ||
    calendarBusyHalfBases.size > 0;

  // Filas de 15 min de Desde a Último inicio + bloque, estiradas para que
  // ningún partido de las canchas visibles quede fuera (gate #146).
  const calendarGridRange = useMemo(() => {
    if (!calendarWindow) return null;
    return buildGridRange({
      windowStartMin: calendarWindow.startMin,
      windowEndMin: calendarWindow.endMin,
      durationMinutes: calendarMatchDurationMinutes,
      extendWith: calendarMatchItems.filter((item) => courtVisibleIn(item.court, calendarGridColumns)),
    });
  }, [calendarWindow, calendarGridColumns, calendarMatchDurationMinutes, calendarMatchItems]);

  const calendarScheduledMatches = useMemo(
    () =>
      calendarMatches.filter(
        (m) =>
          m.scheduled_date === calendarDate &&
          Boolean(m.scheduled_time || m.court) &&
          isProgrammedStatus(m) &&
          // 3ª ronda #157: Desprogramar Todos y los conteos operan SOLO
          // sobre la sede activa — sin esto mandaban ids de partidos que el
          // grid oculta por sede (desprogramación silenciosa de otra sede).
          (m.venue_name ?? null) === calendarVenueName,
      ),
    [calendarMatches, calendarDate, calendarVenueName],
  );

  // ---- El día completo con todas las categorías (2026-10-02) ---------------
  // (El alcance «Esta categoría» / «Todas» y tournamentWideMatches se definen
  // arriba, antes de las canchas: en «Todas» el grid suma columnas.)
  // Lo que cuentan los chips de días, la barra del día, la lista y el PDF.
  const dayScopeMatches = calendarAllCategories ? tournamentWideMatches : matches;
  const calendarDayViewShown: "grid" | "list" =
    calendarDayView ?? (isNarrowViewport && calendarAllCategories ? "list" : "grid");
  // En el celular con «Todas», la lista automática es la «Del día» (se lee
  // de corrido); elegida a mano, la de entrada es «Por cancha».
  const calendarListKindShown: "court" | "day" | "jornada" =
    calendarDayView === null && calendarListKind === "court" ? "day" : calendarListKind;
  // Al pasar a la lista (o de «Por cancha» a «Del día» / «De la jornada», que
  // no tienen Confirmar/Cancelar) un cambio propuesto se descarta; si no, se
  // quedaba escondido y bloqueaba «Publicar» (revisión 2026-10-02). Lo que se
  // propone ya estando en «Por cancha» (desde el panel) se ve en su barra con
  // Confirmar/Cancelar, igual que en la cuadrícula (revisión Torni #587).
  const calendarPlainListShown = calendarDayViewShown === "list" && calendarListKindShown !== "court";
  useEffect(() => {
    if (calendarDayViewShown === "list") setCalendarProposal(null);
  }, [calendarDayViewShown, calendarPlainListShown]);
  // Celular: la cancha de la pestaña (si ya no existe, la primera).
  const calendarCourtGroups = useMemo(
    () => Array.from(new Set(calendarGridColumns.map((court) => (halfOf(court) ? courtBase(court) : court)))),
    [calendarGridColumns],
  );
  const calendarMobileCourtKey =
    calendarMobileCourt && calendarCourtGroups.includes(calendarMobileCourt)
      ? calendarMobileCourt
      : (calendarCourtGroups[0] ?? null);
  const calendarVisibleColumns = useMemo(
    () =>
      isNarrowViewport && calendarMobileCourtKey
        ? calendarGridColumns.filter((court) => (halfOf(court) ? courtBase(court) : court) === calendarMobileCourtKey)
        : calendarGridColumns,
    [isNarrowViewport, calendarMobileCourtKey, calendarGridColumns],
  );
  const dayRoll = useMemo(
    () => dayRollRows(dayScopeMatches, { date: calendarDate, divisions }),
    [dayScopeMatches, calendarDate, divisions],
  );
  // Etapa de playoffs de cada ronda, por categoría (el rol mezcla categorías).
  const playoffStagesByDivision = useMemo(() => {
    const byDivision = new Map<number | null, TournamentMatch[]>();
    for (const match of tournamentWideMatches) {
      const key = match.division_id ?? null;
      const list = byDivision.get(key) ?? [];
      list.push(match);
      byDivision.set(key, list);
    }
    return new Map([...byDivision.entries()].map(([key, list]) => [key, playoffStageLabelsByRound(list)]));
  }, [tournamentWideMatches]);
  const vistaParam = searchParams.get("vista");
  useEffect(() => {
    setCalendarScope(vistaParam === "todas" ? "all" : "division");
  }, [vistaParam]);
  useEffect(() => {
    setCalendarForeignPick(null);
  }, [calendarDate, calendarScope, selectedDivisionId, calendarVenueName, calendarMode, tournamentId, selectedMatchId]);
  const foreignPickId = calendarForeignPick?.id ?? null;
  useEffect(() => {
    if (foreignPickId === null) return;
    const node = foreignPickRef.current;
    if (node && typeof node.scrollIntoView === "function") {
      // Con la página recorrida, el aviso puede quedar arriba, fuera de la vista.
      const rect = node.getBoundingClientRect();
      if (rect.top < 112 || rect.bottom > window.innerHeight) {
        node.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
    if (foreignPickViaKeyboardRef.current) foreignOpenRef.current?.focus({ preventScroll: true });
  }, [foreignPickId]);
  // «Abrir su categoría»: en cuanto llegan sus partidos, queda elegido para
  // tocar su nueva hora.
  useEffect(() => {
    const pending = pendingSelectMatchIdRef.current;
    if (pending === null || !matches.some((match) => match.id === pending)) return;
    pendingSelectMatchIdRef.current = null;
    setSelectedMatchId(pending);
  }, [matches]);

  // ---- Publicar horarios (Eduardo 2026-10-01) ------------------------------
  // Antes de publicar, el jugador no ve fecha, hora ni cancha y el organizador
  // mueve o quita sin motivo ni aviso. Se cuenta sobre TODOS los partidos de
  // la categoría abierta (no los filtrados ni solo la sede en pantalla):
  // «Publicar horarios» publica la categoría y el día completos, en todas las
  // sedes y sin importar los filtros.
  const unpublished = useMemo(() => unpublishedByDate(matches), [matches]);
  const unpublishedTotal = useMemo(() => {
    let total = unpublished.withoutDate.length;
    for (const list of unpublished.byDate.values()) total += list.length;
    return total;
  }, [unpublished]);
  const unpublishedHere = useMemo(
    () => unpublished.byDate.get(calendarDate) ?? [],
    [unpublished, calendarDate],
  );
  // De los del día: cuántos no se ven por los filtros y cuántos van en otra
  // sede. Los filtros solo cuentan en la sede en pantalla: uno de otra sede
  // ya sale en su línea «Incluye N en …» (no se cuenta dos veces).
  const unpublishedHereHidden = useMemo(() => {
    const filteredIds = new Set(calendarMatches.map((match) => match.id));
    return unpublishedHere.filter(
      (match) => !filteredIds.has(match.id) && (match.venue_name ?? null) === calendarVenueName,
    ).length;
  }, [unpublishedHere, calendarMatches, calendarVenueName]);
  const unpublishedHereOtherVenues = useMemo(() => {
    const byVenue = new Map<string, number>();
    for (const match of unpublishedHere) {
      if ((match.venue_name ?? null) === calendarVenueName) continue;
      const name = match.venue_name ?? tournament?.venue_name ?? "la sede principal";
      byVenue.set(name, (byVenue.get(name) ?? 0) + 1);
    }
    return [...byVenue.entries()].map(([name, count]) => ({ name, count }));
  }, [unpublishedHere, calendarVenueName, tournament?.venue_name]);
  // «Todas las categorías»: lo sin publicar de todo el torneo (chips de días
  // y otros días pendientes) y, del día en pantalla, por categoría (cada una
  // se publica con su botón: el servidor publica por categoría y día).
  const dayScopeUnpublished = useMemo(
    () => (calendarAllCategories ? unpublishedByDate(tournamentWideMatches) : unpublished),
    [calendarAllCategories, tournamentWideMatches, unpublished],
  );
  const dayPendingDivisions = useMemo(
    () => (calendarAllCategories ? dayPendingByDivision(tournamentWideMatches, calendarDate, divisions) : []),
    [calendarAllCategories, tournamentWideMatches, calendarDate, divisions],
  );
  // Sin calendario de canchas no hay horarios que publicar.
  const showPublishUi = canEditSchedule && !resultsOnly && !courtCalendarOff;
  const tournamentClosed = tournament?.status === "closed";
  // Capturista: programa, pero no publica (el servidor responde 403; a
  // Finanzas tampoco le toca).
  const publishDeniedForRole =
    publishForbidden || myTournamentRole === "capturista" || myTournamentRole === "finanzas";
  // «¿Usa calendario de canchas?» es un ajuste del torneo: capturista y
  // finanzas no lo cambian (el servidor responde 403); lo ven sin poder tocarlo.
  const courtCalendarRoleLocked = myTournamentRole === "capturista" || myTournamentRole === "finanzas";
  const canPublishSchedule =
    showPublishUi && !publishDeniedForRole && (!tournamentClosed || hasRole("superadmin"));
  // Torneo aún en borrador (paso 10 antes de publicarlo) o candado del
  // jugador (409 al intentarlo): el botón no sirve todavía y se dice por qué
  // (en borrador, con la liga a «Publicar el torneo», al final de esta
  // página desde Eduardo 2026-10-05).
  const publishBlockedMessage =
    tournament?.status === "draft"
      ? "El torneo sigue en borrador: los horarios se publican solos al publicar el torneo."
      : publishLockMessage;
  // Fuera del asistente, en borrador la liga de la barra «Publicar horarios»
  // lleva al paso 10 con «Publicar el torneo» enfocado (?publicar=1); con el
  // candado del jugador, al Draw.
  const drawStepHref =
    tournament?.status === "draft"
      ? publishStepPath(tournamentId, selectedDivisionId !== null ? String(selectedDivisionId) : null)
      : `/admin/tournaments/${tournamentId}/setup/rounds${
          selectedDivisionId !== null ? `?division=${selectedDivisionId}` : ""
        }`;

  // Días del torneo (Eduardo 2026-10-02): chips con los partidos y los
  // horarios sin publicar de la categoría abierta (todas sus sedes, sin
  // importar los filtros: igual que «Publicar horarios»).
  // Con «Todas las categorías», las cuentas son de todo el torneo.
  const calendarDayChipList = useMemo(() => {
    const unpublishedCounts = new Map<string, number>();
    for (const [date, list] of dayScopeUnpublished.byDate) unpublishedCounts.set(date, list.length);
    return tournamentDayChips({
      startDate: tournament?.start_date,
      endDate: tournament?.end_date,
      matchesByDate: matchCountByDate(dayScopeMatches),
      unpublishedByDate: unpublishedCounts,
      selected: calendarDate,
      today: todayIso(),
    });
  }, [tournament?.start_date, tournament?.end_date, dayScopeMatches, dayScopeUnpublished, calendarDate]);
  // Partidos de la categoría el día en pantalla (sin cancelados).
  const calendarDayMatchCount =
    calendarDayChipList.find((chip) => chip.date === calendarDate)?.matches ?? 0;

  /** Elegir un día arriba (chip o Fecha): el calendario lo abre y, si la
   * lista de partidos ya está filtrada por un día, la lista lo sigue (con
   * «Todos los días» se queda como está). */
  function selectCalendarDay(date: string) {
    setCalendarDate(date);
    if (!date) return;
    setListDay((current) => (current === "all" || current === "none" ? current : date));
  }

  // El chip del día elegido a la vista en la tira (celular).
  useEffect(() => {
    const strip = calendarDayChipsRef.current;
    if (!strip) return;
    const chip = strip.querySelector<HTMLElement>(`[data-day-chip="${calendarDate}"]`);
    if (!chip) return;
    // La tira es position: relative, así que offsetLeft ya es dentro de ella.
    const left = chip.offsetLeft;
    if (left < strip.scrollLeft || left + chip.offsetWidth > strip.scrollLeft + strip.clientWidth) {
      strip.scrollLeft = Math.max(0, left - 16);
    }
  }, [calendarDate, calendarDayChipList]);

  // El perfil en el torneo: la barra del día siempre está a la vista, así que
  // se pide aunque no haya nada pendiente (un capturista en un torneo en
  // borrador lee «Pide al organizador», no «Publica el torneo…»).
  // También con «No» calendario de canchas: el Sí / No de arriba solo lo
  // cambia el organizador (a capturista y finanzas el servidor les dice 403).
  const needsMyTournamentRole = canEditSchedule && !resultsOnly;
  useEffect(() => {
    if (!token || !canEditSchedule || Number.isNaN(tournamentId)) return;
    let cancelled = false;
    api.referees
      .overview(token, tournamentId)
      .then((response) => {
        if (cancelled) return;
        setTournamentReferees(sortRefereesAZ(response.data.referees));
        setChiefReferees(response.data.settings.chief_referees ?? {});
      })
      .catch(() => {
        if (!cancelled) setTournamentReferees([]);
      });
    return () => {
      cancelled = true;
    };
  }, [token, canEditSchedule, tournamentId]);
  const canAssignReferees = myTournamentRole !== "capturista" && myTournamentRole !== "finanzas";

  async function handleAssignReferee(match: TournamentMatch, refereeId: number | null) {
    if (!token) return;
    setCalendarBusy(true);
    try {
      await api.referees.assign(token, tournamentId, [{ match_id: match.id, referee_user_id: refereeId }]);
      const name = refereeId === null ? null : tournamentReferees.find((ref) => ref.user_id === refereeId)?.full_name ?? null;
      const patch = (list: TournamentMatch[]) =>
        list.map((item) => (item.id === match.id ? { ...item, referee_user_id: refereeId, referee_name: name } : item));
      setMatches(patch);
      setCrossDivisionMatches(patch);
    } catch (err) {
      flashErr(err, "No se pudo asignar el árbitro.");
    } finally {
      setCalendarBusy(false);
    }
  }
  useEffect(() => {
    if (!token || !needsMyTournamentRole || myTournamentRole !== undefined) return;
    if (Number.isNaN(tournamentId)) return;
    let cancelled = false;
    api.tournaments
      .listTeam(token, tournamentId)
      .then((response) => {
        if (!cancelled) setMyTournamentRole(response?.data?.my_role ?? null);
      })
      .catch(() => {
        // Sin el dato se ofrece el botón; si no puede, el servidor lo dice.
        if (!cancelled) setMyTournamentRole(null);
      });
    return () => {
      cancelled = true;
    };
  }, [token, tournamentId, needsMyTournamentRole, myTournamentRole]);

  // Eduardo 2026-09-17: la lista muestra TODOS los partidos programados que
  // cumplen los filtros (jornada elegida o "Todas"), sin recorte por la
  // semana del día abierto en el calendario.
  const scheduledListByJornada = filterJornada !== "all";

  // Eduardo 2026-09-21 (torneo 129): con todo ya jugado la lista salía vacía.
  // Entran también los partidos JUGADOS (con o sin horario), para ver la
  // jornada completa; los que no tienen fecha van al final.
  const scheduledWeekMatches = useMemo(
    () =>
      calendarMatches
        .filter(
          (m) =>
            isPlayedStatus(m) ||
            (Boolean(m.scheduled_date && m.scheduled_time) &&
              (m.status === "scheduled" || m.status === "postponed" || m.status === "in_progress")),
        )
        .sort(
          (a, b) =>
            `${a.scheduled_date ?? "9999"} ${a.scheduled_time ?? ""}`.localeCompare(
              `${b.scheduled_date ?? "9999"} ${b.scheduled_time ?? ""}`,
              "es-MX",
            ) ||
            matchSortKey(a)[0] - matchSortKey(b)[0] ||
            matchSortKey(a)[1] - matchSortKey(b)[1],
        ),
    [calendarMatches],
  );
  // Eduardo 2026-10-02: «aquí también poder filtrar los partidos por día»
  // (junto con Jornada y Grupo).
  const scheduledListDayOptions = useMemo(
    () => listDayOptions(scheduledWeekMatches, listDay),
    [scheduledWeekMatches, listDay],
  );
  const scheduledListMatches = useMemo(
    () => filterByListDay(scheduledWeekMatches, listDay),
    [scheduledWeekMatches, listDay],
  );

  /** "Editar" en la lista: abre el calendario en el día (y sede) del partido
   * y lo deja seleccionado para moverlo a otra celda o posponerlo. */
  function handleEditScheduledMatch(match: TournamentMatch) {
    if (match.scheduled_date) setCalendarDate(match.scheduled_date);
    setCalendarVenueName(match.venue_name ?? null);
    setSelectedMatchId(match.id);
    // Se mueve en la cuadrícula (la lista del día solo se lee).
    setCalendarDayView("grid");
    const node = calendarDisclosureRef.current;
    if (node) {
      window.requestAnimationFrame(() => node.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  }

  /** Lleva a la barra del día del calendario (desde el aviso corto de
   * arriba). Si el día en pantalla no tiene nada sin publicar, abre el
   * primer día que sí (para no caer en un botón apagado). */
  function handleGoToPublishNotice() {
    if (unpublishedHere.length === 0) {
      const firstPending = unpublished.byDate.keys().next().value;
      if (firstPending) selectCalendarDay(firstPending);
    }
    const node = calendarDisclosureRef.current;
    window.requestAnimationFrame(() => {
      const target = publishNoticeRef.current ?? node;
      target?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  function handlePublishError(err: unknown) {
    if (err instanceof ApiError && err.status === 409) {
      // Candado del jugador: el aviso del calendario lo explica y el botón
      // se desactiva (no es un error de la acción: es el estado del torneo).
      setPublishLockMessage(
        err.message || "Los jugadores aún no ven el calendario: termina la configuración del torneo y publica los horarios.",
      );
      setMessage(null);
      setError(null);
      return;
    }
    if (err instanceof ApiError && err.status === 403) {
      setPublishForbidden(true);
      setMessage(null);
      setError(null);
      return;
    }
    flashErr(err, "No se pudieron publicar los horarios.");
  }

  /** «Publicar horarios» de la categoría y el día en pantalla (todas sus
   * sedes, sin importar los filtros). Primero cuenta (dry_run) para decir en
   * el diálogo cuántos horarios y jugadores; al confirmar, cada jugador
   * recibe UN aviso con su horario de ese día. Con «Todas las categorías»,
   * `target` es la categoría de su renglón en la barra del día. */
  async function handlePublishSchedule(target?: { divisionId: number | null; divisionName: string; here: TournamentMatch[] }) {
    if (!token || !canPublishSchedule || publishBlockedMessage || calendarProposal || calendarBusy) return;
    // Antes de la revisión: después, la señal del momento ya sería la nueva.
    const signal = currentSignal();
    const date = calendarDate;
    const divisionName = target ? target.divisionName : activeDivision?.name ?? null;
    const payload = { division_id: target ? target.divisionId : selectedDivisionId, scheduled_date: date };
    const here = target ? target.here : unpublished.byDate.get(date) ?? [];
    // Los filtros de la pantalla son de la categoría abierta: no ocultan nada de otra.
    const filtersApply = !target || target.divisionId === selectedDivisionId;
    setCalendarBusy(true);
    let preview: SchedulePublishPreview;
    try {
      preview = (await api.matches.previewSchedulePublish(token, tournamentId, payload)).data;
    } catch (err) {
      setCalendarBusy(false);
      handlePublishError(err);
      return;
    }
    setCalendarBusy(false);
    // Se salió o se cambió de torneo/categoría mientras llegaba la revisión.
    if (signal?.aborted) return;
    if (preview.to_publish === 0) {
      flashOk(publishSuccessMessage({ published: 0, notified: 0, date, divisionName }));
      setCalendarClubVersion((v) => v + 1);
      await reloadSchedule();
      return;
    }
    // Lo que el servidor va a publicar: los filtros y la sede en pantalla no
    // cuentan, así que el diálogo dice cuántos no se están viendo.
    const previewIds = new Set(preview.match_ids ?? here.map((match) => match.id));
    const included = here.filter((match) => previewIds.has(match.id));
    const filteredIds = new Set(calendarMatches.map((match) => match.id));
    const otherVenues = new Map<string, number>();
    for (const match of included) {
      if ((match.venue_name ?? null) === calendarVenueName) continue;
      const name = match.venue_name ?? tournament?.venue_name ?? "la sede principal";
      otherVenues.set(name, (otherVenues.get(name) ?? 0) + 1);
    }
    const confirmed = await askConfirm(
      publishConfirmMessage({
        count: preview.to_publish,
        date,
        divisionName,
        players: preview.players ?? 0,
        noticesOpen: preview.notices_open !== false && preview.muted !== true,
        closed: tournamentClosed,
        hiddenByFilters: filtersApply
          ? included.filter(
              (match) => !filteredIds.has(match.id) && (match.venue_name ?? null) === calendarVenueName,
            ).length
          : 0,
        otherVenues: [...otherVenues.entries()].map(([name, count]) => ({ name, count })),
      }),
      { acceptLabel: `Sí, publicar ${horariosText(preview.to_publish)}`, signal },
    );
    if (!confirmed || signal?.aborted) return;
    setCalendarBusy(true);
    try {
      const result = (await api.matches.publishSchedule(token, tournamentId, payload)).data;
      setSelectedMatchId(null);
      flashOk(
        publishSuccessMessage({
          published: result.published,
          notified: result.notified_players ?? 0,
          date,
          divisionName,
          silent: result.muted === true || result.notices_open === false,
        }),
      );
      setCalendarClubVersion((v) => v + 1);
      await reloadSchedule();
      // El aviso desaparece: el foco pasa al calendario para no perderse.
      calendarMatrixRef.current?.focus({ preventScroll: true });
    } catch (err) {
      handlePublishError(err);
    } finally {
      setCalendarBusy(false);
    }
  }

  /** «Despublicar (vuelve a borrador)» (Eduardo 2026-10-15): conserva
   * fecha, hora y cancha; los jugadores dejan de verlo hasta el siguiente
   * «Publicar horarios». Como «quitar un horario publicado», sus jugadores
   * reciben «Tu partido quedó sin horario» (con el motivo, si hay). */
  async function handleUnpublishScheduleSelected(match: TournamentMatch) {
    if (!token || !canPublishSchedule) return;
    const label = matchShortLabel(match);
    const reason = scheduleChangeReason.trim();
    const players = hasKnownPlayer(match);
    const confirmed = await askConfirm(
      `¿Despublicar el horario de ${label}? Conserva su fecha, hora y cancha, pero vuelve a borrador: los jugadores dejan de verlo hasta que toques «Publicar horarios».` +
        (players
          ? `\nSus jugadores recibirán un aviso de que su partido quedó sin horario${reason ? ` (motivo: ${reason})` : ""}.`
          : ""),
      { acceptLabel: players ? "Sí, despublicar y avisar" : "Sí, despublicar" },
    );
    if (!confirmed) return;
    setCalendarBusy(true);
    try {
      const response = await api.matches.unpublishMatchSchedule(token, tournamentId, match.id, reason ? { reason } : {});
      const notified = response.data.notified_players ?? 0;
      setScheduleChangeReason("");
      flashOk(
        `Despublicado: ${label} vuelve a borrador (los jugadores ya no lo ven).${
          notified > 0 ? ` Avisamos a ${jugadoresText(notified)} que quedó sin horario.` : ""
        }`,
      );
      setCalendarClubVersion((v) => v + 1);
      await reloadSchedule();
    } catch (err) {
      flashErr(err, "No se pudo despublicar el horario.");
    } finally {
      setCalendarBusy(false);
    }
  }

  async function handlePostponeSelected(match: TournamentMatch) {
    if (!token || !canEditResults) return;
    setCalendarBusy(true);
    try {
      await api.matches.postpone(token, tournamentId, match.id, { reason: scheduleChangeReason.trim() || null });
      setSelectedMatchId(null);
      flashOk(`Pospuesto: ${matchShortLabel(match)}`);
      await reloadSchedule();
    } catch (err) {
      flashErr(err, "No se pudo posponer el partido.");
    } finally {
      setCalendarBusy(false);
    }
  }

  /** Solo un programado o pospuesto con horario y sin resultado (no uno
   * jugado, en juego, walkover ni cancelado). */
  function canUnscheduleOne(match: TournamentMatch): boolean {
    return (
      (match.status === "scheduled" || match.status === "postponed") &&
      !match.winner_participant_id &&
      Boolean(match.scheduled_date || match.scheduled_time || match.court?.trim())
    );
  }

  /** «Desprogramar este partido» (Eduardo 2026-10-01): quita fecha, hora y
   * cancha de UN partido sin dejarlo pospuesto. Sigue seleccionado en
   * «Partido a programar» para tocar su nueva hora.
   * Publicar horarios: uno SIN PUBLICAR se quita sin preguntar (nadie lo vio
   * y se puede volver a poner); uno PUBLICADO pregunta, avisa «sin horario» a
   * sus jugadores (con el motivo elegido) y vuelve a «sin publicar». */
  async function handleUnscheduleSelected(match: TournamentMatch) {
    if (!token || !canEditSchedule) return;
    const label = matchShortLabel(match);
    const published = isSchedulePublished(match);
    const reason = published ? scheduleChangeReason.trim() : "";
    if (published) {
      const confirmed = await askConfirm(
        `¿Desprogramar ${label}? Se quitará fecha, hora y cancha y vuelve a «Por programar» (sin publicar). El resultado no se toca.\n` +
          `Sus jugadores recibirán un aviso de que su partido quedó sin horario${reason ? ` (motivo: ${reason})` : ""}. La nueva hora no se les avisa hasta que toques «Publicar horarios».`,
        { acceptLabel: "Sí, desprogramar y avisar" },
      );
      if (!confirmed) return;
    }
    setCalendarBusy(true);
    try {
      const response = await api.matches.bulkUnschedule(token, tournamentId, {
        match_ids: [match.id],
        ...(reason ? { reason } : {}),
      });
      const { updated, notified_players: notified = 0 } = response.data.summary;
      const skipped = updated === 0;
      if (!skipped) {
        setSelectedMatchId(match.id);
        // El motivo era para mover desde su hora anterior; ya no aplica.
        setScheduleChangeReason("");
        flashOk(
          published
            ? `Desprogramado: ${label}.${notified > 0 ? ` Avisamos a ${jugadoresText(notified)} que quedó sin horario.` : ""} Toca la nueva hora en el calendario.`
            : `Quitado del calendario (no se avisó a nadie): ${label}. Toca la nueva hora en el calendario.`,
        );
      }
      setCalendarClubVersion((v) => v + 1);
      await reloadSchedule();
      // Después de recargar: la carga limpia el error al empezar.
      if (skipped) {
        setError("No se pudo desprogramar: el partido ya tiene resultado o no tenía horario.");
        setMessage(null);
      }
    } catch (err) {
      flashErr(err, "No se pudo desprogramar el partido.");
    } finally {
      setCalendarBusy(false);
    }
  }

  /** «Quitar pospuesto» (Eduardo 2026-10-01: «Este tuve un error, quitarle
   * pospuesto y sólo dejar programado»): se queda en su misma fecha, hora y
   * cancha como «Programado». Si ya estaba publicado, sus jugadores
   * recibieron «Partido pospuesto… Te avisaremos la nueva fecha», así que el
   * servidor les avisa que se juega en esa hora (revisión). */
  async function handleUnpostponeSelected(match: TournamentMatch) {
    if (!token || !canEditSchedule) return;
    setCalendarBusy(true);
    try {
      const response = await api.matches.update(token, tournamentId, match.id, { status: "scheduled" });
      const notified = response?.data?.players_notified === true;
      flashOk(
        `Listo: ${matchShortLabel(match)} queda «Programado» en su misma hora.${
          notified ? " Avisamos a sus jugadores que ya no está pospuesto." : ""
        }`,
      );
      setCalendarClubVersion((v) => v + 1);
      await reloadSchedule();
    } catch (err) {
      flashErr(err, "No se pudo quitar el «Pospuesto».");
    } finally {
      setCalendarBusy(false);
    }
  }

  function isMyMatch(match: TournamentMatch): boolean {
    if (!myParticipantId) return false;
    return (
      match.player1_participant_id === myParticipantId ||
      match.player2_participant_id === myParticipantId
    );
  }

  function isFinalMatch(match: TournamentMatch): boolean {
    return ["completed", "walkover", "no_show", "defaulted", "retired", "double_walkover", "cancelled"].includes(String(match.status));
  }

  function playerDateKey(match: TournamentMatch): string {
    return match.scheduled_date ?? "unscheduled";
  }

  function playerDateTitle(dateKey: string): string {
    if (dateKey === "unscheduled") return "Por confirmar";
    return formatDate(dateKey, "Por confirmar");
  }

  function playerScheduleSort(a: TournamentMatch, b: TournamentMatch): number {
    const dateA = a.scheduled_date ?? "9999-12-31";
    const dateB = b.scheduled_date ?? "9999-12-31";
    return (
      dateA.localeCompare(dateB) ||
      (a.scheduled_time ?? "99:99").localeCompare(b.scheduled_time ?? "99:99") ||
      matchSortKey(a)[0] - matchSortKey(b)[0] ||
      matchSortKey(a)[1] - matchSortKey(b)[1]
    );
  }

  function groupMatchesByDate(rows: TournamentMatch[]): Array<{ dateKey: string; matches: TournamentMatch[] }> {
    const map = new Map<string, TournamentMatch[]>();
    for (const match of [...rows].sort(playerScheduleSort)) {
      const key = playerDateKey(match);
      const list = map.get(key) ?? [];
      list.push(match);
      map.set(key, list);
    }
    return Array.from(map.entries()).map(([dateKey, matchesForDate]) => ({
      dateKey,
      matches: matchesForDate,
    }));
  }

  const ownPlayerMatches = useMemo(
    () => visibleMatches.filter(isMyMatch).sort(playerScheduleSort),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleMatches, myParticipantId],
  );

  const currentPlayerMatch = useMemo(() => {
    const today = todayIso();
    return (
      ownPlayerMatches.find(
        (m) => !isFinalMatch(m) && (!m.scheduled_date || m.scheduled_date >= today),
      ) ?? ownPlayerMatches.find((m) => !isFinalMatch(m)) ?? ownPlayerMatches[0] ?? null
    );
  }, [ownPlayerMatches]);

  const ownPlayerMatchesByDate = useMemo(() => {
    const rows = ownPlayerMatches.filter((m) =>
      playerDateFilter ? m.scheduled_date === playerDateFilter : true,
    );
    return groupMatchesByDate(rows);
  }, [ownPlayerMatches, playerDateFilter]);

  const searchedPlayerMatchesByDate = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length < 2) return [];
    const rows = visibleMatches.filter((m) => {
      if (playerDateFilter && m.scheduled_date !== playerDateFilter) return false;
      const a = matchPlayerName(m.player1).toLowerCase();
      const b = matchPlayerName(m.player2).toLowerCase();
      return a.includes(q) || b.includes(q);
    });
    return groupMatchesByDate(rows);
  }, [visibleMatches, playerDateFilter, search]);

  useEffect(() => {
    if (calendarDate) return;
    setCalendarDate(todayIso());
  }, [calendarDate]);

  if (Number.isNaN(tournamentId)) {
    return (
      <section className="card">
        <p className="form-error">ID de torneo inválido.</p>
      </section>
    );
  }

  // ---- render --------------------------------------------------------------

  // Filtros de ronda/grupo JUNTO a las tablas que filtran (pedido Eduardo
  // 2026-07-28: "bajar" Ronda a Resultados y Ronda+Grupo al calendario —
  // arriba en la toolbar quedaban demasiado lejos de lo que afectan).
  // Resultados y Calendario: la jornada en un <select className="t123-select"> (formato de Calendario
  // de la vista jugador, Eduardo 2026-09-05 y 2026-09-09) en vez de pills.
  // Eduardo 2026-10-05: con fechas guardadas, debajo va de cuándo a cuándo
  // es la jornada (resaltado, como en «Juegos» del jugador) en vez de
  // «Mostrando jornada N»; sin fechas o con «Todas», como antes.
  const selectedRange = selectedJornadaRange();
  const resultsRoundFilter =
    jornadaOptions.length > 1 ? (
      <label className="player-games-round-filter admin-results-round-filter">
        <span>{adminRoundSingularLabel}</span>
        <select className="t123-select"
          value={jornadaFilterValue(filterJornada)}
          onChange={(event) => handleJornadaFilterClick(parseJornadaFilterValue(event.target.value))}
          aria-label={`Filtrar por ${adminRoundSingularLabel.toLowerCase()}`}
        >
          {jornadaOptions.map((opt) => (
            <option key={jornadaFilterValue(opt)} value={jornadaFilterValue(opt)}>
              {jornadaLabel(opt)}
            </option>
          ))}
        </select>
        {selectedRange ? (
          <small className="player-games-round-filter__window" data-no-toast data-jornada-window>
            {selectedRange}
          </small>
        ) : (
          <small>Mostrando {jornadaLabel(filterJornada).toLowerCase()}</small>
        )}
      </label>
    ) : null;
  // En el Calendario, junto al selector: lo que quedó por jugar en las
  // jornadas anteriores a la elegida (ver `earlierJornadasPending`).
  const earlierPendingNotice =
    resultsRoundFilter && earlierJornadasPending.length > 0 ? (
      <div className="schedule-daybar__elsewhere" role="note" data-earlier-pending>
        <span>Por jugar en jornadas anteriores:</span>
        {earlierJornadasPending.map(([numero, count]) => (
          <button
            key={numero}
            type="button"
            className="schedule-daybar__elsewhere-chip"
            onClick={() => handleJornadaFilterClick(numero)}
            title={`Ver los partidos de ${jornadaLabel(numero)}`}
            data-earlier-pending-jornada={numero}
          >
            {jornadaLabel(numero)} · {partidosText(count)}
          </button>
        ))}
      </div>
    ) : null;
  const roundFilterRow = (
    <>
      {resultsRoundFilter}
      {earlierPendingNotice}
    </>
  );

  // Pantalla única: el grupo se elige en un desplegable (riel y lista de jornadas).
  const groupFilterRow = !resultsOnly ? (
    <label className="schedule-toolbar__field cal-field schedule-table-filters">
      <span className="schedule-toolbar__label">Grupo</span>
      <select className="t123-select"
        value={String(filterGroupId)}
        onChange={(event) => {
          const picked = groupsWithUnassigned.find((g) => String(g.id) === event.target.value);
          setFilterGroupId(picked ? picked.id : "all");
        }}
        aria-label="Filtrar por grupo"
      >
        <option value="all">Todos los grupos</option>
        {groupsWithUnassigned.map((g) => (
          <option key={String(g.id)} value={String(g.id)}>
            {g.name}
          </option>
        ))}
      </select>
    </label>
  ) : null;

  if (!playerMode && !resultsOnly) return renderCalendarPage();

  return (
    <>
      {setupMode && <TournamentSetupProgress current={10} tournamentStatus={tournament?.status} usesCourtCalendar={usesCourtCalendar !== false} />}
      {setupMode && (
        // Eduardo 2026-10-02: «¿dónde está el botón publicar horarios por
        // día?». El aviso dice dónde está: arriba del calendario, siempre.
        // En borrador el botón no sirve todavía: primero se publica el
        // torneo, al final de esta página (Eduardo 2026-10-05).
        <p className="muted" style={{ margin: "0 0 0.75rem" }} data-setup-schedule-hint>
          {courtCalendarOff
            ? tournament?.status === "draft"
              ? "Este torneo no usa calendario de canchas: aquí no hay horarios que programar. Al final de la página publicas el torneo."
              : "Paso opcional: este torneo no usa calendario de canchas, así que aquí no hay horarios que programar."
            : tournament?.status === "draft"
            ? "Programa lo que ya tengas (es opcional) y, al final de la página, publica el torneo: los horarios programados se publican junto con él. Lo que cambies después se publica con «Publicar horarios», arriba del calendario."
            : "Paso opcional: programa lo que ya tengas. Los jugadores verán los horarios cuando toques «Publicar horarios», arriba del calendario de cada día."}
        </p>
      )}
      {!playerMode && (
        <section className="card">
          <div className="card__header-row">
            <div>
              <h1 className="card__title">
                <span className="hint-label">
                  {resultsOnly ? "Resultados" : "Calendario del torneo"}
                  {tournament ? ` · ${tournament.name}` : ""}
                  <GuideHelpButton />
                </span>
              </h1>
              <p className="card__subtitle">
                {resultsOnly
                  ? canEditResults
                    ? "Publica resultados y administra estatus."
                    : "Consulta resultados, estatus y reportes de jugadores. Sólo el admin puede editar."
                  : courtCalendarOff
                    ? "Vista filtrada sólo a este torneo."
                    : "Vista filtrada sólo a este torneo. Cuando son varios torneos del mismo club, usa la sección Canchas para acomodarlos sin cruces."}
              </p>
              {resultsOnly && captureFromQueryId !== null ? (
                <p className="results-from-draw">
                  Vienes del cuadro.{" "}
                  <Link to={`/admin/tournaments/${tournamentId}/rounds${divisionFromQuery ? `?division=${encodeURIComponent(divisionFromQuery)}` : ""}`}>
                    ← Volver al Draw
                  </Link>
                </p>
              ) : null}
            </div>
            {!resultsOnly && !courtCalendarOff && (
              <Link className="btn btn--ghost" to="/admin/calendar">
                Ir a la sección Canchas
              </Link>
            )}
          </div>
          {error && (
            <p role="alert" className="form-error">
              {error}
            </p>
          )}
          {message && <p className="form-message">{message}</p>}
          {resultsOnly && matchesMissingStructuredScores > 0 && (
            <p className="form-message structured-score-monitor-badge">
              Atención: {matchesMissingStructuredScores} resultado(s) visible(s) tienen score_text histórico sin score estructurado. Al guardar de nuevo se normalizan automáticamente.
            </p>
          )}

          {isRoundRobinPlayoffFormat && (
            <div className="schedule-phase-callout">
              <strong>{resultsOnly ? "Resultados por fase" : "Calendario por fase"}</strong>
              <span>{roundRobinPhaseSummary(phaseConfig)}</span>
              <small>Los partidos con grupo alimentan la tabla; los partidos sin grupo marcados como Playoffs ya son cruces reales del bracket.</small>
            </div>
          )}

          <div className="schedule-summary">
            <div className="schedule-summary__stat">
              <span className="schedule-summary__label">Partidos</span>
              <strong>{totals.total}</strong>
            </div>
            {courtCalendarOff ? (
              // Sin calendario de canchas no hay nada que programar.
              <div className="schedule-summary__stat">
                <span className="schedule-summary__label">Por jugar</span>
                <strong>{totals.unscheduled + totals.scheduled}</strong>
              </div>
            ) : (
              <>
                <div className="schedule-summary__stat">
                  <span className="schedule-summary__label">Sin programar</span>
                  <strong>{totals.unscheduled}</strong>
                </div>
                <div className="schedule-summary__stat">
                  <span className="schedule-summary__label">Programados</span>
                  <strong>{totals.scheduled}</strong>
                </div>
              </>
            )}
            <div className="schedule-summary__stat">
              <span className="schedule-summary__label">Jugados</span>
              <strong>{totals.played}</strong>
            </div>
            {totals.cancelledByDrop > 0 ? (
              <div className="schedule-summary__stat" data-drop-cancelled-stat>
                <span className="schedule-summary__label">Cancelados por baja</span>
                <strong>{totals.cancelledByDrop}</strong>
              </div>
            ) : null}
            {showPublishUi && unpublishedTotal > 0 ? (
              <button
                type="button"
                className={`schedule-summary__stat schedule-summary__stat--unpublished${filterStatus === "unpublished" ? " is-active" : ""}`}
                onClick={() => setFilterStatus(filterStatus === "unpublished" ? "all" : "unpublished")}
                aria-pressed={filterStatus === "unpublished"}
                title="Horarios que los jugadores aún no ven. Toca para ver solo esos."
                data-unpublished-stat
              >
                <span className="schedule-summary__label">Sin publicar</span>
                <strong>{unpublishedTotal}</strong>
              </button>
            ) : null}
          </div>
        </section>
      )}

      {/* Eduardo 2026-10-03: el calendario de canchas se decide solo aquí,
          arriba de todo lo de canchas. Con «No» se esconde el grid, los
          ajustes, «Ordenar automático» y «Publicar horarios». */}
      {renderCourtCalendarChoice()}

      {showPublishUi && unpublishedTotal > 0 ? (
        // La lista va antes que el calendario: este aviso corto lleva al
        // aviso completo (con el botón «Publicar horarios») del calendario.
        <section
          className="alert alert--warning schedule-publish-summary"
          data-no-toast
          role="status"
          data-unpublished-summary
        >
          <p>
            <strong>
              {unpublishedTopSummary({
                total: unpublishedTotal,
                days: unpublished.byDate.size,
                withoutDate: unpublished.withoutDate.length,
                divisionName: activeDivision?.name ?? null,
              })}
            </strong>
          </p>
          {unpublished.byDate.size > 0 ? (
            <button type="button" className="btn btn--ghost btn--compact" onClick={handleGoToPublishNotice}>
              Ir al calendario
            </button>
          ) : null}
        </section>
      ) : null}

      {playerMode && (error || message) && (
        <section className="card">
          {error && (
            <p role="alert" className="form-error">
              {error}
            </p>
          )}
          {message && <p className="form-message">{message}</p>}
        </section>
      )}

      {!playerMode && (
      <section className="card schedule-toolbar">
        <div className="schedule-toolbar__row">
          {/* --inline: los 4 botones en UNA fila (pedido Eduardo 2026-08-27:
              la columna apilada dejaba media card vacía). El modificador es
              local — .schedule-toolbar__group lo comparte el club. */}
          <div className="schedule-toolbar__group schedule-toolbar__group--inline">
            <span className="schedule-toolbar__label">Imprimir</span>
            {/* Cada pantalla imprime lo suyo (Eduardo 2026-10-05): Calendario
                los PDF de calendario (también sin calendario de canchas: van
                las jornadas con sus fechas y quién descansa) y Resultados los
                de resultados. Antes Resultados mostraba los cuatro y el
                Calendario sin canchas solo los de resultados. */}
            {resultsOnly ? (
              <button type="button" className="btn btn--compact btn--ghost" onClick={() => printResultsPdf("all")}>
                🖨 Resultados PDF
              </button>
            ) : (
              <button type="button" className="btn btn--compact btn--ghost" onClick={() => void handlePrintCalendarPdf("all")}>
                🖨 Calendario PDF
              </button>
            )}
            {selectedJornadaPrintLabel() !== null && (
              resultsOnly ? (
                <button type="button" className="btn btn--compact btn--ghost" onClick={() => printResultsPdf("selected")}>
                  🖨 Resultados · {selectedJornadaPrintLabel()}
                </button>
              ) : (
                <button type="button" className="btn btn--compact btn--ghost" onClick={() => void handlePrintCalendarPdf("selected")}>
                  🖨 Calendario · {selectedJornadaPrintLabel()}
                </button>
              )
            )}
          </div>
        </div>
        <div className="schedule-toolbar__row">
          <label className="schedule-toolbar__field">
            <span className="schedule-toolbar__label">Estatus</span>
            <select className="t123-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as StatusFilter)}
            >
              {statusFilterOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <label className="schedule-toolbar__field schedule-toolbar__field--grow">
            <span className="schedule-toolbar__label">Buscar jugador</span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nombre o apellido…"
            />
          </label>
        </div>
      </section>
      )}

      {loading && !hasLoadedOnce ? (
        <section className="card">
          <p className="muted">Cargando…</p>
        </section>
      ) : (
        <>
          {resultsOnly ? (
            <section className="card schedule-results-section">
              {renderMobileResultSheet()}
              <div className="card__header-row">
                <div>
                  <h2 className="card__title">Resultados</h2>
                  <p className="card__subtitle">
                    {canEditResults
                      ? "Captura rápido en la misma estructura de la tabla: ganador, sets y excepción. Al publicar, el ranking del torneo se recalcula automáticamente."
                      : "Vista de sólo lectura para jugadores."}
                  </p>
                </div>
                {canEditResults && (
                  <div className="table-actions schedule-results-bulk-actions">
                    {/* Eduardo 2026-09-21: capturar resultados por layout (CSV)
                        confundía junto a la captura normal → no se renderiza. La
                        lógica se conserva tras SHOW_RESULTS_LAYOUT por si un torneo
                        grande la vuelve a necesitar. */}
                    {SHOW_RESULTS_LAYOUT ? (
                      <>
                        <button
                          type="button"
                          className="btn btn--ghost"
                          onClick={handleDownloadResultsLayout}
                          disabled={bulkSavingResults || visibleGroupRows.length === 0}
                          title="Descarga un CSV con los partidos visibles para capturar resultados masivos"
                        >
                          Descargar layout
                        </button>
                        <label className="btn btn--ghost schedule-results-upload-button">
                          Cargar layout
                          <input
                            type="file"
                            accept=".csv,text/csv,.txt"
                            onChange={(event) => {
                              const file = event.target.files?.[0] ?? null;
                              void handleBulkResultsLayoutUpload(file);
                              event.currentTarget.value = "";
                            }}
                            disabled={bulkSavingResults || visibleGroupRows.length === 0}
                          />
                        </label>
                      </>
                    ) : null}
                    <button
                      type="button"
                      className="btn"
                      onClick={() => void handlePublishAllResults()}
                      disabled={bulkSavingResults || visibleGroupRows.length === 0 || allVisibleResultsCompleted}
                      title={allVisibleResultsCompleted ? "Los partidos visibles ya tienen resultado." : "Publica todos los resultados visibles; no avanza si alguno está incompleto"}
                    >
                      {bulkSavingResults ? "Publicando…" : allVisibleResultsCompleted ? "Todos publicados" : "Publicar todos los resultados"}
                    </button>
                  </div>
                )}
              </div>
              {resultsRoundFilter}
              {visibleGroupRows.length === 0 ? (
                <p className="muted">
                  No hay partidos que coincidan con los filtros.
                </p>
              ) : (
                visibleGroupRows.map((g, idx) => renderGroupAccordion(g, idx === 0))
              )}
            </section>
          ) : (
            playerMode ? renderPlayerCalendarView() : courtCalendarOff ? (
              <>
                {/* Sin calendario de canchas el organizador solo fecha cada
                    jornada (Eduardo 2026-10-04). La eliminatoria va por rondas. */}
                {!isElimination ? (
                  <JornadaDatesPanel
                    token={token}
                    tournamentId={tournamentId}
                    divisions={divisions}
                    initialDivisionId={selectedDivisionId ?? (divisions.length === 1 ? divisions[0].id : null)}
                    canEdit={canEditSchedule}
                    reloadKey={matches.length}
                    onSaved={() => setJornadaOverviewReload((value) => value + 1)}
                  />
                ) : null}
                {renderNoCourtMatchList()}
              </>
            ) : (
              <>
                {renderScheduledWeekTable()}
                {renderCalendarView()}
              </>
            )
          )}
        </>
      )}

      {!resultsOnly && (
      <details className="card group-accordion">
        <summary className="group-accordion__summary">
          <span className="group-accordion__title-wrap">
            <span className="group-accordion__title">Historial</span>
            <span className="group-accordion__meta">
              {history.length} movimiento(s)
            </span>
          </span>
          <span className="group-accordion__chevron" aria-hidden="true">
            ▾
          </span>
        </summary>
        <div className="group-accordion__body">
          {history.length === 0 ? (
            <p className="muted">Sin movimientos registrados.</p>
          ) : (
            <ul className="list">
              {history.slice(0, 50).map((entry) => (
                <li key={entry.id}>
                  <strong>
                    {ACTION_LABELS[entry.action] ?? entry.action}
                  </strong>
                  {entry.match_id && (
                    <span className="muted"> · partido #{entry.match_id}</span>
                  )}
                  {historyReasonLabel(entry.reason) && (
                    <span className="muted"> · {historyReasonLabel(entry.reason)}</span>
                  )}
                  {entry.created_at && (
                    <span className="muted">
                      {" "}
                      · {formatDateTime(entry.created_at)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </details>
      )}
      {setupMode && tournament?.status === "draft" && (
        // Eduardo 2026-10-05: el torneo se publica AQUÍ, al final de
        // Calendario (antes al final del Draw, y aquí solo había «Ir a
        // publicar el torneo»). Al publicar el admin se queda en Calendario
        // y la tarjeta cambia a «Empezar torneo».
        <TournamentPublishCard tournamentId={tournamentId} onPublished={handleTournamentPublished} />
      )}
      {setupMode && tournament && tournament.status !== "draft" && (
        // Cierre del wizard (Eduardo 2026-08-31): el paso 10 terminaba en el
        // grid sin salida — este botón es el fin del recorrido 1→10. El
        // torneo ya quedó publicado (con la tarjeta de arriba); aquí solo se
        // navega al home del torneo para empezar a operarlo.
        <section className="card setup-next-card">
          <div>
            <p className="card__eyebrow">Listo</p>
            <h2>Empezar torneo</h2>
            <p className="muted">
              La configuración quedó completa. Desde el home del torneo operas
              resultados, calendario y comunicados
              {courtCalendarOff ? "." : "; puedes volver a esta sección cuando quieras para seguir programando canchas."}
            </p>
            {showPublishUi && unpublishedTotal > 0 ? (
              <p className="schedule-publish-setup-note" data-no-toast>
                Te {unpublishedTotal === 1 ? "queda 1 horario" : `quedan ${unpublishedTotal} horarios`} sin publicar:
                los jugadores no {unpublishedTotal === 1 ? "lo ven" : "los ven"} hasta que toques «Publicar horarios».
              </p>
            ) : null}
          </div>
          <div className="form__actions">
            <Link className="btn" to={`/admin/tournaments/${tournamentId}`}>
              Empezar torneo
            </Link>
          </div>
        </section>
      )}
      {playerMode ? (
        <PlayerTournamentBottomNav tournamentId={tournamentId} />
      ) : (
        <TournamentBottomNav tournamentId={tournamentId} />
      )}
    </>
  );

  /** Calendario en una sola pantalla (maquetas Cal-Unico y Cal-Unico-cel,
   * aprobadas 2026-10-15): encabezado azul (título e indicadores) → barra
   * blanca de herramientas → «Ajustes del día» → barra de publicar → riel
   * «Por programar» + cuadrícula + panel del partido → «Publicar el torneo»
   * (solo en el paso 10). La categoría se elige arriba, en el selector
   * «Categoría seleccionada» de todas las secciones. */
  function renderCalendarPage() {
    const kpis: Array<{ key: string; label: string; value: number }> = courtCalendarOff
      ? [
          { key: "toplay", label: "Por jugar", value: totals.unscheduled + totals.scheduled },
          { key: "total", label: "Partidos", value: totals.total },
          { key: "played", label: "Jugados", value: totals.played },
        ]
      : [
          { key: "unscheduled", label: "Sin programar", value: totals.unscheduled },
          { key: "scheduled", label: "Programados", value: totals.scheduled },
          { key: "total", label: "Partidos", value: totals.total },
          { key: "played", label: "Jugados", value: totals.played },
        ];
    const unpublishedKpi =
      showPublishUi && unpublishedTotal > 0 ? (
        <button
          type="button"
          className={`cal-kpi cal-kpi--unpublished schedule-summary__stat schedule-summary__stat--unpublished${
            filterStatus === "unpublished" ? " is-active" : ""
          }`}
          onClick={() => setFilterStatus(filterStatus === "unpublished" ? "all" : "unpublished")}
          aria-pressed={filterStatus === "unpublished"}
          title="Horarios que los jugadores aún no ven. Toca para ver solo esos."
          data-unpublished-stat
        >
          <strong>{unpublishedTotal}</strong>
          <span className="schedule-summary__label">Sin publicar</span>
        </button>
      ) : null;
    const hero = (
      <section className="wiz-hero wiz-hero--navy cal-hero" aria-label="Resumen del calendario">
        <div className="cal-hero__top">
          <div className="cal-hero__title">
            <p className="eyebrow">
              {[setupMode ? "Paso 10 de 10" : "Operación", tournament?.name ?? null, tournament?.venue_name ?? null]
                .filter(Boolean)
                .join(" · ")}
            </p>
            <h1>Calendario del torneo</h1>
          </div>
          <div className="cal-hero__kpis schedule-summary">
            {kpis.slice(0, courtCalendarOff ? 1 : 2).map((kpi) => (
              <span key={kpi.key} className="cal-kpi schedule-summary__stat">
                <strong>{kpi.value}</strong>
                <span className="schedule-summary__label">{kpi.label}</span>
              </span>
            ))}
            {unpublishedKpi}
            {kpis.slice(courtCalendarOff ? 1 : 2).map((kpi) => (
              <span key={kpi.key} className="cal-kpi schedule-summary__stat">
                <strong>{kpi.value}</strong>
                <span className="schedule-summary__label">{kpi.label}</span>
              </span>
            ))}
            {totals.cancelledByDrop > 0 ? (
              <span className="cal-kpi schedule-summary__stat" data-drop-cancelled-stat>
                <strong>{totals.cancelledByDrop}</strong>
                <span className="schedule-summary__label">Cancelados por baja</span>
              </span>
            ) : null}
          </div>
        </div>
        {setupMode ? (
          // Eduardo 2026-10-02: «¿dónde está el botón publicar horarios por
          // día?». El aviso dice dónde está: arriba del calendario, siempre.
          // En borrador el botón no sirve todavía: primero se publica el
          // torneo, al final de esta página (Eduardo 2026-10-05).
          <p className="wiz-hero__lead cal-hero__lead" data-setup-schedule-hint>
            {courtCalendarOff
              ? tournament?.status === "draft"
                ? "Este torneo no usa calendario de canchas: aquí no hay horarios que programar. Al final de la página publicas el torneo."
                : "Paso opcional: este torneo no usa calendario de canchas, así que aquí no hay horarios que programar."
              : tournament?.status === "draft"
              ? "Programa lo que ya tengas (es opcional) y, al final de la página, publica el torneo: los horarios programados se publican junto con él. Lo que cambies después se publica con «Publicar horarios», arriba del calendario."
              : "Paso opcional: programa lo que ya tengas. Los jugadores verán los horarios cuando toques «Publicar horarios», arriba del calendario de cada día."}
          </p>
        ) : null}
      </section>
    );
    return (
      <div className={`wiz-step cal-page${courtCalendarOff ? " is-off" : ""}`}>
        {setupMode ? (
          <TournamentSetupProgress current={10} tournamentStatus={tournament?.status} usesCourtCalendar={usesCourtCalendar !== false} />
        ) : null}
        {hero}
        {renderCalendarToolbar()}
        {error || message ? (
          <div className="cal-messages">
            {error ? (
              <p role="alert" className="form-error">
                {error}
              </p>
            ) : null}
            {message ? <p className="form-message">{message}</p> : null}
          </div>
        ) : null}
        {loading && !hasLoadedOnce ? (
          <section className="card">
            <p className="muted">Cargando…</p>
          </section>
        ) : courtCalendarOff ? (
          renderNoCourtMatchList()
        ) : (
          <>
            {renderCalendarSettingsCard()}
            {renderPublishBar()}
            {renderCalendarView()}
          </>
        )}
        <details ref={calendarHistoryRef} className="card group-accordion cal-history">
          <summary className="group-accordion__summary">
            <span className="group-accordion__title-wrap">
              <span className="group-accordion__title">Historial de movimientos</span>
              <span className="group-accordion__meta">{history.length} movimiento(s)</span>
            </span>
            <span className="group-accordion__chevron" aria-hidden="true">
              ▾
            </span>
          </summary>
          <div className="group-accordion__body">
            {history.length === 0 ? (
              <p className="muted">Sin movimientos registrados.</p>
            ) : (
              <ul className="list">
                {history.slice(0, 50).map((entry) => (
                  <li key={entry.id}>
                    <strong>{ACTION_LABELS[entry.action] ?? entry.action}</strong>
                    {entry.match_id && <span className="muted"> · partido #{entry.match_id}</span>}
                    {historyReasonLabel(entry.reason) && (
                      <span className="muted"> · {historyReasonLabel(entry.reason)}</span>
                    )}
                    {entry.created_at && <span className="muted"> · {formatDateTime(entry.created_at)}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </details>
        {setupMode && tournament?.status === "draft" && (
          // Eduardo 2026-10-05: el torneo se publica AQUÍ, al final de
          // Calendario. Al publicar el admin se queda en Calendario y la
          // tarjeta cambia a «Empezar torneo».
          <TournamentPublishCard tournamentId={tournamentId} onPublished={handleTournamentPublished} />
        )}
        {setupMode && tournament && tournament.status !== "draft" && (
          // Cierre del wizard (Eduardo 2026-08-31): el fin del recorrido 1→10.
          <section className="card setup-next-card">
            <div>
              <p className="card__eyebrow">Listo</p>
              <h2>Empezar torneo</h2>
              <p className="muted">
                La configuración quedó completa. Desde el home del torneo operas resultados, calendario y comunicados
                {courtCalendarOff ? "." : "; puedes volver a esta sección cuando quieras para seguir programando canchas."}
              </p>
              {showPublishUi && unpublishedTotal > 0 ? (
                <p className="schedule-publish-setup-note" data-no-toast>
                  Te {unpublishedTotal === 1 ? "queda 1 horario" : `quedan ${unpublishedTotal} horarios`} sin publicar:
                  los jugadores no {unpublishedTotal === 1 ? "lo ven" : "los ven"} hasta que toques «Publicar horarios».
                </p>
              ) : null}
            </div>
            <div className="form__actions">
              <Link className="btn" to={`/admin/tournaments/${tournamentId}`}>
                Empezar torneo
              </Link>
            </div>
          </section>
        )}
        <TournamentBottomNav tournamentId={tournamentId} />
      </div>
    );
  }

  function renderGroupAccordion(
    group: { id: number | "none" | "playoffs"; name: string; isActive: boolean },
    isFirst: boolean,
  ) {
    const list = visibleByGroup.get(group.id) ?? [];
    const groupSummary = computeGroupSummary(list);
    return (
      <details
        className="card group-accordion"
        key={String(group.id)}
        open={isFirst}
      >
        <summary className="group-accordion__summary">
          <span className="group-accordion__title-wrap">
            <span className="group-accordion__title">
              {group.name}
              {!group.isActive && group.id !== "none" && (
                <span className="kbd">inactivo</span>
              )}
            </span>
            <span className="group-accordion__meta" data-group-summary={String(group.id)}>
              {groupSummary.total} partido(s) · {groupSummary.unscheduled} sin
              programar · {groupSummary.scheduled} programado(s) ·{" "}
              {groupSummary.played} jugado(s)
              {groupSummary.cancelledByDrop > 0 ? ` · ${cancelledCountText(groupSummary.cancelledByDrop, true)}` : ""}
              {groupSummary.cancelled > 0 ? ` · ${cancelledCountText(groupSummary.cancelled, false)}` : ""}
            </span>
          </span>
          <span className="group-accordion__chevron" aria-hidden="true">
            ▾
          </span>
        </summary>
        <div className="group-accordion__body">
          {list.length === 0 ? (
            <p className="muted">Sin partidos en este grupo.</p>
          ) : (
            <div className="responsive-table-wrap">
              <table className={`data-table player-games-table admin-results-table admin-results-table--grid${canEditResults ? "" : " admin-results-table--readonly"}`}>
                <thead>
                  <tr>
                    {/* Maqueta «Resultados · acciones» (2026-10-15): rejilla fija
                        Cuándo · Partido · Marcador · Excepción · Estado · Acciones. */}
                    <th className="admin-results-when-heading">Cuándo</th>
                    <th className="player-games-table__match-heading">Partido</th>
                    <th className="admin-results-score-heading">Marcador</th>
                    <th className="admin-results-exception-heading">Excepción</th>
                    <th className="admin-results-status-heading">Estado</th>
                    <th className="admin-results-actions-heading">Acciones</th>
                    {showPlayerReportColumns && <th>Reporte retador</th>}
                    {showPlayerReportColumns && <th>Reporte retado</th>}
                  </tr>
                </thead>
                <tbody>
                  {list.flatMap((match) => [
                    renderMatchRow(match),
                    ...(match.match_kind === "tie" ? (match.lines ?? []).map((line, index) => renderMatchRow(line, { parent: match, lineIndex: index })) : []),
                  ])}
                </tbody>
              </table>
            </div>
          )}
          {typeof group.id === "number"
            ? selectedJornadaRests(list, group.id).map((rest) => (
                <p key={rest.group_id} className="muted schedule-rest-line" data-no-toast data-jornada-rest={rest.group_id}>
                  {restScreenLine(rest)}
                </p>
              ))
            : null}
        </div>
      </details>
    );
  }

  // Series por equipos (Mini Copa 2026): la serie es una fila resumen (se
  // resuelve sola con sus líneas) y cada línea es una fila normal de captura
  // debajo, con los jugadores de esa línea en vez de los nombres de equipo.
  // OJO: nada de `const` aquí — renderMatchRow (hoisted) se invoca desde JSX
  // construido más arriba en el cuerpo del componente, y una constante
  // declarada después no existe todavía (ReferenceError en prod, #298).
  function teamPlayWinsNeeded(match: TournamentMatch): number {
    const lines = match.lines ?? [];
    const division = divisions.find((item) => item.id === match.division_id);
    const block = (division?.format_config_json as { team_play?: { wins_to_take_tie?: number } } | null | undefined)?.team_play;
    return block?.wins_to_take_tie ?? Math.floor(lines.length / 2) + 1;
  }

  function lineSideLabel(match: TournamentMatch, side: "team1" | "team2"): string {
    const players = match.line_players?.[side] ?? [];
    if (players.length === 0) return "Por definir";
    return players.map((player) => player.name).join(" / ");
  }

  // Alineación de una línea (lineup_mode captain o corrección del sorteo):
  // en singles un <select className="t123-select"> por lado con los integrantes del equipo; en dobles
  // juegan los dos. Solo mientras la línea no tenga resultado.
  async function handleLineLineupChange(line: TournamentMatch, parent: TournamentMatch, side: "team1" | "team2", profileId: number) {
    if (!token) return;
    const current = {
      team1: (line.line_players?.team1 ?? []).map((player) => player.id),
      team2: (line.line_players?.team2 ?? []).map((player) => player.id),
    };
    const next = { ...current, [side]: [profileId] };
    if (line.line_kind !== "singles") {
      next.team1 = (parent.tie_members?.team1 ?? []).slice(0, 2).map((player) => player.id);
      next.team2 = (parent.tie_members?.team2 ?? []).slice(0, 2).map((player) => player.id);
    }
    setSavingLineupId(line.id);
    setError(null);
    try {
      await api.matches.updateLineLineup(token, tournamentId, line.id, next);
      await reloadContext();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo guardar la alineación.");
    } finally {
      setSavingLineupId(null);
    }
  }

  function renderLineSide(line: TournamentMatch, parent: TournamentMatch, side: "team1" | "team2") {
    const isWinner = Boolean(line.winner_participant_id) && line.winner_participant_id === (side === "team1" ? line.player1_participant_id : line.player2_participant_id);
    const decided = Boolean(line.winner_participant_id) || ["completed", "walkover", "no_show", "defaulted", "retired", "double_walkover", "cancelled"].includes(line.status);
    const members = parent.tie_members?.[side] ?? [];
    const canPick = canEditResults && !decided && line.line_kind === "singles" && members.length > 1;
    if (!canPick) {
      return <strong className={isWinner ? "is-winner" : undefined}>{lineSideLabel(line, side)}</strong>;
    }
    const currentId = line.line_players?.[side]?.[0]?.id ?? "";
    return (
      <select
        className="t123-select schedule-input results-line__pick"
        aria-label={`Jugador del ${side === "team1" ? "equipo 1" : "equipo 2"} en esta línea`}
        value={currentId}
        disabled={savingLineupId === line.id}
        onChange={(event) => void handleLineLineupChange(line, parent, side, Number(event.target.value))}
      >
        {currentId === "" ? <option value="">Por definir</option> : null}
        {members.map((member) => (
          <option key={member.id} value={member.id}>{member.name}</option>
        ))}
      </select>
    );
  }

  // Acciones con texto (maqueta «Resultados · acciones», Eduardo 2026-10-15):
  // nada de símbolos sueltos. Cada botón aparece solo en su estado:
  // «Publicar resultado» (sin publicar o con captura nueva), «Despublicar»
  // (ya publicado), «Posponer» (horario ya publicado), «Eliminar partido»
  // (sin resultado), «Cancelar por baja» / «Cambiar a cancelado» (bajas) y
  // «Reactivar» (cancelado por baja). En móvil la hoja de captura publica,
  // así que ahí no se repite «Publicar resultado».
  function renderActionsCell(
    match: TournamentMatch,
    flags: { isTie: boolean; finalLike: boolean; pending: boolean; saving: boolean },
  ) {
    const { isTie, finalLike, pending, saving } = flags;
    const busy = saving || bulkSavingResults;
    if (isCancelledByDrop(match)) {
      return (
        <td data-label="Acciones" className="admin-results-actions-cell">
          {canManageDropMatches() && !isTie && match.parent_match_id == null ? (
            <div className="table-actions schedule-actions admin-results-actions">
              <button
                type="button"
                className="btn btn--ghost admin-results-action"
                onClick={() => void handleReviveDropCancel(match)}
                disabled={busy}
                title="Reactivar partido: vuelve a pendiente y su rival ya no descansa esa jornada"
                data-revive-drop-cancel={match.id}
                data-result-action="revive"
              >
                Reactivar
              </button>
            </div>
          ) : (
            <span className="muted" data-drop-no-actions>—</span>
          )}
        </td>
      );
    }
    const published = finalLike || Boolean(match.winner_participant_id);
    const showPublish = !isTie && !isNarrowViewport && (!published || pending || hasResultDraft(match));
    // Una serie no se captura aquí, pero su Doble W.O. sí se corrige.
    const showUnpublish = (!isTie || match.status === "double_walkover") && match.status !== "cancelled" && published;
    // Publicar horarios: un horario sin publicar no se pospone (nadie lo ha
    // visto): se mueve o se quita desde el Calendario.
    const showPostpone = !isScheduleUnpublished(match) && match.status !== "double_walkover";
    const showDelete = !finalLike && !match.winner_participant_id;
    return (
      <td data-label="Acciones" className="admin-results-actions-cell">
        {canEditResults ? (
          <div className="table-actions schedule-actions admin-results-actions">
            {canCancelByDrop(match) && (
              <button
                type="button"
                className="btn btn--danger admin-results-action"
                onClick={() => void handleCancelByDrop(match)}
                disabled={busy}
                title="Cancelar este partido por la baja: su rival descansa esa jornada"
                data-cancel-by-drop={match.id}
                data-result-action="cancel-by-drop"
              >
                Cancelar por baja
              </button>
            )}
            {canTurnDropWalkoverIntoCancel(match) && (
              <button
                type="button"
                className="btn btn--danger admin-results-action"
                onClick={() => void handleTurnDropWalkoverIntoCancel(match)}
                disabled={busy}
                title="Quitar el W.O. y cancelar por la baja: su rival descansa esa jornada"
                data-drop-walkover-to-cancel={match.id}
                data-result-action="walkover-to-cancel"
              >
                Cambiar a cancelado
              </button>
            )}
            {showPublish && (
              <button
                type="button"
                className="btn admin-results-action"
                onClick={() => void handleAdvancedSave(match)}
                disabled={busy}
                title="Publica el marcador o la excepción: lo ven los jugadores y avanza el cuadro"
                data-result-action="publish"
              >
                {saving ? "Publicando…" : "Publicar resultado"}
              </button>
            )}
            {showUnpublish && (
              <button
                type="button"
                className="btn btn--ghost admin-results-action"
                onClick={() => void handleUnpublish(match)}
                disabled={busy}
                title="Despublicar resultado (el partido vuelve a pendiente y el avance del cuadro se regresa)"
                data-result-action="unpublish"
              >
                Despublicar
              </button>
            )}
            {showPostpone && (
              <button
                type="button"
                className="btn btn--ghost admin-results-action"
                onClick={() => void handleAction(match, "postpone")}
                disabled={busy}
                title="Posponer el partido"
                data-result-action="postpone"
              >
                Posponer
              </button>
            )}
            {showDelete && (
              <button
                type="button"
                className="btn btn--ghost btn--danger admin-results-action admin-results-action--danger"
                onClick={() => void handleDelete(match)}
                disabled={busy}
                title="Eliminar el partido (solo si no tiene resultado)"
                data-result-action="delete"
              >
                Eliminar partido
              </button>
            )}
          </div>
        ) : (
          <span className="muted">Sólo lectura</span>
        )}
      </td>
    );
  }

  /** «Cuándo»: jornada/fecha arriba, hora · cancha abajo. */
  function renderWhenCell(match: TournamentMatch, isLine: boolean) {
    const top = playerUsesCourtCalendar && match.scheduled_date
      ? formatDate(match.scheduled_date, "—")
      : isLine
        ? "—"
        : displayMatchupLabel(match.round_label) || "—";
    const bottom = playerUsesCourtCalendar
      ? [match.scheduled_time ? formatTimeLabel(match.scheduled_time) : null, match.court?.trim() ? courtDisplayName(match.court) : null]
          .filter(Boolean)
          .join(" · ")
      : "";
    return (
      <td data-label="Cuándo" className="admin-results-when-cell">
        <strong>{top}</strong>
        {bottom ? <span>{bottom}</span> : playerUsesCourtCalendar ? <span className="muted">Sin horario</span> : null}
        {!playerMode && isScheduleUnpublished(match) ? (
          <small className="muted schedule-cell-unpublished" title="Los jugadores aún no ven este horario.">
            sin publicar
          </small>
        ) : null}
      </td>
    );
  }

  function renderMatchRow(match: TournamentMatch, opts: { parent?: TournamentMatch; lineIndex?: number } = {}) {
    const isTie = match.match_kind === "tie";
    const isLine = Boolean(opts.parent);
    const completed = match.status === "completed";
    const finalLike =
      completed ||
      match.status === "walkover" ||
      match.status === "no_show" ||
      match.status === "defaulted" ||
      match.status === "retired" ||
      match.status === "double_walkover" ||
      match.status === "cancelled";
    const pending = hasPendingEdit(match);
    const saving = savingMatchId === match.id;
    const challengerReport = getParticipantReport(match, match.player1?.id);
    const challengedReport = getParticipantReport(match, match.player2?.id);
    // Cancelado por una baja (Eduardo 2026-10-05): renglón atenuado, sin
    // controles de captura; su chip lo dice.
    const cancelledByDrop = isCancelledByDrop(match);
    const pill = resultsStatusPill(match, hasResultDraft(match));
    const lineKindLabel = `L${(opts.lineIndex ?? 0) + 1} · ${LINE_KIND_LABELS[match.line_kind ?? ""] ?? match.line_kind}`;
    const metaText = [
      isLine
        ? `Línea de la serie ${opts.parent?.match_number ?? opts.parent?.id}`
        : isTie
          ? `Serie ${match.match_number ?? match.id}`
          : `Juego ${match.match_number ?? match.id}`,
      isLine ? null : displayMatchupLabel(match.round_label),
      isLine ? null : describeMatchGroup(match),
    ]
      .filter(Boolean)
      .join(" · ");
    const rowProps = {
      id: `results-match-${match.id}`,
      className: [
        pending ? "schedule-row--dirty" : "",
        focusedMatchId === match.id ? "schedule-row--focus" : "",
        isTie ? "schedule-row--tie" : "",
        isLine ? "schedule-row--line" : "",
        cancelledByDrop ? "schedule-row--drop" : "",
      ].filter(Boolean).join(" ") || undefined,
      style: matchDivisionStyle(match),
      "data-drop-match": dropMatchState(match)?.kind,
    };
    const reportCells = (
      <>
        {showPlayerReportColumns && (
          <td data-label="Reporte retador" className="admin-results-report-cell">{renderReportCell(challengerReport)}</td>
        )}
        {showPlayerReportColumns && (
          <td data-label="Reporte retado" className="admin-results-report-cell">{renderReportCell(challengedReport)}</td>
        )}
      </>
    );

    // Celular: tarjeta con «Capturar resultado» (la hoja publica) y debajo las
    // acciones con texto.
    if (isNarrowViewport) {
      return (
        <tr key={match.id} {...rowProps}>
          <td data-label="Partido" className="player-games-match-cell">
            <div className="player-games-match-card">
              <div className="admin-results-mobile-head">
                <small>{metaText}</small>
                <span className={`${pill.className} admin-results-status`}>{pill.label}</span>
              </div>
              {renderDisplayPlayers(match, opts, lineKindLabel)}
              {isTie ? renderTieScore(match) : null}
              {canEditResults && isTie ? (
                <small className="muted">Se captura por línea; la serie se resuelve sola.</small>
              ) : null}
              {canEditResults && !isTie && !cancelledByDrop ? renderMobileCaptureCell(match) : null}
            </div>
          </td>
          {renderWhenCell(match, isLine)}
          {renderActionsCell(match, { isTie, finalLike, pending, saving })}
          {reportCells}
        </tr>
      );
    }

    const capture = canEditResults && !isTie && !cancelledByDrop;
    // Sobre un Doble W.O. no se captura: su marcador se lee, no se edita.
    const editableResult = capture && match.status !== "double_walkover";
    return (
      <tr key={match.id} {...rowProps}>
        {renderWhenCell(match, isLine)}
        <td data-label="Partido" className="player-games-match-cell admin-results-match-cell">
          {capture ? (
            <div className="admin-results-names">
              {isLine ? (
                <>
                  <small className="results-line__kind">{lineKindLabel}</small>
                  {renderLineSide(match, opts.parent!, "team1")}
                  <span className="admin-results-names__vs">vs</span>
                  {renderLineSide(match, opts.parent!, "team2")}
                </>
              ) : (
                <>
                  {renderCaptureName(match.player1)}
                  <span className="admin-results-names__vs">vs</span>
                  {renderCaptureName(match.player2)}
                </>
              )}
              <small className="admin-results-names__meta">{metaText}</small>
            </div>
          ) : (
            <div className="player-games-match-card">
              {renderDisplayPlayers(match, opts, lineKindLabel)}
              <small>{metaText}</small>
              {canEditResults && isTie ? (
                <small className="muted">Se captura por línea; la serie se resuelve sola.</small>
              ) : null}
            </div>
          )}
        </td>
        <td data-label="Marcador" className="admin-results-score-cell">
          {editableResult ? (
            <div className="quick-result-entry" data-results-quick-entry="structured-winner-score">
              {renderAdminScoreboard(match, { compact: true })}
              <input
                type="hidden"
                data-generated-score="structured-score-hidden"
                value={resultScoreValue(match)}
                readOnly
              />
            </div>
          ) : isTie ? (
            renderTieScore(match)
          ) : (
            <span className="admin-results-score-text">{matchScoreFromWinner(match).trim() || "—"}</span>
          )}
        </td>
        <td data-label="Excepción" className="admin-results-exception-cell">
          {editableResult ? (
            <select
              className="t123-select schedule-input admin-results-exception"
              aria-label="Excepción"
              value={resultExceptionValue(match)}
              onChange={(e) =>
                setResultTypeInputs((prev) => ({
                  ...prev,
                  [match.id]: e.target.value as ResultTypeInput,
                }))
              }
            >
              <option value="">Sin excepción</option>
              {/* Eduardo 2026-09-16: el W/O se conserva solo para
                  partidos ya capturados así. */}
              {resultTypeInputs[match.id] === "walkover" || resultExceptionValue(match) === "walkover" ? (
                <option value="walkover">W/O / no se presentó</option>
              ) : null}
              {resultExceptionOptions(match, legacyCrossIds).filter(([value]) => value !== "").map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
              {match.status === "double_walkover" ? <option value="double_walkover">Doble W.O.</option> : null}
            </select>
          ) : (
            <span className="muted">—</span>
          )}
        </td>
        <td data-label="Estado" className="admin-results-status-cell">
          <span className={`${pill.className} admin-results-status`}>{pill.label}</span>
        </td>
        {renderActionsCell(match, { isTie, finalLike, pending, saving })}
        {reportCells}
      </tr>
    );
  }

  function renderCaptureName(participant: TournamentMatch["player1"]) {
    if (!participant) return <span className="admin-results-names__name muted">Por definir</span>;
    const displayName = nameWithDropMark(matchPlayerName(participant), participant);
    return (
      <Link className="admin-results-names__name" to={`/admin/tournaments/${tournamentId}/players/${participant.id}`}>
        {displayName}
      </Link>
    );
  }

  function renderTieScore(match: TournamentMatch) {
    return (
      <span className="results-tie-score">
        Serie {matchScoreFromWinner(match).trim() || "0-0"}
        {match.winner_participant_id ? " · definida" : ` · a ${teamPlayWinsNeeded(match)} victoria(s)`}
      </span>
    );
  }

  function renderDisplayPlayers(match: TournamentMatch, opts: { parent?: TournamentMatch; lineIndex?: number }, lineKindLabel: string) {
    if (opts.parent && match.winner_participant_id != null) {
      // Eduardo 2026-09-20: la línea ya jugada se ve como en la vista del
      // jugador (dos renglones, games por set, tie-break en superíndice,
      // ganador resaltado) en vez del texto "7-6(10-8) 6-0 · Gana X".
      return (
        <div className="results-line__played">
          <span className="results-line__kind">{lineKindLabel}</span>
          <LineCompetitorLines line={match} />
        </div>
      );
    }
    if (opts.parent) {
      return (
        <div className="results-line__players">
          <span className="results-line__kind">{lineKindLabel}</span>
          {renderLineSide(match, opts.parent, "team1")}
          <span className="results-line__vs">vs</span>
          {renderLineSide(match, opts.parent, "team2")}
        </div>
      );
    }
    return (
      <MatchCompetitorLines
        match={match}
        hrefFor={(participant) => `/admin/tournaments/${tournamentId}/players/${participant.id}`}
      />
    );
  }

  function renderPlayerMatchCard(match: TournamentMatch, opts: { highlight?: boolean } = {}) {
    const reportHref = `/player/results?match_id=${match.id}`;
    return (
      <article
        className={`player-schedule-readonly__match${opts.highlight ? " is-highlighted" : ""}`}
        key={match.id}
      >
        <div>
          <span className={matchStatusPill(match).className}>
            {matchStatusPill(match).label}
          </span>
          <strong>{matchShortLabel(match)}</strong>
          <small className="muted">
            {[tieSeriesLabel(match), displayPlayerRoundLabel(match.round_label), match.group_id === null ? null : describeMatchGroup(match)]
              .filter(Boolean)
              .join(" · ")}
          </small>
          {isMyMatch(match) && <span className="kbd kbd--ok">Tu partido</span>}
        </div>
        {playerUsesCourtCalendar && (
          <dl>
            <div>
              <dt>Fecha</dt>
              {/* Horario aún sin publicar: el servidor no manda fecha/hora/cancha. */}
              <dd>{match.schedule_pending === true ? SCHEDULE_PENDING_LABEL : formatDate(match.scheduled_date, "Por confirmar")}</dd>
            </div>
            <div>
              <dt>Hora</dt>
              <dd>{match.scheduled_time ? formatTimeLabel(match.scheduled_time) : match.schedule_pending === true ? "—" : "Por confirmar"}</dd>
            </div>
            <div>
              <dt>Cancha</dt>
              <dd>{match.court ? courtDisplayName(match.court) : match.schedule_pending === true ? "—" : "Por confirmar"}</dd>
            </div>
          </dl>
        )}
        {isMyMatch(match) && !isFinalMatch(match) && (
          <div className="player-schedule-readonly__actions">
            <Link className="btn btn--compact" to={reportHref}>
              Reportar resultado
            </Link>
          </div>
        )}
      </article>
    );
  }

  function renderPlayerDateGroup(group: { dateKey: string; matches: TournamentMatch[] }) {
    return (
      <section className="player-schedule-date-group" key={group.dateKey}>
        <div className="player-schedule-date-group__header">
          <h3>{playerDateTitle(group.dateKey)}</h3>
          <span className="kbd">{group.matches.length} partido(s)</span>
        </div>
        <div className="player-schedule-readonly__list">
          {group.matches.map((match) =>
            renderPlayerMatchCard(match, { highlight: match.id === currentPlayerMatch?.id }),
          )}
        </div>
      </section>
    );
  }

  function renderPlayerCalendarView() {
    const hasSearch = search.trim().length >= 2;
    return (
      <section className="card player-schedule-readonly" aria-label="Calendario de jugador">
        <div className="card__header-row">
          <div>
            <h2 className="card__title">Mi calendario</h2>
            <p className="card__subtitle">
              Consulta tu {playerUsesWeekLabels ? "semana" : "jornada"} actual y tus partidos de este torneo. Si quieres ver a otro jugador, búscalo abajo.
            </p>
          </div>
          {playerUsesCourtCalendar && (
            <label className="schedule-toolbar__field player-schedule-readonly__date-filter">
              <span className="schedule-toolbar__label">Ir a fecha</span>
              <input
                type="date"
                value={playerDateFilter}
                onChange={(e) => setPlayerDateFilter(e.target.value)}
              />
            </label>
          )}
        </div>

        {playerUsesCourtCalendar && playerDateFilter && (
          <button
            type="button"
            className="btn btn--ghost btn--compact player-schedule-readonly__clear"
            onClick={() => setPlayerDateFilter("")}
          >
            Ver todas mis fechas
          </button>
        )}

        {currentPlayerMatch && (
          <div className="player-schedule-current">
            <div className="player-schedule-current__header">
              <span className="kbd kbd--ok">{playerCurrentBadgeLabel()}</span>
              <strong>{displayPlayerRoundLabel(currentPlayerMatch.round_label)}</strong>
            </div>
            {renderPlayerMatchCard(currentPlayerMatch, { highlight: true })}
          </div>
        )}

        <div className="player-schedule-section-title">
          <h3>{playerUsesCourtCalendar ? "Mis fechas del torneo" : "Mis partidos del torneo"}</h3>
          <p className="muted">
            {playerUsesCourtCalendar
              ? "Incluye fechas pasadas, futuras y partidos por confirmar."
              : "Incluye tus partidos pendientes y jugados; la coordinación se hace directo entre jugadores."}
          </p>
        </div>

        {ownPlayerMatchesByDate.length === 0 ? (
          <p className="muted">No hay partidos tuyos que coincidan con la fecha seleccionada.</p>
        ) : (
          ownPlayerMatchesByDate.map(renderPlayerDateGroup)
        )}

        <div className="player-schedule-player-search">
          <div className="player-schedule-section-title">
            <h3>Buscar otro jugador del torneo</h3>
            <p className="muted">Busca por nombre o apellido para ver sus fechas dentro de este torneo.</p>
          </div>
          <label className="schedule-toolbar__field schedule-toolbar__field--grow">
            <span className="schedule-toolbar__label">Jugador</span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Ej. Jimmy, Abraham, Isaac…"
            />
          </label>
          {!hasSearch ? (
            <p className="muted">Escribe al menos 2 letras para consultar partidos de otros jugadores.</p>
          ) : searchedPlayerMatchesByDate.length === 0 ? (
            <p className="muted">No encontramos partidos para ese jugador en este torneo.</p>
          ) : (
            searchedPlayerMatchesByDate.map(renderPlayerDateGroup)
          )}
        </div>
      </section>
    );
  }

  /** Dueño legible de una tarjeta de solo lectura. */
  function calendarOccupantLabel(match: TournamentMatch, kind: CourtGridItemKind): string {
    if (kind === "club" || match.tournament_id !== tournamentId) {
      return match.tournament_name?.trim() || "Otro torneo del club";
    }
    const division = divisionForMatch(match);
    if (division && division.id !== selectedDivisionId) return division.name;
    return "Este torneo · fuera de esta vista";
  }

  /** Mini canchas: dónde está lo que estorba cuando no es la columna tocada
   * (" en la Cancha 9-A" o " en la Cancha 9 completa"). */
  function calendarOccupantCourtNote(itemCourt: string, court?: string): string {
    if (!court || courtKey(itemCourt) === courtKey(court)) return "";
    if (isHalfCourt(itemCourt)) return ` en la ${courtDisplayName(itemCourt)}`;
    if (isHalfCourt(court)) return ` en la ${courtDisplayName(itemCourt)} completa`;
    return "";
  }

  /** Por qué no se puede empezar (o bloquear) en esa hora: nombra el partido
   * o bloqueo y su rango real, p. ej. "choca con Ana vs Beto (17:00–18:30)".
   * `court`: la columna tocada (con mini canchas dice en cuál está lo que
   * estorba). */
  function calendarConflictText(
    item: CalendarGridItem,
    mode: "programar" | "bloquear" = "programar",
    court?: string,
  ): string {
    const range = intervalLabel(item.startMin, item.endMin);
    const where = calendarOccupantCourtNote(item.court, court);
    const { block, match } = item.payload;
    if (block !== null) {
      const reason = block.reason ? ` · ${block.reason}` : "";
      if (where) {
        return mode === "bloquear"
          ? `Ya hay un bloqueo${where} ${range}${reason}.`
          : `Bloqueo${where} ${range}${reason}.`;
      }
      return mode === "bloquear"
        ? `Ya está bloqueada ${range}${reason}.`
        : `Cancha bloqueada ${range}${reason}.`;
    }
    const owner = item.kind === "own" || item.kind === "own-hidden"
      ? ""
      : ` · ${calendarOccupantLabel(match, item.kind)}`;
    return mode === "bloquear"
      ? `Ahí hay un partido programado${where} (${matchShortLabel(match)}, ${range}${owner}) — muévelo antes de bloquear.`
      : `No cabe aquí: choca con ${matchShortLabel(match)} (${range})${where}${owner}.`;
  }

  /** Cada celda de 15 min es un posible INICIO: se puede tocar solo si
   * [hora, hora+bloque) cabe libre en esa cancha. */
  function calendarCellState(court: string, slotMin: number): CourtTimeGridCell {
    const time = timeFromMinutes(slotMin);
    // «Todas las categorías»: columna que solo está porque otra categoría
    // juega ahí (no es de las canchas de la abierta): solo se lee.
    if (calendarExtraCourtKeys.has(courtKey(court))) {
      return {
        enabled: false,
        tone: "outside",
        title: `${courtDisplayName(court)} no es de las canchas de ${activeDivision?.name ?? "esta categoría"}: se ve porque otra categoría juega ahí. Para programar aquí, sube #Canchas de ${activeDivision?.name ?? "esta categoría"}.`,
      };
    }
    if (!calendarWindow || slotMin < calendarWindow.startMin || slotMin > calendarWindow.endMin) {
      return {
        enabled: false,
        tone: "outside",
        title: `Fuera del horario: los partidos pueden empezar de ${friendlyTime12h(calendarStartTime)} a ${friendlyTime12h(calendarEndTime)}.`,
      };
    }
    if (calendarMode === "bloquear") {
      const range = intervalLabel(slotMin, slotMin + calendarMatchDurationMinutes);
      const check = startAvailability(court, slotMin, calendarMatchDurationMinutes, calendarGridItems);
      if (!check.ok && check.reason !== "window") {
        return { enabled: false, tone: "nofit", title: calendarConflictText(check.item, "bloquear", court) };
      }
      return {
        enabled: true,
        tone: "free",
        title: `Bloquear ${courtDisplayName(court)} ${range} · ${calendarBlockReason}`,
      };
    }
    const placingRange = intervalLabel(slotMin, slotMin + calendarPlacingDuration);
    const check = startAvailability(court, slotMin, calendarPlacingDuration, calendarGridItems, {
      excludeMatchId: selectedMatch?.id ?? null,
    });
    if (!check.ok && check.reason !== "window") {
      return { enabled: false, tone: "nofit", title: calendarConflictText(check.item, "programar", court) };
    }
    if (!selectedMatch) {
      return {
        enabled: false,
        tone: "idle",
        title: `${courtDisplayName(court)} ${time} libre. Selecciona un partido primero.`,
      };
    }
    return {
      enabled: true,
      tone: "free",
      title: `${selectedMatch.scheduled_date ? "Mover" : "Programar"} ${matchShortLabel(selectedMatch)} a ${courtDisplayName(court)} ${placingRange}`,
    };
  }

  /** Programar: el toque solo PROPONE el inicio (la barra pide Confirmar);
   * así un doble clic o un dedo corrido una fila no mueve un partido real ni
   * avisa a sus jugadores. Bloquear sigue de un toque (se libera tocándolo). */
  function handleCalendarCellClick(court: string, time: string, info?: { viaKeyboard: boolean }) {
    if (calendarMode === "bloquear") {
      setCalendarNotice(null);
      void handleCalendarBlockCreate(time, court);
      return;
    }
    if (!selectedMatch) return;
    if (isCurrentCalendarSlot(selectedMatch, court, time)) {
      setCalendarProposal(null);
      setCalendarNotice(`Ese ya es su horario (${courtDisplayName(court)} ${time}). ${selectedMatch.status === "postponed" ? "Para dejarlo aquí como «Programado» usa «Quitar pospuesto» en el panel." : "Toca otra hora para moverlo."}`);
      return;
    }
    setCalendarNotice(null);
    calendarFocusConfirmRef.current = Boolean(info?.viaKeyboard);
    setCalendarProposal({ court, time });
  }

  /** Desplegables del panel (Eduardo 2026-10-15): con hora y cancha, la
   * MISMA propuesta que tocar la celda (misma revisión de choques y la barra
   * pide «Confirmar»). */
  function handlePanelSlotChange(next: { time: string; court: string }) {
    setPanelSlotDraft(next);
    if (!selectedMatch || !next.time || !next.court) return;
    const min = parseTimeMinutes(next.time);
    if (min === null) return;
    if (isCurrentCalendarSlot(selectedMatch, next.court, next.time)) {
      setCalendarProposal(null);
      setCalendarNotice(`Ese ya es su horario (${courtDisplayName(next.court)} ${next.time}).`);
      return;
    }
    const cell = calendarCellState(next.court, min);
    if (!cell.enabled) {
      setCalendarProposal(null);
      setCalendarNotice(cell.title);
      return;
    }
    setCalendarNotice(null);
    calendarFocusConfirmRef.current = false;
    setCalendarProposal({ court: next.court, time: next.time });
  }

  /** «Hasta» posibles: del primer inicio + 15 al último inicio + bloque. */
  function blockRangeEndOptions(): string[] {
    if (!calendarWindow) return [];
    const last = Math.min(calendarWindow.endMin + calendarMatchDurationMinutes, 23 * 60 + 45);
    const out: string[] = [];
    for (let min = calendarWindow.startMin + 15; min <= last; min += 15) out.push(timeFromMinutes(min));
    return out;
  }

  /** Desde / Hasta efectivos del rango (con «Todo el día», la ventana completa). */
  function blockRangeTimes(): { from: string; to: string } {
    const ends = blockRangeEndOptions();
    if (blockRange.allDay && calendarWindow) {
      return { from: timeFromMinutes(calendarWindow.startMin), to: ends[ends.length - 1] ?? "" };
    }
    return { from: blockRange.from, to: blockRange.to };
  }

  /** Bloquear por rango: los mismos bloqueos que tocar celdas (uno por
   * cancha). Si en alguna cancha hay un partido en ese rango, no se bloquea
   * ninguna y se dice cuál estorba (como hoy al tocar una celda ocupada). */
  async function handleCalendarBlockRange() {
    if (!token || !canEditSchedule) return;
    if (!calendarEffectiveVenueLabel) {
      setError("Define la sede del torneo en Datos para poder bloquear horarios.");
      setMessage(null);
      return;
    }
    const { from, to } = blockRangeTimes();
    const startMin = parseTimeMinutes(from);
    const endMin = parseTimeMinutes(to);
    if (startMin === null || endMin === null || endMin <= startMin) {
      setError("Elige «Desde» y «Hasta» (Hasta después de Desde).");
      setMessage(null);
      return;
    }
    const courts = blockRange.courts;
    if (courts.length === 0) {
      setError("Elige al menos una cancha para bloquear.");
      setMessage(null);
      return;
    }
    for (const court of courts) {
      const check = startAvailability(court, startMin, endMin - startMin, calendarGridItems);
      if (!check.ok && check.reason !== "window") {
        setError(calendarConflictText(check.item, "bloquear", court));
        setMessage(null);
        return;
      }
    }
    setCalendarBusy(true);
    const done: string[] = [];
    try {
      for (const court of courts) {
        await api.matches.createCourtBlock(token, {
          venue_name: calendarEffectiveVenueLabel,
          scheduled_date: calendarDate,
          start_time: from,
          end_time: to,
          court,
          reason: calendarBlockReason,
          match_duration_minutes: durationForServer(calendarMatchDurationMinutes),
        });
        done.push(courtDisplayName(court));
      }
      flashOk(`${done.length === 1 ? "Bloqueada" : "Bloqueadas"} ${done.join(", ")} ${from}–${to} · ${calendarBlockReason}`);
      setBlockRange((current) => ({ ...current, courts: [] }));
    } catch (err) {
      flashErr(
        err,
        done.length > 0 ? `Se bloquearon ${done.join(", ")}, pero no las demás.` : "No se pudo bloquear el rango.",
      );
    } finally {
      setCalendarClubVersion((v) => v + 1);
      setCalendarBusy(false);
    }
  }

  /** ¿La propuesta puede recorrerse `delta` minutos? (cabe y no es su hora actual) */
  function calendarProposalCanShift(delta: number): boolean {
    if (!calendarProposal || !selectedMatch) return false;
    const start = parseTimeMinutes(calendarProposal.time);
    if (start === null) return false;
    const next = start + delta;
    if (next < 0 || next >= 24 * 60) return false;
    if (isCurrentCalendarSlot(selectedMatch, calendarProposal.court, timeFromMinutes(next))) return false;
    return calendarCellState(calendarProposal.court, next).enabled;
  }

  function handleCalendarNudge(delta: number) {
    if (!calendarProposal || !calendarProposalCanShift(delta)) return;
    const start = parseTimeMinutes(calendarProposal.time);
    if (start === null) return;
    setCalendarProposal({ court: calendarProposal.court, time: timeFromMinutes(start + delta) });
  }

  function handleCalendarConfirm() {
    if (!selectedMatch || !calendarProposal) return;
    void handleCalendarSchedule(
      selectedMatch,
      calendarDate,
      calendarProposal.time,
      calendarProposal.court,
      activeRoundOrderWarning() !== null,
    );
  }

  function renderCalendarGridItem(item: CalendarGridItem, info: CourtTimeGridItemInfo) {
    const compact = info.span <= 2;
    // Mini canchas: tarjeta de media columna (o carril) → hora y nombres cortos.
    const narrow = info.narrow;
    const shortContent = compact || narrow;
    const range = intervalLabel(item.startMin, item.endMin);
    const stateClass = `${compact ? " is-compact" : ""}${info.conflict ? " is-conflict" : ""}${
      narrow ? " schedule-timegrid__card--half" : ""
    }`;
    const conflictNote = info.conflict ? " Se encima con otro partido o bloqueo de esta cancha." : "";
    // Media cancha en una columna sin dividir: «A» / «B» y el nombre en el título.
    const halfTag = info.half && info.lanes > 1 ? (
      <span className="schedule-timegrid__half-tag" aria-hidden="true">
        {info.half}
      </span>
    ) : null;
    const halfTitle = info.half ? `${courtDisplayName(item.court)} · ` : "";
    const { block, match } = item.payload;
    if (block !== null) {
      const reason = block.reason || "Bloqueada";
      return (
        <button
          type="button"
          className={`schedule-timegrid__card schedule-timegrid__card--block${stateClass}`}
          disabled={calendarBusy}
          title={
            calendarMode === "bloquear"
              ? `Toca para liberar ${courtDisplayName(block.court)} ${range} · ${reason}`
              : `${halfTitle}Cancha bloqueada ${range} · ${reason}. Toca el bloqueo para desbloquearlo.${conflictNote}`
          }
          onClick={() => {
            if (calendarMode === "bloquear") {
              void handleCalendarBlockDelete(block);
              return;
            }
            setSelectedMatchId(null);
            setSelectedPlayedId(null);
            setSelectedBlockId(block.id);
          }}
        >
          {/* Tarjeta angosta (comparte la hora con un partido): solo el ícono;
              el motivo completo va en el título y para lector de pantalla, sin
              partir la palabra a la mitad («Mantenimient / o»). */}
          <strong className="schedule-timegrid__block-title">
            <span aria-hidden="true">⛔</span>
            {narrow ? <span className="sr-only">{reason}</span> : <> {reason}</>}
          </strong>
          <small>
            {halfTag}
            {range}
          </small>
        </button>
      );
    }
    const label = matchShortLabel(match);
    if (isPlayedStatus(match)) {
      const [side1, side2] = matchSideNames(match);
      const chip = playedChipLabel(match);
      const groupText = describeMatchGroup(match);
      const divisionText = calendarAllCategories || hasSeveralDivisions ? divisionForMatch(match)?.name ?? match.division_name ?? "" : "";
      return (
        <button
          type="button"
          className={`schedule-timegrid__card schedule-timegrid__card--played${stateClass}${
            selectedPlayedId === match.id ? " is-selected" : ""
          }`}
          title={`${halfTitle}Ya jugado: ${label} (${range}) · ${chip}${match.score_text ? ` ${match.score_text}` : ""}. Solo se consulta; el resultado se edita en Resultados.`}
          onClick={() => {
            if (calendarMode === "bloquear" || selectedMatch) {
              // Con un partido elegido para programar no se suelta: solo avisa.
              setCalendarNotice(`Ahí ya se jugó ${label} (${range}).`);
              return;
            }
            setSelectedMatchId(null);
            setSelectedBlockId(null);
            setSelectedPlayedId(match.id);
          }}
          data-calendar-played={match.id}
        >
          {shortContent ? (
            <>
              <small>{timeFromMinutes(item.startMin)}</small>
              <strong>{narrow ? matchShortLabel(match, { short: true }) : label}</strong>
            </>
          ) : (
            <>
              {divisionText ? <span className="schedule-timegrid__division-tag">{divisionText}</span> : null}
              <strong className="schedule-timegrid__card-names">
                <span>{side1}</span> <span className="schedule-timegrid__card-vs">vs</span> <span>{side2}</span>
              </strong>
              <small className="schedule-timegrid__card-time">
                {range} · {item.endMin - item.startMin} min
              </small>
              <span className="schedule-timegrid__card-chips">
                {groupText ? <span className="schedule-timegrid__chip">{groupText}</span> : null}
                <span className="schedule-timegrid__chip schedule-timegrid__chip--played">{chip}</span>
                {match.score_text ? <span className="schedule-timegrid__card-score">{match.score_text}</span> : null}
              </span>
            </>
          )}
        </button>
      );
    }
    const cardLabel = narrow ? matchShortLabel(match, { short: true }) : label;
    const editable = item.kind === "own" || item.kind === "own-hidden";
    const isSelected = editable && selectedMatchId === match.id;
    // «Todas las categorías» (2026-10-02): un partido de otra categoría del
    // torneo se ve con su color y su nombre corto; tocarlo dice de cuál es y
    // ofrece abrirla (ahí se mueve: aquí solo se mueve la categoría abierta).
    const otherCategory =
      calendarAllCategories &&
      item.kind === "division" &&
      match.tournament_id === tournamentId &&
      match.division_id != null &&
      match.division_id !== selectedDivisionId;
    const categorized = otherCategory || (editable && hasSeveralDivisions && calendarShowsAllDivisions);
    const matchDivision = categorized ? divisionForMatch(match) : null;
    const divisionName = matchDivision?.name ?? match.division_name?.trim() ?? "";
    const categoryText = matchDivision ? divisionShortName(matchDivision) : divisionName;
    const categoryTag = categorized && categoryText ? (
      <span className="schedule-timegrid__division-tag" title={divisionName || undefined}>
        {categoryText}
      </span>
    ) : null;
    // Publicar horarios: el partido de esta categoría sin publicar lleva la
    // marca escrita «Sin publicar» (los bordes punteados, rayas y ámbar ya
    // significan otra cosa en el grid). Los de otras categorías solo lo dicen
    // en el título: ocupan igual, pero su hora aún no es definitiva. Con
    // «Todas las categorías» cada tarjeta lleva su marca.
    const unpublishedCard = isScheduleUnpublished(match);
    const showsDraft = (editable || otherCategory) && unpublishedCard;
    const kindClass = item.kind === "own"
      ? ""
      : item.kind === "own-hidden"
        ? " schedule-timegrid__card--hidden"
        : item.kind === "club"
          ? " schedule-timegrid__card--club"
          : otherCategory
            ? " schedule-timegrid__card--other"
            : " schedule-timegrid__card--division";
    const detail = editable
      ? `${tieSeriesLabel(match) ? `${tieSeriesLabel(match)} · ` : ""}${unpublishedCard ? "Sin publicar · " : ""}${matchStatusPill(match).label}${
          item.kind === "own-hidden" ? " · fuera del filtro" : ""
        }`
      : otherCategory
        ? `${tieSeriesLabel(match) ? `${tieSeriesLabel(match)} · ` : ""}${unpublishedCard ? "Sin publicar · " : ""}${matchStatusPill(match).label}`
        : calendarOccupantLabel(match, item.kind);
    const className = `schedule-timegrid__card${kindClass}${stateClass}${isSelected ? " is-selected is-moving" : ""}${
      showsDraft ? " is-unpublished" : ""
    }${categorized ? " is-categorized" : ""}`;
    // Tarjeta corta o de media cancha (angosta): el punto, no la etiqueta
    // «SIN PUBLICAR», que no cabe y le quita renglones a los nombres.
    // Con categoría (revisión 2026-10-02) también el punto: va en el mismo
    // renglón que el nombre de la categoría y no se come los nombres (el
    // «Sin publicar» escrito sigue abajo, en el detalle).
    const sideNames = matchSideNames(match);
    const groupText = editable || otherCategory ? describeMatchGroup(match) : "";
    const draftMark = showsDraft
      ? shortContent || categoryTag
        ? <span className="schedule-timegrid__draft-dot" role="img" aria-label="Sin publicar" />
        : <span className="schedule-timegrid__draft-tag">Sin publicar</span>
      : null;
    const content = shortContent ? (
      <>
        {draftMark}
        {categoryTag}
        <small>
          {halfTag}
          {timeFromMinutes(item.startMin)}
        </small>
        <strong>{cardLabel}</strong>
      </>
    ) : (
      <>
        {categoryTag ? (
          <span className="schedule-timegrid__card-tags">
            {draftMark}
            {categoryTag}
          </span>
        ) : (
          draftMark
        )}
        <strong className="schedule-timegrid__card-names">
          <span>{sideNames[0]}</span> <span className="schedule-timegrid__card-vs">vs</span> <span>{sideNames[1]}</span>
        </strong>
        <small className="schedule-timegrid__card-time">
          {range} · {item.endMin - item.startMin} min
        </small>
        <span className="schedule-timegrid__card-chips">
          {groupText ? <span className="schedule-timegrid__chip">{groupText}</span> : null}
          <span className="schedule-timegrid__chip schedule-timegrid__chip--state">{detail}</span>
          {match.score_text ? <span className="schedule-timegrid__card-score">{match.score_text}</span> : null}
        </span>
        {editable && match.referee_name ? (
          <small className="schedule-timegrid__referee" data-card-referee={match.id}>
            Árbitro: {match.referee_name}
          </small>
        ) : editable && tournamentReferees.length > 0 && !isPlayedStatus(match) ? (
          <small className="schedule-timegrid__referee is-empty" data-card-referee={match.id}>
            Sin árbitro ▾
          </small>
        ) : null}
      </>
    );
    if (otherCategory) {
      const blockText = `Ahí hay un partido programado (${label}, ${range} · ${divisionName}) — muévelo desde su categoría antes de bloquear.`;
      return (
        <button
          type="button"
          className={className}
          style={matchDivisionStyle(match)}
          disabled={calendarBusy}
          title={
            calendarMode === "bloquear"
              ? `${halfTitle}${blockText}`
              : `${halfTitle}${divisionName}: ${label} (${range})${unpublishedCard ? " · sin publicar" : ""}. Abre su categoría para moverlo.${conflictNote}`
          }
          onClick={(event) => {
            if (calendarMode === "bloquear") {
              setCalendarNotice(blockText);
              return;
            }
            setCalendarNotice(null);
            // detail 0 = se activó con Enter/Espacio (no con el dedo o el mouse).
            foreignPickViaKeyboardRef.current = event.detail === 0;
            setCalendarForeignPick(match);
          }}
          data-calendar-other-division={match.division_id ?? undefined}
        >
          {content}
        </button>
      );
    }
    if (!editable) {
      return (
        <div
          className={className}
          role="note"
          title={`${halfTitle}${unpublishedCard ? "Sin publicar (su hora aún no es definitiva). " : ""}Ocupado por ${detail}: ${label} (${range}).${conflictNote}`}
        >
          {content}
        </div>
      );
    }
    const draftTitle = unpublishedCard ? "Sin publicar: los jugadores aún no ven este horario. Puedes moverlo sin aviso. " : "";
    return (
      <button
        type="button"
        className={className}
        style={matchDivisionStyle(match)}
        disabled={calendarBusy}
        title={
          calendarMode === "bloquear"
            ? `${halfTitle}${draftTitle}Ahí hay un partido programado (${label}, ${range}) — muévelo antes de bloquear.`
            : `${halfTitle}${draftTitle}Toca para seleccionar y mover: ${label} (${range}).${conflictNote}`
        }
        onClick={() => {
          if (calendarMode === "bloquear") {
            setCalendarNotice(`Ahí hay un partido programado (${label}, ${range}) — muévelo antes de bloquear.`);
            return;
          }
          handleCalendarCardSelect(match);
        }}
      >
        {content}
      </button>
    );
  }

  /** Aviso discreto junto a los campos: de qué categoría son los valores y
   * si ya se guardaron (data-no-toast: no es un mensaje flotante). Si el
   * Bloque min vino de otra categoría, lo dice y ofrece confirmarlo.
   * Con «Ajustes» cerrado, «ya guardados» no se repite (la línea del resumen
   * ya dice «se guardan solos»); lo que hay que revisar sí se ve. */
  function renderCalendarSettingsStatus(collapsed = false) {
    // Sin categoría, las notas van en «Ordenar automático».
    if (!canEditSchedule || selectedDivisionId === null) return null;
    const divisionName = activeDivision?.name ?? "esta categoría";
    const status = calendarSettingsStatus;
    let tone = "";
    let text: string;
    if (status.kind === "pending" && status.onBlur) {
      text = `Se guarda para ${divisionName} al salir del campo.`;
    } else if (status.kind === "pending" || status.kind === "saving") {
      text = `Guardando para ${divisionName}…`;
    } else if (status.kind === "saved") {
      tone = " is-saved";
      text = `Guardado para ${divisionName}.`;
    } else if (status.kind === "error") {
      tone = " is-error";
      text = `No se pudo guardar para ${divisionName}: ${status.message} Mientras tanto se usan los valores de la pantalla.`;
    } else if (status.kind === "inherited") {
      tone = " is-inherited";
      text = `${divisionName} aún no tiene bloque guardado: ${
        status.from ? `se tomaron de ${status.from}` : "se usan los últimos del torneo"
      }.`;
    } else {
      text = `Bloque, canchas y horario de ${divisionName}: se guardan solos para esta categoría.`;
    }
    const block = validBlockFieldMinutes(calendarInterval);
    let blockWarning: ReactNode = null;
    if (calendarInterval.trim() && block === null) {
      blockWarning = (
        <p className="schedule-calendar__settings-block" data-calendar-block-invalid>
          El Bloque min va de 15 a 240 minutos: ese valor no se guarda.
        </p>
      );
    } else if (!calendarBlockOwn) {
      const shown = calendarInterval.trim() ? `${calendarInterval} min` : "el bloque";
      // En el aviso de "heredado" ya se dijo de dónde vino.
      const from = calendarInheritedFrom && status.kind !== "inherited" ? ` (viene de ${calendarInheritedFrom})` : "";
      blockWarning = (
        <p className="schedule-calendar__settings-block" data-calendar-block-warning>
          {divisionHasScheduledMatches(selectedDivisionId)
            ? `Revisa el Bloque min${from}: ${divisionName} ya tiene partidos programados, así que ${shown} no se le guarda solo. Si sus partidos duran otra cosa, cámbialo; si es correcto, confírmalo.`
            : `Revisa el Bloque min${from}: ${shown} se guarda para ${divisionName} al cambiarlo, al confirmarlo o al programar.`}
          {block !== null ? (
            <button
              type="button"
              className="btn btn--ghost btn--compact schedule-calendar__settings-confirm"
              onClick={handleConfirmCalendarBlock}
              disabled={calendarBusy}
            >
              Usar {block} min en {divisionName}
            </button>
          ) : null}
        </p>
      );
    }
    const quiet = collapsed && blockWarning === null && (status.kind === "loaded" || status.kind === "idle");
    return (
      <div
        className={`schedule-calendar__settings-status${tone}`}
        data-calendar-settings-status={status.kind}
        data-no-toast
        hidden={quiet}
      >
        <p className="schedule-calendar__settings-text" role="status" aria-live="polite">
          {text}
        </p>
        {blockWarning}
      </div>
    );
  }

  /** Vista sin categoría con categorías de Mini cancha: aquí la 9 es una
   * sola columna; las mini canchas se acomodan desde la categoría. */
  function renderMiniCourtsAllNote() {
    if (!divisions.some((division) => divisionCourtSetup(division).courtType === "mini")) return null;
    return (
      <p className="schedule-calendar__settings-status is-inherited" data-calendar-mini-note data-no-toast>
        Las categorías de Mini cancha se acomodan en mini canchas desde su propia categoría. Aquí cada cancha
        dividida se ve como una sola columna y sus partidos como tarjetas angostas con su letra.
      </p>
    );
  }

  /** Mini canchas: marcar o desmarcar una cancha dividida de la categoría
   * abierta (en la sede del calendario). Se guarda de inmediato con su propio
   * PUT (no pasa por el guardado de Bloque min / #Canchas) que manda solo el
   * CAMBIO ({split_courts_add} o {split_courts_remove}); el servidor lo aplica
   * a la lista guardada y esta pestaña se queda con la que regresa (así no
   * borra lo que otra pestaña marcó). Si falla, la casilla regresa. */
  async function handleToggleSplitCourt(base: string) {
    const divisionId = selectedDivisionId;
    if (!token || !canEditSchedule || divisionId === null || !activeIsMini || calendarSplitSaving) return;
    const venue = calendarSplitVenueKey;
    const overrideKey = `${divisionId}|${venue}`;
    // Lo guardado completo: una cancha marcada arriba del #Canchas actual se
    // conserva (no aplica mientras no exista esa columna).
    const stored = new Set(calendarSplitOverrides[overrideKey] ?? activeCourtSetup.splitBases);
    const checked = calendarTickedSplitBases.has(base);
    if (checked) {
      const halves = halfCourts(base, calendarSplitParts.get(base));
      const onHalves = calendarMatchItems.filter(
        (item) => isHalfCourt(item.court) && courtBase(item.court) === base,
      ).length;
      if (onHalves > 0) {
        const confirmed = await askConfirm(
          `Hay ${onHalves === 1 ? "1 partido" : `${onHalves} partidos`} en la Cancha ${halves.join(" / ")} este día. ` +
            `${onHalves === 1 ? "Seguirá" : "Seguirán"} ahí y la cancha se seguirá viendo dividida en los días ` +
            "que tengan partidos en sus mini canchas.",
          { acceptLabel: "Sí, quitar la división", dismissLabel: "No, dejarla dividida" },
        );
        if (!confirmed) return;
      }
    }
    if (checked) stored.delete(base);
    else stored.add(base);
    const next = sortedSplitBases(stored);
    const previous = calendarSplitOverrides[overrideKey];
    setCalendarSplitOverrides((prev) => ({ ...prev, [overrideKey]: next }));
    setCalendarProposal(null);
    setCalendarSplitSaving(true);
    setCalendarSplitStatus({ divisionId, kind: "saving" });
    try {
      // Lo que ya va en camino de Bloque min / #Canchas termina primero: los
      // dos guardados tocan los mismos ajustes de la categoría.
      for (;;) {
        const running = calendarSavingRef.current[divisionId];
        if (!running) break;
        await running;
      }
      // Solo el cambio de ESTA casilla: la lista completa de esta pestaña
      // puede estar vieja (otra pestaña o el coadmin marcaron otra cancha).
      const response = await api.tournaments.updateDivisionCalendarSettings(
        token,
        tournamentId,
        divisionId,
        checked ? { split_courts_remove: { [venue]: [base] } } : { split_courts_add: { [venue]: [base] } },
      );
      const saved = response?.data?.division;
      if (saved) {
        setDivisions((prev) => prev.map((division) => (division.id === divisionId ? { ...division, ...saved } : division)));
        setCalendarSplitOverrides((prev) => {
          const rest = { ...prev };
          delete rest[overrideKey];
          return rest;
        });
      }
      setCalendarSplitStatus({ divisionId, kind: "saved" });
    } catch (err) {
      setCalendarSplitOverrides((prev) => {
        const rest = { ...prev };
        if (previous === undefined) delete rest[overrideKey];
        else rest[overrideKey] = previous;
        return rest;
      });
      setCalendarSplitStatus({
        divisionId,
        kind: "error",
        message: err instanceof ApiError && err.message ? err.message : "Revisa tu conexión e intenta otra vez.",
      });
    } finally {
      setCalendarSplitSaving(false);
    }
  }

  /** Mini canchas (2026-10-08): en cuántas (2, 3 o 4) se parte una cancha
   * dividida de la categoría abierta. Se guarda de inmediato con su propio
   * PUT ({split_parts_set}); si falla, el selector regresa. Bajar de partes
   * con partidos en las mini canchas que sobran pide confirmar (siguen ahí y
   * se siguen viendo ese día). */
  async function handleSplitPartsChange(base: string, nextParts: number) {
    const divisionId = selectedDivisionId;
    if (!token || !canEditSchedule || divisionId === null || !activeIsMini || calendarSplitSaving) return;
    const parts = cleanSplitParts(nextParts);
    const current = calendarTickedSplitParts.get(base) ?? 2;
    if (parts === current) return;
    if (parts < current) {
      const dropped = halfCourts(base, current).slice(parts);
      const onDropped = calendarMatchItems.filter((item) =>
        dropped.some((court) => courtKey(court) === courtKey(item.court)),
      ).length;
      if (onDropped > 0) {
        const confirmed = await askConfirm(
          `Hay ${onDropped === 1 ? "1 partido" : `${onDropped} partidos`} en la Cancha ${dropped.join(" / ")} este día. ` +
            `${onDropped === 1 ? "Seguirá" : "Seguirán"} ahí y esa mini cancha se seguirá viendo en los días que tengan partidos.`,
          { acceptLabel: `Sí, dividir en ${parts}`, dismissLabel: "No, dejarla como está" },
        );
        if (!confirmed) return;
      }
    }
    const venue = calendarSplitVenueKey;
    const overrideKey = `${divisionId}|${venue}`;
    const previous = calendarPartsOverrides[overrideKey];
    setCalendarPartsOverrides((prev) => ({ ...prev, [overrideKey]: { ...(prev[overrideKey] ?? {}), [base]: parts } }));
    setCalendarProposal(null);
    setCalendarSplitSaving(true);
    setCalendarSplitStatus({ divisionId, kind: "saving" });
    try {
      for (;;) {
        const running = calendarSavingRef.current[divisionId];
        if (!running) break;
        await running;
      }
      const response = await api.tournaments.updateDivisionCalendarSettings(token, tournamentId, divisionId, {
        split_parts_set: { [venue]: { [base]: parts } },
      });
      const saved = response?.data?.division;
      if (saved) {
        setDivisions((prev) => prev.map((division) => (division.id === divisionId ? { ...division, ...saved } : division)));
        setCalendarPartsOverrides((prev) => {
          const rest = { ...prev };
          delete rest[overrideKey];
          return rest;
        });
      }
      setCalendarSplitStatus({ divisionId, kind: "saved" });
    } catch (err) {
      setCalendarPartsOverrides((prev) => {
        const rest = { ...prev };
        if (previous === undefined) delete rest[overrideKey];
        else rest[overrideKey] = previous;
        return rest;
      });
      setCalendarSplitStatus({
        divisionId,
        kind: "error",
        message: err instanceof ApiError && err.message ? err.message : "Revisa tu conexión e intenta otra vez.",
      });
    } finally {
      setCalendarSplitSaving(false);
    }
  }

  /** «¿Este torneo usa calendario de canchas?» Sí / No (Eduardo 2026-10-03:
   * se decide solo aquí). Se guarda al tocarlo en los ajustes del torneo; con
   * «No» se esconde de inmediato todo lo de canchas y, si el servidor no
   * deja, regresa a como estaba y dice por qué. Nada se borra: con «Sí»
   * vuelve todo como estaba. */
  async function handleUsesCourtCalendarChange(next: boolean) {
    if (!token || !canEditSchedule || courtCalendarRoleLocked || courtCalendarSaving) return;
    const previous = usesCourtCalendar !== false;
    if (previous === next) return;
    setUsesCourtCalendar(next);
    setCourtCalendarSaving(true);
    setCourtCalendarStatus(null);
    try {
      const response = await api.tournaments.updateSettings(token, tournamentId, { uses_court_calendar: next });
      const saved = response?.data?.settings?.uses_court_calendar;
      const value = typeof saved === "boolean" ? saved : next;
      setUsesCourtCalendar(value);
      setCourtCalendarStatus({
        kind: "saved",
        text: value ? "Guardado: este torneo usa calendario de canchas." : "Guardado: este torneo no usa calendario de canchas.",
      });
    } catch (err) {
      setUsesCourtCalendar(previous);
      setCourtCalendarStatus({
        kind: "error",
        text: `No se pudo guardar: ${
          err instanceof ApiError && err.message ? err.message : "revisa tu conexión e intenta otra vez."
        } Se quedó como estaba.`,
      });
    } finally {
      setCourtCalendarSaving(false);
    }
  }

  /** «Tipo de cancha: Normal / Mini cancha» de la categoría abierta (antes en
   * Formato; Eduardo 2026-10-03 lo quiere en Calendario, junto a las mini
   * canchas). Se guarda al tocarlo con su propio PUT ({court_type}), después
   * de lo que ya vaya en camino de los ajustes de la categoría. Si el
   * servidor no deja (409: hay partidos en media cancha), se queda como
   * estaba y dice por qué. */
  async function handleCourtTypeChange(next: DivisionCourtType) {
    const divisionId = selectedDivisionId;
    if (!token || !canEditSchedule || divisionId === null || courtTypeSaving || calendarSplitSaving) return;
    if (activeCourtSetup.courtType === next) return;
    setCourtTypeSaving({ divisionId, next });
    setCourtTypeStatus(null);
    setCalendarProposal(null);
    try {
      for (;;) {
        const running = calendarSavingRef.current[divisionId];
        if (!running) break;
        await running;
      }
      const response = await api.tournaments.updateDivisionCalendarSettings(token, tournamentId, divisionId, {
        court_type: next,
      });
      const saved = response?.data?.division;
      setDivisions((prev) =>
        prev.map((division) => {
          if (division.id !== divisionId) return division;
          if (saved) return { ...division, ...saved };
          const settings = { ...(division.calendar_settings_json ?? {}) } as Record<string, unknown>;
          if (next === "mini") settings.court_type = "mini";
          else delete settings.court_type;
          return { ...division, calendar_settings_json: settings };
        }),
      );
      setCourtTypeStatus({
        divisionId,
        kind: "saved",
        text:
          next === "mini"
            ? "Guardado: Mini cancha. Abajo marca qué canchas se dividen y en cuántas mini canchas (2, 3 o 4)."
            : "Guardado: cancha normal.",
      });
    } catch (err) {
      setCourtTypeStatus({
        divisionId,
        kind: "error",
        text:
          err instanceof ApiError && err.message
            ? err.message
            : "No se pudo guardar el tipo de cancha. Revisa tu conexión e intenta otra vez.",
      });
    } finally {
      setCourtTypeSaving(null);
    }
  }

  /** Arriba del calendario: «¿Este torneo usa calendario de canchas?». */
  function renderCourtCalendarChoice() {
    if (playerMode || resultsOnly) return null;
    const on = usesCourtCalendar !== false;
    const locked = !canEditSchedule || tournamentClosed || courtCalendarRoleLocked;
    const options: Array<[boolean, string]> = [
      [true, "Sí"],
      [false, "No"],
    ];
    return (
      <section className="card schedule-court-calendar" data-court-calendar-choice={on ? "si" : "no"}>
        <div className="schedule-court-calendar__row">
          <h2 className="schedule-court-calendar__title" id="court-calendar-choice-title">
            ¿Este torneo usa calendario de canchas?
          </h2>
          <div className="segmented court-type-choice__options" role="radiogroup" aria-labelledby="court-calendar-choice-title">
            {options.map(([value, label]) => (
              <label
                key={label}
                className={`segmented__btn court-type-choice__option${on === value ? " is-active" : ""}${
                  courtCalendarSaving || locked ? " is-disabled" : ""
                }`}
              >
                <input
                  type="radio"
                  className="sr-only"
                  name="uses-court-calendar"
                  value={value ? "si" : "no"}
                  checked={on === value}
                  disabled={courtCalendarSaving || locked}
                  onChange={() => void handleUsesCourtCalendarChange(value)}
                />
                {label}
              </label>
            ))}
          </div>
        </div>
        <p className="muted schedule-court-calendar__hint" data-court-calendar-hint>
          {on
            ? "Programas fecha, hora y cancha de cada partido en el calendario de abajo."
            : "Los jugadores coordinan su partido por WhatsApp o llamada: cada uno ve su próximo reto con el celular de su rival."}
        </p>
        {courtCalendarRoleLocked ? (
          <p className="muted schedule-court-calendar__hint" data-court-calendar-role-lock>
            Solo el organizador lo cambia.
          </p>
        ) : null}
        {courtCalendarSaving ? (
          <p className="court-type-choice__status" role="status" aria-live="polite" data-no-toast data-court-calendar-status="saving">
            Guardando…
          </p>
        ) : courtCalendarStatus ? (
          <p
            className={`court-type-choice__status${courtCalendarStatus.kind === "error" ? " is-error" : " is-saved"}`}
            role="status"
            aria-live="polite"
            data-no-toast
            data-court-calendar-status={courtCalendarStatus.kind}
          >
            {courtCalendarStatus.text}
          </p>
        ) : null}
      </section>
    );
  }

  /** «Tipo de cancha: Normal / Mini cancha» dentro de «Ajustes de esta
   * categoría» (solo con una categoría abierta). */
  function renderCourtTypeChoice() {
    if (!canEditSchedule || selectedDivisionId === null) return null;
    const saving = courtTypeSaving !== null;
    const shown =
      courtTypeSaving && courtTypeSaving.divisionId === selectedDivisionId ? courtTypeSaving.next : activeCourtSetup.courtType;
    const labelId = `court-type-label-${selectedDivisionId}`;
    return (
      <div className="court-type-choice" data-court-type={shown}>
        <span className="hint-label schedule-toolbar__label" id={labelId}>
          Tipo de cancha
          <FieldHint label="¿Qué es Mini cancha?">
            Mini cancha: en esta categoría puedes dividir canchas en 2, 3 o 4 mini canchas (A, B, C, D) para jugar varios partidos a la vez.
          </FieldHint>
        </span>
        <div className="segmented court-type-choice__options" role="radiogroup" aria-labelledby={labelId}>
          {(
            [
              ["normal", "Normal"],
              ["mini", "Mini cancha"],
            ] as const
          ).map(([value, label]) => (
            <label
              key={value}
              className={`segmented__btn court-type-choice__option${shown === value ? " is-active" : ""}${
                saving ? " is-disabled" : ""
              }`}
            >
              <input
                type="radio"
                className="sr-only"
                name={`court-type-${selectedDivisionId}`}
                value={value}
                checked={shown === value}
                disabled={saving || calendarSplitSaving || calendarBusy}
                onChange={() => void handleCourtTypeChange(value)}
              />
              {label}
            </label>
          ))}
        </div>
      </div>
    );
  }

  /** Guardando / guardado / no se pudo del tipo de cancha. Fuera de los
   * campos de «Ajustes» (como el de mini canchas): un error se ve aunque
   * se hayan cerrado; cerrado, «Guardado» no se repite. */
  function renderCourtTypeStatus(collapsed = false) {
    if (!canEditSchedule || selectedDivisionId === null) return null;
    if (courtTypeSaving && courtTypeSaving.divisionId === selectedDivisionId) {
      return (
        <p className="court-type-choice__status" role="status" aria-live="polite" data-no-toast data-court-type-status="saving">
          Guardando tipo de cancha…
        </p>
      );
    }
    const status = courtTypeStatus && courtTypeStatus.divisionId === selectedDivisionId ? courtTypeStatus : null;
    if (!status || (collapsed && status.kind === "saved")) return null;
    return (
      <p
        className={`court-type-choice__status${status.kind === "error" ? " is-error" : " is-saved"}`}
        role="status"
        aria-live="polite"
        data-no-toast
        data-court-type-status={status.kind}
      >
        {status.text}
      </p>
    );
  }

  /** Torneo sin calendario de canchas (pantalla única): la lista de jornadas,
   * cada una con sus fechas (Desde / Hasta + Guardar), sus partidos con su
   * estatus y quién descansa. La cuadrícula y el riel se esconden; nada se
   * borra. Los partidos de jornadas sin renglón de fechas (eliminatoria,
   * playoffs) van en la tabla de abajo. */
  function renderNoCourtMatchList() {
    const list = calendarMatches;
    const played = list.filter((match) => isPlayedStatus(match)).length;
    // Eduardo 2026-10-05 (torneo 69): lo cancelado no está «por jugar»; lo
    // cancelado por una baja se cuenta aparte.
    const cancelledByDrop = list.filter((match) => isCancelledByDrop(match)).length;
    const cancelled = list.filter((match) => match.status === "cancelled").length - cancelledByDrop;
    const toPlay = list.length - played - cancelledByDrop - cancelled;
    const showPanel = !isElimination;
    const panelRounds = new Set(showPanel ? noCourtPanelRounds : []);
    const jornadaOf = (match: TournamentMatch): number | null =>
      isRoundRobinPlayoffMatch(match) ? null : parseRoundNumber(match.round_label);
    const inPanel = (match: TournamentMatch) => {
      const n = jornadaOf(match);
      return n !== null && panelRounds.has(n);
    };
    const leftovers = list.filter((match) => !inPanel(match));
    const leftoverRests =
      typeof filterJornada === "number" && panelRounds.has(filterJornada)
        ? []
        : restScreenLines(selectedJornadaRests(leftovers), repeatedGroupNames);
    const table = (rows: TournamentMatch[], showRound: boolean) => (
      <div className="responsive-table-wrap schedule-week-table-wrap">
        <table className="data-table schedule-week-table cal-jornadas__table">
          <thead>
            <tr>
              <th>Partido</th>
              {showRound ? <th>{adminRoundSingularLabel}</th> : <th>Grupo</th>}
              <th>Estatus</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((match) => {
              const pill = matchStatusPill(match);
              return (
                <tr
                  key={match.id}
                  style={matchDivisionStyle(match)}
                  data-no-court-match={match.id}
                  data-drop-match={dropMatchState(match)?.kind}
                  className={isCancelledByDrop(match) ? "schedule-row--drop" : undefined}
                >
                  <td data-label="Partido">
                    <div className="schedule-week-matchup">
                      <strong>{matchShortLabel(match)}</strong>
                      {tieSeriesLabel(match) ? <span className="muted">{tieSeriesLabel(match)}</span> : null}
                    </div>
                  </td>
                  <td data-label={showRound ? adminRoundSingularLabel : "Grupo"}>
                    {showRound ? `${displayMatchupLabel(match.round_label)} · ` : ""}
                    {describeMatchGroup(match)}
                  </td>
                  <td data-label="Estatus">
                    <span className={pill.className}>{pill.label}</span>
                    {canCancelByDrop(match) ? (
                      <>
                        {" "}
                        <button
                          type="button"
                          className="btn btn--compact btn--danger schedule-drop-cancel"
                          onClick={() => void handleCancelByDrop(match)}
                          disabled={savingMatchId === match.id}
                          title="Cancelar este partido por la baja: su rival descansa esa jornada"
                          data-cancel-by-drop={match.id}
                        >
                          Cancelar por baja
                        </button>
                      </>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
    const renderJornadaMatches = (roundNumber: number) => {
      const rows = list.filter((match) => jornadaOf(match) === roundNumber);
      // Quién descansa: el de la jornada elegida (con «Todas», nada).
      const rests =
        filterJornada === roundNumber ? restScreenLines(selectedJornadaRests(rows), repeatedGroupNames) : [];
      if (rows.length === 0 && rests.length === 0) {
        return <p className="muted cal-jornadas__empty">Sin partidos con los filtros actuales.</p>;
      }
      return (
        <>
          {rows.length > 0 ? table(rows, false) : null}
          {rests.length > 0 ? (
            <div className="schedule-rest-lines" data-no-toast data-jornada-rests>
              {rests.map((line) => (
                <p key={line.key} className="muted schedule-rest-line">
                  {line.text}
                </p>
              ))}
            </div>
          ) : null}
        </>
      );
    };
    return (
      <section className="card schedule-week-card cal-jornadas" data-no-court-match-list>
        <div className="card__header-row schedule-week-card__header cal-jornadas__head">
          <div>
            <p className="cal-eyebrow">Jornadas · sin calendario de canchas</p>
            <h2 className="card__title">{scheduledListByJornada ? "Partidos de la jornada" : "Partidos del torneo"}</h2>
            <p className="card__subtitle">
              Sin fecha, hora ni cancha: los jugadores los coordinan entre ellos (WhatsApp o llamada). Los resultados se
              capturan en Resultados.
            </p>
          </div>
          <div className="cal-jornadas__head-side">
            <span className="kbd schedule-week-card__count" data-no-court-count>
              {toPlay} por jugar · {played} jugado(s)
              {cancelledByDrop > 0 ? ` · ${cancelledCountText(cancelledByDrop, true)}` : ""}
              {cancelled > 0 ? ` · ${cancelledCountText(cancelled, false)}` : ""}
            </span>
            <Link
              className="btn btn--ghost cal-toolbar__btn"
              to={`/admin/tournaments/${tournamentId}/results${
                divisionFromQuery ? `?division=${encodeURIComponent(divisionFromQuery)}` : ""
              }`}
            >
              Ir a Resultados →
            </Link>
          </div>
        </div>
        <div className="cal-jornadas__filters">
          {roundFilterRow}
          {groupFilterRow}
          <div className="cal-jornadas__fields">{renderStatusSearchFields()}</div>
        </div>
        {showPanel ? (
          <JornadaDatesPanel
            token={token}
            tournamentId={tournamentId}
            divisions={divisions}
            initialDivisionId={selectedDivisionId ?? (divisions.length === 1 ? divisions[0].id : null)}
            canEdit={canEditSchedule}
            reloadKey={matches.length}
            onSaved={() => setJornadaOverviewReload((value) => value + 1)}
            renderRowExtra={renderJornadaMatches}
            onRoundsLoaded={setNoCourtPanelRounds}
          />
        ) : null}
        {list.length === 0 ? (
          <p className="muted schedule-week-card__empty">No hay partidos que coincidan con los filtros.</p>
        ) : leftovers.length > 0 ? (
          <>
            {panelRounds.size > 0 ? <h3 className="cal-eyebrow cal-jornadas__others">Otros partidos</h3> : null}
            {table(leftovers, true)}
          </>
        ) : null}
        {leftoverRests.length > 0 ? (
          <div className="schedule-rest-lines" data-no-toast data-jornada-rests>
            {leftoverRests.map((line) => (
              <p key={line.key} className="muted schedule-rest-line">
                {line.text}
              </p>
            ))}
          </div>
        ) : null}
      </section>
    );
  }

  /** «9 y 10 → 9-A, 9-B, 10-A, 10-B». */
  function splitCourtsSummary(bases: string[]): string {
    if (bases.length === 0) return "Ninguna dividida: «Programar pendientes» usa las canchas completas.";
    const names = bases.length === 1 ? bases[0] : `${bases.slice(0, -1).join(", ")} y ${bases[bases.length - 1]}`;
    return `${names} → ${bases.flatMap((base) => halfCourts(base, calendarTickedSplitParts.get(base))).join(", ")}`;
  }

  /** Casillas «Canchas divididas en mini canchas» (solo en una categoría
   * de Mini cancha), junto a #Canchas; cada marcada con «en 2 / 3 / 4». */
  function renderSplitCourtsPanel() {
    if (!canEditSchedule || !activeIsMini || selectedDivisionId === null) return null;
    const divisionName = activeDivision?.name ?? "esta categoría";
    const bases = calendarBaseCourts.filter((base) => isSplittableBase(base));
    const ticked = sortedSplitBases(calendarTickedSplitBases);
    // Desmarcadas que siguen partidas este día porque tienen partidos o bloqueos.
    const stillSplit = sortedSplitBases([...calendarBusyHalfBases].filter((base) => !calendarTickedSplitBases.has(base)))
      .filter((base) => calendarBaseCourts.includes(base));
    return (
      <fieldset className="schedule-calendar__split" data-split-courts>
        <legend>Canchas divididas en mini canchas</legend>
        <p className="schedule-calendar__split-hint">
          Marca las canchas que se parten ({divisionName}) y elige en cuántas mini canchas: 2, 3 o 4. Cada una se ve
          como Cancha 9-A, 9-B (y 9-C, 9-D) y se juega un partido a la vez en cada una. Los partidos de otras
          categorías en esa cancha ocupan todas sus mini canchas.
        </p>
        <div className="schedule-calendar__split-chips">
          {bases.map((base) => {
            const checked = calendarTickedSplitBases.has(base);
            const parts = calendarTickedSplitParts.get(base) ?? 2;
            return (
              <span key={base} className="schedule-calendar__split-item">
              <label
                className={`schedule-calendar__split-chip${checked ? " is-checked" : ""}${
                  calendarSplitSaving ? " is-disabled" : ""
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={calendarSplitSaving || calendarBusy}
                  onChange={() => void handleToggleSplitCourt(base)}
                  aria-label={`Dividir la Cancha ${base} en ${halfCourts(base, parts)
                    .join(", ")
                    .replace(/, ([^,]*)$/, " y $1")}`}
                  data-split-base={base}
                />
                <span>{base}</span>
              </label>
              {checked ? (
                <select
                  className="t123-select schedule-calendar__split-parts"
                  value={parts}
                  disabled={calendarSplitSaving || calendarBusy}
                  onChange={(event) => void handleSplitPartsChange(base, Number(event.target.value))}
                  aria-label={`En cuántas mini canchas se divide la Cancha ${base}`}
                  data-split-parts={base}
                >
                  {SPLIT_PARTS_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      en {option}
                    </option>
                  ))}
                </select>
              ) : null}
              </span>
            );
          })}
        </div>
        <p className="schedule-calendar__split-summary" data-split-summary>
          {splitCourtsSummary(ticked)}
        </p>
        {stillSplit.length > 0 ? (
          <p className="schedule-calendar__split-hint" data-split-still>
            {stillSplit.length === 1 ? `La ${stillSplit[0]} se sigue` : `Las ${stillSplit.join(", ")} se siguen`} viendo
            dividida{stillSplit.length === 1 ? "" : "s"} este día porque tiene{stillSplit.length === 1 ? "" : "n"} partidos o
            bloqueos en sus mini canchas.
          </p>
        ) : null}
      </fieldset>
    );
  }

  /** Guardando / guardado / no se pudo de las mini canchas. Va FUERA de los
   * campos de «Ajustes»: si el admin toca «Listo» con el guardado en camino
   * y falla, el error se ve con los ajustes cerrados (la casilla regresó y
   * el resumen ya no dice «cancha 9 dividida»). Cerrado, «Guardado» no se
   * repite. */
  function renderSplitCourtsStatus(collapsed = false) {
    if (!canEditSchedule || !activeIsMini || selectedDivisionId === null) return null;
    const status = calendarSplitStatus && calendarSplitStatus.divisionId === selectedDivisionId ? calendarSplitStatus : null;
    if (!status || (collapsed && status.kind === "saved")) return null;
    const divisionName = activeDivision?.name ?? "esta categoría";
    return (
      <p
        className={`schedule-calendar__split-status${status.kind === "saved" ? " is-saved" : status.kind === "error" ? " is-error" : ""}`}
        role="status"
        aria-live="polite"
        data-no-toast
        data-split-status={status.kind}
      >
        {status.kind === "saving"
          ? `Mini canchas: guardando para ${divisionName}…`
          : status.kind === "saved"
            ? `Mini canchas: guardado para ${divisionName}.`
            : `Mini canchas: no se pudo guardar. ${status.message ?? ""} Regresó a como estaba.`}
      </p>
    );
  }

  /** Vista sin categoría: «Programar pendientes» acomoda cada partido con el
   * bloque de SU categoría; si hay bloques distintos, lo avisa (los pasos
   * de la corrida son los de la pantalla y pueden quedar huecos). */
  function renderMixedBlocksNote() {
    if (calendarOrderedUnscheduledMatches.length === 0) return null;
    const blocks = [...new Set(calendarOrderedUnscheduledMatches.map((match) => calendarOwnDurationFor(match)))].sort(
      (a, b) => a - b,
    );
    if (blocks.length < 2) return null;
    return (
      <p className="schedule-calendar__settings-status is-inherited" data-calendar-mixed-blocks data-no-toast>
        Hay partidos de categorías con bloques distintos ({blocks.join(", ")} min): cada uno se acomoda con el
        bloque de su categoría (sin bloque guardado, {calendarMatchDurationMinutes} min). Para no dejar huecos,
        elige una categoría y prográmala.
      </p>
    );
  }

  /** Barra del día del calendario (Eduardo 2026-10-01 y 2026-10-02: «¿dónde
   * está el botón publicar horarios por día?»). Va arriba del calendario y
   * SIEMPRE a la vista: dice el día y la categoría, su estado (sin publicar,
   * publicado, listos pero el torneo aún no se publica, o sin horarios; con
   * este día al corriente, los otros días que tienen pendientes), trae el
   * botón «Publicar horarios» (día +
   * categoría, todas las sedes, sin importar los filtros) y, si todavía no
   * sirve, el porqué (torneo en borrador, candado del jugador, perfil). Abajo,
   * los días del torneo como chips y la Fecha. Describe el estado
   * (data-no-toast, nunca un mensaje flotante). */
  function renderPublishBar() {
    // «Todas las categorías»: lo sin publicar del día de todas, y un botón por
    // categoría (el servidor publica por categoría y día).
    const allCategories = calendarAllCategories;
    const count = allCategories
      ? dayPendingDivisions.reduce((total, entry) => total + entry.count, 0)
      : unpublishedHere.length;
    const divisionName = allCategories ? null : activeDivision?.name ?? null;
    // Otros días de la categoría con horarios sin publicar: con este día ya
    // publicado, la barra lo dice y lleva a ellos (no «Todo publicado»).
    const otherPendingDates = [...dayScopeUnpublished.byDate.entries()].filter(([date]) => date !== calendarDate);
    const idle = publishBarIdle({
      dayMatches: calendarDayMatchCount,
      otherPendingDays: otherPendingDates.length,
      blocked: !publishBlockedMessage ? null : tournament?.status === "draft" ? "draft" : "lock",
    });
    const state: PublishBarState = count > 0 ? "here" : idle.state;
    const pendingChange = calendarProposal !== null;
    const closedForMe = showPublishUi && tournamentClosed && !hasRole("superadmin") && !publishDeniedForRole;
    const extras: string[] = [];
    if (count > 0 && allCategories) {
      const byVenue = new Map<string, number>();
      for (const entry of dayPendingDivisions) {
        for (const match of entry.matches) {
          if ((match.venue_name ?? null) === calendarVenueName) continue;
          const name = match.venue_name ?? tournament?.venue_name ?? "la sede principal";
          byVenue.set(name, (byVenue.get(name) ?? 0) + 1);
        }
      }
      for (const [name, venueCount] of byVenue) extras.push(`Incluye ${venueCount} en ${name}.`);
    } else if (count > 0) {
      if (unpublishedHereHidden > 0) extras.push(`Incluye ${unpublishedHereHidden} que tus filtros ocultan.`);
      for (const venue of unpublishedHereOtherVenues) extras.push(`Incluye ${venue.count} en ${venue.name}.`);
    }
    let reason: ReactNode = null;
    if (showPublishUi) {
      if (publishDeniedForRole) {
        reason = (
          <p className="schedule-daybar__reason" data-publish-denied>
            Pide al organizador que publique los horarios.
          </p>
        );
      } else if (closedForMe) {
        reason = (
          <p className="schedule-daybar__reason" data-publish-closed>
            El torneo está cerrado: ya no se publican horarios.
          </p>
        );
      } else if (publishBlockedMessage) {
        reason = (
          <p className="schedule-daybar__reason">
            <span data-publish-blocked>{publishBlockedMessage}</span>
            {/* La liga solo con el perfil ya sabido: a un capturista o a
                finanzas no les sirve (no publican ni tocan el draw). En el
                paso 10 con el torneo en borrador, baja a «Publicar el
                torneo», al final de esta misma página. */}
            {myTournamentRole !== undefined ? (
              <>
                {" "}
                {setupMode && tournament?.status === "draft" ? (
                  <a
                    href="#publicar-torneo"
                    className="schedule-daybar__reason-link"
                    data-publish-tournament-link
                    onClick={(event) => {
                      event.preventDefault();
                      goToPublishTournamentCard();
                    }}
                  >
                    Publica el torneo al final de esta página
                  </a>
                ) : (
                  <Link to={drawStepHref} className="schedule-daybar__reason-link" data-publish-draw-link>
                    {tournament?.status === "draft" ? "Publica el torneo en el paso 10 (Calendario)" : "Revisa el paso 9 (Draw)"}
                  </Link>
                )}
              </>
            ) : null}
          </p>
        );
      }
    }
    const blocked = Boolean(reason);
    const cannotPublishNow = !canPublishSchedule || Boolean(publishBlockedMessage) || calendarBusy || pendingChange;
    const disabled = cannotPublishNow || count === 0;
    // Con varias categorías pendientes, un renglón y un botón por categoría.
    const perDivision = allCategories && showPublishUi && count > 0;
    const statusText = count > 0 ? unpublishedHeadline(count) : idle.text;
    const elsewhere =
      showPublishUi && count === 0 && otherPendingDates.length > 0 ? (
        <div className="schedule-daybar__elsewhere" data-publish-elsewhere>
          <span>Sin publicar en otros días:</span>
          {otherPendingDates.map(([date, list]) => (
            <button
              key={date}
              type="button"
              className="schedule-daybar__elsewhere-chip"
              onClick={() => selectCalendarDay(date)}
              title={`Ver el ${dayMonthEs(date) || date}: ${horariosText(list.length)} sin publicar`}
              data-publish-date={date}
            >
              {dayTitleEs(date)} · {list.length}
            </button>
          ))}
        </div>
      ) : null;
    // Aviso corto del torneo completo (antes arriba de la página): «Tienes N
    // horarios sin publicar en X días» y «Ir al calendario» al primer día.
    const topSummary =
      showPublishUi && unpublishedTotal > 0 ? (
        <p className="cal-pub__summary" data-no-toast role="status" data-unpublished-summary>
          <span>
            {unpublishedTopSummary({
              total: unpublishedTotal,
              days: unpublished.byDate.size,
              withoutDate: unpublished.withoutDate.length,
              divisionName: activeDivision?.name ?? null,
            })}
          </span>
          {unpublished.byDate.size > 0 ? (
            <button type="button" className="cal-pub__link" onClick={handleGoToPublishNotice}>
              Ir al calendario
            </button>
          ) : null}
        </p>
      ) : null;
    return (
      <section
        ref={publishNoticeRef}
        className={`schedule-daybar cal-pub is-${state}${blocked ? " is-blocked" : ""}`}
        aria-label="Día del calendario y «Publicar horarios»"
        data-no-toast
        data-publish-notice={state}
      >
        <div className="cal-pub__row">
          <div className="cal-pub__info">
            <p className="cal-pub__title">
              <strong className="schedule-daybar__title" data-publish-scope>
                {dayTitleEs(calendarDate)} · {divisionName ?? "Todas las categorías"}
              </strong>
              {showPublishUi ? (
                <span className={`cal-pub__status is-${state}`} role="status" aria-live="polite" data-publish-status>
                  {statusText}
                </span>
              ) : null}
            </p>
            {showPublishUi && count > 0 && !perDivision ? (
              <p className="schedule-daybar__note cal-pub__note">
                Puedes {count === 1 ? "moverlo o quitarlo" : "moverlos o quitarlos"} sin motivo y sin avisar a nadie.
                {extras.length > 0 ? ` ${extras.join(" ")}` : ""}
              </p>
            ) : null}
            {reason}
            {elsewhere}
            {showPublishUi && pendingChange && count > 0 ? (
              <p className="schedule-daybar__note cal-pub__note is-warn">Confirma o cancela el cambio pendiente antes de publicar.</p>
            ) : null}
            {topSummary}
          </div>
          {perDivision ? (
            <details className="cal-pub__divisions" open>
              <summary className="btn btn--ghost cal-pub__divisions-toggle">Por categoría ({count}) ▾</summary>
              <div className="cal-pub__divisions-panel">
                <p className="schedule-daybar__note">
                  Por categoría, o todas a la vez con «Publicar todas»: cada jugador recibe un aviso con su horario del día.
                </p>
                <ul className="schedule-daybar__divisions" aria-label="Sin publicar por categoría" data-publish-divisions>
                  {dayPendingDivisions.map((entry) => {
                    const division = divisions.find((item) => item.id === entry.divisionId) ?? null;
                    return (
                      <li
                        key={entry.divisionId}
                        className="schedule-daybar__division"
                        style={division ? (divisionAccentStyle(division) as CSSProperties) : undefined}
                        data-publish-division={entry.divisionId}
                      >
                        <span className="schedule-daybar__division-name">
                          <span className="schedule-daybar__division-dot" aria-hidden="true" />
                          {entry.name}
                        </span>
                        <span className="schedule-daybar__division-count">{horariosText(entry.count)} sin publicar</span>
                        <button
                          type="button"
                          className="btn btn--primary btn--compact schedule-daybar__division-publish"
                          onClick={() =>
                            void handlePublishSchedule({
                              divisionId: entry.divisionId,
                              divisionName: entry.name,
                              here: entry.matches,
                            })
                          }
                          disabled={cannotPublishNow}
                          aria-label={`Publicar horarios de ${entry.name} (${entry.count})`}
                          data-publish-division-button
                        >
                          Publicar ({entry.count})
                        </button>
                      </li>
                    );
                  })}
                </ul>
                {extras.length > 0 ? <p className="schedule-daybar__note">{extras.join(" ")}</p> : null}
              </div>
            </details>
          ) : null}
          {perDivision ? (
            <button
              type="button"
              className="btn btn--primary schedule-daybar__publish cal-pub__publish"
              onClick={() =>
                void handlePublishSchedule({
                  divisionId: null,
                  divisionName: "todas las categorías",
                  here: dayPendingDivisions.flatMap((entry) => entry.matches),
                })
              }
              disabled={cannotPublishNow}
              title={pendingChange ? "Confirma o cancela el cambio pendiente antes de publicar" : undefined}
              data-publish-all-button
            >
              Publicar todas ({count})
            </button>
          ) : null}
          {showPublishUi && !perDivision ? (
            <button
              type="button"
              className="btn btn--primary schedule-daybar__publish cal-pub__publish"
              onClick={() => void handlePublishSchedule()}
              disabled={disabled}
              title={
                pendingChange && count > 0
                  ? "Confirma o cancela el cambio pendiente antes de publicar"
                  : count === 0
                    ? state === "published" || state === "waiting"
                      ? "Todo lo de este día ya está publicado"
                      : "Este día no tiene horarios"
                    : undefined
              }
              data-publish-button
            >
              {count > 0 ? `Publicar horarios (${count})` : "Publicar horarios"}
            </button>
          ) : null}
        </div>
      </section>
    );
  }

  /** Chips de los días del torneo (barra blanca de herramientas): día, Hoy,
   * N partidos y N sin publicar. Tocar uno abre ese día. */
  function renderCalendarDayChips() {
    return (
      <div
        ref={calendarDayChipsRef}
        className="schedule-daybar__chips cal-toolbar__days"
        role="group"
        aria-label="Días del torneo"
        data-calendar-days
      >
        {calendarDayChipList.map((chip) => {
          const parts = dayChipParts(chip.date);
          const active = chip.date === calendarDate;
          const showUnpublished = showPublishUi && chip.unpublished > 0;
          return (
            <button
              key={chip.date}
              type="button"
              className={`schedule-daychip${active ? " is-active" : ""}${showUnpublished ? " has-unpublished" : ""}`}
              aria-pressed={active}
              onClick={() => selectCalendarDay(chip.date)}
              title={`${dayTitleEs(chip.date)}: ${chip.matches > 0 ? partidosText(chip.matches) : "sin partidos"}${
                showUnpublished ? `, ${chip.unpublished} sin publicar` : ""
              }`}
              data-day-chip={chip.date}
            >
              <span className="schedule-daychip__date">
                <span className="schedule-daychip__weekday">{parts.weekday}</span> <strong>{parts.day}</strong>
                {chip.isToday ? (
                  <span className="schedule-daychip__today" data-day-today>
                    Hoy
                  </span>
                ) : null}
              </span>
              <small className="schedule-daychip__count">
                {chip.matches > 0 ? partidosText(chip.matches) : "Sin partidos"}
                {showUnpublished ? (
                  <>
                    {" · "}
                    <span className="schedule-daychip__badge" data-day-unpublished>
                      {chip.unpublished} sin publicar
                    </span>
                  </>
                ) : null}
              </small>
            </button>
          );
        })}
      </div>
    );
  }

  /** «Ajustes de esta categoría» (Eduardo 2026-10-02: la sección se veía
   * muy apretada): una línea con el resumen y «Editar», que abre Desde,
   * Último inicio, Bloque min, #Canchas y las mini canchas. Abierto solo
   * cuando la categoría aún no tiene su bloque guardado. */
  function renderCalendarSettingsCard() {
    const saveMode = !canEditSchedule ? "readonly" : selectedDivisionId === null ? "view" : "division";
    const summary = calendarSettingsSummary({
      startTime: calendarStartTime,
      lastEndTime: addMinutesToTime(calendarEndTime, Number(calendarInterval) || 0),
      blockMinutes: calendarInterval,
      courtCount: calendarBaseCourts.length,
      splitBases: activeIsMini ? sortedSplitBases(calendarTickedSplitBases) : [],
      miniCourt: activeIsMini,
      saveMode,
    });
    const title = selectedDivisionId === null ? "Ajustes del día" : "Ajustes del día / categoría";
    const fieldsId = "schedule-calendar-settings-fields";
    const orderLabel = { manual: "Manual", random: "Al azar", group: "Por grupo" }[calendarOrderMode];
    return (
      <section
        ref={calendarSettingsRef}
        className={`schedule-settings cal-settings${calendarSettingsOpen ? " is-open" : ""}`}
        aria-label={title}
        data-calendar-settings={calendarSettingsOpen ? "open" : "closed"}
      >
        <div className="schedule-settings__head cal-settings__head">
          <div className="schedule-settings__text cal-settings__text">
            <h2 className="schedule-settings__title cal-eyebrow">{title}</h2>
            <p className="schedule-settings__summary cal-settings__summary">
              <span data-calendar-settings-summary>{summary}</span>
              {canEditSchedule ? <span className="cal-settings__order"> · automático: {orderLabel}</span> : null}
            </p>
          </div>
          <button
            type="button"
            className="btn btn--ghost schedule-settings__toggle cal-settings__toggle"
            aria-expanded={calendarSettingsOpen}
            aria-controls={fieldsId}
            // La lista de partidos también tiene botones «Editar».
            aria-label={calendarSettingsOpen ? "Listo, cerrar ajustes" : "Editar ajustes"}
            onClick={() => setCalendarSettingsOpen((open) => !open)}
            data-calendar-settings-toggle
          >
            {calendarSettingsOpen ? "Cerrar ▴" : "Editar ▾"}
          </button>
        </div>
        <div id={fieldsId} className="schedule-settings__fields cal-settings__body" hidden={!calendarSettingsOpen}>
          <div className="cal-settings__group" data-settings-group="horario">
            <h3 className="cal-eyebrow">Horario</h3>
            <div className="cal-settings__fields">
              {/* Fecha libre (fuera de los chips de días): con el día de la
                  semana y «No es hoy» (Eduardo 2026-10-01). */}
              <CalendarDateField value={calendarDate} onChange={selectCalendarDay} today={todayIso()} />
              {/* Selects cada 15 min en 12h (Eduardo 2026-08-28: el picker
                  nativo de columnas 12/00/p.m. no se entendía) y semántica
                  explícita: "Último inicio" es INCLUSIVE — un partido puede
                  ARRANCAR a esa hora y termina bloque después. */}
              <label className="schedule-toolbar__field">
                <span className="schedule-toolbar__label">Desde</span>
                <select className="t123-select"
                  value={calendarStartTime}
                  onChange={(e) => handleCalendarFieldChange({ startTime: e.target.value })}
                  onBlur={() => void flushCalendarSettingsSave()}
                  aria-label="Hora del primer partido"
                >
                  {calendarTimeOptionsFor(calendarStartTime).map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <label className="schedule-toolbar__field">
                <span className="schedule-toolbar__label">Último inicio</span>
                <select className="t123-select"
                  value={calendarEndTime}
                  onChange={(e) => handleCalendarFieldChange({ endTime: e.target.value })}
                  onBlur={() => void flushCalendarSettingsSave()}
                  aria-label="Hora del último partido que puede iniciar"
                  title="Los partidos pueden EMPEZAR hasta esta hora; el último termina un bloque después."
                >
                  {calendarTimeOptionsFor(calendarEndTime).map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <label className="schedule-toolbar__field">
                <span className="schedule-toolbar__label">Bloque min</span>
                <input
                  type="number"
                  min={15}
                  max={240}
                  step={15}
                  inputMode="numeric"
                  value={calendarInterval}
                  // Se ve de inmediato pero se guarda al salir del campo o con
                  // Enter: a cada tecla se guardaba un "12" de camino a "120".
                  onChange={(e) => handleCalendarFieldChange({ blockMinutes: e.target.value })}
                  onBlur={() => void flushCalendarSettingsSave()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void flushCalendarSettingsSave();
                  }}
                />
              </label>
              <label className="schedule-toolbar__field">
                <span className="schedule-toolbar__label">#Canchas</span>
                <input
                  type="number"
                  min={1}
                  max={50}
                  step={1}
                  inputMode="numeric"
                  placeholder="3"
                  value={activeAdditionalVenue?.court_count ? String(activeAdditionalVenue.court_count) : calendarCourtCount}
                  onChange={(e) => handleCalendarFieldChange({ courtCount: e.target.value })}
                  onBlur={() => void flushCalendarSettingsSave()}
                  disabled={Boolean(activeAdditionalVenue?.court_count)}
                  title={activeAdditionalVenue?.court_count ? "Las canchas de esta sede se configuran en Datos" : undefined}
                />
              </label>
            </div>
            <p className="muted schedule-calendar__window-hint">
              Inicios cada 15 min de {friendlyTime12h(calendarStartTime)} a {friendlyTime12h(calendarEndTime)}; el último
              partido termina ≈ {friendlyTime12h(addMinutesToTime(calendarEndTime, Number(calendarInterval) || 0))}
            </p>
          </div>
          {canEditSchedule && selectedDivisionId !== null ? (
            <div className="cal-settings__group" data-settings-group="canchas">
              <h3 className="cal-eyebrow">Canchas</h3>
              {renderCourtTypeChoice()}
              {renderSplitCourtsPanel()}
            </div>
          ) : null}
          {multiVenueCalendar ? (
            <div className="cal-settings__group" data-settings-group="sedes">
              <h3 className="cal-eyebrow">Sedes</h3>
              {renderVenueChips()}
            </div>
          ) : null}
          {canEditSchedule ? (
            <div className="cal-settings__group cal-settings__group--order" data-settings-group="ordenar">
              {renderOrderCard()}
            </div>
          ) : null}
        </div>
        {renderCalendarSettingsStatus(!calendarSettingsOpen)}
        {renderCourtTypeStatus(!calendarSettingsOpen)}
        {renderSplitCourtsStatus(!calendarSettingsOpen)}
      </section>
    );
  }

  /** Chips de sede del calendario (Ajustes → Sedes). */
  function renderVenueChips() {
    return (
      <div className="schedule-calendar__venues" role="tablist" aria-label="Sede del calendario">
        {tournamentVenues.map((venue) => {
          const value = venue.is_primary ? null : venue.name;
          const active = calendarVenueName === value;
          return (
            <button
              key={venue.name}
              type="button"
              className={`schedule-calendar__venue-chip${active ? " is-active" : ""}`}
              onClick={() => {
                setCalendarVenueName(value);
                setSelectedMatchId(null);
              }}
            >
              {venue.name}
              <small>{venue.is_primary ? "principal" : `${venue.court_count ?? "?"} cancha(s)`}</small>
            </button>
          );
        })}
      </div>
    );
  }

  /** Abre «Ajustes del día» y lo lleva a la vista (desde «Más» o el riel). */
  function openCalendarSettings(focusOrder = false) {
    setCalendarSettingsOpen(true);
    window.requestAnimationFrame(() => {
      if (focusOrder) orderCardTitleRef.current?.focus();
      calendarSettingsRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    });
  }

  function handleCalendarOrderModeChange(mode: CalendarOrderMode) {
    setCalendarOrderMode(mode);
    if (mode === "random") setCalendarRandomSeed((seed) => seed + 1);
  }

  /** «Ordenar automático» (Eduardo 2026-10-02: «Manual - Random - Por grupo»
   * se veía comprimido y en el celular no se entendía): una tarjeta con las
   * tres opciones explicadas en una línea, el botón «Programar N partidos
   * pendientes» y «Desprogramar el día…», cuyo paso de confirmación trae los
   * motivos del aviso (antes siempre estaban en pantalla). */
  function renderOrderCard() {
    if (!canEditSchedule) return null;
    const pending = calendarOrderedUnscheduledMatches.length;
    const scheduledCount = calendarScheduledMatches.length;
    const publishedCount = calendarScheduledMatches.filter((match) => isSchedulePublished(match)).length;
    const options: Array<{ value: CalendarOrderMode; label: string; hint: string }> = [
      { value: "manual", label: "Manual", hint: "En el orden de la lista" },
      { value: "random", label: "Al azar", hint: "Mezcla la lista antes de acomodar" },
      { value: "group", label: "Por grupo", hint: "Junta los del mismo grupo a la misma hora" },
    ];
    const confirmId = "schedule-unschedule-day-confirm";
    // «Todas las categorías» (revisión 2026-10-02): el grid muestra todas,
    // pero ordenar y desprogramar siguen siendo de la categoría abierta: se
    // dice cuál, para que nadie crea que limpia el día completo.
    const ofDivision = calendarAllCategories && activeDivision ? ` de ${activeDivision.name}` : "";
    // Lo mismo con la jornada elegida (ver `calendarGridJornadaLabel`).
    const gridJornada = calendarGridJornadaLabel();
    const ofJornada = gridJornada ? ` (${gridJornada})` : "";
    return (
      <section className="schedule-order" aria-labelledby="schedule-order-title" data-order-card>
        <h3 id="schedule-order-title" ref={orderCardTitleRef} tabIndex={-1} className="schedule-order__title cal-eyebrow">
          Ordenar automático
        </h3>
        <p className="schedule-order__hint">
          Acomoda los pendientes{ofDivision} en las horas libres de este día.
          {gridJornada ? ` Filtro de jornada: ${gridJornada}.` : ""}
        </p>
        {gridJornada ? (
          <button
            type="button"
            className="schedule-order__link"
            onClick={() => handleJornadaFilterClick("all")}
            data-order-all-jornadas
          >
            Ver todas las jornadas
          </button>
        ) : null}
        <fieldset className="schedule-order__options">
          <legend className="sr-only">Orden de los partidos pendientes</legend>
          {options.map((option) => {
            const active = calendarOrderMode === option.value;
            return (
              <label key={option.value} className={`schedule-order__option${active ? " is-active" : ""}`}>
                <input
                  type="radio"
                  name="schedule-calendar-order"
                  value={option.value}
                  checked={active}
                  onChange={() => handleCalendarOrderModeChange(option.value)}
                  data-order-mode={option.value}
                />
                <span className="schedule-order__option-text">
                  <strong>{option.label}</strong>
                  <small>{option.hint}</small>
                </span>
              </label>
            );
          })}
        </fieldset>
        {calendarOrderMode === "random" && pending > 1 ? (
          <button
            type="button"
            className="schedule-order__link"
            onClick={() => setCalendarRandomSeed((seed) => seed + 1)}
          >
            Mezclar otra vez
          </button>
        ) : null}
        {selectedDivisionId === null ? (
          <>
            {renderMixedBlocksNote()}
            {renderMiniCourtsAllNote()}
          </>
        ) : null}
        <button
          type="button"
          className="btn btn--primary schedule-order__run"
          onClick={() => void handleAutoCalendarProgram()}
          disabled={calendarBusy || pending === 0}
          title="Acomoda un partido por horario y cancha libre, en el orden elegido"
          data-order-run
        >
          {programPendingLabel(pending)}
          {pending > 0 ? ofDivision : ""}
          {ofJornada}
        </button>
        {calendarWaitingPrevious.length > 0 ? (
          <div className="schedule-order__waiting" role="note" data-no-toast data-order-waiting>
            <p className="schedule-order__waiting-title">
              <strong>Quedaron sin programar (esperan a sus partidos previos):</strong>
            </p>
            <ul>
              {calendarWaitingPrevious.map((reason, index) => (
                <li key={`${index}-${reason}`}>{reason}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {bulkUnscheduleConfirmOpen && scheduledCount > 0 ? (
          <div id={confirmId} className="schedule-order__confirm" role="group" aria-label="Confirmar: desprogramar el día" data-unschedule-confirm>
            <p className="schedule-order__confirm-title">
              <strong>
                ¿Desprogramar {partidosText(scheduledCount)}
                {ofDivision}{ofJornada} del {dayMonthEs(calendarDate) || calendarDate}?
              </strong>
            </p>
            <p>Se quita fecha, hora y cancha; los resultados no se borran.</p>
            {publishedCount > 0 ? (
              <>
                <p data-unschedule-published>
                  {publishedCount === 1
                    ? "1 ya estaba publicado: sus jugadores reciben un aviso de que quedó sin horario."
                    : `${publishedCount} ya estaban publicados: sus jugadores reciben un aviso de que quedaron sin horario.`}
                </p>
                <div
                  className="schedule-order__reasons"
                  role="group"
                  aria-label="Motivo del aviso al desprogramar el día"
                >
                  <small>Motivo del aviso (opcional):</small>
                  {SCHEDULE_CHANGE_REASONS.map((reason) => (
                    <button
                      type="button"
                      key={reason}
                      className={`result-sheet__chip${bulkUnscheduleReason === reason ? " is-active" : ""}`}
                      aria-pressed={bulkUnscheduleReason === reason}
                      onClick={() => setBulkUnscheduleReason(bulkUnscheduleReason === reason ? "" : reason)}
                    >
                      {reason}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <p data-unschedule-published>Ninguno estaba publicado: no se avisa a nadie.</p>
            )}
            <div className="schedule-order__confirm-actions">
              <button
                type="button"
                className="btn btn--compact schedule-order__confirm-go"
                onClick={() => void handleBulkUnscheduleCalendarDate()}
                disabled={calendarBusy}
              >
                Sí, desprogramar el día
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--compact"
                onClick={() => {
                  // El foco vuelve al título de «Ordenar automático» antes de
                  // quitar este paso («Desprogramar el día…» vive en «Más ▾»,
                  // cerrado; si no, se iría al inicio de la página).
                  orderCardTitleRef.current?.focus();
                  setBulkUnscheduleConfirmOpen(false);
                  setBulkUnscheduleReason("");
                }}
                data-unschedule-cancel
              >
                No, regresar
              </button>
            </div>
          </div>
        ) : null}
      </section>
    );
  }

  /** «Más ▾ → Desprogramar el día…»: abre «Ajustes del día» con el paso de
   * confirmación (motivos del aviso) de «Ordenar automático». */
  function renderUnscheduleDayTrigger() {
    if (!canEditSchedule) return null;
    const scheduledCount = calendarScheduledMatches.length;
    const confirmId = "schedule-unschedule-day-confirm";
    const ofDivision = calendarAllCategories && activeDivision ? ` de ${activeDivision.name}` : "";
    const gridJornada = calendarGridJornadaLabel();
    const ofJornada = gridJornada ? ` (${gridJornada})` : "";
    return (
        <button
          ref={unscheduleDayButtonRef}
          type="button"
          className="cal-more__item is-danger"
          onClick={() => {
            const opening = !bulkUnscheduleConfirmOpen;
            setBulkUnscheduleConfirmOpen(opening);
            setBulkUnscheduleReason("");
            if (opening) {
              closeCalendarMore();
              openCalendarSettings(true);
            }
          }}
          disabled={calendarBusy || scheduledCount === 0}
          aria-expanded={bulkUnscheduleConfirmOpen && scheduledCount > 0}
          aria-controls={confirmId}
          title={`Quita fecha, hora y cancha de todos los partidos${ofDivision} programados en el día visible${
            gridJornada ? ` (solo ${gridJornada})` : ""
          }`}
          data-unschedule-day
        >
          Desprogramar el día{ofDivision}{ofJornada}…
        </button>
    );
  }

  /** «Esta categoría» / «Todas las categorías»: se recuerda en la liga
   * (?vista=todas) y sigue al cambiar de categoría arriba. */
  function handleCalendarScopeChange(scope: "division" | "all") {
    if (scope === calendarScope) return;
    setCalendarScope(scope);
    setCalendarForeignPick(null);
    const next = new URLSearchParams(searchParams);
    if (scope === "all") next.set("vista", "todas");
    else next.delete("vista");
    setSearchParams(next, { replace: true });
  }

  /** «Abrir {categoría}» desde un partido de otra categoría: abre esa
   * categoría en el mismo día y deja el partido elegido para moverlo. */
  function handleOpenMatchDivision(match: TournamentMatch) {
    if (match.division_id == null) return;
    pendingSelectMatchIdRef.current = canEditSchedule && isActiveCalendarStatus(match.status) ? match.id : null;
    setCalendarForeignPick(null);
    setSelectedMatchId(null);
    const next = new URLSearchParams(searchParams);
    next.set("division", String(match.division_id));
    setSearchParams(next);
  }

  /** Barra blanca de herramientas (pantalla única): días del torneo, «Esta
   * categoría / Todas», «Cuadrícula / Lista», «Rol de juego del día» y
   * «Más ▾» con lo que se usa poco. Sin calendario de canchas solo «Más». */
  function renderCalendarToolbar() {
    const scopeName = calendarShowsAllDivisions
      ? hasSeveralDivisions
        ? "todas las categorías"
        : divisions[0]?.name ?? "el torneo"
      : activeDivision?.name ?? "esta categoría";
    return (
      <section className="card cal-toolbar" aria-label="Herramientas del calendario" data-calendar-scopebar>
        {!courtCalendarOff ? renderCalendarDayChips() : (
          <p className="cal-toolbar__off" data-no-toast>
            Sin calendario de canchas: cada jornada con sus fechas, sus partidos y quién descansa.
          </p>
        )}
        <div className="cal-toolbar__actions">
          {!courtCalendarOff && calendarScopeAvailable ? (
            <div className="segmented cal-seg" role="group" aria-label="Categorías del calendario">
              <button
                type="button"
                className={`segmented__btn${calendarAllCategories ? "" : " is-active"}`}
                aria-pressed={!calendarAllCategories}
                onClick={() => handleCalendarScopeChange("division")}
                title={activeDivision ? `Solo ${activeDivision.name}: las demás categorías se ven como «Ocupado».` : undefined}
                data-calendar-scope="division"
              >
                Esta categoría
              </button>
              <button
                type="button"
                className={`segmented__btn${calendarAllCategories ? " is-active" : ""}`}
                aria-pressed={calendarAllCategories}
                onClick={() => handleCalendarScopeChange("all")}
                title="El día completo: los partidos de todas las categorías, cada una con su color."
                data-calendar-scope="all"
              >
                Todas
              </button>
            </div>
          ) : null}
          {!courtCalendarOff ? (
            <div className="segmented cal-seg" role="group" aria-label="Cómo ver el día">
              <button
                type="button"
                className={`segmented__btn${calendarDayViewShown === "grid" ? " is-active" : ""}`}
                aria-pressed={calendarDayViewShown === "grid"}
                onClick={() => chooseCalendarDayView("grid")}
                data-calendar-view="grid"
              >
                Cuadrícula
              </button>
              <button
                type="button"
                className={`segmented__btn${calendarDayViewShown === "list" ? " is-active" : ""}`}
                aria-pressed={calendarDayViewShown === "list"}
                onClick={() => chooseCalendarDayView("list")}
                title="Los partidos en lista: del día (hora y cancha) o de la jornada (con Editar)."
                data-calendar-view="list"
              >
                Lista
              </button>
            </div>
          ) : null}
          {!courtCalendarOff ? (
            <button
              type="button"
              className="btn btn--ghost cal-toolbar__btn schedule-scopebar__print"
              onClick={() => void handlePrintDayRoll()}
              disabled={dayRoll.length === 0}
              title={
                dayRoll.length === 0
                  ? "Este día no tiene partidos"
                  : `Orden de juego del ${dayMonthEs(calendarDate) || calendarDate} por hora y cancha (${scopeName}).`
              }
              data-print-day-roll
            >
              🖨 Rol de juego del día
            </button>
          ) : null}
          {!courtCalendarOff ? (
            <button
              type="button"
              className="btn btn--ghost cal-toolbar__btn schedule-scopebar__print"
              onClick={() => openControlSheet()}
              disabled={dayRoll.length === 0}
              title={
                dayRoll.length === 0
                  ? "Este día no tiene partidos"
                  : "Hoja para el árbitro general: partidos del día, árbitro asignado, casillas de sets y firma."
              }
              data-print-control-sheet
            >
              🖨 Hoja de control
            </button>
          ) : null}
          {controlSheetOpen ? renderControlSheetDialog() : null}
          {renderCalendarMore()}
        </div>
      </section>
    );
  }

  function closeCalendarMore() {
    const node = calendarMoreRef.current;
    if (node) node.open = false;
  }

  /** «Más ▾» de la barra de herramientas: impresiones, ajustes, historial,
   * la sección Canchas, la guía, «¿Usa calendario de canchas?» y
   * «Desprogramar el día…». */
  function renderCalendarMore() {
    const jornadaPrint = selectedJornadaPrintLabel();
    return (
      <details ref={calendarMoreRef} className="cal-more" data-calendar-more>
        <summary className="btn btn--ghost cal-toolbar__btn cal-more__toggle">Más ▾</summary>
        <div className="cal-more__panel" role="group" aria-label="Más opciones del calendario">
          <button
            type="button"
            className="cal-more__item"
            onClick={() => {
              closeCalendarMore();
              void handlePrintCalendarPdf("all");
            }}
          >
            🖨 Calendario PDF
          </button>
          {jornadaPrint !== null ? (
            <button
              type="button"
              className="cal-more__item"
              onClick={() => {
                closeCalendarMore();
                void handlePrintCalendarPdf("selected");
              }}
            >
              🖨 Calendario · {jornadaPrint}
            </button>
          ) : null}
          {!courtCalendarOff ? (
            <button
              type="button"
              className="cal-more__item"
              onClick={() => {
                closeCalendarMore();
                openCalendarSettings();
              }}
              data-more-settings
            >
              Ajustes del día / categoría
            </button>
          ) : null}
          {!courtCalendarOff && multiVenueCalendar ? (
            <button
              type="button"
              className="cal-more__item"
              onClick={() => {
                closeCalendarMore();
                openCalendarSettings();
              }}
            >
              Sedes adicionales <small>{tournamentVenues.length} sedes</small>
            </button>
          ) : null}
          {!courtCalendarOff && canEditSchedule && selectedDivisionId !== null ? (
            <button
              type="button"
              className="cal-more__item"
              onClick={() => {
                closeCalendarMore();
                openCalendarSettings();
              }}
            >
              Tipo de cancha y mini canchas
            </button>
          ) : null}
          <button
            type="button"
            className="cal-more__item"
            onClick={() => {
              closeCalendarMore();
              const node = calendarHistoryRef.current;
              if (node) {
                node.open = true;
                window.requestAnimationFrame(() => node.scrollIntoView?.({ behavior: "smooth", block: "start" }));
              }
            }}
          >
            Historial de movimientos <small>{history.length}</small>
          </button>
          {!courtCalendarOff ? (
            <Link className="cal-more__item" to="/admin/calendar">
              Ir a la sección Canchas
            </Link>
          ) : null}
          <span className="cal-more__item cal-more__guide">
            Guía de esta pantalla <GuideHelpButton />
          </span>
          {renderCourtCalendarChoice()}
          {!courtCalendarOff ? renderUnscheduleDayTrigger() : null}
        </div>
      </details>
    );
  }

  /** Partido de otra categoría tocado en «Todas las categorías»: de cuál es
   * y «Abrir {categoría}» (ahí se mueve). */
  function renderForeignPick() {
    const match = calendarForeignPick;
    if (!match || !calendarAllCategories) return null;
    const division = divisionForMatch(match);
    const name = division?.name ?? (match.division_name?.trim() || "otra categoría");
    const where = [
      match.scheduled_time ? formatTimeLabel(match.scheduled_time) : "",
      match.court?.trim() ? courtDisplayName(match.court) : "",
    ]
      .filter(Boolean)
      .join(" · ");
    // Flota sobre el borde de arriba del grid (un ancla de alto 0): tocar un
    // partido de otra categoría ya no empuja el grid hacia abajo bajo el dedo
    // (revisión 2026-10-02; la barra de arriba es fija por lo mismo).
    return (
      <div className="schedule-foreign-pick-anchor">
      <div
        ref={foreignPickRef}
        className="schedule-foreign-pick"
        style={division ? (divisionAccentStyle(division) as CSSProperties) : undefined}
        role="status"
        aria-live="polite"
        data-no-toast
        data-foreign-pick={match.id}
      >
        <p>
          <strong>{matchShortLabel(match)}</strong> es de <strong>{name}</strong>
          {where ? ` (${where})` : ""}
          {isScheduleUnpublished(match) ? ", sin publicar" : ""}. Abre su categoría para moverlo.
        </p>
        <div className="schedule-foreign-pick__actions">
          <button
            ref={foreignOpenRef}
            type="button"
            className="btn btn--compact"
            onClick={() => handleOpenMatchDivision(match)}
            data-foreign-open
          >
            Abrir {name}
          </button>
          <button type="button" className="btn btn--ghost btn--compact" onClick={() => setCalendarForeignPick(null)}>
            Cerrar
          </button>
        </div>
      </div>
      </div>
    );
  }

  /** «Lista del día» (para el celular: 15 canchas no caben a 375px): hora →
   * cancha → categoría → partido, con su marca «Sin publicar». Solo se lee;
   * para mover, la cuadrícula. */
  function renderDayList() {
    const slots = dayRollSlots(dayRoll);
    const showVenue = tournamentVenues.length > 1 || dayRoll.some((row) => row.venue !== null);
    const showDivision = calendarShowsAllDivisions && hasSeveralDivisions;
    const dayText = dayMonthEs(calendarDate) || calendarDate;
    return (
      <section className="schedule-daylist" aria-label={`Partidos del ${dayText} por hora y cancha`} data-day-list>
        {slots.length === 0 ? (
          <p className="muted schedule-daylist__empty">
            {calendarShowsAllDivisions ? "No hay partidos este día." : "No hay partidos de esta categoría este día."}
          </p>
        ) : (
          slots.map((slot) => (
            <section key={slot.time || "sin-hora"} className="schedule-daylist__slot" data-day-list-time={slot.time || "sin-hora"}>
              <h4 className="schedule-daylist__time">{slot.time ? friendlyTime12h(slot.time) : "Hora por definir"}</h4>
              <ul className="schedule-daylist__items">
                {slot.rows.map((row) => {
                  const division = row.divisionId != null ? divisions.find((item) => item.id === row.divisionId) ?? null : null;
                  const meta = [dayRollRoundLabel(row.match), dayRollNote(row.match)].filter(Boolean).join(" · ");
                  return (
                    <li
                      key={row.match.id}
                      className="schedule-daylist__item"
                      style={division ? (divisionAccentStyle(division) as CSSProperties) : undefined}
                      data-day-list-match={row.match.id}
                    >
                      <div className="schedule-daylist__where">
                        <span className="schedule-daylist__court">
                          {row.court ? courtDisplayName(row.court) : "Cancha por definir"}
                          {showVenue && row.venue ? ` · ${row.venue}` : ""}
                        </span>
                        {showDivision ? (
                          <span className="schedule-daylist__division" data-day-list-division>
                            {row.divisionName}
                          </span>
                        ) : null}
                        {showPublishUi && isScheduleUnpublished(row.match) ? (
                          <span className="schedule-publish-tag is-unpublished" data-day-list-unpublished>
                            Sin publicar
                          </span>
                        ) : null}
                      </div>
                      <strong className="schedule-daylist__match">
                        {tieSeriesLabel(row.match) ? `${tieSeriesLabel(row.match)} · ` : ""}
                        {matchShortLabel(row.match)}
                      </strong>
                      {meta ? <small className="schedule-daylist__meta">{meta}</small> : null}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))
        )}
        <p className="muted schedule-daylist__hint">Para mover un partido, usa la cuadrícula.</p>
      </section>
    );
  }

  function renderCalendarView() {
    const hasInvalidSlots = !calendarGridRange || calendarGridColumns.length === 0;
    // Partidos de la fecha que el grid no puede pintar (sin hora/cancha o en
    // una cancha fuera de las columnas): se avisan, nunca desaparecen callados.
    // Una mini cancha (9-A) sí se ve en la columna 9 aunque no esté dividida.
    const offGrid = (m: TournamentMatch) =>
      parseTimeMinutes(m.scheduled_time) === null || !courtVisibleIn(m.court, calendarGridColumns);
    // «Todas las categorías»: también los de las demás categorías (revisión
    // 2026-10-02: el grid y la lista del día no deben decir cosas distintas).
    const offGridCount =
      calendarScheduledMatches.filter(offGrid).length +
      (calendarAllCategories
        ? tournamentWideMatches.filter(
            (m) =>
              m.division_id !== selectedDivisionId &&
              m.scheduled_date === calendarDate &&
              Boolean(m.scheduled_time || m.court) &&
              isProgrammedStatus(m) &&
              (m.venue_name ?? null) === calendarVenueName &&
              offGrid(m),
          ).length
        : 0);
    const firstOwnStart = calendarMatchItems
      .filter(
        (item) => (item.kind === "own" || item.kind === "own-hidden") && courtVisibleIn(item.court, calendarGridColumns),
      )
      .reduce<number | null>((min, item) => (min === null ? item.startMin : Math.min(min, item.startMin)), null);
    // En el celular, una categoría de Mini cancha abre en su primera mini cancha.
    const scrollToCourtKey = activeIsMini ? calendarGridColumns.find((court) => isHalfCourt(court)) ?? null : null;
    const scrollToMin = calendarWindow
      ? firstOwnStart === null ? calendarWindow.startMin : Math.min(calendarWindow.startMin, firstOwnStart)
      : null;
    // Publicar horarios: el elegido ya lo ven los jugadores (moverlo avisa)
    // o sigue sin publicar (se mueve o se quita callado).
    const selectedIsDraft = Boolean(selectedMatch && isScheduleUnpublished(selectedMatch));
    const selectedIsPublished = Boolean(
      selectedMatch && isActiveCalendarStatus(selectedMatch.status) && isSchedulePublished(selectedMatch),
    );
    const proposalStart =
      calendarMode === "programar" && selectedMatch && calendarProposal
        ? parseTimeMinutes(calendarProposal.time)
        : null;
    const calendarProposalView =
      calendarProposal && proposalStart !== null
        ? {
            court: calendarProposal.court,
            courtLabel: courtDisplayName(calendarProposal.court),
            startMin: proposalStart,
            endMin: proposalStart + calendarPlacingDuration,
          }
        : null;
    const roundOrderWarning = activeRoundOrderWarning();
    // Los conteos y la lista por programar son de la categoría abierta y de
    // la jornada elegida: el resumen y la lista dicen cuáles.
    const gridJornada = calendarGridJornadaLabel();
    const calendarSummaryScope = [calendarAllCategories && activeDivision ? activeDivision.name : null, gridJornada]
      .filter(Boolean)
      .join(" · ");
    const listShown = calendarDayViewShown === "list";
    // «Por cancha» se programa como la cuadrícula (con el panel del partido);
    // «Del día» y «De la jornada» son listas de lectura.
    const plainList = listShown && calendarListKindShown !== "court";
    const drawerState = calendarMode === "bloquear" ? "block" : selectedMatch || selectedBlock || selectedPlayed ? "match" : "idle";

    // Barra de la propuesta (Confirmar / Cancelar / ±15 min): la misma en la
    // cuadrícula y en «Lista → Por cancha», que también propone horarios
    // (revisión Torni #587: ahí la propuesta quedaba escondida y bloqueaba
    // «Publicar»).
    const proposalBar =
      hasInvalidSlots || !calendarGridRange ? null : (
      <CourtTimeGridBar
        ref={calendarConfirmRef}
        hint={
          calendarMode === "bloquear"
            ? `Bloquear: toca una hora libre para bloquear ${calendarMatchDurationMinutes} min desde ahí, o un bloqueo para liberarlo.`
            : listShown
              ? "Elige un partido (de «Por programar» o tocando su tarjeta) y luego toca «Programar aquí» en un hueco libre."
              : "Elige un partido (de «Por programar» o tocando su tarjeta) y luego toca la hora de inicio en una cancha libre."
        }
        selection={
          calendarMode === "programar" && selectedMatch
            ? {
                verb: selectedMatch.scheduled_date ? "Mover" : "Programar",
                label: matchShortLabel(selectedMatch),
                durationMinutes: calendarPlacingDuration,
              }
            : null
        }
        proposal={calendarProposalView}
        canEarlier={calendarProposalCanShift(-15)}
        canLater={calendarProposalCanShift(15)}
        onNudge={handleCalendarNudge}
        onConfirm={handleCalendarConfirm}
        onCancelProposal={() => setCalendarProposal(null)}
        onClearSelection={() => setSelectedMatchId(null)}
        notice={calendarNotice}
        busy={calendarBusy}
        date={calendarDate}
        today={todayIso()}
        consequence={
          showPublishUi && selectedMatch
            ? scheduleBarConsequence(selectedIsPublished, scheduleChangeReason, hasKnownPlayer(selectedMatch))
            : null
        }
        warning={
          roundOrderWarning
            ? `${roundOrderWarning.message} ${
                roundOrderWarning.afterFeeders
                  ? "Si ya terminaron, toca «Confirmar de todos modos»."
                  : "Si así lo quieres, toca «Confirmar de todos modos»."
              }`
            : null
        }
        confirmLabel={roundOrderWarning ? "Confirmar de todos modos" : "Confirmar"}
      />
      );

    const grid =
      hasInvalidSlots || !calendarGridRange ? (
        <p className="muted cal-grid__empty">Define una hora inicial/final válida y al menos una cancha en «Ajustes del día».</p>
      ) : (
        <>
          {proposalBar}
          {renderForeignPick()}
          <CourtTimeGrid
            courts={calendarVisibleColumns.map((court) => {
              const half = halfOf(court);
              return half
                ? { key: court, label: courtDisplayName(court), group: courtBase(court), sub: half }
                : { key: court, label: courtDisplayName(court) };
            })}
            range={calendarGridRange}
            items={calendarDrawnItems}
            blockMinutes={calendarMatchDurationMinutes}
            cellState={calendarCellState}
            onCellClick={handleCalendarCellClick}
            onDisabledCellClick={(_court, _time, cell) => setCalendarNotice(cell.title)}
            renderItem={renderCalendarGridItem}
            passThroughKey={
              calendarMode === "programar" && selectedMatchId !== null ? `m-${selectedMatchId}` : null
            }
            preview={
              calendarProposalView
                ? {
                    court: calendarProposalView.court,
                    startMin: calendarProposalView.startMin,
                    endMin: calendarProposalView.endMin,
                  }
                : null
            }
            busy={calendarBusy}
            placing={calendarMode === "programar" && Boolean(selectedMatch)}
            scrollKey={`${calendarDate}|${calendarVenueName ?? ""}|${calendarStartTime}`}
            scrollToMin={scrollToMin}
            scrollToCourtKey={scrollToCourtKey}
            ariaLabel="Calendario del torneo por cancha y hora"
            date={calendarDate}
          />
          {renderCalendarLegend()}
          {calendarAllCategories ? (
            <p className="muted schedule-timegrid__note">
              Los partidos de las demás categorías se ven con su color: para moverlos, abre su categoría.
            </p>
          ) : null}
          {offGridCount > 0 ? (
            <p className="muted schedule-timegrid__note" data-calendar-offgrid={offGridCount}>
              {offGridCount} partido(s) programado(s) de esta fecha no caben en el grid: no tienen hora o cancha, o están en una cancha
              fuera de las {calendarGridColumns.length} columnas. Revísalos en la vista «Lista».
            </p>
          ) : null}
          {calendarExtraCourts.length > 0 ? (
            <p className="muted schedule-timegrid__note" data-calendar-extra-courts>
              {calendarExtraCourts.length === 1
                ? `Se agregó la cancha ${calendarExtraCourts[0]} (ahí juega otra categoría este día). Solo se ve: `
                : `Se agregaron las canchas ${calendarExtraCourts.join(", ")} (ahí juegan otras categorías este día). Solo se ven: `}
              {activeDivision?.name ?? "esta categoría"} programa en{" "}
              {calendarBaseCourts.length === 1 ? "su cancha" : `sus ${calendarBaseCourts.length} canchas`}.
            </p>
          ) : null}
        </>
      );

    return (
      <section
        ref={calendarDisclosureRef}
        className={`cal-work cal-work--overlay schedule-calendar-disclosure${plainList ? " is-list" : ""}${
          drawerState !== "idle" ? " has-sheet" : ""
        }${calendarRailOpen ? " has-rail" : ""}`}
        aria-label="Programar el día"
        data-calendar-workspace={listShown ? "list" : "grid"}
      >
        {renderCalendarRail(calendarSummaryScope)}
        <div className="cal-center schedule-calendar__matrix-wrap" ref={calendarMatrixRef} tabIndex={-1}>
          {plainList ? null : renderCalendarViewBar()}
          {listShown ? renderCalendarListView(offGridCount, proposalBar) : grid}
        </div>
        {!plainList ? renderCalendarDrawer(selectedIsDraft, selectedIsPublished, drawerState) : null}
      </section>
    );
  }

  /** Leyenda bajo la cuadrícula. */
  function renderCalendarLegend() {
    return (
      <div className="schedule-calendar__legend cal-legend" aria-label="Leyenda">
        <span className="schedule-calendar__legend-item schedule-calendar__legend-item--free">Libre</span>
        <span className="schedule-calendar__legend-item schedule-calendar__legend-item--busy">Ocupado</span>
        <span className="schedule-calendar__legend-item schedule-calendar__legend-item--blocked">Bloqueado</span>
        <span className="schedule-calendar__legend-item schedule-calendar__legend-item--selected">Seleccionado</span>
        <span className="schedule-calendar__legend-item schedule-calendar__legend-item--nofit">No cabe</span>
        {calendarHasMiniCourts ? (
          // Las mismas letras A / B que llevan las tarjetas de media cancha
          // en una columna sin dividir (y los encabezados de la 9-A / 9-B).
          <span
            className="schedule-calendar__legend-item schedule-calendar__legend-item--half"
            title="Partido en una mini cancha (9-A a 9-D): ocupa solo su parte de la cancha. En una cancha sin dividir sale como tarjeta angosta con su letra. Una cancha completa ocupa todas sus mini canchas."
            data-legend-half
          >
            <span className="schedule-timegrid__half-tag" aria-hidden="true">
              A
            </span>
            <span className="schedule-timegrid__half-tag" aria-hidden="true">
              B
            </span>
            Media cancha
          </span>
        ) : null}
        {showPublishUi && (unpublishedTotal > 0 || (calendarAllCategories && dayScopeUnpublished.byDate.size > 0)) ? (
          <span
            className="schedule-calendar__legend-item schedule-calendar__legend-item--unpublished"
            title="Horario que los jugadores aún no ven: se publica con «Publicar horarios»."
          >
            Sin publicar
          </span>
        ) : null}
        <small className="muted cal-legend__note">
          Tocar solo propone: nada se guarda sin «Confirmar». Teclado: flechas para moverte, Enter propone y el foco pasa a
          «Confirmar».
        </small>
      </div>
    );
  }

  /** Riel «Por programar» (izquierda; en el celular, hoja inferior plegable):
   * filtros de jornada, grupo, estatus y jugador, la cola y quién descansa. */
  function renderCalendarRail(scopeText: string) {
    const restLines = restScreenLines(selectedJornadaRests(calendarMatches), repeatedGroupNames);
    const pending = calendarOrderedUnscheduledMatches.length;
    return (
      <aside
        className={`cal-rail schedule-calendar__queue${calendarRailOpen ? " is-open" : ""}`}
        aria-label="Partidos por programar"
      >
        <div className="cal-rail__head">
          <p className="cal-rail__title schedule-calendar__queue-header">
            <span className="cal-eyebrow">Por programar</span>
            <strong className="cal-rail__count">{pending}</strong>
          </p>
          <button
            type="button"
            className="btn btn--ghost cal-rail__sheet-toggle"
            aria-expanded={calendarRailOpen}
            onClick={() => setCalendarRailOpen((open) => !open)}
          >
            {calendarRailOpen ? "Ocultar ▾" : `Ver (${pending}) ▴`}
          </button>
        </div>
        <div className="cal-rail__body">
          <p className="cal-rail__summary schedule-calendar__summary" data-calendar-summary>
            <span className="group-accordion__meta">
              {scopeText ? `${scopeText}: ` : ""}
              {pending} por programar · {calendarScheduledMatches.length} programados
              {showPublishUi && unpublishedHere.length > 0 ? ` · ${unpublishedHere.length} sin publicar` : ""} ·{" "}
              {calendarBaseCourts.length || "—"} canchas
            </span>
          </p>
          {canEditSchedule ? (
            <button type="button" className="btn btn--ghost cal-rail__auto" onClick={() => openCalendarSettings(true)}>
              ⚡ Ordenar automático
            </button>
          ) : null}
          <div className="cal-rail__filters">
            {resultsRoundFilter}
            {groupFilterRow}
            {renderStatusSearchFields()}
          </div>
          {noProgrammableBecauseCompleted ? (
            <p className="form-message cal-rail__note" data-no-toast>
              Todos los partidos visibles ya están jugados o cerrados; no hay partidos pendientes por programar.
            </p>
          ) : null}
          {pending === 0 ? (
            <p className="muted schedule-calendar__empty">No hay partidos sin programar con los filtros actuales.</p>
          ) : (
            <div className="schedule-calendar__match-list cal-rail__list">
              {calendarOrderedUnscheduledMatches.map((match) => (
                <button
                  type="button"
                  key={match.id}
                  className={`schedule-calendar__match-chip${selectedMatchId === match.id ? " is-selected" : ""}`}
                  onClick={() => {
                    handleCalendarQueueSelect(match);
                    setCalendarRailOpen(false);
                  }}
                >
                  <span>{matchShortLabel(match)}</span>
                  <small>
                    {tieSeriesLabel(match) ? `${tieSeriesLabel(match)} · ` : ""}
                    {displayMatchupLabel(match.round_label)} · {describeMatchGroup(match)}
                  </small>
                </button>
              ))}
            </div>
          )}
          {earlierPendingNotice}
          {/* En la vista «Lista» los descansos van debajo de la tabla de la jornada. */}
          {restLines.length > 0 && calendarDayViewShown === "grid" ? (
            <div className="schedule-rest-lines cal-rail__rests" data-no-toast data-rail-rests>
              {restLines.map((line) => (
                <p key={line.key} className="muted schedule-rest-line">
                  {line.text}
                </p>
              ))}
            </div>
          ) : null}
        </div>
      </aside>
    );
  }

  /** Filtros «Estatus» y «Buscar jugador» (riel o lista de jornadas). */
  function renderStatusSearchFields() {
    return (
      <>
        <label className="schedule-toolbar__field cal-field">
          <span className="schedule-toolbar__label">Estatus</span>
          <select className="t123-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as StatusFilter)}>
            {statusFilterOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label className="schedule-toolbar__field cal-field">
          <span className="schedule-toolbar__label">Buscar jugador</span>
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nombre o apellido…" />
        </label>
      </>
    );
  }

  /** Vista «Lista» (ocupa el lugar de la cuadrícula): «Del día» (hora →
   * cancha) o «De la jornada» (tabla con Editar → panel del partido). */
  /** Barra arriba de la cuadrícula / lista por cancha (Eduardo 2026-10-25):
   * «Por programar» y «Bloquear» se abren encima, así la cuadrícula usa todo
   * el ancho; en el celular, pestañas C1–C5 (una cancha a la vez). */
  function renderCalendarViewBar() {
    const pending = calendarOrderedUnscheduledMatches.length;
    return (
      <div className="cal-viewbar" data-calendar-viewbar>
        <div className="cal-viewbar__actions">
          <button
            type="button"
            className={`btn btn--ghost btn--compact cal-viewbar__btn${calendarRailOpen ? " is-active" : ""}`}
            aria-expanded={calendarRailOpen}
            onClick={() => setCalendarRailOpen((open) => !open)}
            data-calendar-rail-toggle
          >
            Por programar ({pending})
          </button>
          {canEditSchedule && calendarMode === "programar" ? (
            <button
              type="button"
              className="btn btn--ghost btn--compact cal-viewbar__btn"
              onClick={() => setCalendarMode("bloquear")}
              title={calendarEffectiveVenueLabel ? undefined : "Define la sede del torneo en Datos para bloquear"}
              data-calendar-block-entry
            >
              Bloquear
            </button>
          ) : null}
        </div>
        {isNarrowViewport && calendarCourtGroups.length > 1 ? (
          <div className="cal-viewbar__tabs" role="tablist" aria-label="Canchas">
            {calendarCourtGroups.map((court) => {
              const active = court === calendarMobileCourtKey;
              const canonical = courtDisplayName(court).replace(/^Cancha\s+/i, "");
              const short = /^\d+$/.test(canonical) ? `C${canonical}` : canonical;
              return (
                <button
                  key={court}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={`cal-viewbar__tab${active ? " is-active" : ""}`}
                  onClick={() => setCalendarMobileCourt(court)}
                  title={courtDisplayName(court)}
                  data-calendar-court-tab={court}
                >
                  {short}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  }

  /** «Programar aquí» de un hueco de la lista por cancha: con partido
   * elegido, pasa a la cuadrícula con la hora propuesta (se guarda con
   * «Confirmar»); sin partido, abre «Por programar» y espera la elección. */
  function handleCourtListSlot(court: string, time: string) {
    if (!canEditSchedule) return;
    if (!selectedMatch) {
      setCalendarPendingSlot({ court, time });
      setCalendarRailOpen(true);
      setCalendarNotice(`Elige un partido de «Por programar»: se propondrá en ${courtDisplayName(court)} a las ${time}.`);
      return;
    }
    setCalendarDayView("grid");
    handleCalendarCellClick(court, time);
  }

  /** Lista por cancha (maqueta Cal-Partidos-Grandes «B»): cada cancha una
   * columna de tarjetas del mismo alto, en orden de hora, con los huecos
   * libres («Libre 12:00–13:30 · Programar aquí»).
   *
   * Mini canchas: la ocupación es la MISMA que la cuadrícula y el servidor
   * (courtsOverlap / startAvailability; backend `_slot_conflict_match`): lo
   * de la Cancha 10 completa ocupa también la 10-A y la 10-B, y lo de una
   * mitad ocupa la 10 completa. Revisión Torni #587: antes se filtraba por
   * nombre exacto y la mitad salía «Libre» bajo un bloqueo de la completa. */
  function renderCourtListView() {
    if (!calendarGridRange || calendarGridColumns.length === 0) {
      return <p className="muted cal-grid__empty">Define una hora inicial/final válida y al menos una cancha en «Ajustes del día».</p>;
    }
    const dayStart = calendarWindow?.startMin ?? calendarGridRange.startMin;
    const dayEnd = calendarWindow ? calendarWindow.endMin + calendarMatchDurationMinutes : calendarGridRange.endMin;
    const canPlace = canEditSchedule && calendarMode === "programar";
    return (
      <div className="cal-courtlist" data-calendar-court-list>
        {calendarVisibleColumns.map((court) => {
          // Jugados incluidos (#588): ocupan la franja con la MISMA regla de
          // completa/mitades, así que «Libre» nunca se anuncia encima de uno.
          const items = calendarDrawnItems
            .filter((item) => courtsOverlap(item.court, court))
            .sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);
          const rows: ReactNode[] = [];
          let cursor = dayStart;
          const pushGap = (from: number, to: number) => {
            if (to - from < calendarMatchDurationMinutes) return;
            const fromTime = timeFromMinutes(from);
            rows.push(
              <div key={`gap-${from}`} className="cal-courtlist__gap" data-calendar-gap={`${court}|${fromTime}`}>
                <strong>
                  Libre {fromTime}–{timeFromMinutes(to)}
                </strong>
                {canPlace ? (
                  <button
                    type="button"
                    className="btn btn--ghost btn--compact"
                    disabled={calendarBusy}
                    onClick={() => handleCourtListSlot(court, fromTime)}
                    aria-label={`Programar aquí: ${courtDisplayName(court)} ${fromTime}`}
                  >
                    Programar aquí
                  </button>
                ) : null}
              </div>,
            );
          };
          for (const item of items) {
            if (item.startMin > cursor) pushGap(cursor, Math.min(item.startMin, dayEnd));
            // De otra cancha que comparte espacio: dice dónde está («En la
            // Cancha 10 completa» / «En la Cancha 10-A»).
            const sharedNote = calendarOccupantCourtNote(item.court, court).trim();
            rows.push(
              <div key={item.key} className="cal-courtlist__item" data-item-key={item.key} data-shared-court={sharedNote ? item.court : undefined}>
                {sharedNote ? (
                  <small className="cal-courtlist__shared">{sharedNote.charAt(0).toUpperCase() + sharedNote.slice(1)}</small>
                ) : null}
                {renderCalendarGridItem(item, {
                  lane: 0,
                  lanes: 1,
                  conflict: false,
                  span: Math.max(3, Math.round((item.endMin - item.startMin) / 15)),
                  half: halfOf(item.court),
                  narrow: false,
                })}
              </div>,
            );
            cursor = Math.max(cursor, item.endMin);
          }
          if (cursor < dayEnd) pushGap(cursor, dayEnd);
          return (
            <section key={court} className="cal-courtlist__col" aria-label={courtDisplayName(court)} data-calendar-court-col={court}>
              <h3 className="cal-courtlist__head">{courtDisplayName(court)}</h3>
              {rows.length > 0 ? rows : <p className="muted cal-courtlist__empty">Sin horario libre.</p>}
            </section>
          );
        })}
      </div>
    );
  }

  function renderCalendarListView(offGridCount: number, proposalBar: ReactNode) {
    return (
      <div className="cal-list" data-calendar-list={calendarListKindShown}>
        <div className="cal-list__head">
          <div className="segmented cal-seg" role="group" aria-label="Qué partidos listar">
            <button
              type="button"
              className={`segmented__btn${calendarListKindShown === "court" ? " is-active" : ""}`}
              aria-pressed={calendarListKindShown === "court"}
              onClick={() => {
                setCalendarListKind("court");
                setCalendarDayView("list");
              }}
              data-calendar-list-kind="court"
            >
              Por cancha
            </button>
            <button
              type="button"
              className={`segmented__btn${calendarListKindShown === "day" ? " is-active" : ""}`}
              aria-pressed={calendarListKindShown === "day"}
              onClick={() => {
                setCalendarListKind("day");
                setCalendarDayView("list");
              }}
              data-calendar-list-kind="day"
            >
              Del día
            </button>
            <button
              type="button"
              className={`segmented__btn${calendarListKindShown === "jornada" ? " is-active" : ""}`}
              aria-pressed={calendarListKindShown === "jornada"}
              onClick={() => {
                setCalendarListKind("jornada");
                setCalendarDayView("list");
              }}
              data-calendar-list-kind="jornada"
            >
              De la jornada
            </button>
          </div>
        </div>
        {isRoundRobinPlayoffFormat ? (
          <div className="schedule-phase-callout">
            <strong>Calendario por fase</strong>
            <span>{roundRobinPhaseSummary(phaseConfig)}</span>
            <small>Los partidos con grupo alimentan la tabla; los partidos sin grupo marcados como Playoffs ya son cruces reales del bracket.</small>
          </div>
        ) : null}
        {offGridCount > 0 ? (
          <p className="muted schedule-timegrid__note" data-calendar-offgrid={offGridCount}>
            {offGridCount} partido(s) programado(s) de esta fecha no caben en la cuadrícula: no tienen hora o cancha, o están en una
            cancha fuera de las {calendarGridColumns.length} columnas.
          </p>
        ) : null}
        {calendarListKindShown === "court" ? proposalBar : null}
        {renderForeignPick()}
        {calendarListKindShown === "court"
          ? renderCourtListView()
          : calendarListKindShown === "day"
            ? renderDayList()
            : renderScheduledWeekTable()}
      </div>
    );
  }

  /** Panel derecho (en el celular, hoja inferior): todo lo del partido tocado
   * (mover, motivo, posponer, quitar pospuesto, desprogramar, alineación) o
   * «Bloquear» (motivo y cómo se bloquea). */
  function renderCalendarDrawer(selectedIsDraft: boolean, selectedIsPublished: boolean, state: "block" | "match" | "idle") {
    return (
      <aside className={`cal-drawer is-${state}`} aria-label="Panel del partido" data-calendar-drawer={state}>
        {calendarMode === "bloquear" ? (
          <section className="cal-drawer__section cal-block" aria-label="Bloquear canchas" data-calendar-block-panel>
            <div className="cal-drawer__head">
              <h2 className="cal-eyebrow">Bloquear</h2>
              <button
                type="button"
                className="cal-drawer__close"
                onClick={() => setCalendarMode("programar")}
                aria-label="Salir de Bloquear"
              >
                ✕
              </button>
            </div>
            <p className="cal-drawer__text">
              Toca una hora libre para bloquear {calendarMatchDurationMinutes} min desde ahí, o un bloqueo para liberarlo. El
              llenado automático y la programación respetan los bloqueos.
            </p>
            <span className="cal-eyebrow">Motivo</span>
            <div className="schedule-calendar__block-controls cal-chips">
              {(["Mantenimiento", "Comida", "Reservada", "Otro"] as const).map((reason) => (
                <button
                  key={reason}
                  type="button"
                  className={`schedule-calendar__reason-chip${calendarBlockReason === reason ? " is-active" : ""}`}
                  aria-pressed={calendarBlockReason === reason}
                  onClick={() => setCalendarBlockReason(reason)}
                >
                  {reason}
                </button>
              ))}
            </div>
            {renderBlockRange()}
            <button type="button" className="btn btn--ghost cal-drawer__wide" onClick={() => setCalendarMode("programar")}>
              Programar
            </button>
          </section>
        ) : null}
        {selectedMatch ? renderSelectedMatchPanel(selectedIsDraft, selectedIsPublished) : null}
        {!selectedMatch && selectedPlayed && calendarMode === "programar" ? (
          <section className="cal-drawer__section cal-block-info" aria-label="Partido jugado" data-calendar-played-info={selectedPlayed.id}>
            <div className="cal-drawer__head">
              <h2 className="cal-eyebrow">Partido jugado</h2>
              <button
                type="button"
                className="cal-drawer__close"
                onClick={() => setSelectedPlayedId(null)}
                aria-label="Cerrar partido jugado"
              >
                ✕
              </button>
            </div>
            <p className="cal-drawer__text cal-played__names">{matchShortLabel(selectedPlayed)}</p>
            <dl className="cal-block-info__list">
              <dt>Cancha</dt>
              <dd>{courtDisplayName(selectedPlayed.court)}</dd>
              <dt>Horario</dt>
              <dd>{selectedPlayed.scheduled_time}</dd>
              <dt>Estado</dt>
              <dd>{playedChipLabel(selectedPlayed)}</dd>
              {selectedPlayed.score_text ? (
                <>
                  <dt>Marcador</dt>
                  <dd>{selectedPlayed.score_text}</dd>
                </>
              ) : null}
            </dl>
            <p className="muted cal-drawer__text">Ya se jugó: no se mueve desde el calendario.</p>
            <Link
              className="btn btn--ghost cal-drawer__wide"
              to={`/admin/tournaments/${tournamentId}/results${
                selectedPlayed.division_id != null ? `?division=${selectedPlayed.division_id}` : ""
              }`}
              data-calendar-played-edit
            >
              Editar resultado en Resultados →
            </Link>
          </section>
        ) : null}
        {!selectedMatch && selectedBlock && calendarMode === "programar" ? (
          <section className="cal-drawer__section cal-block-info" aria-label="Bloqueo" data-calendar-block-info={selectedBlock.id}>
            <div className="cal-drawer__head">
              <h2 className="cal-eyebrow">Bloqueo</h2>
              <button
                type="button"
                className="cal-drawer__close"
                onClick={() => setSelectedBlockId(null)}
                aria-label="Cerrar bloqueo"
              >
                ✕
              </button>
            </div>
            <dl className="cal-block-info__list">
              <dt>Cancha</dt>
              <dd>{courtDisplayName(selectedBlock.court)}</dd>
              <dt>Horario</dt>
              <dd>
                {selectedBlock.start_time}–{selectedBlock.end_time}
              </dd>
              <dt>Motivo</dt>
              <dd>{selectedBlock.reason || "Bloqueada"}</dd>
            </dl>
            {canEditSchedule ? (
              <button
                type="button"
                className="btn btn--primary cal-drawer__wide"
                disabled={calendarBusy}
                onClick={() => void handleSelectedBlockRelease(selectedBlock)}
              >
                Desbloquear
              </button>
            ) : null}
            <button type="button" className="btn btn--ghost cal-drawer__wide" onClick={() => setSelectedBlockId(null)}>
              Cancelar
            </button>
          </section>
        ) : null}
        {state === "idle" ? (
          <section className="cal-drawer__section cal-drawer__idle">
            <h2 className="cal-eyebrow">Partido</h2>
            <p className="cal-drawer__text">
              Toca un partido de «Por programar» o de la cuadrícula: aquí lo mueves, pospones o desprogramas.
            </p>
          </section>
        ) : null}
      </aside>
    );
  }

  /** Bloquear por rango (Eduardo 2026-10-15, maqueta Cal-Unico «B»). */
  function renderBlockRange() {
    const ends = blockRangeEndOptions();
    const { from, to } = blockRangeTimes();
    const startMin = parseTimeMinutes(from);
    const endMin = parseTimeMinutes(to);
    const valid = startMin !== null && endMin !== null && endMin > startMin && blockRange.courts.length > 0;
    const count = blockRange.courts.length;
    return (
      <div className="cal-blockrange" data-block-range>
        <span className="cal-eyebrow">Por rango</span>
        <div className="cal-slotpick__row cal-slotpick__row--two">
          <label className="cal-field">
            <span className="cal-eyebrow">Desde</span>
            <select className="t123-select"
              value={from}
              disabled={blockRange.allDay || calendarBusy}
              onChange={(event) => setBlockRange((current) => ({ ...current, from: event.target.value }))}
              data-block-from
            >
              <option value="">—</option>
              {calendarStartOptions.map((time) => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </select>
          </label>
          <label className="cal-field">
            <span className="cal-eyebrow">Hasta</span>
            <select className="t123-select"
              value={to}
              disabled={blockRange.allDay || calendarBusy}
              onChange={(event) => setBlockRange((current) => ({ ...current, to: event.target.value }))}
              data-block-to
            >
              <option value="">—</option>
              {ends.map((time) => (
                <option key={time} value={time} disabled={startMin !== null && (parseTimeMinutes(time) ?? 0) <= startMin}>
                  {time}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="cal-check">
          <input
            type="checkbox"
            checked={blockRange.allDay}
            onChange={(event) => setBlockRange((current) => ({ ...current, allDay: event.target.checked }))}
            data-block-allday
          />
          <span>Todo el día en esta cancha</span>
        </label>
        <div className="cal-blockrange__courts" role="group" aria-label="Canchas a bloquear" data-block-courts>
          {calendarOwnColumns.map((court) => {
            const checked = blockRange.courts.includes(court);
            return (
              <label key={court} className={`cal-check cal-check--chip${checked ? " is-active" : ""}`}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(event) =>
                    setBlockRange((current) => ({
                      ...current,
                      courts: event.target.checked
                        ? [...current.courts, court]
                        : current.courts.filter((item) => item !== court),
                    }))
                  }
                />
                <span>{courtDisplayName(court)}</span>
              </label>
            );
          })}
        </div>
        <button
          type="button"
          className="btn btn--primary cal-drawer__wide"
          onClick={() => void handleCalendarBlockRange()}
          disabled={!valid || calendarBusy}
          data-block-range-submit
        >
          {count > 1 ? `Bloquear ${count} canchas` : "Bloquear"}
        </button>
      </div>
    );
  }

  /** «Cambiar horario / cancha» del panel (Eduardo 2026-10-15): día del
   * torneo, hora del día y cancha (incluye medias canchas). Con hora y
   * cancha queda la misma propuesta que tocar la celda: «Confirmar». */
  function renderPanelSlotPicker(match: TournamentMatch) {
    const time = calendarProposal?.time ?? panelSlotDraft.time;
    const court = calendarProposal?.court ?? panelSlotDraft.court;
    const dayKnown = calendarDayChipList.some((chip) => chip.date === calendarDate);
    return (
      <div className="cal-slotpick" data-panel-slot-picker>
        <span className="cal-eyebrow">{match.scheduled_date ? "Cambiar horario / cancha" : "Programar aquí"}</span>
        <small className="muted">Toca otra hora en la cuadrícula, o elige aquí y luego «Confirmar»:</small>
        <div className="cal-slotpick__row">
          <select className="t123-select"
            aria-label="Día"
            value={calendarDate}
            onChange={(event) => selectCalendarDay(event.target.value)}
            disabled={calendarBusy}
            data-panel-slot-day
          >
            {!dayKnown ? <option value={calendarDate}>{calendarDate ? dayTitleEs(calendarDate) : "Día"}</option> : null}
            {calendarDayChipList.map((chip) => (
              <option key={chip.date} value={chip.date}>
                {dayTitleEs(chip.date)}
              </option>
            ))}
          </select>
          <select className="t123-select"
            aria-label="Hora"
            value={time}
            onChange={(event) => handlePanelSlotChange({ time: event.target.value, court })}
            disabled={calendarBusy}
            data-panel-slot-time
          >
            <option value="">Hora</option>
            {calendarStartOptions.map((option) => {
              const min = parseTimeMinutes(option) ?? 0;
              const fits = !court || calendarCellState(court, min).enabled || isCurrentCalendarSlot(match, court, option);
              return (
                <option key={option} value={option} disabled={!fits}>
                  {option}
                </option>
              );
            })}
          </select>
          <select className="t123-select"
            aria-label="Cancha"
            value={court}
            onChange={(event) => handlePanelSlotChange({ time, court: event.target.value })}
            disabled={calendarBusy}
            data-panel-slot-court
          >
            <option value="">Cancha</option>
            {calendarOwnColumns.map((option) => (
              <option key={option} value={option}>
                {courtDisplayName(option)}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  }

  /** Alineación por línea de una serie por equipos (singles: un jugador por
   * lado, solo sin resultado; dobles: los dos). Se guarda al elegir. */
  function renderSeriesLineup(match: TournamentMatch) {
    const lines = match.lines ?? [];
    if (lines.length === 0) return null;
    return (
      <div className="cal-lineup" data-calendar-lineup>
        <span className="cal-eyebrow">{tieSeriesLabel(match) ?? "Serie"} · Alineación</span>
        {lines.map((line, index) => (
          <div key={line.id} className="cal-lineup__row">
            <span className="cal-lineup__label">
              L{index + 1} · {LINE_KIND_LABELS[line.line_kind ?? ""] ?? line.line_kind ?? "Línea"}
            </span>
            <div className="cal-lineup__sides">
              {renderLineSide(line, match, "team1")}
              <span className="cal-lineup__vs">vs</span>
              {renderLineSide(line, match, "team2")}
            </div>
          </div>
        ))}
        <small className="muted">Singles: un jugador por lado, solo en líneas sin resultado. Dobles: juegan los dos.</small>
      </div>
    );
  }

  function renderSelectedMatchPanel(selectedIsDraft: boolean, selectedIsPublished: boolean) {
    if (!selectedMatch) return null;
    return (
      <section className="cal-drawer__section schedule-calendar__selected" aria-label="Partido seleccionado">
        <div className="cal-drawer__head">
          <h2 className="cal-eyebrow schedule-toolbar__label">Partido · Seleccionado</h2>
          <button
            type="button"
            className="cal-drawer__close"
            onClick={() => setSelectedMatchId(null)}
            aria-label="Limpiar selección"
            title="Limpiar selección"
          >
            ✕
          </button>
        </div>
        <strong className="cal-drawer__match">{matchShortLabel(selectedMatch)}</strong>
        <small className="muted">
          {[
            hasSeveralDivisions ? divisionForMatch(selectedMatch)?.name ?? null : null,
            tieSeriesLabel(selectedMatch),
            displayMatchupLabel(selectedMatch.round_label),
            describeGroup(selectedMatch.group_id),
          ]
            .filter(Boolean)
            .join(" · ")}
        </small>
        {selectedIsDraft ? (
          <span className="schedule-publish-tag is-unpublished" data-selected-publication="unpublished">
            Sin publicar
          </span>
        ) : selectedIsPublished ? (
          <span className="schedule-publish-tag is-published" data-selected-publication="published">
            Publicado · los jugadores ya lo ven
          </span>
        ) : null}
        {tournamentReferees.length > 0 ? (
          <label className="cal-field schedule-referee-field">
            <span className="cal-eyebrow">Árbitro</span>
            <select
              value={selectedMatch.referee_user_id ?? ""}
              disabled={calendarBusy || !canAssignReferees}
              className={`t123-select ${selectedMatch.referee_user_id == null ? "is-empty" : ""}`}
              onChange={(event) =>
                void handleAssignReferee(selectedMatch, event.target.value ? Number(event.target.value) : null)
              }
              data-selected-referee
            >
              <option value="">— Sin árbitro —</option>
              {tournamentReferees.map((ref) => (
                <option key={ref.user_id} value={ref.user_id}>
                  {ref.full_name}
                </option>
              ))}
            </select>
          </label>
        ) : selectedMatch.referee_name ? (
          <small className="muted">Árbitro: {selectedMatch.referee_name}</small>
        ) : null}
        {canEditSchedule && calendarMode === "programar" ? renderPanelSlotPicker(selectedMatch) : null}
        {selectedMatch.scheduled_date ? (
          <div className="schedule-change-reason">
            <small className="muted">
              Hoy: {formatDate(selectedMatch.scheduled_date)}
              {selectedMatch.scheduled_time ? ` · ${formatTimeLabel(selectedMatch.scheduled_time)}` : ""}
              {selectedMatch.court?.trim() ? ` · ${courtDisplayName(selectedMatch.court)}` : ""}
            </small>
            {selectedMatch.status === "postponed" ? (
              <p className="schedule-calendar__selected-hint is-postponed">
                Está pospuesto. Al moverlo a una hora nueva vuelve a «Programado». Si fue un error y
                debe quedarse en esta misma hora, usa «Quitar pospuesto».
                {selectedIsPublished
                  ? " Sus jugadores ya recibieron el aviso de pospuesto: al quitarlo les avisamos que se juega en esa hora."
                  : ""}
              </p>
            ) : null}
            {selectedIsDraft ? (
              <p className="schedule-calendar__selected-hint is-draft">
                Aún no se publica: muévelo o quítalo sin motivo y sin avisar a nadie.
                {calendarMode === "programar" ? " Toca la nueva hora y luego «Confirmar»." : ""}
              </p>
            ) : calendarMode === "programar" ? (
              <p className="schedule-calendar__selected-hint">
                Para cambiarlo de hora: toca la nueva hora en el calendario y luego «Confirmar». El motivo es opcional.
              </p>
            ) : null}
            {/* Sin publicar: nadie lo ha visto, así que ni motivo ni
                «Posponer» (el servidor responde 409 a posponerlo). */}
            {!selectedIsDraft ? (
              <>
                <label className="schedule-change-reason__field cal-field">
                  <span className="cal-eyebrow">Motivo del cambio (opcional)</span>
                  <input
                    type="text"
                    maxLength={200}
                    value={scheduleChangeReason}
                    onChange={(event) => setScheduleChangeReason(event.target.value)}
                    placeholder="Lluvia, retraso…"
                  />
                </label>
                <div className="schedule-change-reason__chips cal-chips" role="group" aria-label="Motivos frecuentes">
                  {SCHEDULE_CHANGE_REASONS.map((reason) => (
                    <button
                      type="button"
                      key={reason}
                      className={`result-sheet__chip${scheduleChangeReason === reason ? " is-active" : ""}`}
                      onClick={() => setScheduleChangeReason(reason)}
                    >
                      {reason}
                    </button>
                  ))}
                </div>
                <small className="muted">Los jugadores reciben el aviso del cambio con este motivo.</small>
              </>
            ) : null}
            <div className="cal-drawer__actions">
              {canEditSchedule && selectedMatch.status === "postponed" && !selectedMatch.winner_participant_id ? (
                <button
                  type="button"
                  className="btn btn--secondary cal-drawer__wide"
                  onClick={() => void handleUnpostponeSelected(selectedMatch)}
                  disabled={calendarBusy}
                >
                  Quitar pospuesto (dejar en esta hora)
                </button>
              ) : null}
              {canEditSchedule && canUnscheduleOne(selectedMatch) ? (
                <>
                  <button
                    type="button"
                    className="btn btn--ghost cal-drawer__wide is-danger"
                    onClick={() => void handleUnscheduleSelected(selectedMatch)}
                    disabled={calendarBusy}
                    title="Quita fecha, hora y cancha; vuelve a «Por programar» sin dejarlo pospuesto"
                  >
                    Desprogramar este partido
                  </button>
                  <small className="muted">
                    {selectedIsDraft
                      ? "Quitarlo no avisa a nadie: su horario aún no se publica."
                      : "Ya está publicado: sus jugadores recibirán un aviso de que su partido quedó sin horario (con el motivo, si eliges uno). Vuelve a «sin publicar»."}
                  </small>
                </>
              ) : null}
              {canPublishSchedule && selectedIsPublished && !selectedMatch.winner_participant_id ? (
                <>
                  <button
                    type="button"
                    className="btn btn--ghost cal-drawer__wide"
                    onClick={() => void handleUnpublishScheduleSelected(selectedMatch)}
                    disabled={calendarBusy}
                    data-unpublish-schedule
                  >
                    Despublicar (vuelve a borrador)
                  </button>
                  <small className="muted">
                    Conserva fecha, hora y cancha; los jugadores dejan de verlo y reciben un aviso de que quedó sin horario.
                  </small>
                </>
              ) : null}
              {canEditResults && selectedMatch.status !== "postponed" && !selectedIsDraft ? (
                <>
                  <button
                    type="button"
                    className="btn btn--ghost cal-drawer__wide"
                    onClick={() => void handlePostponeSelected(selectedMatch)}
                    disabled={calendarBusy}
                  >
                    Posponer sin nueva fecha
                  </button>
                  <small className="muted">
                    Pospuesto: queda sin fecha nueva y los jugadores lo ven como pospuesto (reciben un aviso con el motivo).
                  </small>
                </>
              ) : null}
            </div>
          </div>
        ) : (
          <p className="schedule-calendar__selected-hint">Toca una hora libre en la cuadrícula y luego «Confirmar».</p>
        )}
        {renderSeriesLineup(selectedMatch)}
      </section>
    );
  }

  function renderScheduledWeekTable() {
    const visible = scheduledListMatches;
    const dayFiltered = listDay !== "all";
    const dayLabel =
      listDay === "none" ? "sin fecha" : listDay !== "all" ? `el ${dayMonthEs(listDay) || listDay}` : "";
    const showDayFilter = scheduledWeekMatches.length > 0 || dayFiltered;
    // Eduardo 2026-10-05: también con calendario de canchas, quién descansa
    // la jornada elegida. Por los grupos de TODOS sus partidos con los
    // filtros (programados o no, de cualquier día): el descanso es de la
    // jornada, no de un día.
    const restLines = restScreenLines(selectedJornadaRests(calendarMatches), repeatedGroupNames);
    return (
      <section className="schedule-week-card cal-list__table" data-scheduled-week-table="programmed-matches">
        <div className="card__header-row schedule-week-card__header">
          <div>
            <h2 className="card__title">
              {scheduledListByJornada ? "Partidos de la jornada" : "Partidos programados y jugados"}
            </h2>
            <p className="card__subtitle">
              {scheduledListByJornada
                ? `${selectedJornadaPrintLabel() ?? "Jornada seleccionada"} · ${
                    dayFiltered ? `solo ${dayLabel}.` : "todas sus fechas, sin importar el día abierto en el calendario."
                  }`
                : dayFiltered
                  ? `Solo ${dayLabel} · usa Editar para abrir el calendario en el día del partido.`
                  : "Todas las jornadas, programados y ya jugados · usa Editar para abrir el calendario en el día del partido."}
            </p>
          </div>
          <span className="kbd schedule-week-card__count">
            {visible.filter((m) => !isPlayedStatus(m)).length} programado(s) ·{" "}
            {showPublishUi && visible.some((m) => isScheduleUnpublished(m))
              ? `${visible.filter((m) => isScheduleUnpublished(m)).length} sin publicar · `
              : ""}
            {visible.filter((m) => isPlayedStatus(m)).length} jugado(s)
          </span>
        </div>
        {/* Jornada, grupo, estatus y jugador se filtran en el riel «Por programar». */}
        {showDayFilter ? (
          <div className="schedule-toolbar__row schedule-table-filters schedule-listdays">
            <div className="schedule-toolbar__group">
              <span className="schedule-toolbar__label" id="schedule-listdays-label">
                Día
              </span>
              <div
                className="schedule-listdays__chips"
                role="group"
                aria-labelledby="schedule-listdays-label"
                data-list-days
              >
                {scheduledListDayOptions.map((option) => {
                  const active = listDay === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={`schedule-listdays__chip${active ? " is-active" : ""}`}
                      aria-pressed={active}
                      onClick={() => setListDay(option.value)}
                      data-list-day={option.value}
                    >
                      {option.label}
                      <span className="schedule-listdays__count">{option.count}</span>
                    </button>
                  );
                })}
              </div>
              {dayFiltered && listDay !== "none" ? (
                <small className="muted schedule-listdays__hint">Cambia junto con el día que elijas en el calendario.</small>
              ) : null}
            </div>
          </div>
        ) : null}

        {visible.length === 0 ? (
          <p className="muted schedule-week-card__empty">
            {dayFiltered
              ? "No hay partidos programados ni jugados ese día con los filtros actuales."
              : scheduledListByJornada
                ? "No hay partidos programados ni jugados en esta jornada con los filtros actuales."
                : "No hay partidos programados ni jugados con los filtros actuales."}
          </p>
        ) : (
          <div className="responsive-table-wrap schedule-week-table-wrap">
            <table className="data-table schedule-week-table">
              <thead>
                <tr>
                  <th>Enfrentamiento</th>
                  <th>Fecha</th>
                  <th>Hora</th>
                  <th>Cancha</th>
                  <th>Estatus</th>
                  <th>Editar</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((match) => (
                  <tr key={match.id} style={matchDivisionStyle(match)}>
                    <td data-label="Enfrentamiento">
                      <div className="schedule-week-matchup">
                        <strong>{matchShortLabel(match)}</strong>
                        <span className="muted">
                          {tieSeriesLabel(match) ? `${tieSeriesLabel(match)} · ` : ""}
                          {displayMatchupLabel(match.round_label)} · {describeMatchGroup(match)}
                        </span>
                        {match.schedule_change_reason ? (
                          <span className="schedule-week-matchup__reason">Motivo del cambio: {match.schedule_change_reason}</span>
                        ) : null}
                      </div>
                    </td>
                    <td data-label="Fecha">{match.scheduled_date ? formatDate(match.scheduled_date) : "—"}</td>
                    <td data-label="Hora" className="schedule-cell-mono">
                      {match.scheduled_time ? formatTimeLabel(match.scheduled_time) : "—"}
                    </td>
                    <td data-label="Cancha">{match.court?.trim() ? courtDisplayName(match.court) : "—"}</td>
                    <td data-label="Estatus">
                      <span className={matchStatusPill(match).className}>
                        {matchStatusPill(match).label}
                      </span>
                      {canCancelByDrop(match) ? (
                        <>
                          {" "}
                          <button
                            type="button"
                            className="btn btn--compact btn--danger schedule-drop-cancel"
                            onClick={() => void handleCancelByDrop(match)}
                            disabled={savingMatchId === match.id}
                            data-cancel-by-drop={match.id}
                          >
                            Cancelar por baja
                          </button>
                        </>
                      ) : null}
                      {showPublishUi && isScheduleUnpublished(match) ? (
                        <>
                          {" "}
                          <span
                            className="kbd kbd--unpublished"
                            title="Los jugadores aún no ven este horario: se publica con «Publicar horarios»."
                            data-unpublished-pill
                          >
                            sin publicar
                          </span>
                        </>
                      ) : null}
                    </td>
                    <td data-label="Editar">
                      {isPlayedStatus(match) ? (
                        <span className="muted" title="Ya se jugó: el marcador se corrige en Resultados">—</span>
                      ) : (
                        <button
                          type="button"
                          className="btn btn--ghost btn--compact"
                          onClick={() => handleEditScheduledMatch(match)}
                          title="Abrir el calendario en el día de este partido para moverlo o posponerlo"
                        >
                          Editar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {restLines.length > 0 ? (
          <div className="schedule-rest-lines" data-no-toast data-jornada-rests>
            {restLines.map((line) => (
              <p key={line.key} className="muted schedule-rest-line">
                {line.text}
              </p>
            ))}
          </div>
        ) : null}
      </section>
    );
  }

  function computeGroupSummary(list: TournamentMatch[]) {
    let scheduled = 0;
    let unscheduled = 0;
    let played = 0;
    // Eduardo 2026-10-05 (torneo 69): un cancelado (por una baja o no) ya no
    // cuenta como «sin programar» ni «programado»; se cuenta aparte.
    let cancelledByDrop = 0;
    let cancelled = 0;
    for (const m of list) {
      if (isPlayedStatus(m)) {
        played += 1;
      } else if (isCancelledByDrop(m)) {
        cancelledByDrop += 1;
      } else if (m.status === "cancelled") {
        cancelled += 1;
      } else if (resultsOnly ? isProgrammedStatus(m) : !isUnscheduled(m)) {
        scheduled += 1;
      } else {
        unscheduled += 1;
      }
    }
    return { total: list.length, scheduled, unscheduled, played, cancelledByDrop, cancelled };
  }
}

